// ---------------------------------------------------------------------------
// Internationalization (i18n) — locale state + translation dictionaries (CLE-277).
//
// Mirrors the module-level pattern used by lib/testMode.ts: a tiny module that
// holds the active locale in a variable backed by localStorage, so both the
// React `I18nProvider` and any non-component code share one source of truth.
//
// Translations are flat key dictionaries keyed by a dotted namespace, e.g.
// `sidebar.dashboard`, `dashboard.revenue`, `common.save`. The `t(key)` helper
// looks up the key for the active locale and falls back to the RO string, then
// to the raw key if missing — so an untranslated key is always visible rather
// than blank.
//
// Default locale is Romanian ('ro'); the app's primary audience is RO SRLs.
// ---------------------------------------------------------------------------

export type Locale = 'ro' | 'en';

const STORAGE_KEY = 'ccc.locale';
const DEFAULT_LOCALE: Locale = 'ro';

function readInitial(): Locale {
  if (typeof localStorage === 'undefined') return DEFAULT_LOCALE;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'ro' || stored === 'en') return stored;
  } catch {
    // localStorage may be unavailable (private mode); fall through.
  }
  return DEFAULT_LOCALE;
}

let locale: Locale = readInitial();

type Listener = (locale: Locale) => void;
const listeners = new Set<Listener>();

/** Synchronous read used outside React (e.g. data/formatting helpers). */
export function getLocale(): Locale {
  return locale;
}

/** Set the active locale, persist it and notify subscribers. */
export function setLocale(next: Locale): void {
  if (next === locale) return;
  locale = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Ignore storage failures (private mode, quota) — state still lives in memory.
  }
  try {
    document.documentElement.lang = next;
  } catch {
    // ignore (non-browser env)
  }
  for (const listener of listeners) listener(next);
}

/** Subscribe to locale changes (used by the React provider). Returns unsubscribe. */
export function subscribeLocale(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Dictionaries
// ---------------------------------------------------------------------------

type Dict = Record<string, string>;

const ro: Dict = {
  // Date picker
  'datePicker.placeholder': 'Alege data',
  'datePicker.clear': 'Șterge data',
  'datePicker.previousMonth': 'Luna anterioară',
  'datePicker.nextMonth': 'Luna următoare',
  'datePicker.today': 'Astăzi',
  'timePicker.placeholder': 'Alege ora',
  'timePicker.clear': 'Șterge ora',
  'timePicker.hour': 'Ora',
  'timePicker.minute': 'Minut',
  'timePicker.now': 'Acum',

  // Sidebar
  'sidebar.brand': 'Firmio',
  'sidebar.dashboard': 'Dashboard',
  'sidebar.dailySummary': 'Rezumat zilnic',
  'sidebar.weeklySummary': 'Rezumat săptămânal',
  'sidebar.chat': 'Firmio AI',
  'sidebar.mcp': 'Firmio MCP',
  'mcp.title': 'Firmio MCP',
  'mcp.subtitle': 'Firmio AI în Claude Desktop, claude.ai, Claude Code sau IDE-ul tău.',
  'mcp.intro':
    'Firmio expune uneltele lui Firmio AI printr-un server MCP (Model Context Protocol). Îți adaugi instanța ca „conector” în Claude, te loghezi cu contul tău Firmio, iar Claude lucrează în Firmio în numele tău.',
  'mcp.intro.sameTools':
    'Aceleași unelte ca în chat-ul Firmio AI: sarcini, proiecte, lead-uri, clienți, financiar, întâlniri, KB.',
  'mcp.intro.samePermissions':
    'Exact permisiunile contului tău din Firmio, nimic în plus; un administrator le schimbă din Setări → Roluri și permisiuni.',
  'mcp.intro.yourPlan':
    'Modelul și conversația sunt ale aplicației Claude, pe abonamentul tău Claude; Firmio nu consumă API.',
  'mcp.intro.noFiles':
    'Nu merge pe acest canal: încărcarea de facturi și contracte din fișiere atașate (folosește chat-ul din aplicație).',
  'mcp.address.title': 'Adresa serverului',
  'mcp.address.subtitle': 'Aceeași adresă pentru orice client MCP.',
  'mcp.copy': 'Copiază',
  'mcp.claude.title': 'Claude Desktop și claude.ai',
  'mcp.claude.subtitle': 'Conector personalizat (plan Claude plătit).',
  'mcp.claude.step1': 'În Claude: Settings → Connectors → „Add custom connector”.',
  'mcp.claude.step2': 'Nume: Firmio. Lipește adresa serverului de mai sus și apasă „Add”.',
  'mcp.claude.step3':
    'Apasă „Connect”: se deschide pagina de login Firmio; loghează-te și apasă „Permite accesul”.',
  'mcp.claude.step4':
    'Într-o conversație nouă, activează conectorul Firmio și întreabă, de exemplu, „ce sarcini am săptămâna asta?”.',
  'mcp.claude.button': 'Copiază adresa și deschide Connectors în Claude',
  'mcp.claude.buttonCopied': 'Adresa e copiată, se deschide Claude…',
  'mcp.claude.buttonHint':
    'Claude nu are încă un link de instalare pentru conectori remote, așa că butonul copiază adresa și deschide pagina unde o lipești. Pe un plan Team/Enterprise conectorul îl adaugă un owner din Organization settings → Connectors, apoi fiecare se conectează cu contul lui Firmio.',
  'mcp.code.title': 'Claude Code',
  'mcp.code.subtitle': 'O comandă în terminal, apoi /mcp → Firmio → Authenticate.',
  'mcp.code.hint':
    'Login-ul se face în browser, pe aceeași pagină Firmio; token-ul rămâne în Claude Code.',
  'mcp.ide.title': 'Cursor și VS Code',
  'mcp.ide.subtitle': 'Instalare cu un click, dacă ai aplicația instalată.',
  'mcp.ide.cursor': 'Adaugă în Cursor',
  'mcp.ide.vscode': 'Adaugă în VS Code',
  'mcp.ide.hint':
    'Link-ul deschide aplicația cu serverul precompletat; la prima folosire ți se cere login-ul Firmio.',
  'mcp.security.title': 'Securitate',
  'mcp.security.subtitle': 'Cum e protejat accesul.',
  'mcp.security.oauth':
    'Autentificare OAuth 2.1 cu PKCE: nicio parolă nu ajunge în Claude; te loghezi doar pe pagina Firmio.',
  'mcp.security.rbac':
    'Lista de unelte e filtrată după rolul tău, iar fiecare apel e verificat din nou pe server.',
  'mcp.security.tokens':
    'Token-ul de acces expiră după o oră și se reînnoiește singur până la 90 de zile de inactivitate; resetarea parolei sau dezactivarea contului îl invalidează.',
  'mcp.security.revoke': 'Poți deconecta oricând o aplicație din lista de mai jos sau din Profil.',
  'mcp.security.conversations':
    'Conversațiile rămân în aplicația Claude; Firmio înregistrează doar acțiunile executate.',
  'mcp.tips.title': 'Sfaturi',
  'mcp.tips.context':
    'La începutul unei discuții Claude apelează singur „firmio_context” ca să afle data, snapshot-ul firmei și memoriile relevante; dacă pare să nu știe ce zi e, cere-i să-l apeleze.',
  'mcp.tips.plan':
    'Conectorii personalizați cer un plan Claude plătit (Pro, Max, Team sau Enterprise).',
  'mcp.tips.noAccess':
    'Dacă primești „nu are acces la Firmio AI”, un administrator trebuie să bifeze Firmio AI pentru rolul tău în Setări → Roluri și permisiuni.',
  'profile.connectedApps.guideLink': 'Cum te conectezi: ghidul Firmio MCP →',
  'sidebar.teamChat': 'Chat echipă',
  // Firmio Voice — asistentul vocal (GPT-Live) peste knowledge base.
  'sidebar.voice': 'Firmio Voice',
  'chat.voiceMode': 'Mod vocal',
  'voice.backToChat': 'Înapoi la Firmio AI',
  'voice.title': 'Firmio Voice',
  'voice.subtitle':
    'Vorbește cu Firmio AI. Răspunde vocal, în română, din knowledge base și din datele companiei.',
  'voice.modelLabel': 'Voce: {voice} ({model}) · Raționament: {backend}',
  'voice.start': 'Începe conversația',
  'voice.end': 'Încheie conversația',
  'voice.mute': 'Oprește microfonul',
  'voice.unmute': 'Pornește microfonul',
  'voice.newSession': 'Apasă microfonul pentru o conversație nouă',
  'voice.hintLive': 'Vorbește natural. Poți întrerupe oricând.',
  'voice.phase.idle': 'Apasă microfonul ca să începi',
  'voice.phase.requestingMic': 'Cer acces la microfon…',
  'voice.phase.connecting': 'Mă conectez…',
  'voice.phase.live': 'În direct',
  'voice.phase.ending': 'Închei conversația…',
  'voice.phase.ended': 'Conversație încheiată',
  'voice.phase.error': 'Nu am putut porni conversația',
  'voice.activity.thinking': 'Se gândește…',
  'voice.activity.tool': 'Caută în Firmio…',
  'voice.activity.toolNamed': 'Caută în {label}…',
  'voice.captions.empty': 'Transcriptul conversației apare aici. Încearcă, de exemplu:',
  'voice.captions.listening': 'Ascult. Spune ce vrei să afli despre firmă.',
  'voice.captions.you': 'Tu',
  'voice.captions.assistant': 'Firmio',
  'voice.example1': 'Ce proiecte avem în lucru și care sunt în urmă?',
  'voice.example2': 'Ce am discutat ultima dată cu clientul RetuRO?',
  'voice.example3': 'Creează un task în ADNV: verificat contractul, până vineri.',
  'voice.elapsed': 'Durată',
  'voice.cost': 'Cost estimat (audio)',
  'voice.endedSummary': 'Conversația a durat {duration}.',
  'voice.viewConversation': 'Vezi conversația în Firmio AI',
  'voice.closeReason.expired': 'S-a atins durata maximă a unei sesiuni.',
  'voice.closeReason.budget': 'Bugetul lunar AI a fost epuizat.',
  'voice.closeReason.connectionLost': 'Conexiunea s-a pierdut.',
  'voice.notConfigured':
    'Asistentul vocal nu este configurat. Adaugă cheia OpenAI în Setări → Integrări (sau OPENAI_API_KEY pe server).',
  'voice.demoMode': 'Asistentul vocal nu este disponibil în modul demo.',
  'voice.error.micDenied':
    'Accesul la microfon a fost refuzat. Permite microfonul pentru acest site din setările browserului și încearcă din nou.',
  'voice.error.micUnavailable': 'Nu am găsit un microfon disponibil.',
  'voice.error.notConfigured': 'Asistentul vocal nu este configurat pe server.',
  'voice.error.noCredits':
    'Contul OpenAI nu mai are credit, deci Firmio Voice nu poate porni. Administratorii au fost alertați.',
  'voice.error.budget':
    'Bugetul lunar AI a fost epuizat. Un administrator îl poate ajusta din Setări.',
  'voice.error.busy':
    'Prea multe conversații vocale active acum. Încearcă din nou în câteva minute.',
  'voice.error.network':
    'Nu am putut porni sesiunea cu OpenAI. Verifică conexiunea și cheia OpenAI, apoi încearcă din nou.',
  'voice.error.generic': 'A apărut o eroare neașteptată. Încearcă din nou.',
  'voice.settings.button': 'Voce: {voice}',
  'voice.settings.title': 'Vocea asistentului',
  'voice.settings.subtitle':
    'Ascultă mostrele și alege vocea cu care îți răspunde Firmio. Alegerea e a ta, nu a firmei.',
  'voice.settings.appliesNext':
    'Conversația curentă păstrează vocea de acum; alegerea se aplică la următoarea.',
  'voice.settings.greetingHint': 'Fiecare mostră e generată de vocea reală GPT-Live și spune:',
  'voice.settings.group.recommended': 'Recomandate',
  'voice.settings.group.live': 'Catalog GPT-Live',
  'voice.settings.group.classic': 'Voci clasice OpenAI',
  'voice.settings.play': 'Ascultă mostra',
  'voice.settings.use': 'Folosește',
  'voice.settings.selected': 'Aleasă',
  'voice.settings.default': 'implicit',
  'voice.settings.sampleError': 'Mostra nu a putut fi redată. Încearcă din nou.',
  'voice.endedSuggestions': '{count} propuneri de aprobat',
  'voice.reviewSuggestions': 'Verifică propunerile în Firmio AI',
  'voice.suggestions.title': 'Propuneri din conversația vocală',
  'voice.suggestions.pendingCount': '{count} de aprobat',
  'voice.suggestions.allDecided': 'toate au fost decise',
  'voice.suggestions.hint':
    'Nimic nu a fost creat încă. Aprobă ce e în regulă: taskurile și reminderele se creează atunci, iar întâlnirile Meet se programează și abia atunci pleacă invitațiile pe email.',
  'voice.suggestions.accept': 'Creează',
  'voice.suggestions.run': 'Execută',
  'voice.suggestions.acceptMeeting': 'Programează și invită',
  'voice.suggestions.dismiss': 'Ignoră',
  'voice.suggestions.restore': 'Readu',
  'voice.suggestions.accepted': 'Creat',
  'voice.suggestions.failed': 'Nu a mers',
  'voice.suggestions.open': 'Deschide {label}',
  'voice.suggestions.openMeeting': 'Deschide întâlnirea Meet',
  'voice.suggestions.showDecided': 'Arată și pe cele decise ({count})',
  'voice.suggestions.hideDecided': 'Ascunde-le pe cele decise',
  'voice.suggestions.actionError': 'Acțiunea a eșuat. Încearcă din nou.',
  // Firmio Meet — întâlniri video cu invitați prin link.
  'sidebar.meet': 'Firmio Meet',
  'meet.title': 'Firmio Meet',
  'meet.subtitle':
    'Întâlniri video cu echipa și cu clienții, direct din browser. Apelurile sunt transcrise automat.',
  'meet.instant': 'Întâlnire acum',
  'meet.instantNamePrompt':
    'Cum se numește întâlnirea? Apare în istoric și în jurnalul de întâlniri.',
  'meet.instantStart': 'Pornește',
  'meet.schedule': 'Programează',
  'meet.testMode': 'Meet e dezactivat în Test Mode.',
  'meet.tabs.upcoming': 'Viitoare',
  'meet.tabs.past': 'Istoric',
  'meet.emptyUpcoming': 'Nicio întâlnire programată. Pornește una acum sau programeaz-o.',
  'meet.emptyPast': 'Nicio întâlnire încheiată încă.',
  'meet.joinPlaceholder': 'Introdu un cod sau un link de întâlnire',
  'meet.joinAction': 'Participă',
  'meet.joinHint': 'Ai primit un link? Lipește-l aici ca să intri direct.',
  'meet.hero.title': 'Întâlniri video, în Firmio',
  'meet.hero.subtitle':
    'Pornești o întâlnire într-o secundă, inviți pe oricine printr-un link, iar la final transcriptul ajunge singur în jurnalul de întâlniri.',
  'meet.empty.upcomingTitle': 'Nicio întâlnire programată',
  'meet.empty.upcomingHint': 'Pornește una acum sau programeaz-o pentru mai târziu.',
  'meet.empty.pastTitle': 'Încă nu s-a încheiat nicio întâlnire',
  'meet.empty.pastHint': 'După fiecare întâlnire găsești aici transcriptul și participanții.',
  'meet.search.placeholder': 'Caută după titlu, persoană, client sau proiect',
  'meet.search.clear': 'Șterge căutarea',
  'meet.search.emptyTitle': 'Nicio întâlnire pentru „{q}”',
  'meet.search.emptyHint': 'Încearcă alt cuvânt sau uită-te și în tabul celălalt.',
  'meet.day.today': 'Azi',
  'meet.day.tomorrow': 'Mâine',
  'meet.day.yesterday': 'Ieri',
  'meet.cancel': 'Anulează',
  'meet.confirmCancel': 'Anulezi întâlnirea? Invitații primesc un email de anulare.',
  'meet.hostedBy': 'organizată de {name}',
  'meet.details.when': '{day} la {time}',
  'meet.details.joinLink': 'Link pentru conectare: {url}',
  'meet.status.scheduled': 'Programată',
  'meet.status.live': 'În desfășurare',
  'meet.status.ended': 'Încheiată',
  'meet.status.cancelled': 'Anulată',
  'meetPreview.when': 'Când',
  'meetPreview.duration': 'Durată',
  'meetPreview.host': 'Organizator',
  'meetPreview.client': 'Client',
  'meetPreview.participants': 'Participanți',
  'meet.join': 'Intră',
  'meet.open': 'Deschide',
  'meet.viewTranscript': 'Vezi transcriptul',
  'meet.copyLink': 'Copiază linkul',
  'meet.copyDetails': 'Copiază detaliile',
  'meet.copied': 'Copiat',
  'meet.you': 'Tu',
  'meet.guest': 'invitat',
  'meet.notFound': 'Întâlnirea nu există sau a fost ștearsă.',
  'meet.back': 'Înapoi la Meet',
  'meet.backToApp': 'Înapoi în Firmio',
  'meet.delete': 'Șterge întâlnirea',
  'meet.confirmDelete':
    'Ștergi întâlnirea „{title}” și transcriptul ei din istoric? Intrarea din jurnalul de întâlniri rămâne.',
  'meet.noParticipants': 'Nimeni nu a intrat în întâlnire.',
  'meet.participants': '{count} în întâlnire',
  'meet.sharingYours': 'Îți partajezi ecranul. Ceilalți îl văd acum.',
  'meet.waitingRoom': 'Sala de așteptare',
  'meet.admit': 'Admite',
  'meet.deny': 'Respinge',
  'meet.admitPopup.title': '{name} vrea să intre',
  'meet.admitPopup.subtitle': 'în întâlnirea „{title}”',
  'meet.admitPopup.guest': 'Invitat din afara firmei',
  'meet.admitPopup.accept': 'Acceptă',
  'meet.admitPopup.deny': 'Respinge',
  'meet.admitPopup.later': 'Mai târziu',
  'meet.admitPopup.acceptAll': 'Acceptă toți ({count})',
  'meet.admitPopup.more': '+{count} în așteptare',
  'meet.admitPopup.keys': 'Enter = Acceptă · Esc = Mai târziu',
  'meet.inMeeting': 'În întâlnire',
  'meet.layout.grid': 'Toți pe ecran',
  'meet.layout.speaker': 'Vorbitorul mare',
  'meet.transcript.off': 'Transcrierea nu este configurată pe server.',
  'meet.transcript.empty':
    'Nimic transcris încă. Replicile apar la câteva secunde după ce vorbește cineva.',
  'meet.transcript.hint':
    'Întâlnirea este transcrisă automat. Invitații sunt informați, dar nu văd transcriptul.',
  'meet.transcript.liveElsewhere': 'Transcriptul se vede în întâlnire, cât timp e în desfășurare.',
  'meet.log.written':
    'Transcriptul a fost trecut în jurnalul de întâlniri, cu rezumat, decizii și action items.',
  'meet.log.open': 'Deschide în Întâlniri',
  'meet.log.skipped':
    'Nimeni nu a intrat în această întâlnire, așa că nu a fost trecută în jurnal.',
  'meet.log.pending': 'Transcriptul se trece în jurnalul de întâlniri în câteva minute.',
  'meet.log.create': 'Trece în jurnal',
  'meet.controls.mute': 'Oprește microfonul',
  'meet.controls.unmute': 'Pornește microfonul',
  'meet.controls.muted': 'Microfon oprit',
  'meet.controls.cameraOn': 'Pornește camera',
  'meet.controls.cameraOff': 'Oprește camera',
  'meet.controls.share': 'Partajează ecranul',
  'meet.controls.stopSharing': 'Oprește partajarea',
  'meet.controls.people': 'Participanți',
  'meet.controls.transcript': 'Transcript',
  'meet.controls.chat': 'Chat',
  'meet.controls.leave': 'Ieși',
  // In-meeting chat (everyone in the room; kept with the meeting)
  'meet.chat.title': 'Chat',
  'meet.chat.placeholder': 'Scrie un mesaj…',
  'meet.chat.send': 'Trimite',
  'meet.chat.empty':
    'Niciun mesaj încă. Ce scrii aici văd toți cei din întâlnire și rămâne în istoricul ei.',
  'meet.chat.none': 'Nu s-a scris nimic în chat.',
  'meet.chat.sendFailed': 'Mesajul nu a putut fi trimis. Încearcă din nou.',
  // Recording (host's screen + everyone's audio → Google Drive)
  'meet.recording.start': 'Înregistrează',
  'meet.recording.stop': 'Oprește înregistrarea',
  'meet.recording.badge': 'REC {time}',
  'meet.recording.you': 'Înregistrezi întâlnirea ({seconds} s)',
  'meet.recording.by': '{name} înregistrează',
  'meet.recording.error.denied': 'Nu ai ales un ecran de înregistrat.',
  'meet.recording.error.unsupported': 'Browserul nu poate înregistra (încearcă Chrome pe desktop).',
  'meet.recording.error.upload':
    'Înregistrarea s-a oprit: nu am putut trimite datele către server.',
  'meet.recording.error.generic': 'Înregistrarea nu a putut porni.',
  'meet.recordings.title': 'Înregistrări',
  'meet.recordings.hint': 'Salvate în Google Drive, în Meet / <data> <titlu>.',
  'meet.recordings.none': 'Nicio înregistrare pentru această întâlnire.',
  'meet.recordings.disabled':
    'Înregistrarea are nevoie de Google Drive conectat în Setări → Integrări.',
  'meet.recordings.status.RECORDING': 'se înregistrează',
  'meet.recordings.status.UPLOADING': 'se urcă în Drive…',
  'meet.recordings.status.READY': 'gata',
  'meet.recordings.status.FAILED': 'a eșuat',
  'meet.recordings.download': 'Descarcă',
  'meet.recordings.open': 'Deschide',
  'meet.recordings.play': 'Redă',
  'meet.recordings.badge': 'Înregistrare',
  'meet.recordings.watch': 'Vezi înregistrarea',
  'meet.recordings.playerTitle': 'Înregistrare',
  'meet.recordings.close': 'Închide',
  'meet.recordings.playError':
    'Browserul nu poate reda înregistrarea aici. Descarc-o și deschide-o cu un player (VLC, Chrome).',
  'meet.controls.endForAll': 'Închide pentru toți',
  'meet.controls.confirmEndForAll':
    'Închizi întâlnirea pentru toți participanții? Transcriptul se salvează și se trece în jurnal.',
  'meet.error.micDenied': 'Microfonul e blocat. Permite accesul din setările browserului.',
  'meet.error.cameraDenied': 'Camera e blocată. Permite accesul din setările browserului.',
  'meet.error.connect': 'Conectarea la întâlnire a eșuat. Încearcă din nou.',
  'meet.lobby.notOpen': 'Întâlnirea nu s-a deschis încă.',
  'meet.lobby.full': 'Întâlnirea e plină.',
  'meet.lobby.ended': 'Întâlnirea s-a încheiat.',
  'meet.lobby.left': 'Ai ieșit din întâlnire. Poți reintra din același link.',
  'meet.lobby.cancelled': 'Întâlnirea a fost anulată.',
  'meet.lobby.notConfigured':
    'Întâlnirile video nu sunt configurate. Un administrator le poate configura din Setări → Integrări → LiveKit.',
  'meet.lobby.checkLink': 'Verifică linkul primit sau cere unul nou organizatorului.',
  'meet.lobby.denied': 'Organizatorul nu te-a admis în întâlnire.',
  'meet.lobby.removed': 'Organizatorul te-a scos din întâlnire.',
  'meet.moderate.mute': 'Oprește-i microfonul',
  'meet.moderate.alreadyMuted': 'Microfonul e deja oprit',
  'meet.moderate.cameraOff': 'Oprește-i camera',
  'meet.moderate.cameraAlreadyOff': 'Camera e deja oprită',
  'meet.moderate.remove': 'Scoate din întâlnire',
  'meet.moderate.removeAsk': 'Scoți?',
  'meet.moderate.removeConfirm': 'Scoate',
  'meet.moderate.removeMessage':
    'Scoți pe {name} din întâlnire? Nu va putea reintra decât dacă îl admiți din nou.',
  'meet.lobby.thanks': 'Mulțumim că ai participat.',
  'meet.lobby.joining': 'Se conectează…',
  'meet.lobby.waiting': 'Aștepți să fii admis în întâlnire…',
  'meet.lobby.hostNotIn':
    'Organizatorul nu a intrat încă. Vei fi admis când intră cineva din echipă.',
  'meet.lobby.transcribedNotice':
    'Această întâlnire este transcrisă automat pentru notițele echipei organizatoare.',
  'meet.lobby.joiningAs': 'Intri ca {name}.',
  'meet.lobby.name': 'Numele tău',
  'meet.lobby.namePlaceholder': 'ex. Ioana Popescu',
  'meet.lobby.email': 'E-mailul tău',
  'meet.lobby.emailPlaceholder': 'ex. ioana@firma.ro',
  'meet.lobby.emailNotice': 'E-mailul va fi folosit pentru a primi minuta întâlnirii pe mail.',
  'meet.lobby.colleagueSignIn': 'Faci parte din echipă?',
  'meet.lobby.signIn': 'Intră în cont',
  'meet.lobby.micOn': 'Microfon pornit',
  'meet.lobby.micOff': 'Microfon oprit',
  'meet.lobby.camOn': 'Cameră pornită',
  'meet.lobby.camOff': 'Cameră oprită',
  'meet.lobby.opensAt': 'Linkul se deschide la {time}.',
  'meet.lobby.join': 'Intră în întâlnire',
  'meet.lobby.askToJoin': 'Cere să intri',
  'meet.form.title': 'Titlu',
  'meet.form.titlePlaceholder': 'ex. Ofertă Construct Vest',
  'meet.form.date': 'Data',
  'meet.form.time': 'Ora',
  'meet.form.duration': 'Durata',
  'meet.form.colleagues': 'Colegi',
  'meet.form.noColleagues': 'Niciun coleg de invitat.',
  'meet.form.guests': 'Invitați din afară',
  'meet.form.guestsPlaceholder': 'email@client.ro, Enter',
  'meet.form.guestsHint':
    'Primesc un email cu linkul și invitația în calendar. La intrare așteaptă să fie admiși.',
  'meet.form.client': 'Client',
  'meet.form.project': 'Proiect',
  'meet.form.none': 'Fără',
  'meet.form.projectHint':
    'După întâlnire, transcriptul ajunge în jurnalul de întâlniri; dacă e legată de un proiect, action items devin taskuri.',
  'meet.form.submit': 'Programează și trimite invitațiile',
  'meet.form.editTitle': 'Editează întâlnirea',
  'meet.form.liveNoReschedule':
    'Întâlnirea e în desfășurare: îi poți schimba numele și invitații, dar nu ora.',
  'meet.form.invalidEmail': 'Adresa de email nu e validă.',
  'meet.form.hostEmail':
    'Ești gazda: întâlnirea apare automat în Google Calendar-ul tău conectat, nu trebuie invitată adresa ta.',
  'meet.form.titleRequired': 'Scrie un titlu pentru întâlnire.',
  'meet.form.whenRequired': 'Alege data și ora.',
  'sidebar.tasks': 'Sarcini',

  // Team chat (internal Discord-like messaging)
  'teamChat.backToApp': 'Înapoi la aplicație',
  'teamChat.groups': 'Grupuri',
  'teamChat.newGroup': 'Grup nou',
  'teamChat.noGroups': 'Niciun grup încă.',
  'teamChat.directMessages': 'Mesaje directe',
  'teamChat.newDm': 'Mesaj nou',
  'teamChat.noConversations': 'Nicio conversație încă.',
  'teamChat.searchPeople': 'Caută colegi…',
  'teamChat.noPeople': 'Niciun coleg găsit.',
  'teamChat.groupName': 'Numele grupului',
  'teamChat.members': 'Membri',
  'teamChat.groupSettings': 'Setări grup',
  'teamChat.deleteGroup': 'Șterge grupul',
  'teamChat.confirmDeleteGroup':
    'Ștergi grupul „{name}” și toate mesajele lui? Acțiunea nu poate fi anulată.',
  'teamChat.addReaction': 'Adaugă reacție',
  // Hover text on a reaction pill: who reacted, and with what.
  'teamChat.reaction.nameList': '{names} și {last}',
  'teamChat.reaction.nameListMore': '{names} și încă {count}',
  'teamChat.reaction.tooltipOne': '{names} a reacționat cu {emoji}',
  'teamChat.reaction.tooltipMany': '{names} au reacționat cu {emoji}',
  'teamChat.addEmoji': 'Adaugă emoji',
  'teamChat.reply': 'Răspunde',
  'teamChat.replyingTo': 'Răspuns pentru {name}',
  'teamChat.edited': 'editat',
  'teamChat.deletedMessage': 'Mesaj șters',
  'teamChat.confirmDeleteMessage': 'Ștergi acest mesaj?',
  'teamChat.pin': 'Fixează',
  'teamChat.unpin': 'Anulează fixarea',
  'teamChat.pinned': 'Mesaje fixate',
  'teamChat.noPins': 'Niciun mesaj fixat.',
  'teamChat.jumpLoading': 'Se caută mesajul în istoric…',
  'teamChat.jumpTooOld': 'Mesajul e prea departe în istoric ca să fie deschis automat.',
  'teamChat.sent': 'Trimis',
  'teamChat.seen': 'Văzut',
  'teamChat.seenBy': 'Văzut de',
  'teamChat.notSeenYet': 'Încă n-a văzut',
  // Căutare. Secțiunile de rezultate refolosesc etichetele listelor din sidebar
  // (Grupuri / Mesaje directe / Toți colegii), ca să se numească la fel.
  'teamChat.searchPlaceholder': 'Caută în chat…',
  'teamChat.searchInChat': 'Caută în conversație',
  'teamChat.searchMessages': 'Mesaje',
  'teamChat.searchNoResults': 'Niciun rezultat.',
  'teamChat.searching': 'Se caută…',
  'teamChat.searchClear': 'Șterge căutarea',
  'teamChat.attachFile': 'Atașează fișier',
  'teamChat.send': 'Trimite',
  'teamChat.uploadTooLarge': '{name} depășește 10MB.',
  'teamChat.unsupportedType': 'Tip de fișier nesuportat: {name}.',
  'teamChat.emptyConversation': 'Niciun mesaj încă — scrie primul!',
  'teamChat.messagePlaceholder': 'Mesaj către {name}',
  'teamChat.notFound': 'Conversația nu există sau nu ai acces la ea.',
  'chatWidget.open': 'Chat echipă',
  'chatWidget.close': 'Închide chatul',
  'chatWidget.title': 'Chat echipă',
  'chatWidget.empty': 'Nicio conversație încă.',
  'chatWidget.back': 'Înapoi la conversații',
  'chatWidget.openFull': 'Deschide chatul complet',
  'teamChat.pickConversation': 'Alege o conversație din stânga sau începe una nouă.',
  'teamChat.online': 'Online',
  'teamChat.offline': 'Offline',
  'teamChat.showMembers': 'Arată membrii',
  'teamChat.hideMembers': 'Ascunde membrii',
  'teamChat.allColleagues': 'Toți colegii',
  // Cardul de profil deschis din lista de membri (ca pe Discord).
  'teamChat.profile.back': 'Înapoi la membri',
  'teamChat.profile.close': 'Închide profilul',
  'teamChat.profile.loadError': 'Profilul nu a putut fi încărcat.',
  'teamChat.profile.inactive': 'Cont dezactivat',
  'teamChat.profile.role.ADMIN': 'Administrator',
  'teamChat.profile.role.MANAGER': 'Manager',
  'teamChat.profile.role.MEMBER': 'Membru',
  'teamChat.profile.role.VIEWER': 'Vizitator',
  'teamChat.profile.contact': 'Contact',
  'teamChat.profile.phone': 'Telefon',
  'teamChat.profile.location': 'Locație',
  'teamChat.profile.department': 'Departament',
  'teamChat.profile.memberSince': 'Membru din',
  'teamChat.profile.lastSeen': 'Ultima activitate',
  'teamChat.profile.lastSeenNever': 'Niciodată',
  'teamChat.profile.projects': 'Proiecte',
  'teamChat.profile.noProjects': 'Niciun proiect cu task-uri deschise.',
  'teamChat.profile.projectTasks': '{count} task-uri',
  'teamChat.profile.activeTasks': 'Task-uri active',
  'teamChat.profile.noTasks': 'Niciun task deschis.',
  'teamChat.profile.inProgress': '{count} în lucru',
  'teamChat.profile.overdue': '{count} întârziate',
  'teamChat.profile.andMore': 'și încă {count}',
  'teamChat.profile.viewFull': 'Vezi profilul complet',
  // Canale de voce (LiveKit) — permanente, ca pe Discord: intri și ieși.
  'teamChat.voiceChannel': 'Voce',
  'teamChat.voiceJoin': 'Intră în canalul de voce',
  'teamChat.voiceTestMode': 'Vocea e dezactivată în Test Mode.',
  'teamChat.voiceConnecting': 'Se conectează…',
  'teamChat.voiceConnected': 'Conectat la voce',
  'teamChat.voiceReconnecting': 'Se reconectează…',
  'teamChat.voiceLive': 'Cineva partajează ecranul',
  'teamChat.voiceMobileHint': 'Ține aplicația deschisă — apelul cade dacă blochezi ecranul.',
  'teamChat.voiceYou': 'Tu',
  'teamChat.voiceMute': 'Oprește microfonul',
  'teamChat.voiceUnmute': 'Pornește microfonul',
  'teamChat.voiceLeave': 'Ieși',
  'teamChat.soundboard': 'Sunete',
  'teamChat.soundboard.bullshit': 'Bullshit',
  'teamChat.soundboard.rimshot': 'Tobe',
  'teamChat.soundboard.quack': 'Rață',
  'teamChat.soundboard.airhorn': 'Goarnă',
  'teamChat.soundboard.applause': 'Aplauze',
  'teamChat.soundboard.crickets': 'Greieri',
  'teamChat.voiceShare': 'Partajează ecranul',
  'teamChat.voiceStopSharing': 'Oprește partajarea',
  'teamChat.voiceSharingBy': '{name} partajează ecranul',
  'teamChat.voiceSharingYours': 'Îți partajezi ecranul.',
  'teamChat.voiceErrorMicDenied': 'Microfonul e blocat. Permite accesul din setările browserului.',
  'teamChat.voiceErrorUnavailable':
    'Canalele de voce nu sunt configurate. Un administrator le poate configura din Setări → Integrări → LiveKit.',
  'teamChat.voiceErrorGeneric': 'Conectarea la canalul de voce a eșuat.',
  // Canale de voce ca entități proprii (sus în sidebar), pagina canalului și
  // panoul „Conectat la voce” din stânga jos, ca pe Discord.
  'teamChat.voiceChannels': 'Canale de voce',
  'teamChat.textChannels': 'Canale text',
  'teamChat.newVoiceChannel': 'Canal de voce nou',
  'teamChat.voiceChannelName': 'Numele canalului',
  'teamChat.noVoiceChannels': 'Niciun canal de voce încă.',
  'teamChat.voiceChannelSettings': 'Setări canal de voce',
  'teamChat.deleteVoiceChannel': 'Șterge canalul',
  'teamChat.confirmDeleteVoiceChannel':
    'Ștergi canalul de voce „{name}”? Toți cei conectați vor fi deconectați.',
  'teamChat.voiceChannelNotFound': 'Canalul de voce nu există sau a fost șters.',
  'teamChat.voiceInVoice': 'În voce',
  'teamChat.voiceOpenChannel': 'Deschide canalul',
  'teamChat.voiceMuted': 'Microfon oprit',
  'teamChat.voiceLiveBadge': 'LIVE',
  'teamChat.voiceWatch': 'Vezi ecranul',
  'teamChat.voiceWatchOf': 'Vezi ecranul lui {name}',
  'teamChat.voiceParticipants': '{count} în canal',
  'teamChat.voiceEmptyChannel': 'Nimeni în canal încă. Fii primul care intră.',
  'teamChat.voiceJoinToWatch': 'Intră în canal ca să vezi ecranul partajat.',
  'teamChat.voiceNoShares': 'Nimeni nu partajează ecranul acum.',
  'teamChat.voiceSharedScreens': 'Ecrane partajate',
  'teamChat.voiceMembers': 'În canal',
  // Transcript automat al apelurilor și istoricul lor.
  'teamChat.voiceTranscript': 'Transcript',
  'teamChat.voiceTranscriptLive': 'Transcript live',
  'teamChat.voiceTranscriptShow': 'Arată transcriptul',
  'teamChat.voiceTranscriptHide': 'Ascunde transcriptul',
  'teamChat.voiceTranscriptCount': '{count} replici',
  'teamChat.voiceTranscriptOn': 'Transcript activ',
  'teamChat.voiceTranscriptHint':
    'Apelurile sunt transcrise automat. Fiecare replică apare cu numele celui care a spus-o.',
  'teamChat.voiceTranscriptOff': 'Transcrierea apelurilor nu este configurată pe server.',
  'teamChat.voiceTranscriptEmpty':
    'Nimic transcris încă. Replicile apar la câteva secunde după ce vorbește cineva.',
  'teamChat.voiceTranscriptBudget':
    'Transcrierea ta s-a oprit: bugetul lunar de AI este epuizat. Apelul continuă.',
  'teamChat.voiceTranscriptUnsupported':
    'Browserul tău nu poate înregistra microfonul pentru transcript. Apelul continuă.',
  'teamChat.voiceTranscriptStopped': 'Transcrierea ta s-a oprit. Apelul continuă.',
  'teamChat.voiceTranscriptUpstream':
    'Transcrierea nu funcționează: serviciul de transcriere a respins cererile (cont fără credit sau cheie invalidă). Administratorii au fost alertați. Apelul continuă.',
  'teamChat.voiceCallHistory': 'Istoric apeluri',
  'teamChat.voiceRecentCalls': 'Apeluri recente',
  'teamChat.voiceCallAllCalls': 'Toate apelurile',
  'teamChat.voiceNoCalls': 'Niciun apel încă.',
  'teamChat.voiceCallNotFound': 'Apelul nu există sau a fost șters.',
  'teamChat.voiceCallBack': 'Înapoi la istoric',
  'teamChat.voiceCallParticipants': 'Participanți',
  'teamChat.voiceCallSegments': '{count} replici',
  'teamChat.voiceCallNoTranscript': 'Fără transcript',
  'teamChat.voiceCallPendingSuggestions': '{count} sugestii de sarcini',
  // Sugestiile de sarcini din discuția dintr-o cameră de voce (aceleași carduri
  // ca la întâlniri, de aici doar textele care diferă).
  'teamChat.suggestions.hint':
    'După fiecare discuție, AI-ul citește transcriptul și propune sarcini — pe proiect, pe om, cu termen. Nimic nu se creează singur: tu alegi ce intră în platformă.',
  'teamChat.suggestions.empty':
    'Nicio sarcină propusă din discuția asta. Poți cere o nouă citire oricând.',
  'teamChat.suggestions.needTranscript':
    'Discuția nu are transcript, deci nu e nimic de citit pentru sugestii.',
  'teamChat.suggestions.live':
    'Discuția e în desfășurare. Sugestiile apar după ce se închide apelul.',
  // Minuta discuției dintr-o cameră de voce — același document ca la întâlniri.
  'teamChat.minutes.title': 'Minuta discuției',
  'teamChat.minutes.hint':
    'Firmio scrie, la cerere, minuta discuției din cameră: despre ce s-a vorbit, ce s-a hotărât, cine a luat ce în sarcină. O citești aici sau o descarci ca document Word.',
  'teamChat.minutes.generate': 'Generează minuta',
  'teamChat.minutes.regenerate': 'Generează din nou',
  'teamChat.minutes.generating': 'Se scrie minuta…',
  'teamChat.minutes.download': 'Descarcă .docx',
  'teamChat.minutes.sendEmail': 'Trimite participanților',
  'teamChat.minutes.sending': 'Se trimite…',
  'teamChat.minutes.emailedTo': 'Trimisă pe e-mail la',
  'teamChat.minutes.empty': 'Nu există încă o minută pentru discuția asta.',
  'teamChat.minutes.live': 'Discuția e în desfășurare. Minuta se scrie după ce se închide apelul.',
  'teamChat.minutes.needsTranscript':
    'Discuția nu are transcript, deci nu e nimic de citit pentru minută.',
  'teamChat.minutes.generatedAt': 'Generată',
  'teamChat.minutes.cost': 'Cost',
  'teamChat.minutes.tokens': 'tokeni',
  'teamChat.minutes.history': 'Versiuni anterioare',
  'teamChat.minutes.failed': 'Generarea minutei a eșuat.',
  'teamChat.minutes.keyFacts': 'Informații cheie',
  'teamChat.minutes.decisions': 'Decizii',
  'teamChat.minutes.actions': 'Acțiuni',
  'teamChat.minutes.openQuestions': 'Întrebări deschise',
  'teamChat.minutes.nextSteps': 'Pașii următori',
  'teamChat.minutes.noDueDate': 'fără termen',
  'teamChat.voiceCallCopy': 'Copiază transcriptul',
  'teamChat.voiceCallCopied': 'Copiat',
  'teamChat.voiceCallDelete': 'Șterge apelul',
  'teamChat.voiceCallConfirmDelete':
    'Ștergi apelul din {date} și transcriptul lui? Nu se poate anula.',
  'teamChat.voiceDurationH': '{h} h {m} min',
  'teamChat.voiceDurationM': '{m} min',
  'teamChat.voiceDurationS': '{s} s',
  'teamChat.voiceFullscreen': 'Ecran complet',
  'teamChat.voiceExitFullscreen': 'Ieși din ecran complet',
  'teamChat.selectAll': 'Selectează pe toți',
  'teamChat.selectNone': 'Deselectează',
  'sidebar.personalTasks': 'Sarcini personale',
  'personalTasks.title': 'Sarcini personale',
  'personalTasks.error': 'Nu am putut încărca sarcinile personale.',
  'personalTasks.transfer.menuHeader': 'Trimite către…',
  'personalTasks.transfer.confirm':
    'Trimiți această sarcină către {name}? Va fi mutată în lista personală a colegului și va dispărea din lista ta.',
  'personalTasks.transfer.success': 'Sarcina a fost trimisă către {name}.',
  'personalTasks.transfer.failed': 'Sarcina nu a putut fi trimisă.',
  'sidebar.reminders': 'Remindere',

  // Concedii (leave)
  'sidebar.leave': 'Concedii',
  'leave.title': 'Concedii',
  'leave.subtitle': 'Cererile tale de concediu și zilele rămase.',
  'leave.tab.mine': 'Cererile mele',
  'leave.tab.team': 'Toate cererile',
  'leave.tab.report': 'Raport',
  'leave.new': 'Cerere nouă',
  'leave.edit': 'Modifică cererea',
  'leave.empty': 'Nicio cerere de concediu. Adaugă una mai sus.',
  'leave.emptyFiltered': 'Nicio cerere care să se potrivească filtrelor.',
  'leave.emptyTeam': 'Nicio cerere de la echipă.',
  'leave.error': 'Nu am putut încărca cererile de concediu.',
  'leave.noAccess': 'Nu ai acces la concediile echipei.',
  'leave.loading': 'Se încarcă…',

  // Tipuri și stări
  'leave.type.ANNUAL': 'Concediu de odihnă',
  'leave.type.SICK': 'Concediu medical',
  'leave.type.UNPAID': 'Concediu fără plată',
  'leave.type.SPECIAL_EVENT': 'Eveniment special',
  'leave.type.SHORT_ABSENCE': 'Învoire',
  'leave.status.PENDING': 'În așteptare',
  'leave.status.APPROVED': 'Aprobat',
  'leave.status.REJECTED': 'Respins',
  'leave.status.CANCELLED': 'Retras',

  // Formular
  'leave.field.type': 'Tipul concediului',
  'leave.field.start': 'Din data',
  'leave.field.end': 'Până în data',
  'leave.field.day': 'Ziua',
  'leave.field.hours': 'Ore',
  'leave.field.reason': 'Motiv (opțional)',
  'leave.field.reasonPlaceholder': 'Scrie pe scurt de ce, dacă ajută la aprobare.',
  'leave.field.person': 'Pentru cine',
  'leave.field.note': 'Mesaj (opțional)',
  'leave.save': 'Trimite cererea',
  'leave.saveEdit': 'Salvează',
  'leave.cancel': 'Renunță',
  'leave.cost': '{count} zile lucrătoare',
  'leave.costOne': '1 zi lucrătoare',
  'leave.costHours': '{count} ore',
  'leave.costNone': 'Perioada nu conține nicio zi lucrătoare.',
  'leave.costHint': 'Weekendurile și sărbătorile legale nu se scad.',
  'leave.lengthDay': '1 zi',
  'leave.lengthDays': '{count} zile',
  'leave.lengthHours': '{count} ore',

  // Acțiuni
  'leave.approve': 'Aprobă',
  'leave.reject': 'Respinge',
  'leave.withdraw': 'Retrage cererea',
  'leave.delete': 'Șterge',
  'leave.confirmWithdraw': 'Retragi cererea de concediu?',
  'leave.confirmDelete': 'Ștergi definitiv cererea? Nu se mai poate recupera.',
  'leave.rejectPrompt': 'De ce respingi cererea? (opțional)',
  'leave.decidedBy': 'Decis de {name}',

  // Sold
  'leave.balance.title': 'Zile de odihnă {year}',
  'leave.balance.entitled': 'Cuvenite',
  'leave.balance.used': 'Luate',
  'leave.balance.pending': 'În așteptare',
  'leave.balance.remaining': 'Rămase',
  'leave.balance.default': 'Zile implicite; se pot schimba pe pagina persoanei.',
  'leave.balance.hint': 'Doar concediul de odihnă scade din sold.',
  'leave.entitlement': 'Zile de concediu pe an',
  'leave.entitlementSave': 'Salvează zilele',
  'leave.entitlementReset': 'Revino la implicit',

  // Tabel
  'leave.column.person': 'Persoana',
  'leave.column.type': 'Tip',
  'leave.column.period': 'Perioada',
  'leave.column.length': 'Durata',
  'leave.column.status': 'Stare',
  'leave.column.filed': 'Depusă',
  'leave.column.decision': 'Decizie',
  'leave.filter.status': 'Stare',
  'leave.filter.type': 'Tip',
  'leave.filter.person': 'Persoana',
  'leave.filter.all': 'Toate',

  // Raport
  'leave.report.title': 'Raport concedii {year}',
  'leave.report.export': 'Descarcă CSV',
  'leave.report.empty': 'Niciun angajat cu cont de acces.',

  // Calendar + avertisment la task
  'leave.calendarChip': '{name} — {type}',
  'leave.onLeaveWarning': '{name} este în concediu în perioada {range}.',

  'reminders.title': 'Remindere',
  'reminders.add': 'Adaugă un reminder',
  'reminders.titlePlaceholder': 'Titlu',
  'reminders.descriptionPlaceholder': 'Descriere (opțional)',
  'reminders.estimatePlaceholder': 'Estimare (min)',
  'reminders.save': 'Salvează',
  'reminders.empty': 'Niciun reminder. Adaugă unul mai sus.',
  'reminders.completed': 'Finalizate ({count})',
  'reminders.edit': 'Editează',
  'reminders.delete': 'Șterge',
  'reminders.markDone': 'Marchează finalizat',
  'reminders.markOpen': 'Redeschide',
  'reminders.done': 'Finalizat',
  'reminders.snooze': 'Amână',
  'reminders.snooze10m': '+10 min',
  'reminders.snooze1h': '+1 oră',
  'reminders.snoozeTomorrow': 'Mâine la 09:00',
  'reminders.dismiss': 'Închide notificarea',
  'reminders.today': 'Astăzi',
  'reminders.tomorrow': 'Mâine',
  'reminders.error': 'Nu am putut încărca reminderele.',
  // Meetings module (Întâlniri)
  'sidebar.meetings': 'Întâlniri',
  'meetings.title': 'Întâlniri',
  'meetings.subtitle': 'Întâlnirile echipei cu clienți, parteneri și alte persoane externe',
  'meetings.new': 'Întâlnire nouă',
  'meetings.tabMeetings': 'Întâlniri',
  'meetings.tabContacts': 'Persoane',
  'meetings.search': 'Caută după titlu, persoană, companie…',
  'meetings.filterClient': 'Client',
  'meetings.filterProject': 'Proiect',
  'meetings.filterOwner': 'Responsabil',
  'meetings.filterPeriod': 'Perioadă',
  'meetings.filterFrom': 'De la',
  'meetings.filterTo': 'Până la',
  'meetings.allClients': 'Toți clienții',
  'meetings.allProjects': 'Toate proiectele',
  'meetings.allOwners': 'Toți',
  'meetings.filteredByContact': 'Afișezi doar întâlnirile cu persoana selectată.',
  'meetings.colDate': 'Data',
  'meetings.colDuration': 'Durată',
  'meetings.colTitle': 'Întâlnire',
  'meetings.colAttendees': 'Participanți',
  'meetings.colContext': 'Client / Proiect',
  'meetings.colOwner': 'Responsabil',
  'meetings.colNotes': 'Notițe',
  'meetings.empty': 'Nicio întâlnire înregistrată încă. Adaugă prima întâlnire.',
  'meetings.emptyFiltered': 'Nicio întâlnire nu corespunde filtrelor.',
  'meetings.error': 'Nu am putut încărca întâlnirile.',
  'meetings.noNotes': 'fără notițe',
  'meetings.notFound': 'Întâlnirea nu a fost găsită.',
  'meetings.access.title': 'Nu ai acces la această întâlnire',
  'meetings.access.body':
    'O văd doar responsabilul și colegii adăugați pe ea. Poți cere acces, iar responsabilul te adaugă cu un click.',
  'meetings.access.request': 'Cere acces',
  'meetings.access.sending': 'Se trimite…',
  'meetings.access.sent':
    'Am trimis cererea către {owner}. Vei vedea întâlnirea după ce te adaugă.',
  'meetings.access.failed': 'Nu a mers. Încearcă din nou.',
  'meetings.access.banner': '{name} cere acces la această întâlnire.',
  'meetings.access.added': '{name} are acum acces și poate deschide întâlnirea.',
  'meetings.share.title': 'Au acces',
  'meetings.share.hint':
    'Colegi care pot deschide întâlnirea fără să fi fost în ea. Cine are un rol care vede toate întâlnirile nu trebuie adăugat.',
  'meetings.share.empty': 'Nimeni în afară de participanți.',
  'meetings.share.view': 'Poate vedea',
  'meetings.share.edit': 'Poate edita',
  'meetings.share.colleague': 'Coleg',
  'meetings.share.pick': 'Alege un coleg',
  'meetings.share.level': 'Nivel de acces',
  'meetings.share.levelFor': 'Nivelul de acces pentru {name}',
  'meetings.share.add': 'Dă acces',
  'meetings.share.remove': 'Scoate accesul pentru {name}',
  'meetings.back': 'Întâlniri',
  'meetings.confirmDelete': 'Ștergi această întâlnire împreună cu notițele și transcrierile ei?',
  'meetings.details': 'Detalii',
  'meetings.responsible': 'Responsabil',
  'meetings.team': 'Colegi prezenți',
  'meetings.client': 'Client',
  'meetings.project': 'Proiect',
  'meetings.lead': 'Lead',
  'meetings.filteredByLead': 'Doar întâlnirile de pe acest lead.',
  'meetings.createdBy': 'Adăugată de',
  'meetings.externalAttendees': 'Participanți externi',
  'meetings.noAttendees': 'Nicio persoană externă adăugată.',
  'meetings.manualNotes': 'Notițe',
  'meetings.transcripts': 'Transcriere și notițe AI',
  'meetings.transcriptsHint':
    'Lipește transcrierea de pe dispozitivul de înregistrare sau încarcă fișierul audio/PDF. Firmio o rezumă, extrage deciziile și acțiunile și o indexează în baza de cunoștințe, ca să o poți întreba oricând ce s-a discutat.',
  'meetings.notesForbidden': 'Nu ai acces la notițele și transcrierile întâlnirilor.',
  'leads.suggestions.title': 'Sugestii de acțiuni',
  'leads.suggestions.hint':
    'Acțiunile propuse de Firmio AI (din conversații sau din notițe) așteaptă aici. Nimic nu se creează singur — verifici fiecare sugestie, ajustezi tipul, responsabilul și termenul, și o adaugi la acțiuni sau o ignori.',
  'leads.suggestions.generate': 'Generează sugestii',
  'leads.suggestions.regenerate': 'Regenerează',
  'leads.suggestions.generating': 'Se generează…',
  'leads.suggestions.generateFailed': 'Generarea sugestiilor a eșuat.',
  'leads.suggestions.empty':
    'Nicio sugestie încă. Apasă „Generează sugestii” sau cere-i lui Firmio AI să propună acțiuni.',
  'leads.suggestions.allDecided': 'Toate sugestiile au fost decise.',
  'leads.suggestions.accept': 'Adaugă la acțiuni',
  'leads.suggestions.accepting': 'Se adaugă…',
  'leads.suggestions.acceptFailed': 'Adăugarea acțiunii a eșuat.',
  'leads.suggestions.dismiss': 'Ignoră',
  'leads.suggestions.dismissFailed': 'Ignorarea a eșuat.',
  'leads.suggestions.restore': 'Readu',
  'leads.suggestions.accepted': 'Adăugate ({count})',
  'leads.suggestions.dismissed': 'Ignorate ({count})',
  'leads.suggestions.decidedBy': 'decis de {name}',
  'leads.suggestions.assigneeHint': 'AI a propus responsabil: {name} (nu e în echipă)',
  'leads.suggestions.field.title': 'Titlu',
  'leads.suggestions.field.type': 'Tip',
  'leads.suggestions.field.assignee': 'Responsabil',
  'leads.suggestions.field.dueDate': 'Termen',
  'leads.suggestions.field.description': 'Detalii',
  'leads.suggestions.noAssignee': 'Fără responsabil',
  'leads.suggestions.addDescription': 'adaugă detalii',
  'meetings.minutes.title': 'Minuta întâlnirii',
  'meetings.minutes.hint':
    'Firmio scrie minuta din transcriere: rezumat, ce s-a discutat, informații cheie, decizii, acțiuni cu responsabili și termene, întrebări deschise. O descarci ca document Word, gata de trimis participanților.',
  'meetings.minutes.generate': 'Generează minuta',
  'meetings.minutes.regenerate': 'Generează din nou',
  'meetings.minutes.generating': 'Se scrie minuta…',
  'meetings.minutes.download': 'Descarcă .docx',
  'meetings.minutes.openInDrive': 'Deschide în Drive',
  'meetings.minutes.saveToDrive': 'Salvează în Drive',
  'meetings.minutes.sendEmail': 'Trimite pe e-mail',
  'meetings.minutes.sending': 'Se trimite…',
  'meetings.minutes.emailedTo': 'Trimisă pe e-mail la',
  'meetings.minutes.empty': 'Nu există încă o minută pentru această întâlnire.',
  'meetings.minutes.needsTranscript': 'Adaugă mai întâi transcrierea întâlnirii.',
  'meetings.minutes.generatedAt': 'Generată',
  'meetings.minutes.cost': 'Cost',
  'meetings.minutes.tokens': 'tokeni',
  'meetings.minutes.history': 'Versiuni anterioare',
  'meetings.minutes.failed': 'Generarea minutei a eșuat.',
  'meetings.suggestions.title': 'Sugestii de sarcini',
  'meetings.suggestions.hint':
    'După ce transcrierea este analizată, Firmio propune sarcinile care reies din discuție: pe ce proiect, cine se ocupă, cât de urgent, până când. Nimic nu se creează singur — verifici fiecare sugestie și o adaugi în proiect sau o ignori.',
  'meetings.suggestions.generate': 'Generează sugestii',
  'meetings.suggestions.regenerate': 'Regenerează',
  'meetings.suggestions.generating': 'Se analizează…',
  'meetings.suggestions.generateFailed': 'Sugestiile nu au putut fi generate. Încearcă din nou.',
  'meetings.suggestions.processing':
    'Transcrierea se analizează. Sugestiile apar aici imediat ce procesarea se termină.',
  'meetings.suggestions.needTranscript':
    'Adaugă mai întâi transcrierea întâlnirii (mai sus), ca să existe din ce propune sarcini.',
  'meetings.suggestions.empty':
    'Nicio sugestie încă. Apasă „Generează sugestii” ca să citească transcrierea.',
  'meetings.suggestions.allDecided': 'Toate sugestiile au fost decise. Poți regenera oricând.',
  'meetings.suggestions.accepted': 'Adăugate în platformă ({count})',
  'meetings.suggestions.dismissed': 'Ignorate ({count})',
  'meetings.suggestions.decidedBy': 'de {name}',
  'meetings.suggestions.assigneeHint': 'AI a menționat: {name} (nu este coleg în platformă)',
  'meetings.suggestions.projectHint': 'Proiect menționat: {name} (negăsit)',
  'meetings.suggestions.field.title': 'Titlul sarcinii',
  'meetings.suggestions.field.project': 'Proiect',
  'meetings.suggestions.field.assignee': 'Responsabil',
  'meetings.suggestions.field.priority': 'Prioritate',
  'meetings.suggestions.field.dueDate': 'Termen',
  'meetings.suggestions.field.estimate': 'Estimare (ore)',
  'meetings.suggestions.estimatePlaceholder': 'ex. 2 sau 1,5',
  'meetings.suggestions.field.description': 'Detalii',
  'meetings.suggestions.addDescription': 'Adaugă detalii',
  'meetings.suggestions.pickProject': 'Alege proiectul',
  'meetings.suggestions.noAssignee': 'Fără responsabil',
  'meetings.suggestions.accept': 'Adaugă în proiect',
  'meetings.suggestions.accepting': 'Se adaugă…',
  'meetings.suggestions.acceptedNotice': 'Sarcina {task} a fost adăugată în {project}.',
  'meetings.suggestions.openTask': 'Deschide sarcina',
  'meetings.suggestions.acceptFailed': 'Sarcina nu a putut fi creată.',
  'meetings.suggestions.dismiss': 'Ignoră',
  'meetings.suggestions.dismissFailed': 'Sugestia nu a putut fi ignorată.',
  'meetings.suggestions.restore': 'Restaurează',
  'meetings.form.createTitle': 'Întâlnire nouă',
  'meetings.form.editTitle': 'Editează întâlnirea',
  'meetings.form.title': 'Titlu *',
  'meetings.form.date': 'Data *',
  'meetings.form.time': 'Ora *',
  'meetings.form.endTime': 'Ora de sfârșit',
  'meetings.form.type': 'Tip întâlnire',
  'meetings.form.location': 'Locație',
  'meetings.form.locationPlaceholder': 'ex. sediul clientului, Google Meet',
  'meetings.form.owner': 'Responsabil',
  'meetings.form.team': 'Alți colegi prezenți',
  'meetings.form.teamNone': 'Niciun coleg',
  'meetings.form.teamSummary': '{count} colegi',
  'meetings.form.client': 'Client',
  'meetings.form.noClient': 'Fără client',
  'meetings.form.project': 'Proiect',
  'meetings.form.noProject': 'Fără proiect',
  'meetings.form.lead': 'Lead',
  'meetings.form.noLead': 'Fără lead',
  'leads.meetings.title': 'Întâlniri',
  'leads.meetings.hint':
    'Întâlnirile din jurnal legate de acest lead, cu transcrierea, minuta și sarcinile propuse pe pagina fiecăreia.',
  'leads.meetings.empty': 'Nicio întâlnire legată de acest lead încă.',
  'leads.meetings.add': 'Adaugă întâlnire',
  'leads.meetings.viewAll': 'Vezi în Întâlniri',
  'leads.meetings.error': 'Nu am putut încărca întâlnirile.',
  'meetings.form.notes': 'Notițe (markdown)',
  'meetings.form.transcript': 'Transcrierea întâlnirii',
  'meetings.form.transcriptPlaceholder':
    'Lipește aici transcrierea de pe dispozitivul de înregistrare…',
  'meetings.form.transcriptHint':
    'Opțional. Este procesată de AI (rezumat, decizii, acțiuni) și indexată în baza de cunoștințe. Poți adăuga transcrierea și mai târziu, din pagina întâlnirii.',
  'meetings.form.attendees': 'Persoane externe',
  'meetings.form.titleRequired': 'Titlul este obligatoriu.',
  'meetings.form.dateRequired': 'Data și ora sunt obligatorii.',
  'meetings.attendees.search': 'Caută o persoană existentă…',
  'meetings.attendees.new': 'Persoană nouă',
  'meetings.attendees.newBadge': 'nou',
  'meetings.attendees.createFromQuery': 'Adaugă „{name}” ca persoană nouă',
  'meetings.attendees.firstName': 'Prenume *',
  'meetings.attendees.lastName': 'Nume',
  'meetings.attendees.email': 'Email',
  'meetings.attendees.phone': 'Telefon',
  'meetings.attendees.company': 'Companie',
  'meetings.attendees.role': 'Funcție',
  'meetings.attendees.client': 'Client asociat',
  'meetings.attendees.noClient': 'Fără client',
  'meetings.attendees.add': 'Adaugă',
  'meetings.attendees.noResults': 'Nicio persoană găsită',
  'meetings.attendees.firstNameRequired': 'Prenumele este obligatoriu.',
  'meetings.contacts.new': 'Persoană nouă',
  'meetings.contacts.search': 'Caută după nume, companie, email…',
  'meetings.contacts.colName': 'Nume',
  'meetings.contacts.colCompany': 'Companie',
  'meetings.contacts.colClient': 'Client',
  'meetings.contacts.colContact': 'Contact',
  'meetings.contacts.colMeetings': 'Întâlniri',
  'meetings.contacts.empty': 'Nicio persoană înregistrată încă.',
  'meetings.contacts.error': 'Nu am putut încărca persoanele.',
  'meetings.contacts.confirmDelete':
    'Ștergi această persoană? Va fi scoasă din întâlnirile la care a participat.',
  'meetings.contacts.editTitle': 'Editează persoana',
  'meetings.contacts.createTitle': 'Persoană nouă',
  'meetings.contacts.notes': 'Notițe',
  'common.remove': 'Elimină',
  'common.pageOf': 'Pagina {page} din {total}',
  'common.company': 'Firmă',
  'common.defaultCompany': 'Firma implicită',
  'common.clearFilter': 'Șterge filtrul',
  'sidebar.leads': 'Pipeline',
  'sidebar.projects': 'Proiecte',
  'sidebar.allProjects': 'Toate proiectele',
  'sidebar.clients': 'Clienți',
  'sidebar.allClients': 'Toți clienții',
  'sidebar.noClients': 'Niciun client încă',
  'sidebar.addClient': 'Adaugă client',
  'sidebar.suppliers': 'Furnizori',
  'sidebar.allSuppliers': 'Toți furnizorii',
  'sidebar.noSuppliers': 'Niciun furnizor încă',
  'sidebar.addSupplier': 'Adaugă furnizor',
  'sidebar.subscriptions': 'Abonamente',
  'sidebar.allSubscriptions': 'Toate abonamentele',
  'sidebar.noSubscriptions': 'Niciun abonament',
  'sidebar.financials': 'Financiar',
  'sidebar.financialsOverview': 'Prezentare generală (P&L)',
  'sidebar.invoices': 'Facturi',
  'sidebar.smartbill': 'SmartBill',
  'sidebar.spv': 'SPV e-Factura',
  'sidebar.issuedInvoices': 'Facturi emise',
  'sidebar.bankStatements': 'Extrase bancare',
  'sidebar.reconciliation': 'Reconciliere bancară',
  'sidebar.cashflow': 'Cash flow',
  'sidebar.monthlyReports': 'Rapoarte lunare',
  'sidebar.people': 'Echipă',
  'sidebar.allMembers': 'Toți membrii',
  'sidebar.addMember': 'Adaugă membru',
  'sidebar.settings': 'Setări',
  'sidebar.bugReports': 'Bug-uri raportate',
  'tasks.share.copyLink': 'Copiază linkul sarcinii',
  'tasks.share.copied': 'Link copiat.',
  'tasks.share.copyFailed': 'Linkul nu a putut fi copiat.',
  'sidebar.testMode': 'Mod test',
  'sidebar.language': 'Limbă',
  'sidebar.profile': 'Profil',
  'sidebar.logout': 'Deconectare',
  'sidebar.closeMenu': 'Închide meniul',
  'sidebar.resize': 'Redimensionează bara laterală',

  // Profile page (CLE-434)
  'profile.title': 'Profil',
  'profile.subtitle': 'Datele contului tău, limba preferată și notificările push.',
  'profile.firstName': 'Prenume',
  'profile.lastName': 'Nume',
  'profile.email': 'Email',
  'profile.language': 'Limbă',
  'profile.languageHint': 'Alege limba interfeței.',
  'profile.notProvided': '—',
  'profile.connectedApps.title': 'Aplicații conectate',
  'profile.connectedApps.subtitle':
    'Clienții MCP (Claude Desktop, claude.ai, Claude Code) care lucrează în Firmio în numele tău, cu permisiunile contului tău.',
  'profile.connectedApps.howTo': 'Adaugă în Claude un conector personalizat cu adresa:',
  'profile.connectedApps.empty': 'Nicio aplicație conectată.',
  'profile.connectedApps.loadFailed': 'Lista nu a putut fi încărcată.',
  'profile.connectedApps.revokeFailed': 'Deconectarea nu a reușit.',
  'profile.connectedApps.unknownClient': 'Aplicație necunoscută',
  'profile.connectedApps.connectedAt': 'Conectată',
  'profile.connectedApps.lastUsed': 'ultima folosire',
  'profile.connectedApps.disconnect': 'Deconectează',
  'oauth.consent.title': 'Acces la Firmio',
  'oauth.consent.missing': 'Lipsește cererea de autorizare. Reia conectarea din aplicația Claude.',
  'oauth.consent.failed':
    'Autorizarea nu a putut fi procesată. Încearcă din nou din aplicația Claude.',
  'oauth.consent.unknownClient': 'O aplicație',
  'oauth.consent.asks': 'vrea să folosească Firmio AI în numele tău.',
  'oauth.consent.account': 'Cont:',
  'oauth.consent.scopeTools':
    'Poate citi și modifica date în Firmio prin aceleași unelte ca Firmio AI (sarcini, proiecte, lead-uri, financiar etc.).',
  'oauth.consent.scopeSame': 'Primește exact permisiunile contului tău, nimic în plus.',
  'oauth.consent.scopeRevoke': 'Poți revoca accesul oricând din Profil → Aplicații conectate.',
  'oauth.consent.approve': 'Permite accesul',
  'oauth.consent.deny': 'Refuză',
  'oauth.consent.redirecting': 'Se revine în aplicație…',
  'oauth.consent.footer':
    'Conversațiile rămân în aplicația Claude; Firmio execută doar acțiunile cerute.',
  'profile.password.title': 'Schimbă parola',
  'profile.password.subtitle': 'Actualizează parola contului tău.',
  'profile.password.current': 'Parola veche',
  'profile.password.new': 'Parola nouă',
  'profile.password.confirm': 'Confirmare parolă',
  'profile.password.show': 'Arată parola',
  'profile.password.hide': 'Ascunde parola',
  'profile.password.submit': 'Salvează parola',
  'profile.password.submitting': 'Se salvează…',
  'profile.password.success': 'Parola a fost schimbată cu succes.',
  'profile.password.mismatch': 'Parolele nu coincid.',
  'profile.password.weak': 'Parola trebuie să aibă minim 8 caractere, o literă mare și o cifră.',
  'profile.password.wrongCurrent': 'Parola veche este incorectă.',
  'profile.password.failed': 'Nu am putut schimba parola. Încearcă din nou.',
  'profile.deleteAccount.title': 'Ștergerea contului',
  'profile.deleteAccount.subtitle': 'Ceri ștergerea contului tău Firmio.',
  'profile.deleteAccount.explain':
    'Contul se dezactivează imediat: nu te mai poți autentifica, iar notificările se opresc. Administratorii firmei primesc cererea și finalizează ștergerea datelor contului.',
  'profile.deleteAccount.details': 'Ce se șterge și ce rămâne',
  'profile.deleteAccount.password': 'Parola ta, pentru confirmare',
  'profile.deleteAccount.submit': 'Cere ștergerea contului',
  'profile.deleteAccount.submitting': 'Se trimite…',
  'profile.deleteAccount.confirmTitle': 'Ștergi contul?',
  'profile.deleteAccount.confirmMessage':
    'Contul se dezactivează acum și vei fi deconectat. Doar un administrator al firmei îl mai poate reactiva.',
  'profile.deleteAccount.confirmLabel': 'Da, cere ștergerea',
  'profile.deleteAccount.wrongPassword': 'Parola nu este corectă.',
  'profile.deleteAccount.lastAdmin':
    'Ești singurul administrator activ. Numește încă un administrator înainte să ceri ștergerea contului.',
  'profile.deleteAccount.failed': 'Cererea nu a putut fi trimisă. Încearcă din nou.',

  // Multi-company: global firm switcher + Financials multi-select
  'company.switcher.label': 'Firmă',
  'company.switcher.all': 'Toate firmele',
  'financials.companyFilter.label': 'Firme',
  'financials.companyFilter.all': 'Toate firmele',
  'financials.companyFilter.selected': '{count} firme',
  'financials.yearsSelected': '{count} ani',
  'financials.monthsSelected': '{count} luni',

  // Dashboard
  'dashboard.title': 'Command Center',
  'dashboard.subtitle': 'Starea afacerii dintr-o privire — urgențe, bani și echipă.',
  'dashboard.revenue': 'Venituri',
  'dashboard.expenses': 'Cheltuieli',
  'dashboard.profit': 'Profit',
  'dashboard.margin': 'Marjă',
  'dashboard.activeProjects': 'Proiecte active',
  'dashboard.teamTasks': 'Sarcini echipă',
  'dashboard.upcomingPayments': 'Plăți planificate',
  'dashboard.revenueVsExpenses': 'Venituri vs Cheltuieli',
  'dashboard.error.kpis': 'Încărcarea indicatorilor a eșuat.',
  'dashboard.error.charts': 'Încărcarea graficelor a eșuat.',
  'dashboard.error.today': 'Încărcarea panoului „Azi” a eșuat.',
  'dashboard.error.upcoming': 'Încărcarea scadențelor a eșuat.',
  'dashboard.error.team': 'Încărcarea alocării echipei a eșuat.',
  'dashboard.error.teamTasks': 'Încărcarea task-urilor echipei a eșuat.',
  'dashboard.error.projects': 'Încărcarea proiectelor a eșuat.',
  'dashboard.error.activity': 'Încărcarea activității a eșuat.',
  'dashboard.error.overdueInvoices': 'Încărcarea facturilor întârziate a eșuat.',

  // Dashboard — configurable layout (edit mode + widget catalog)
  'dashboard.edit.enter': 'Editează dashboard',
  'dashboard.edit.widgets': 'Widget-uri',
  'dashboard.edit.save': 'Salvează',
  'dashboard.edit.cancel': 'Anulează',
  'dashboard.edit.reset': 'Resetează la implicit',
  'dashboard.edit.resetShort': 'Resetează',
  'dashboard.edit.enterShort': 'Editează',
  'dashboard.edit.resetConfirm':
    'Resetezi dashboard-ul la aspectul implicit? Layout-ul salvat va fi șters.',
  'dashboard.edit.addWidget': 'Adaugă widget',
  'dashboard.edit.remove': 'Elimină widgetul',
  'dashboard.edit.moveUp': 'Mută mai sus',
  'dashboard.edit.moveDown': 'Mută mai jos',
  'dashboard.edit.width': 'Schimbă lățimea',
  'dashboard.edit.dragHint': 'Trage pentru a rearanja',
  'dashboard.edit.empty': 'Niciun widget pe dashboard. Adaugă unul din catalog.',
  'dashboard.edit.saveError': 'Salvarea layout-ului a eșuat. Încearcă din nou.',
  'dashboard.catalog.title': 'Adaugă widget-uri',
  'dashboard.catalog.add': 'Adaugă',
  'dashboard.catalog.remove': 'Elimină',
  'dashboard.catalog.empty': 'Niciun widget disponibil.',
  'dashboard.category.financial': 'Financiar',
  'dashboard.category.team': 'Echipă',
  'dashboard.category.projects': 'Proiecte & Taskuri',
  'dashboard.category.activity': 'Azi & Activitate',
  'dashboard.widget.kpi.revenue.name': 'Venituri luna curentă',
  'dashboard.widget.kpi.revenue.desc': 'Card KPI: venituri în luna curentă, cu trend și sparkline.',
  'dashboard.widget.kpi.expenses.name': 'Cheltuieli luna curentă',
  'dashboard.widget.kpi.expenses.desc':
    'Card KPI: cheltuieli în luna curentă, cu trend și sparkline.',
  'dashboard.widget.kpi.profit.name': 'Profit net luna',
  'dashboard.widget.kpi.profit.desc': 'Card KPI: profit net și marjă în luna curentă.',
  'dashboard.widget.kpi.receivables.name': 'Facturi neîncasate',
  'dashboard.widget.kpi.receivables.desc': 'Card KPI: total facturi emise încă neîncasate.',
  'dashboard.widget.kpi.payables.name': 'Plăți de efectuat',
  'dashboard.widget.kpi.payables.desc': 'Card KPI: facturi de la furnizori scadente la plată.',
  'dashboard.widget.kpi.urgentTasks.name': 'Task-uri urgente',
  'dashboard.widget.kpi.urgentTasks.desc': 'Card KPI: task-uri întârziate și critice.',
  'dashboard.widget.chart.revenueDonut.name': 'Distribuție venituri pe proiecte',
  'dashboard.widget.chart.revenueDonut.desc':
    'Grafic donut cu veniturile pe proiecte în anul curent.',
  'dashboard.widget.chart.incomeExpense.name': 'Venituri vs cheltuieli',
  'dashboard.widget.chart.incomeExpense.desc':
    'Grafic pe bare cu ultimele 6 luni de venituri și cheltuieli.',
  'dashboard.widget.payments.upcoming.name': 'Plăți & încasări viitoare',
  'dashboard.widget.payments.upcoming.desc':
    'Scadențe în următoarele 14 zile, cronologic, cu totaluri.',
  'dashboard.widget.invoices.overdue.name': 'Facturi întârziate',
  'dashboard.widget.invoices.overdue.desc':
    'Facturi SmartBill cu scadența depășită, grupate pe client, cu remindere pe email.',
  'dashboard.widget.today.panel.name': 'Panoul „Azi”',
  'dashboard.widget.today.panel.desc': 'Facturile de azi și task-urile urgente ale zilei.',
  'dashboard.widget.team.allocation.name': 'Alocarea echipei',
  'dashboard.widget.team.allocation.desc':
    'Utilizare per persoană, proiecte active și status de încărcare.',
  'dashboard.widget.team.tasks.name': 'Task-urile echipei',
  'dashboard.widget.team.tasks.desc': 'Task-urile reale grupate pe persoană.',
  'dashboard.widget.projects.status.name': 'Status proiecte',
  'dashboard.widget.projects.status.desc': 'Carduri cu proiectele active, health și progres.',
  'dashboard.widget.activity.feed.name': 'Activitate recentă',
  'dashboard.widget.activity.feed.desc': 'Ultimele evenimente din proiecte, facturi și task-uri.',

  // Dashboard — overdue invoices widget + reminder modal
  'dashboard.overdue.title': 'Facturi întârziate',
  'dashboard.overdue.empty': 'Nicio factură cu scadența depășită.',
  'dashboard.overdue.invoiceCount': '{count} facturi',
  'dashboard.overdue.invoiceCountOne': '1 factură',
  'dashboard.overdue.daysOverdue': '{days} zile întârziere',
  'dashboard.overdue.daysOverdueShort': '{days} zile',
  'dashboard.overdue.sendReminderShort': 'Reminder',
  'dashboard.overdue.lastReminder': 'Ultimul reminder: {when}',
  'dashboard.overdue.noReminder': 'Fără reminder trimis',
  'dashboard.overdue.sendReminder': 'Trimite reminder',
  'dashboard.overdue.unmatchedClient': 'client neasociat',
  'reminder.modal.title': 'Reminder facturi — {client}',
  'reminder.modal.loading': 'Se pregătește emailul…',
  'reminder.modal.error': 'A apărut o eroare. Încearcă din nou.',
  'reminder.modal.summary': '{count} facturi întârziate, total de plată {total}.',
  'reminder.modal.to': 'Destinatar',
  'reminder.modal.subject': 'Subiect',
  'reminder.modal.body': 'Conținut email',
  'reminder.modal.preview': 'Previzualizare',
  'reminder.modal.edit': 'Editează',
  'reminder.modal.send': 'Trimite emailul',
  'reminder.modal.sending': 'Se trimite…',

  // Common actions / states (shared across pages)
  'common.save': 'Salvează',
  'common.cancel': 'Anulează',
  'common.delete': 'Șterge',
  'common.edit': 'Editează',
  'common.create': 'Creează',
  'common.add': 'Adaugă',
  'common.close': 'Închide',
  'common.confirm': 'Confirmă',
  'common.search': 'Caută',
  'common.loading': 'Se încarcă…',
  'common.error': 'A apărut o eroare.',
  'common.empty': 'Niciun rezultat.',
  'common.comingSoon': 'Acest modul va fi disponibil în curând.',
  'common.notFound': 'Pagina nu a fost găsită',
  'notFound.title': 'Pagina asta nu există în Firmio',
  'notFound.sectionHint':
    'Linkul arată ca unul din {section}, dar nu duce nicăieri. Ce era acolo a fost mutat sau șters, ori linkul e incomplet.',
  'notFound.genericHint':
    'Nicio pagină din Firmio nu răspunde la adresa asta. Verifică linkul primit sau pornește din meniul din stânga.',
  'notFound.goTo': 'Mergi la {section}',
  'notFound.back': 'Înapoi',
  'notFound.elsewhere': 'Sau continuă din',
  'notFound.footer': 'Ai ajuns aici dintr-un link din Firmio? Spune-ne:',
  'common.previous': 'Anterior',
  'common.next': 'Următorul',

  // Bulk selection (directory list pages)
  'bulk.selected': '{count} selectate',
  'bulk.delete': 'Șterge',
  'bulk.clear': 'Golește',
  'bulk.error': 'Acțiunea în masă a eșuat.',
  'common.all': 'Toate',
  'common.active': 'Activ',
  'common.inactive': 'Inactiv',
  'common.status': 'Status',
  'common.type': 'Tip',
  'common.category': 'Categorie',
  'common.author': 'Autor',
  'common.user': 'Utilizator',
  'common.exportExcel': 'Exportă Excel',
  'common.exporting': 'Se exportă…',
  'common.importExcel': 'Importă Excel',
  'common.list': 'Listă',

  // Custom Select component
  'ui.select.searchPlaceholder': 'Caută…',
  'ui.select.noResults': 'Niciun rezultat',
  'ui.select.clear': 'Golește selecția',
  'ui.select.loading': 'Se încarcă…',
  'ui.select.create': 'Adaugă „{name}”',

  // Leads
  'leads.title': 'Pipeline',
  'leads.subtitle': 'Vânzări și onboarding, de la primul contact la client.',
  'leads.view.board': 'Board',
  'leads.view.list': 'Listă',
  'leads.add': 'Adaugă lead',
  'leads.new': 'Lead nou',
  'leads.search': 'Caută leaduri…',
  'leads.filter.allTemperatures': 'Toate temperaturile',
  'leads.filter.allSources': 'Toate sursele',
  'leads.filter.allAssignees': 'Toți responsabilii',
  'leads.filter.allLabels': 'Toate etichetele',
  'leads.filter.selectedCount': '{count} selectate',
  'leads.filter.overdue': 'Acțiuni întârziate',
  'leads.filter.overdueOnly': 'Doar cu acțiuni întârziate',
  'leads.filter.clear': 'Golește filtrele',
  'leads.filter.archived': 'Arhivate',
  'leads.filter.archivedHint': 'Arată lead-urile arhivate',
  'leads.empty.archived': 'Niciun lead arhivat.',
  'leads.archivedBanner':
    'Vezi lead-urile arhivate. Deschide unul și apasă „Restaurează” ca să-l readuci în pipeline.',
  'leads.filter.search': 'Căutare',
  'leads.filter.temperature': 'Temperatură',
  'leads.filter.source': 'Sursă',
  'leads.filter.assignee': 'Responsabil',
  'leads.filter.labels': 'Etichete',
  'leads.loading': 'Se încarcă leadurile…',
  'leads.error': 'Încărcarea leadurilor a eșuat.',
  'leads.empty.noStages': 'Nicio etapă de pipeline de afișat.',
  'leads.empty.filtered': 'Niciun lead nu corespunde filtrelor selectate.',
  'leads.empty.none': 'Încă niciun lead.',
  'leads.pagination': 'Pagina {page} din {totalPages} · {total} leaduri',
  'leads.won.toast': '„{title}” a devenit client ({client}) și a trecut în {pipeline}.',
  'leads.won.toastClientOnly': '„{title}” a devenit client ({client}).',
  'leads.won.toastBulk': 'Lead-uri devenite clienți: {count}.',
  'leads.won.openClient': 'Vezi clientul',
  'leads.tabs': 'Pipeline-uri',
  'leads.clientBadge': 'Client',
  'leads.pipelineKind.SALES': 'Vânzări',
  'leads.pipelineKind.ONBOARDING': 'Onboarding',
  'leads.lost.prompt': 'Marchează ca pierdut. Motiv (opțional):',
  'leads.lost.prefix': 'Pierdut',
  'leads.created': 'Lead {identifier} creat',
  // Temperature
  'leads.temp.HOT': 'Fierbinte',
  'leads.temp.WARM': 'Cald',
  'leads.temp.COLD': 'Rece',
  // Source
  'leads.source.MANUAL': 'Manual',
  'leads.source.AI_ASSISTANT': 'Asistent AI',
  'leads.source.REFERRAL': 'Recomandare',
  'leads.source.WEBSITE': 'Site web',
  'leads.source.EMAIL': 'Email',
  'leads.source.PHONE': 'Telefon',
  'leads.source.SOCIAL_MEDIA': 'Rețele sociale',
  'leads.source.EVENT': 'Eveniment',
  'leads.source.OTHER': 'Altele',

  // Clients
  'clients.title': 'Clienți',
  'clients.subtitle': 'CRM-lite: facturi, proiecte, resurse și notițe de ședință per client.',
  'clients.seedFromInvoices': 'Importă din facturi',
  'clients.seeding': 'Se importă…',
  'clients.new': 'Client nou',
  'clients.search': 'Caută după nume, CUI, email…',
  'clients.filter.allProjects': 'Toate proiectele',
  // Actori (opt-in module, Genius Media) + client industry
  'clients.industry': 'Industrie',
  'clients.filter.allIndustries': 'Toate industriile',
  'sidebar.actors': 'Actori',
  'actors.title': 'Actori',
  'actors.subtitle': 'Actorii care apar în filmări și clienții la care au fost folosiți.',
  'actors.add': 'Adaugă actor',
  'actors.search': 'Caută actor…',
  'actors.filter.industry': 'Industrie',
  'actors.filter.client': 'Client',
  'actors.filter.allIndustries': 'Toate industriile',
  'actors.filter.allClients': 'Toți clienții',
  'actors.empty': 'Niciun actor încă. Adaugă primul actor.',
  'actors.emptyFiltered': 'Niciun actor nu se potrivește filtrelor.',
  'actors.noClients': 'Nefolosit încă la niciun client',
  'actors.conflictBadge': 'Conflict',
  'actors.conflictTooltip': 'Folosit la clienți concurenți ({industry}): {clients}',
  'actors.conflictMessage':
    'Atenție, acest actor a fost folosit la {clients}, potențial conflict de interese.',
  'actors.field.firstName': 'Prenume',
  'actors.field.lastName': 'Nume',
  'actors.field.phone': 'Telefon',
  'actors.field.email': 'Email',
  'actors.field.notes': 'Note',
  'actors.field.photo': 'Poză',
  'actors.form.createTitle': 'Actor nou',
  'actors.form.editTitle': 'Editează actorul',
  'actors.form.required': 'Numele și prenumele sunt obligatorii.',
  'actors.detail.back': 'Înapoi la actori',
  'actors.detail.clients': 'Clienți la care a apărut',
  'actors.detail.linkClient': 'Asociază client',
  'actors.detail.edit': 'Editează',
  'actors.detail.delete': 'Șterge',
  'actors.detail.deleteConfirm':
    'Ștergi actorul {name}? Nu mai apare în listă; asocierile cu clienții rămân în istoric.',
  'actors.col.client': 'Client',
  'actors.col.industry': 'Industrie',
  'actors.col.usedAt': 'Ultima filmare',
  'actors.col.note': 'Notă',
  'actors.unlink': 'Elimină asocierea',
  'actors.unlinkConfirm': 'Elimini asocierea cu {client}?',
  'actors.acknowledged': 'Conflict confirmat de {name} pe {date}',
  'actors.link.title': 'Asociază {name} la un client',
  'actors.link.titleForClient': 'Adaugă un actor la {client}',
  'actors.link.client': 'Client',
  'actors.link.actor': 'Actor',
  'actors.link.usedAt': 'Data filmării',
  'actors.link.note': 'Notă (opțional)',
  'actors.link.notePlaceholder': 'ex. Campania de Crăciun',
  'actors.link.check': 'Verifică și asociază',
  'actors.link.checking': 'Se verifică…',
  'actors.link.conflictTitle': 'Potențial conflict de interese',
  'actors.link.conflictLastShoot': 'ultima filmare {date}',
  'actors.link.confirm': 'Asociază oricum',
  'actors.link.cancel': 'Renunță',
  'actors.link.unknownIndustry':
    'Clientul nu are industria setată, așa că nu putem verifica dacă e concurent cu alți clienți ai actorului. Setează industria din fișa clientului.',
  'actors.link.alreadyLinked': 'Actorul e deja asociat acestui client.',
  'actors.link.unknownIndustryPick':
    'Clientul nu are industria setată. Industria ține de client (doi clienți din aceeași industrie sunt concurenți); alege-o aici ca să putem verifica:',
  'actors.setIndustry': 'Setează industria',
  'actors.detail.industries': 'Industrii (din clienții la care a apărut)',
  'actors.client.tab': 'Actori',
  'actors.client.add': 'Adaugă actor',
  'whatsapp.client.group': 'Grup WhatsApp',
  'whatsapp.client.synced': 'Sincronizat: {when}',
  'whatsapp.client.notSynced': 'Încă nesincronizat',
  'whatsapp.client.older': 'Mesaje mai vechi',
  'whatsapp.client.loading': 'Se încarcă…',
  'whatsapp.client.noMessages': 'Niciun mesaj sincronizat încă din acest grup.',
  'whatsapp.client.empty':
    'Niciun grup WhatsApp legat de acest client. Un administrator îl leagă din Setări → Integrări → WhatsApp.',
  'whatsapp.client.loadError': 'Conversațiile WhatsApp nu au putut fi încărcate.',
  'whatsapp.settings.title': 'Conexiunea WhatsApp',
  'whatsapp.settings.notConfigured':
    'Completează mai sus adresa și tokenul serviciului WhatsApp, apoi salvează.',
  'whatsapp.settings.unreachable': 'Serviciul WhatsApp nu răspunde: {error}',
  'whatsapp.settings.connected': 'Conectat',
  'whatsapp.settings.pairing': 'Așteaptă scanarea codului QR',
  'whatsapp.settings.disconnected': 'Deconectat',
  'whatsapp.settings.qrExpired':
    'Codul QR a expirat (WhatsApp îl ține activ doar 2–3 minute). Generează unul nou și scanează-l imediat.',
  'whatsapp.settings.connecting': 'Se conectează la WhatsApp…',
  'whatsapp.settings.newQr': 'Generează cod QR nou',
  'whatsapp.settings.scanHelp':
    'Pe telefonul cu numărul dedicat: WhatsApp → Setări → Dispozitive conectate → Conectează un dispozitiv, apoi scanează codul. Codul se reîmprospătează singur.',
  'whatsapp.settings.groups': 'Grupuri și clienți',
  'whatsapp.settings.groupsCount': '· {linked} legate din {total}',
  'whatsapp.settings.searchGroups': 'Caută grup…',
  'whatsapp.settings.groupsHelp':
    'Leagă fiecare grup de clientul lui. Doar grupurile legate se sincronizează, la {hours} ore.',
  'whatsapp.settings.noGroups':
    'Niciun grup încă. Apar după prima sincronizare cu numărul conectat.',
  'whatsapp.settings.col.group': 'Grup',
  'whatsapp.settings.col.client': 'Client',
  'whatsapp.settings.col.messages': 'Mesaje',
  'whatsapp.settings.col.synced': 'Ultima sincronizare',
  'whatsapp.settings.noClient': 'Nelegat',
  'whatsapp.settings.syncNow': 'Sincronizează acum',
  'whatsapp.settings.syncStarted': 'Sincronizarea a pornit.',
  'whatsapp.settings.lastSync':
    'Ultima sincronizare: {when} · {created} mesaje noi din {synced} grupuri',
  'whatsapp.settings.lastSyncStatus': 'Ultima încercare: {when} · {message}',
  'whatsapp.settings.autoOff': 'Sincronizarea automată e oprită pe acest server.',
  'actors.client.empty': 'Niciun actor folosit încă la acest client.',
  'actors.client.alsoAt': 'Folosit și la concurenți: {clients}',
  'settings.modules.optInLocked': 'Modul la cerere — se activează din consola Firmio.',
  'settings.modules.optInLocal': 'Modul la cerere — pornit doar dacă îl activezi aici.',
  'clients.filter.withProjects': 'Cu proiecte',
  'clients.filter.withoutProjects': 'Fără proiecte',
  'clients.exportError': 'Nu s-au putut exporta clienții. Încearcă din nou.',
  'clients.loadError': 'Nu s-au putut încărca clienții.',
  'clients.empty': 'Niciun client. Creează unul nou sau importă din facturile existente.',
  'clients.col.client': 'Client',
  'clients.col.cui': 'CUI',
  'clients.col.projects': 'Proiecte',
  'clients.col.invoiced': 'Facturat',
  'clients.col.received': 'Încasat',
  'clients.col.outstanding': 'Restant',
  'clients.pagination': 'Pagina {page} din {totalPages} ({total} în total)',
  'clients.bulk.confirmTitle': 'Șterge clienții selectați',
  'clients.bulk.confirmMessage':
    'Ștergi {count} client(clienți)? Vor fi marcați ca inactivi; facturile, proiectele și istoricul rămân.',

  // Suppliers
  'suppliers.title': 'Furnizori',
  'suppliers.subtitle':
    'Furnizori și costuri recurente folosite pentru a potrivi automat cheltuielile din extrasele bancare.',
  'suppliers.new': 'Furnizor nou',
  'suppliers.search': 'Caută după nume, CUI, IBAN, contact…',
  'suppliers.filter.allCategories': 'Toate categoriile',
  'suppliers.filter.recurring': 'Recurent',
  'suppliers.filter.oneTime': 'O singură dată',
  'suppliers.filter.anyStatus': 'Orice status',
  'suppliers.exportError': 'Nu s-au putut exporta furnizorii. Încearcă din nou.',
  'suppliers.loadError': 'Nu s-au putut încărca furnizorii.',
  'suppliers.empty':
    'Niciun furnizor încă. Creează unul sau adaugă-l direct în timpul reconcilierii unui extras bancar.',
  'suppliers.col.supplier': 'Furnizor',
  'suppliers.col.cui': 'CUI',
  'suppliers.col.category': 'Categorie',
  'suppliers.col.totalSpent': 'Total cheltuit',
  'suppliers.col.recurring': 'Recurent',
  'suppliers.recurring.yes': 'Recurent',
  'suppliers.recurring.oneTime': 'O singură dată',
  'suppliers.pagination': 'Pagina {page} din {totalPages} ({total} în total)',
  'suppliers.bulk.confirmTitle': 'Șterge furnizorii selectați',
  'suppliers.bulk.confirmMessage':
    'Ștergi {count} furnizor(i)? Vor fi marcați ca inactivi; istoricul financiar rămâne.',

  // Subscriptions / Abonamente
  'subscriptions.title': 'Abonamente & Utilități',
  'subscriptions.subtitle':
    'Abonamente POS, utilități, SaaS — potrivite prin numele comerciantului.',
  'subscriptions.create': 'Adaugă abonament',
  'subscriptions.search': 'Caută după nume sau patronaj…',
  'subscriptions.name': 'Nume',
  'subscriptions.category': 'Categorie',
  'subscriptions.supplier': 'Furnizor',
  'subscriptions.patterns': 'Patronaje POS',
  'subscriptions.amount': 'Sumă estimată',
  'subscriptions.frequency': 'Frecvență',
  'subscriptions.filter.allCategories': 'Toate categoriile',
  'subscriptions.filter.anyStatus': 'Orice status',
  'subscriptions.exportError': 'Nu s-au putut exporta abonamentele. Încearcă din nou.',
  'subscriptions.loadError': 'Nu s-au putut încărca abonamentele.',
  'subscriptions.empty': 'Niciun abonament. Adaugă primul pentru potrivire automată pe POS.',
  'subscriptions.pagination': 'Pagina {page} din {totalPages} ({total} în total)',
  'subscriptions.bulk.confirmTitle': 'Șterge abonamentele selectate',
  'subscriptions.bulk.confirmMessage':
    'Ștergi {count} abonament(e)? Vor fi marcate ca inactive; plățile potrivite rămân.',
  'subscriptions.saas': 'SaaS',
  'subscriptions.utility': 'Utilitate',
  'subscriptions.telecom': 'Telecom',
  'subscriptions.fuel': 'Carburant',
  'subscriptions.office': 'Birou',
  'subscriptions.hosting': 'Hosting',
  'subscriptions.other': 'Altele',
  'subscriptions.monthly': 'Lunar',
  'subscriptions.quarterly': 'Trimestrial',
  'subscriptions.annual': 'Anual',
  'subscriptions.usage': 'La consum',
  'financials.subscription': 'Abonamente',
  'financials.expenseCategory': 'Categorie cheltuieli',
  'recon.subscription': 'Abonament',
  'recon.addSubscription': 'Adaugă abonament',

  // People / Echipă
  'people.title': 'Director echipă',
  'people.addMember': 'Adaugă membru',
  'people.capacityPlanning': 'Planificare capacitate',
  'people.capacityShort': 'Capacitate',
  'people.view.chart': 'Organigramă',
  'people.view.list': 'Listă',
  'people.view.label': 'Vizualizare echipă',
  'people.editOrg': 'Editează organigrama',
  'people.editOrgDone': 'Gata editare',
  'people.search': 'Caută după nume sau email…',
  'people.filter.allDepartments': 'Toate departamentele',
  'people.error.org': 'Încărcarea organigramei a eșuat.',
  'people.error.directory': 'Încărcarea directorului echipei a eșuat.',
  'people.editList': 'Editează lista',
  'people.editListDone': 'Gata editare',
  'people.col.name': 'Nume',
  'people.col.department': 'Departament',
  'people.col.allocations': 'Alocări (ore/săpt)',
  'people.col.utilization': 'Utilizare',
  'people.noDepartment': 'Fără departament',
  'people.noAllocations': 'Fără alocări',
  'people.hoursPerWeekSuffix': 'h/săpt',
  'people.edit.saveFailed': 'Salvarea a eșuat.',
  'people.edit.hoursRange': 'Orele trebuie să fie între 0 și 168.',

  // People — Angajare tab (CIM/B2B)
  'people.employment.tab': 'Angajare',
  'people.employment.loading': 'Se încarcă datele de angajare…',
  'people.employment.loadError': 'Încărcarea datelor de angajare a eșuat.',
  'people.employment.typeTitle': 'Tip colaborare',
  'people.employment.contractor': 'Colaborator (B2B)',
  'people.employment.contractorHint': 'Facturează printr-o firmă proprie (SRL/PFA)',
  'people.employment.employee': 'Angajat (CIM)',
  'people.employment.employeeHint': 'Contract individual de muncă',
  'people.employment.companyTitle': 'Datele firmei',
  'people.employment.cui': 'CUI',
  'people.employment.anafLookup': 'Caută la ANAF',
  'people.employment.anafInvalidCui': 'CUI invalid — introdu 2–10 cifre (cu sau fără prefixul RO).',
  'people.employment.anafNotFound': 'Firma nu a fost găsită la ANAF.',
  'people.employment.anafFailed': 'Căutarea la ANAF a eșuat.',
  'people.employment.anafSuccess': 'Datele firmei au fost preluate de la ANAF.',
  'people.employment.companyName': 'Denumire firmă',
  'people.employment.regCom': 'Nr. Reg. Com.',
  'people.employment.address': 'Adresă',
  'people.employment.iban': 'IBAN',
  'people.employment.save': 'Salvează',
  'people.employment.saved': 'Datele de angajare au fost salvate.',
  'people.employment.saveFailed': 'Salvarea datelor de angajare a eșuat.',
  'people.employment.cimTitle': 'Contract de muncă (CIM)',
  'people.employment.cimHint': 'PDF, până la 10MB',
  'people.employment.cimEmpty': 'Niciun contract încărcat.',
  'people.employment.cimUploading': 'Se încarcă contractul…',
  'people.employment.cimUploaded': 'Contractul a fost încărcat.',
  'people.employment.cimUploadFailed': 'Încărcarea contractului a eșuat.',
  'people.employment.cimTooLarge': 'Fișierul depășește limita de 10MB.',
  'people.employment.cimPreview': 'Previzualizează',
  'people.employment.cimPreviewFailed': 'Previzualizarea contractului a eșuat.',
  'people.employment.cimDelete': 'Șterge',
  'people.employment.cimDeleteConfirm': 'Ștergi contractul CIM încărcat?',
  'people.employment.cimDeleted': 'Contractul a fost șters.',
  'people.employment.cimDeleteFailed': 'Ștergerea contractului a eșuat.',

  // People — payroll ("ștat de plată")
  'people.payroll.title': 'Ștat de plată',
  'people.payroll.upload': 'Încarcă ștat de plată',
  'people.payroll.loading': 'Se încarcă istoricul salarial…',
  'people.payroll.loadError': 'Încărcarea istoricului salarial a eșuat.',
  'people.payroll.empty': 'Niciun ștat de plată înregistrat.',
  'people.payroll.month': 'Luna',
  'people.payroll.gross': 'Brut',
  'people.payroll.net': 'Net',
  'people.payroll.employeeContributions': 'Contribuții',
  'people.payroll.incomeTax': 'Impozit',
  'people.payroll.cam': 'CAM',
  'people.payroll.totalCost': 'Cost total',
  'people.payroll.source': 'Sursă',
  'people.payroll.sourceFailed': 'Deschiderea PDF-ului sursă a eșuat.',
  'people.payroll.dialogTitle': 'Încarcă ștat de plată',
  'people.payroll.pickIntro':
    'Încarcă PDF-ul ștatului de plată primit de la contabil. Datele sunt extrase automat, iar înainte de salvare poți verifica și corecta fiecare rând.',
  'people.payroll.monthOverride': 'Luna (opțional)',
  'people.payroll.yearOverride': 'Anul (opțional)',
  'people.payroll.autoDetect': 'Detectează automat',
  'people.payroll.pickHint': 'PDF, până la 10MB',
  'people.payroll.parsing': 'Se extrag datele din PDF…',
  'people.payroll.fileTooLarge': 'Fișierul depășește limita de 10MB.',
  'people.payroll.previewFailed': 'Extracția datelor a eșuat.',
  'people.payroll.reviewIntro': '{matched} din {total} rânduri asociate cu membri ai echipei.',
  'people.payroll.extractedName': 'Nume extras',
  'people.payroll.matchedPerson': 'Persoană',
  'people.payroll.unmatched': '— neasociat —',
  'people.payroll.unmatchedHint':
    'Rândurile neasociate (evidențiate) nu vor fi salvate. Alege persoana din listă pentru a le include.',
  'people.payroll.removeRow': 'Elimină rândul',
  'people.payroll.duplicatePerson': 'Aceeași persoană apare pe mai multe rânduri.',
  'people.payroll.confirm': 'Confirmă ({count})',
  'people.payroll.confirmed': '{count} intrări salariale au fost salvate.',
  'people.payroll.confirmFailed': 'Salvarea intrărilor salariale a eșuat.',
  'people.payroll.dash.title': 'Dashboard financiar',
  'people.payroll.dash.subtitle':
    'Cost lunar total (net + taxe), ultimele {count} luni înregistrate',
  'people.payroll.dash.totalPaid': 'Total plătit ({count} luni)',
  'people.payroll.dash.avgMonthly': 'Medie lunară',
  'people.payroll.dash.lastMonth': 'Ultima lună',
  'people.payroll.dash.lastMonthDetail': 'net {net} · taxe {taxes}',

  // Cash flow (Financiar › Plăți & Încasări viitoare)
  'cashflow.title': 'Plăți & Încasări viitoare',
  'cashflow.horizon': 'Orizont',
  'cashflow.monthsShort': '{count} luni',
  'cashflow.addPlanned': 'Adaugă plată/încasare',
  'cashflow.editPlanned': 'Editează plata/încasarea',
  'cashflow.chartTitle': 'Intrări vs ieșiri pe lună',
  'cashflow.monthlyBreakdown': 'Defalcare lunară',
  'cashflow.upcomingPayments': 'Plăți viitoare',
  'cashflow.upcomingReceipts': 'Încasări viitoare',
  'cashflow.net': 'Net',
  'cashflow.overdue': 'Restantă',
  'cashflow.unconfirmed': 'Neconfirmată',
  'cashflow.view.months': 'Pe luni',
  'cashflow.view.days': 'Pe zile',
  'cashflow.days.date': 'Data',
  'cashflow.days.inflow': 'Încasări',
  'cashflow.days.outflow': 'Plăți',
  'cashflow.days.balance': 'Sold',
  'cashflow.days.what': 'Ce se întâmplă',
  'cashflow.days.opening': 'Sold de pornire',
  'cashflow.days.empty': 'Nicio mișcare în orizontul selectat.',
  'cashflow.currency': 'Monedă',
  'cashflow.noMovements': 'Fără mișcări în această lună.',
  'cashflow.noPayments': 'Nicio plată viitoare în orizontul selectat.',
  'cashflow.noReceipts': 'Nicio încasare viitoare în orizontul selectat.',
  'cashflow.loadError': 'Nu s-a putut încărca proiecția de cash flow.',
  'cashflow.excludedNote':
    'Excluse din proiecție: {quarterlyAnnual} abonamente trimestriale/anuale și {usage} la consum (sumă/dată incertă).',
  'cashflow.source.subscription': 'Abonament',
  'cashflow.source.planned': 'Planificat',
  'cashflow.source.invoice': 'Factură',
  'cashflow.direction': 'Tip',
  'cashflow.directionPayment': 'Plată',
  'cashflow.directionReceipt': 'Încasare',
  'cashflow.dueDate': 'Data scadenței',
  'cashflow.form.name': 'Nume',
  'cashflow.form.amount': 'Sumă',
  'cashflow.form.currency': 'Monedă',
  'cashflow.form.company': 'Firmă',
  'cashflow.form.defaultCompany': 'Firma implicită',
  'cashflow.form.status': 'Status',
  'cashflow.form.notes': 'Notițe',
  'cashflow.form.nameRequired': 'Numele este obligatoriu.',
  'cashflow.form.amountInvalid': 'Suma trebuie să fie un număr pozitiv.',
  'cashflow.form.dueDateRequired': 'Data scadenței este obligatorie.',
  'cashflow.status.planned': 'Planificată',
  'cashflow.status.done': 'Efectuată',
  'cashflow.status.cancelled': 'Anulată',
  'cashflow.deleteTitle': 'Șterge plata/încasarea planificată',
  'cashflow.deleteMessage': 'Sigur ștergi „{name}"? Acțiunea nu poate fi anulată.',
  'cashflow.projectedBalance': 'Sold estimat',
  'cashflow.balances.title': 'Bani în conturi',
  'cashflow.balances.current': 'Sold curent',
  'cashflow.balances.asOf': 'la data de {date}',
  'cashflow.balances.accounts': '{count} conturi',
  'cashflow.balances.empty':
    'Niciun cont bancar activ. Adaugă conturile în Setări → Conturi bancare.',
  'cashflow.balances.unknown':
    'Sold necunoscut — încarcă un extras bancar sau setează soldul manual per cont.',
  'cashflow.balances.unknownAccount': 'Sold necunoscut',
  'cashflow.balances.source.statement': 'din extras',
  'cashflow.balances.source.manual': 'setat manual',
  'cashflow.balances.editLabel': 'Setează soldul manual',
  'cashflow.balances.clear': 'Revino la extras',
  'cashflow.balances.clearHint': 'Șterge soldul manual și folosește din nou extrasul.',
  'subscriptions.paymentDay': 'Ziua plății',

  // Financials / Financiar
  'financials.title': 'Financiar',
  'financials.exportCsv': 'Exportă CSV',
  'financials.exportPdf': 'Exportă PDF',
  'financials.exportFailed': 'Exportul a eșuat',
  'financials.allYears': 'Toți anii',
  'financials.allMonths': 'Tot anul',
  'financials.yearLabel': 'An',
  'financials.monthLabel': 'Lună',
  'financials.compare': 'Compară cu perioada anterioară',
  'financials.revenueVsCosts': 'Venituri vs Costuri',
  'financials.error.overview': 'Nu s-a putut încărca prezentarea financiară.',
  'financials.error.trends': 'Nu s-au putut încărca tendințele.',
  'financials.forecastNote':
    'Prognoza proiectează următoarele {count} perioade pe baza tendinței istorice (prognoză de cash-flow).',
  'financials.metric.totalRevenue': 'Venituri totale',
  'financials.metric.totalExpenses': 'Cheltuieli totale',
  'financials.metric.profit': 'Profit',
  'financials.metric.profitMargin': 'Marjă de profit',
  'financials.range.3m': '3 luni',
  'financials.range.6m': '6 luni',
  'financials.range.1y': '1 an',
  'financials.range.all': 'Tot timpul',
  'financials.month.1': 'Ianuarie',
  'financials.month.2': 'Februarie',
  'financials.month.3': 'Martie',
  'financials.month.4': 'Aprilie',
  'financials.month.5': 'Mai',
  'financials.month.6': 'Iunie',
  'financials.month.7': 'Iulie',
  'financials.month.8': 'August',
  'financials.month.9': 'Septembrie',
  'financials.month.10': 'Octombrie',
  'financials.month.11': 'Noiembrie',
  'financials.month.12': 'Decembrie',
  'financials.chart.revenue': 'Venituri',
  'financials.chart.costs': 'Costuri',
  'financials.chart.profit': 'Profit',
  'financials.chart.ariaLabel': 'Venituri, costuri și profit în timp',
  'financials.chart.empty': 'Nu există date de tendință disponibile.',
  'financials.chart.legend': '— linie continuă: realizat · - - linie punctată: estimare',
  'financials.tab.overview': 'Prezentare generală',
  'financials.tab.analytics': 'Analiză',
  'financials.analytics.dimension': 'Dimensiune',
  'financials.analytics.dim.firm': 'Firmă',
  'financials.analytics.dim.department': 'Departament',
  'financials.analytics.dim.service': 'Serviciu',
  'financials.analytics.dim.client': 'Client',
  'financials.analytics.dim.supplier': 'Furnizor',
  'financials.analytics.dim.subscription': 'Abonament',
  'financials.analytics.dim.person': 'Coleg',
  'financials.analytics.col.label': 'Etichetă',
  'financials.analytics.col.income': 'Venituri',
  'financials.analytics.col.expenses': 'Cheltuieli',
  'financials.analytics.col.profit': 'Profit',
  'financials.analytics.total': 'Total',
  'financials.analytics.untagged': 'Neetichetat',
  'financials.analytics.empty': 'Nu există date financiare etichetate pentru perioada selectată.',
  'financials.analytics.error': 'Nu s-a putut încărca analiza financiară.',
  'financials.analytics.notesTitle': 'Cum se calculează',
  'financials.analytics.note.smartbill-overlap':
    'Aceeași factură reală poate exista atât în SmartBill, cât și ca factură încărcată manual — sumele nu sunt deduplicate între cele două surse.',
  'financials.analytics.note.ledger-differs':
    'Totalurile pot diferi de tabul Prezentare generală, care citește registrul financiar (P&L).',
  'financials.analytics.note.finalized-only':
    'Din extrasele bancare contează doar liniile reconciliate din extrase finalizate; liniile asociate unei facturi nu sunt numărate de două ori.',
  'financials.analytics.note.method-filter-channel-only':
    'Filtrul pe metodă include doar liniile bancare/cash (și salariile pentru Transfer); facturile nu au metodă de plată.',

  // Financiar → Rapoarte lunare (raportul lunar al antreprenorului)
  'monthlyReport.title': 'Raport lunar',
  'monthlyReport.subtitle':
    'Tot ce s-a întâmplat financiar în luna selectată: bani, facturi, TVA și ce urmează.',
  'monthlyReport.prevMonth': 'Luna anterioară',
  'monthlyReport.nextMonth': 'Luna următoare',
  'monthlyReport.yearLabel': 'An',
  'monthlyReport.monthLabel': 'Lună',
  'monthlyReport.closed': 'Lună închisă',
  'monthlyReport.open': 'Lună deschisă',
  'monthlyReport.closedAt': 'Închisă la {date}',
  'monthlyReport.closedAtBy': 'Închisă la {date} de {name}',
  'monthlyReport.snapshotNote': 'Cifrele sunt înghețate așa cum erau la închiderea lunii.',
  'monthlyReport.closeMonth': 'Închide luna',
  'monthlyReport.reopenMonth': 'Redeschide luna',
  'monthlyReport.downloadPdf': 'Descarcă PDF',
  'monthlyReport.closeConfirm':
    'Închizi luna {period}? Cifrele se îngheață așa cum sunt acum; poți redeschide luna oricând.',
  'monthlyReport.reopenConfirm':
    'Redeschizi luna {period}? Snapshot-ul se șterge și raportul se recalculează din datele curente.',
  'monthlyReport.actionFailed': 'Acțiunea nu a reușit. Încearcă din nou.',
  'monthlyReport.loadError': 'Raportul nu a putut fi încărcat.',
  'monthlyReport.generatedAt': 'Calculat la {date}',
  'monthlyReport.section.summary': 'Rezumatul lunii',
  'monthlyReport.section.accounts': 'Cash flow pe conturi',
  'monthlyReport.section.revenue': 'Venituri',
  'monthlyReport.section.expenses': 'Cheltuieli pe categorii',
  'monthlyReport.section.receivables': 'Facturi neîncasate (creanțe)',
  'monthlyReport.section.payables': 'Facturi de plătit (datorii)',
  'monthlyReport.section.vat': 'Situația TVA',
  'monthlyReport.section.dataQuality': 'Reconciliere și calitatea datelor',
  'monthlyReport.section.projects': 'Pe proiecte',
  'monthlyReport.section.trends': 'Tendințe pe 12 luni',
  'monthlyReport.summary.inflows': 'Încasări',
  'monthlyReport.summary.outflows': 'Plăți',
  'monthlyReport.summary.net': 'Rezultat net (cash)',
  'monthlyReport.summary.bankBalance': 'Sold bancar la final de lună',
  'monthlyReport.summary.bankBalanceAsOf': 'la {date}',
  'monthlyReport.summary.bankBalanceUnknown': 'Niciun extras cu sold cunoscut',
  'monthlyReport.summary.invoiced': 'Facturat',
  'monthlyReport.summary.collected': 'Încasat',
  'monthlyReport.summary.invoicedVsCollected': 'Facturat vs încasat',
  'monthlyReport.summary.vsPrevious': 'față de luna trecută',
  'monthlyReport.summary.noPrevious': 'Nimic în registru luna trecută',
  'monthlyReport.summary.otherCurrencies': 'Alte valute (neconvertite)',
  'monthlyReport.accounts.account': 'Cont',
  'monthlyReport.accounts.opening': 'Sold inițial',
  'monthlyReport.accounts.credits': 'Intrări',
  'monthlyReport.accounts.debits': 'Ieșiri',
  'monthlyReport.accounts.closing': 'Sold final',
  'monthlyReport.accounts.net': 'Net',
  'monthlyReport.accounts.transactions': '{n} tranzacții',
  'monthlyReport.accounts.internal': '{n} transferuri interne excluse',
  'monthlyReport.accounts.unreconciled': '{n} nereconciliate',
  'monthlyReport.accounts.unfinalized': '{n} extrase nefinalizate',
  'monthlyReport.accounts.missing': 'Extras lipsă pentru această lună',
  'monthlyReport.accounts.unregistered': 'Cont neînregistrat',
  'monthlyReport.accounts.none': 'Niciun cont bancar și niciun extras pentru această lună.',
  'monthlyReport.revenue.invoices': 'Facturi emise',
  'monthlyReport.revenue.creditNotes': '{n} storno',
  'monthlyReport.revenue.net': 'Fără TVA',
  'monthlyReport.revenue.vat': 'TVA',
  'monthlyReport.revenue.total': 'Total facturat',
  'monthlyReport.revenue.collected': 'Încasat în lună',
  'monthlyReport.revenue.topInvoiced': 'Top clienți după facturat',
  'monthlyReport.revenue.topCollected': 'Top clienți după încasat',
  'monthlyReport.revenue.concentration': '{name} reprezintă {share}% din facturat',
  'monthlyReport.revenue.concentrationWarning':
    'Peste jumătate din venit vine de la un singur client.',
  'monthlyReport.revenue.none': 'Nicio factură emisă și nicio încasare în această lună.',
  'monthlyReport.expenses.category': 'Categorie',
  'monthlyReport.expenses.amount': 'Luna aceasta',
  'monthlyReport.expenses.previous': 'Luna trecută',
  'monthlyReport.expenses.average3m': 'Media 3 luni',
  'monthlyReport.expenses.share': 'Pondere',
  'monthlyReport.expenses.topSuppliers': 'Top furnizori',
  'monthlyReport.expenses.subscriptions': 'Abonamente',
  'monthlyReport.expenses.subscriptionsTotal': 'Total abonamente plătite',
  'monthlyReport.expenses.expected': 'așteptat {amount}',
  'monthlyReport.expenses.status.paid': 'Plătit',
  'monthlyReport.expenses.status.missing': 'Lipsește',
  'monthlyReport.expenses.status.amount_differs': 'Sumă diferită',
  'monthlyReport.expenses.status.not_due': 'Nu e scadent',
  'monthlyReport.expenses.none': 'Nicio cheltuială în registru pentru această lună.',
  'monthlyReport.open.total': 'Total',
  'monthlyReport.open.count': '{n} facturi',
  'monthlyReport.open.bucket.current': 'Neajunse la scadență',
  'monthlyReport.open.bucket.d1_30': '1–30 zile',
  'monthlyReport.open.bucket.d31_60': '31–60 zile',
  'monthlyReport.open.bucket.d61_90': '61–90 zile',
  'monthlyReport.open.bucket.d90plus': 'Peste 90 zile',
  'monthlyReport.open.topParty': 'Cea mai mare restanță',
  'monthlyReport.open.invoice': 'Factură',
  'monthlyReport.open.party': 'Partener',
  'monthlyReport.open.amount': 'Sumă',
  'monthlyReport.open.dueDate': 'Scadență',
  'monthlyReport.open.daysOverdue': '+{n} zile',
  'monthlyReport.open.showAll': 'Arată toate ({n})',
  'monthlyReport.open.showLess': 'Arată mai puține',
  'monthlyReport.open.otherCurrencies': 'În alte valute',
  'monthlyReport.open.noneReceivables': 'Nicio factură neîncasată la finalul lunii.',
  'monthlyReport.open.nonePayables': 'Nicio factură de plătit la finalul lunii.',
  'monthlyReport.open.scheduledNext': 'Programate pentru luna următoare',
  'monthlyReport.open.scheduledPayments': 'Plăți',
  'monthlyReport.open.scheduledReceipts': 'Încasări',
  'monthlyReport.vat.notApplicable': 'Firma nu este plătitoare de TVA.',
  'monthlyReport.vat.collected': 'TVA colectată',
  'monthlyReport.vat.deductible': 'TVA deductibilă',
  'monthlyReport.vat.toPay': 'De plată',
  'monthlyReport.vat.toRecover': 'De recuperat',
  'monthlyReport.vat.month': 'Luna',
  'monthlyReport.vat.quarter': 'Trimestrul (cumulat)',
  'monthlyReport.vat.note':
    'Estimare din facturile emise și primite prin SPV în perioadă; decontul final îl face contabilul.',
  'monthlyReport.quality.transactions': 'Tranzacții în lună',
  'monthlyReport.quality.reconciled': 'Reconciliate',
  'monthlyReport.quality.unmatched': 'Nereconciliate',
  'monthlyReport.quality.ignored': 'Ignorate',
  'monthlyReport.quality.statements': 'Extrase',
  'monthlyReport.quality.finalized': 'finalizate',
  'monthlyReport.quality.accountsWithoutStatement': 'Conturi fără extras',
  'monthlyReport.quality.receivedUnpaid': 'Facturi primite în lună, neachitate',
  'monthlyReport.quality.ledgerRows': 'Linii în registru',
  'monthlyReport.quality.allGood': 'Datele lunii sunt complete și reconciliate.',
  'monthlyReport.quality.warning.no_statements':
    'Niciun extras bancar încărcat pentru această lună — secțiunile de cash sunt goale.',
  'monthlyReport.quality.warning.statements_unfinalized':
    'Există extrase nefinalizate: liniile lor nu au ajuns încă în registrul financiar.',
  'monthlyReport.quality.warning.unmatched_lines':
    'Există tranzacții nereconciliate; totalurile pe categorii sunt incomplete.',
  'monthlyReport.quality.warning.ledger_empty':
    'Extrasele există, dar nimic nu a ajuns în registru — finalizează reconcilierea.',
  'monthlyReport.quality.warning.accounts_without_statement':
    'Unele conturi bancare nu au extras pentru această lună.',
  'monthlyReport.quality.goToStatements': 'Mergi la extrase',
  'monthlyReport.projects.project': 'Proiect',
  'monthlyReport.projects.revenue': 'Venit',
  'monthlyReport.projects.expenses': 'Cost',
  'monthlyReport.projects.margin': 'Marjă',
  'monthlyReport.projects.unallocated': 'Fără proiect',
  'monthlyReport.projects.unallocatedShare': '{share}% din cheltuieli nu au proiect asociat.',
  'monthlyReport.projects.none': 'Nicio linie din registru nu are proiect asociat luna aceasta.',
  'monthlyReport.trends.cashflow': 'Încasări vs plăți',
  'monthlyReport.trends.cumulative': 'Rezultat net cumulat',
  'monthlyReport.trends.balance': 'Sold bancar la final de lună',
  'monthlyReport.trends.balanceNone': 'Niciun extras cu sold în ultimele 12 luni.',
  'monthlyReport.trends.runway': 'Runway estimat',
  'monthlyReport.trends.runwayValue': '{n} luni',
  'monthlyReport.trends.runwayUnknown': 'necunoscut',
  'monthlyReport.trends.runwayHint':
    'Soldul de la final de lună împărțit la media cheltuielilor pe 3 luni.',
  'monthlyReport.trends.average3m': 'Media cheltuielilor (3 luni)',
  // Rapoarte: selectorul de perioadă, taburile și sinteza
  'monthlyReport.titleGeneric': 'Raport financiar',
  'monthlyReport.period.kindLabel': 'Perioadă',
  'monthlyReport.period.kind.month': 'Lună',
  'monthlyReport.period.kind.quarter': 'Trimestru',
  'monthlyReport.period.kind.year': 'An',
  'monthlyReport.period.kind.custom': 'Interval',
  'monthlyReport.period.quarterLabel': 'Trimestru',
  'monthlyReport.period.fromLabel': 'De la',
  'monthlyReport.period.toLabel': 'Până la',
  'monthlyReport.period.prev': 'Perioada anterioară',
  'monthlyReport.period.next': 'Perioada următoare',
  'monthlyReport.period.invalidRange': 'Data de început trebuie să fie înaintea celei de sfârșit.',
  'monthlyReport.period.closedMonth': 'închisă',
  'monthlyReport.period.compareLabel': 'Comparat cu',
  'monthlyReport.period.compare.prev': 'Perioada precedentă',
  'monthlyReport.period.compare.yoy': 'Aceeași perioadă anul trecut',
  'monthlyReport.period.comparedWith': 'Comparat cu {period}',
  'monthlyReport.downloadXlsx': 'Descarcă Excel',
  'monthlyReport.staleSnapshot':
    'Raportul a fost înghețat cu o versiune anterioară, așa că secțiunile noi lipsesc.',
  'monthlyReport.recompute': 'Recalculează',
  'monthlyReport.tab.synthesis': 'Sinteză',
  'monthlyReport.tab.revenue': 'Venituri și clienți',
  'monthlyReport.tab.expenses': 'Cheltuieli și abonamente',
  'monthlyReport.tab.people': 'Oameni',
  'monthlyReport.tab.projects': 'Proiecte',
  'monthlyReport.tab.cash': 'Cash și bancă',
  'monthlyReport.tab.tax': 'Taxe',
  'monthlyReport.tab.quality': 'Calitatea datelor',
  'monthlyReport.section.synthesis': 'Pe scurt',
  'monthlyReport.section.people': 'Costul cu oamenii',
  'monthlyReport.section.clients': 'Clienți',
  'monthlyReport.synthesis.marginPct': 'Marjă netă',
  'monthlyReport.synthesis.peopleCost': 'Cost cu oamenii',
  'monthlyReport.synthesis.burn': 'Cheltuieli medii pe lună',
  'monthlyReport.synthesis.topClients': 'Cei mai mari clienți',
  'monthlyReport.synthesis.topExpenses': 'Cele mai mari cheltuieli',
  'monthlyReport.synthesis.attention': 'De verificat',
  'monthlyReport.synthesis.allGood': 'Nimic de semnalat: datele perioadei arată complete.',
  // Oameni
  'monthlyReport.people.empty': 'Niciun fluturaș de salariu încărcat pentru perioada asta.',
  'monthlyReport.people.totalCost': 'Cost total',
  'monthlyReport.people.headcount': 'Oameni',
  'monthlyReport.people.headcountValue': '{n} persoane',
  'monthlyReport.people.shareOfOutflows': 'Din plăți',
  'monthlyReport.people.shareOfInflows': 'Din încasări',
  'monthlyReport.people.revenuePerPerson': 'Încasări per persoană',
  'monthlyReport.people.returnPerLeu': 'Lei încasați / leu de cost',
  'monthlyReport.people.breakdown': 'Din ce se compune costul',
  'monthlyReport.people.gross': 'Salariu brut',
  'monthlyReport.people.contributions': 'Contribuții angajat',
  'monthlyReport.people.incomeTax': 'Impozit pe venit',
  'monthlyReport.people.cam': 'CAM angajator',
  'monthlyReport.people.net': 'Net în mână',
  'monthlyReport.people.reconciliation': 'Plătit din bancă vs cost angajat',
  'monthlyReport.people.ledgerPaid': 'Plătit din bancă',
  'monthlyReport.people.ledgerVsPayroll': 'Diferență',
  'monthlyReport.people.ledgerDividends': 'Dividende plătite',
  'monthlyReport.people.reconciliationHint':
    'Costul angajat include contribuțiile care se virează luna următoare, deci cele două cifre nu trebuie să fie egale. Costul cu oamenii NU se adaugă la cheltuielile perioadei — plățile din extras sunt deja numărate acolo.',
  'monthlyReport.people.person': 'Persoană',
  'monthlyReport.people.department': 'Departament',
  'monthlyReport.people.hours': 'Ore',
  'monthlyReport.people.costPerHour': 'Cost / oră',
  'monthlyReport.people.leaveDays': 'Zile concediu',
  'monthlyReport.people.byDepartment': 'Pe departamente',
  'monthlyReport.people.hoursTotal': 'Ore pontate',
  'monthlyReport.people.hoursUnbooked': 'Ore fără fluturaș',
  'monthlyReport.people.trend': 'Evoluția costului cu oamenii',
  'monthlyReport.people.warning.payroll_missing':
    'Nu există fluturași de salariu pentru perioada asta, deci costul cu oamenii apare zero.',
  'monthlyReport.people.warning.payroll_partial':
    'Lipsesc fluturașii pentru o parte din lunile perioadei; costul e incomplet.',
  'monthlyReport.people.warning.payroll_period_rounded':
    'Fluturașii sunt lunari, deci costul acoperă lunile întregi atinse de interval, nu doar zilele selectate.',
  'monthlyReport.people.warning.ledger_payroll_mismatch':
    'Plățile de salarii din extras diferă semnificativ de costul angajat. Verifică dacă s-a încărcat fluturașul potrivit și când s-au virat contribuțiile.',
  'monthlyReport.people.warning.hours_without_payroll':
    'Există ore pontate de oameni care nu au fluturaș în perioadă.',
  // Clienți
  'monthlyReport.clients.empty': 'Nicio factură și nicio încasare pe clienți în perioada asta.',
  'monthlyReport.clients.client': 'Client',
  'monthlyReport.clients.invoiced': 'Facturat',
  'monthlyReport.clients.collected': 'Încasat',
  'monthlyReport.clients.open': 'Neîncasat',
  'monthlyReport.clients.overdue': 'Restant',
  'monthlyReport.clients.invoiceCount': 'Facturi',
  'monthlyReport.clients.dso': 'Zile până la plată',
  'monthlyReport.clients.dsoShort': 'DSO',
  'monthlyReport.clients.dsoValue': '{n} zile',
  'monthlyReport.clients.dsoHint':
    'Media ponderată pe sumă a zilelor dintre emiterea facturii și marcarea ei ca încasată, pentru facturile încasate în perioadă.',
  'monthlyReport.clients.newBadge': 'nou',
  'monthlyReport.clients.newClients': 'Clienți noi',
  'monthlyReport.clients.concentration': 'Concentrare',
  'monthlyReport.clients.top1': 'Cel mai mare client',
  'monthlyReport.clients.top3': 'Primii trei',
  'monthlyReport.clients.dormant': 'Clienți adormiți',
  'monthlyReport.clients.dormantHint':
    'Au facturat în ultimele 12 luni, dar nimic în perioada asta.',
  'monthlyReport.clients.dormantRow': 'ultima factură {date}, acum {days} zile',
  'monthlyReport.clients.overdueByClient': 'Cine întârzie',
  'monthlyReport.clients.trend': 'Facturat vs încasat',
  'monthlyReport.clients.showAll': 'Vezi toți cei {n} clienți',
  'monthlyReport.clients.showLess': 'Arată mai puțini',
  // Proiecte: cost de muncă și buget
  'monthlyReport.projects.hours': 'Ore',
  'monthlyReport.projects.laborCost': 'Cost de muncă',
  'monthlyReport.projects.marginWithLabor': 'Marjă cu munca',
  'monthlyReport.projects.budget': 'Buget',
  'monthlyReport.projects.budgetUsed': 'Buget consumat',
  'monthlyReport.projects.barter': 'barter',
  'monthlyReport.projects.losing': '{n} proiecte pe pierdere',
  'monthlyReport.projects.warning.labor_estimated':
    'Costul de muncă e o estimare: ore pontate × tarif orar derivat din fluturaș, altfel tariful din profilul persoanei.',
  'monthlyReport.projects.warning.hours_without_rate':
    '{n} ore sunt lucrate de oameni fără fluturaș și fără tarif orar, deci nu intră în costul de muncă.',
  'monthlyReport.projects.warning.budget_other_currency':
    'Unele bugete sunt în altă monedă decât RON, deci consumul lor nu se calculează.',
  // Abonamente: cost recurent
  'monthlyReport.expenses.runRate': 'Cost recurent',
  'monthlyReport.expenses.runRateValue': '{monthly} pe lună · {annual} pe an',
  'monthlyReport.expenses.expectedForPeriod': 'Așteptat în perioadă',
  // TVA pe trimestre (raport anual)
  'monthlyReport.vat.quarters': 'Pe trimestre',
  'monthlyReport.vat.periodLabel': 'Perioada raportată',
  'monthlyReport.trends.inflows': 'Încasări',
  'monthlyReport.trends.outflows': 'Plăți',
  'monthlyReport.trends.net': 'Net',

  // Payment method (Transfer / Card / Cash)
  'paymentMethod.label': 'Metodă de plată',
  'paymentMethod.all': 'Toate metodele',
  'paymentMethod.BANK_TRANSFER': 'Transfer',
  'paymentMethod.CARD': 'Card',
  'paymentMethod.CASH': 'Cash',

  // Cash register (manual cash payments)
  'cash.addButton': 'Adaugă plată cash',
  'cash.modalTitle': 'Adaugă plată cash',
  'cash.direction': 'Tip operațiune',
  'cash.direction.OUTCOME': 'Plată',
  'cash.direction.INCOME': 'Încasare',
  'cash.date': 'Dată',
  'cash.amount': 'Sumă',
  'cash.counterpartyType': 'Contraparte',
  'cash.counterparty.person': 'Persoană',
  'cash.counterparty.supplier': 'Furnizor',
  'cash.counterparty.client': 'Client',
  'cash.counterpartyName': 'Nume afișat (opțional)',
  'cash.category': 'Categorie cheltuială',
  'cash.project': 'Proiect (opțional)',
  'cash.description': 'Descriere',
  'cash.save': 'Salvează',
  'cash.created': 'Plata cash a fost înregistrată. O găsești în Registrul de casă.',
  'cash.deleted': 'Intrarea cash a fost ștearsă.',
  'cash.deleteConfirm': 'Ștergi această intrare cash? Va fi eliminată și din Financiar.',
  'cash.registerName': 'Registru de casă',
  'cash.error.amount': 'Introdu o sumă pozitivă.',
  'cash.error.counterparty': 'Selectează o contraparte (persoană, furnizor sau client).',
  'cash.error.generic': 'A apărut o eroare. Încearcă din nou.',

  // Invoices / Facturi
  'invoices.title': 'Facturi',
  'invoices.subtitle': 'Urmărește facturile de venituri și cheltuieli.',
  'invoices.home': 'Acasă',
  'invoices.newWithAI': 'Factură nouă cu AI',
  'invoices.error': 'Nu s-au putut încărca facturile',
  'invoices.pagination': 'Pagina {page} din {totalPages} ({total} în total)',
  'invoices.empty': 'Nicio factură găsită.',
  'invoices.col.number': 'Număr',
  'invoices.col.type': 'Tip',
  'invoices.col.issueDate': 'Data emiterii',
  'invoices.col.dueDate': 'Scadență',
  'invoices.col.amount': 'Sumă',
  'invoices.col.status': 'Status',
  'invoices.col.project': 'Proiect',
  'invoices.col.actions': 'Acțiuni',
  'invoices.direction.income': 'Încasare',
  'invoices.direction.outcome': 'Cheltuială',
  'invoices.overdue': 'întârziată',
  'invoices.action.download': 'Descarcă',
  'invoices.action.markPaid': 'Marchează plătită',
  'invoices.action.delete': 'Șterge',
  'invoices.status.DRAFT': 'Ciornă',
  'invoices.status.SENT': 'Emisă',
  'invoices.status.PAID': 'Încasată',
  'invoices.status.OVERDUE': 'De plată',
  'invoices.status.CANCELLED': 'Anulată',
  'invoices.filter.type': 'Tip',
  'invoices.filter.status': 'Status',
  'invoices.filter.issueFrom': 'Emisă de la',
  'invoices.filter.issueTo': 'Emisă până la',
  'invoices.filter.all': 'Toate',
  'invoices.filter.clear': 'Șterge filtrele',

  // Projects — list page (CLE-278)
  'projects.title': 'Proiecte',
  'projects.createWithAI': 'Creează cu AI',
  'projects.create': 'Creează proiect',
  'projects.searchPlaceholder': 'Caută după nume sau descriere…',
  'projects.allStatuses': 'Toate statusurile',
  'projects.allHealths': 'Toate stările',
  'projects.sort.label': 'Sortare',
  'projects.sort.newest': 'Cele mai noi',
  'projects.sort.oldest': 'Cele mai vechi',
  'projects.sort.nameAsc': 'Nume (A–Z)',
  'projects.sort.nameDesc': 'Nume (Z–A)',
  'projects.sort.endDateDesc': 'Termen (cel mai apropiat ultimul)',
  'projects.sort.endDateAsc': 'Termen (cel mai apropiat primul)',
  'projects.clearFilters': 'Șterge filtrele',
  'projects.error.list': 'Încărcarea proiectelor a eșuat.',
  'projects.empty': 'Nu s-au găsit proiecte.',
  'projects.col.name': 'Nume',
  'projects.col.status': 'Status',
  'projects.col.health': 'Stare',
  'projects.col.owner': 'Proprietar',
  'projects.col.team': 'Echipă',
  'projects.col.deadline': 'Termen',
  'projects.pageInfo': 'Pagina {page} din {totalPages} · {total} proiecte',
  'projects.prev': 'Anterior',
  'projects.next': 'Următorul',

  // Projects — status labels
  'projects.status.PLANNING': 'Planificare',
  'projects.status.ACTIVE': 'În lucru',
  'projects.status.ON_HOLD': 'În așteptare',
  'projects.status.COMPLETED': 'Finalizat',
  'projects.status.CANCELLED': 'Anulat',
  'projects.status.ALMOST_DONE': 'Aproape gata',
  'projects.status.LEAD': 'Lead',
  'projects.status.IN_HOUSE': 'Intern',

  // Projects — health labels
  'projects.health.ON_TRACK': 'Pe drumul cel bun',
  'projects.health.AT_RISK': 'În risc',
  'projects.health.OFF_TRACK': 'În întârziere',

  // Projects — detail page tabs + header
  'projects.back': '← Proiecte',
  'projects.notFound': 'Proiectul nu a fost găsit.',
  'projects.backToList': '← Înapoi la proiecte',
  'projects.edit': 'Editează proiectul',
  'projects.editWithAI': 'Editează cu AI',
  'projects.deleteWithAI': 'Șterge cu AI',
  'projects.tab.overview': 'Prezentare generală',
  'projects.tab.team': 'Echipă',
  'projects.tab.financials': 'Financiar',
  'projects.tab.tasks': 'Sarcini',
  'projects.tab.resources': 'Resurse',
  'projects.tab.vault': 'Vault',
  'projects.tab.notes': 'Notițe ședință',
  'projects.tab.context': 'Context',
  'projects.context.title': 'Contextul proiectului',
  'projects.context.intro':
    'Povestea proiectului: cum l-ai luat, cine e echipa, clientul, provocările, parcursul, deciziile. Firmio AI ține cont de el ori de câte ori lucrezi pe acest proiect, iar un coleg nou îl citește la onboarding.',
  'projects.context.empty':
    'Încă nu s-a povestit nimic despre acest proiect. Vorbește cu Firmio AI în panoul alăturat sau adaugă direct o notă.',
  'projects.context.updatedAt': 'Actualizat {date}',
  'projects.context.kbPath': 'În KB: {path}',
  'projects.context.copy': 'Copiază',
  'projects.context.copied': 'Copiat',
  'projects.context.edit': 'Editează documentul',
  'projects.context.editHint':
    'Editezi direct documentul consolidat (markdown). Adăugirile viitoare se vor integra peste această versiune.',
  'projects.context.save': 'Salvează',
  'projects.context.cancel': 'Renunță',
  'projects.context.saved': 'Documentul a fost salvat.',
  'projects.context.chat.title': 'Povestește-i lui Firmio AI',
  'projects.context.chat.hint':
    'Tot ce spui aici despre proiect se salvează în context și documentul se rescrie automat.',
  'projects.context.chat.placeholder':
    'Ex.: Proiectul l-am luat în 2024 prin recomandarea lui…, echipa e formată din…, cea mai mare provocare a fost…',
  'projects.context.chat.send': 'Trimite',
  'projects.context.chat.stop': 'Oprește',
  'projects.context.chat.newConversation': 'Conversație nouă',
  'projects.context.chat.openFull': 'Deschide în Firmio AI',
  'projects.context.chat.welcome':
    'Salut! Sunt aici să rețin tot ce contează despre **{name}**. Poți începe cu: cum ai luat proiectul, cine e clientul și ce vrea, cine lucrează pe el, ce provocări au fost, ce ar trebui să știe un coleg nou. Scrie liber, eu organizez.',
  'projects.context.chat.thinking': 'Se procesează…',
  'projects.context.chat.saving': 'Salvez în context…',
  'projects.context.manual.title': 'Adaugă o notă direct',
  'projects.context.manual.placeholder':
    'Scrie ce vrei să intre în context (fără AI la mijloc; documentul se consolidează automat).',
  'projects.context.manual.add': 'Adaugă în context',
  'projects.context.manual.added': 'Nota a fost adăugată și documentul a fost actualizat.',
  'projects.context.manual.addedAppended':
    'Nota a fost adăugată la finalul documentului (consolidarea AI nu a fost disponibilă).',
  'projects.context.journal.title': 'Jurnalul adăugirilor',
  'projects.context.journal.hint':
    'Tot ce s-a spus despre proiect, în ordine inversă. Documentul de mai sus e versiunea consolidată.',
  'projects.context.journal.empty': 'Nicio adăugire încă.',
  'projects.context.journal.source.CHAT': 'din chat',
  'projects.context.journal.source.MANUAL': 'notă directă',
  'projects.context.journal.source.VOICE': 'din voce',
  'projects.context.error.load': 'Nu am putut încărca contextul proiectului.',
  'projects.context.error.save': 'Nu am putut salva.',

  // Projects — credentials vault
  'vault.search': 'Caută după titlu sau câmp',
  'vault.allTypes': 'Toate tipurile',
  'vault.add': 'Adaugă credențial',
  'vault.auditToggle': 'Jurnal de audit',
  'vault.entriesToggle': 'Credențiale',
  'vault.empty': 'Încă nicio credențială pentru acest proiect.',
  'vault.emptyFiltered': 'Nicio credențială pentru filtrele selectate.',
  'vault.loadFailed': 'Încărcarea vault-ului a eșuat.',
  'vault.decryptError':
    'Această intrare nu a putut fi decriptată. Secretul de criptare s-a schimbat?',
  'vault.noRevealPermission': 'Nu ai permisiunea de a dezvălui parolele.',
  'vault.reveal': 'Dezvăluie valoarea',
  'vault.hide': 'Ascunde valoarea',
  'vault.copy': 'Copiază',
  'vault.copied': 'Copiat',
  'vault.copyFailed': 'Copierea a eșuat.',
  'vault.revealFailed': 'Dezvăluirea a eșuat.',
  'vault.editEntry': 'Editează',
  'vault.deleteEntry': 'Șterge',
  'vault.deleteConfirm': 'Ștergi definitiv credențialul „{title}"? Jurnalul de audit rămâne.',
  'vault.deleteFailed': 'Ștergerea a eșuat.',
  'vault.noFields': 'Niciun câmp.',
  'vault.updatedBy': 'Actualizat de {name}',
  'vault.audit.empty': 'Nicio activitate înregistrată.',
  'vault.audit.date': 'Dată',
  'vault.audit.user': 'Utilizator',
  'vault.audit.action': 'Acțiune',
  'vault.audit.entry': 'Intrare',
  'vault.audit.details': 'Detalii',
  'vault.audit.loadFailed': 'Încărcarea jurnalului a eșuat.',
  'vault.action.CREATE': 'Creare',
  'vault.action.UPDATE': 'Actualizare',
  'vault.action.DELETE': 'Ștergere',
  'vault.action.REVEAL': 'Dezvăluire',
  'vault.action.COPY': 'Copiere',
  'vault.type.SERVER': 'Server',
  'vault.type.GITHUB': 'GitHub',
  'vault.type.GITLAB': 'GitLab',
  'vault.type.AZURE': 'Azure',
  'vault.type.AWS': 'AWS',
  'vault.type.DATABASE': 'Bază de date',
  'vault.type.EMAIL': 'Email',
  'vault.type.FTP': 'FTP',
  'vault.type.API': 'API',
  'vault.type.HOSTING': 'Hosting',
  'vault.type.DOMAIN': 'Domeniu',
  'vault.type.ACCOUNT': 'Cont',
  'vault.type.OTHER': 'Altele',
  'vault.modal.createTitle': 'Credențial nou',
  'vault.modal.editTitle': 'Editează credențialul',
  'vault.modal.title': 'Titlu',
  'vault.modal.titlePlaceholder': 'ex. Server producție',
  'vault.modal.type': 'Tip',
  'vault.modal.url': 'URL',
  'vault.modal.notes': 'Notițe',
  'vault.modal.fields': 'Câmpuri',
  'vault.modal.addField': 'Adaugă câmp',
  'vault.modal.fieldLabel': 'Etichetă',
  'vault.modal.fieldValue': 'Valoare',
  'vault.modal.secret': 'Secret',
  'vault.modal.removeField': 'Șterge câmpul',
  'vault.modal.moveUp': 'Mută mai sus',
  'vault.modal.moveDown': 'Mută mai jos',
  'vault.modal.unchanged': '(neschimbat)',
  'vault.modal.loadSecrets': 'Încarcă valorile reale',
  'vault.modal.noFields': 'Niciun câmp încă. Adaugă unul mai jos.',
  'vault.modal.titleRequired': 'Titlul este obligatoriu.',
  'vault.modal.labelRequired': 'Fiecare câmp are nevoie de o etichetă.',
  'vault.modal.saveFailed': 'Salvarea a eșuat.',

  // Projects — services card (services nomenclature)
  'projects.services.title': 'Servicii',
  'projects.services.empty': 'Niciun serviciu atașat.',
  'projects.services.edit': 'Editează',
  'projects.services.none': 'Niciun serviciu definit. Adaugă servicii în Setări.',
  'projects.services.error': 'Actualizarea serviciilor a eșuat.',

  // Projects — overview tab
  'projects.overview.error': 'Încărcarea sumarului a eșuat.',
  'projects.overview.general': 'General',
  'projects.overview.barter': 'Barter',
  'projects.overview.client': 'Client',
  'projects.overview.start': 'Start',
  'projects.overview.deadline': 'Termen',
  'projects.overview.budget': 'Buget',
  'projects.overview.consumed': 'Consumat',
  'projects.overview.team': 'Echipă',
  'projects.overview.noTeam': 'Încă niciun membru de echipă atribuit.',
  'projects.overview.allocPerWeek': 'h/săpt',
  'projects.overview.financialSummary': 'Sumar financiar (P&L)',
  'projects.overview.revenue': 'Venituri',
  'projects.overview.expenses': 'Cheltuieli',
  'projects.overview.profit': 'Profit',
  'projects.overview.loss': 'Pierdere',
  'projects.overview.unpaidIn': 'Neîncasat (in)',
  'projects.overview.payableOut': 'De plată (out)',
  'projects.overview.noMonthly': 'Încă nu există date financiare lunare.',
  'projects.overview.monthlyTrend': 'Evoluție lunară',
  'projects.overview.margin': 'Marjă',
  'projects.overview.milestones': 'Etape cheie',
  'projects.overview.noMilestones': 'Încă nu sunt definite etape cheie.',
  'projects.overview.overdue': 'Întârziat',
  'projects.overview.tasks': 'Sarcini',
  'projects.overview.noTasks': 'Încă nicio sarcină.',
  'projects.overview.active': 'Active',
  'projects.overview.total': 'Total',
  'projects.overview.overdueTasks': 'Întârziate',
  'projects.overview.risks': 'Riscuri active',
  'projects.overview.noRisks': 'Niciun risc activ.',
  'projects.overview.critical': 'Critic',
  'projects.overview.impact': 'Impact:',
  'projects.overview.action': 'Acțiune:',
  'projects.overview.recentActivity': 'Activitate recentă',
  'projects.overview.noActivity': 'Încă nu există activitate înregistrată.',
  'projects.overview.timeline': 'Calendar parcurs',
  'projects.overview.daysLeft': 'încă {n} zile',
  'projects.overview.daysOverdue': 'depășit cu {n} zile',
  'projects.overview.inTotal': 'în total',
  'projects.overview.seeAllTasks': 'Toate sarcinile',
  'projects.overview.doneTasks': 'Finalizate',
  'projects.overview.nextTasks': 'Urmează',
  'projects.overview.allDone': 'Nicio sarcină deschisă.',
  'projects.overview.noDueDate': 'fără termen',
  'projects.overview.dueToday': 'azi',
  'projects.overview.completion': 'Sarcini finalizate',

  // Projects — team tab
  'projects.team.noMembers': 'Încă niciun membru de echipă atribuit.',
  'projects.team.noMembersHint':
    'Persoanele sunt adăugate într-un proiect prin alocări în Baza de cunoștințe. Alocă pe cineva prin AI Chat și va apărea aici.',
  'projects.team.assignWithAI': 'Atribuie un membru cu AI',
  'projects.team.member': 'membru',
  'projects.team.members': 'membri',
  'projects.team.orgChart': 'Organigramă',
  'projects.team.list': 'Listă',
  'projects.team.col.name': 'Nume',
  'projects.team.col.title': 'Titlu',
  'projects.team.col.department': 'Departament',
  'projects.team.col.allocation': 'Alocare (ore/săpt)',
  'projects.team.col.email': 'Email',
  'projects.team.inactive': 'inactiv',
  'projects.team.hoursPerWeekSuffix': 'h/săpt',
  'projects.team.addMember': 'Adaugă membru',
  'projects.team.removeMember': 'Elimină din echipă',
  'projects.team.editMember': 'Editează membrul',
  'projects.team.removeConfirm': 'Elimini {name} din echipa proiectului?',
  'projects.team.removeFailed': 'Eliminarea membrului a eșuat.',
  'projects.team.edit.title': 'Editează membrul echipei',
  'projects.team.edit.submit': 'Salvează modificările',
  'projects.team.edit.saving': 'Se salvează…',
  'projects.team.add.title': 'Adaugă membru în echipă',
  'projects.team.add.person': 'Persoană',
  'projects.team.add.personPlaceholder': 'Alege o persoană',
  'projects.team.add.role': 'Rol',
  'projects.team.add.allocation': 'Alocare (ore/săpt)',
  'projects.team.add.startDate': 'Data de început',
  'projects.team.add.endDate': 'Data de final',
  'projects.team.add.errPerson': 'Alege o persoană.',
  'projects.team.add.errAllocation':
    'Alocarea trebuie să fie un număr întreg de ore între 0 și 168.',
  'projects.team.add.errDates':
    'Data de început trebuie să fie înainte sau egală cu data de final.',
  'projects.team.add.errServer': 'Adăugarea membrului a eșuat.',
  'projects.team.add.saving': 'Se adaugă…',
  'projects.team.add.submit': 'Adaugă membru',
  'projects.team.role.LEAD': 'Lead',
  'projects.team.role.CONTRIBUTOR': 'Contributor',
  'projects.team.role.REVIEWER': 'Reviewer',
  'projects.team.role.STAKEHOLDER': 'Stakeholder',

  // Projects — resources tab (manual upload/delete)
  'resources.uploadFolder': 'Încarcă folder',
  'resources.maxPerFile': 'max. {mb} MB per fișier',
  'projects.resources.dropHint':
    'Trage și plasează fișiere sau foldere întregi aici, sau apasă pentru a alege fișiere',
  'projects.resources.savedIn': 'Salvat în {folder}',
  'projects.resources.invoicesDerived':
    'Folderul Facturi este generat automat din facturi; fișierul va fi salvat în Alte resurse.',
  'projects.resources.uploadFailed': 'Încărcarea a eșuat.',
  'projects.resources.deleteFile': 'Șterge fișierul',
  'projects.resources.deleteConfirm': 'Ștergi fișierul „{name}”?',
  'projects.resources.deleteFailed': 'Ștergerea fișierului a eșuat.',
  'projects.resources.tooLarge': 'Peste limita de {mb} MB per fișier, nu s-au încărcat: {names}.',
  'projects.resources.someFailed': 'Nu s-au putut încărca: {names}.',
  'projects.resources.folderExists': 'Există deja un folder cu acest nume aici.',
  'projects.resources.folderCreateFailed': 'Folderul nu a putut fi creat.',
  'projects.resources.deleteFolderConfirm': 'Ștergi folderul gol „{name}”?',
  'projects.resources.deleteFolderFailed': 'Ștergerea folderului a eșuat.',
  'projects.resources.cat.CONTRACT': 'Contracte',
  'projects.resources.cat.DESIGN': 'Design',
  'projects.resources.cat.OTHER': 'Alte resurse',

  // Upload progress — shared by every upload surface (dropzones, composers)
  'upload.progressLabel': 'Progresul încărcării',
  'upload.uploading': 'Se încarcă…',
  'upload.processing': 'Se finalizează pe server…',
  'upload.processingFile': 'Se procesează fișierul…',
  'upload.processingStatement': 'Se citesc tranzacțiile din PDF…',
  'upload.processingTranscript': 'Se transcrie înregistrarea…',
  'upload.processingExtract': 'AI-ul extrage datele din document…',

  // Projects — financials tab
  'projects.fin.revenue': 'Venituri',
  'projects.fin.expenses': 'Cheltuieli',
  'projects.fin.budgetUsed': '{percent}% din buget',
  'projects.fin.profitLoss': 'Profit / Pierdere',
  'projects.fin.margin': 'Marjă {percent}%',
  'projects.fin.budget': 'Buget',
  'projects.fin.budgetRemaining': '{amount} rămas',
  'projects.fin.smartbillTitle': 'SmartBill — facturi asociate',
  'projects.fin.smartbillEmpty': 'Încă nu există facturi SmartBill asociate acestui proiect.',
  'projects.fin.chart.revenueVsExpenses': 'Venituri vs. Cheltuieli',
  'projects.fin.chart.noMonthly': 'Nu există date lunare de afișat.',
  'projects.fin.chart.expensesByCategory': 'Cheltuieli pe categorie',
  'projects.fin.chart.noExpenses': 'Nu există cheltuieli de defalcat.',
  'projects.fin.chart.profitTrend': 'Tendință profit / pierdere',
  'projects.fin.chart.noTrend': 'Încă nu există date de tendință.',
  'projects.fin.chart.cumulativeProfit': 'Profit cumulat',
  'projects.fin.chart.revVsExpTrend': 'Tendință venituri vs. cheltuieli',
  'projects.fin.chart.cashflow': 'Cashflow — intrări vs. ieșiri & sold cumulat',
  'projects.fin.chart.noCashflow': 'Încă nu există date de cashflow.',
  'projects.fin.chart.income': 'Intrări',
  'projects.fin.chart.outcome': 'Ieșiri',
  'projects.fin.chart.balance': 'Sold cumulat',
  'projects.fin.chart.expenseTrend': 'Evoluție cheltuieli pe categorie (stacked)',
  'projects.fin.chart.noExpenseTrend': 'Nu există cheltuieli de afișat în timp.',
  'projects.fin.transactions': 'Tranzacții',
  'projects.fin.record': 'înregistrare',
  'projects.fin.records': 'înregistrări',
  'projects.fin.noEntries': 'Încă nu există înregistrări financiare.',
  'projects.fin.col.date': 'Dată',
  'projects.fin.col.type': 'Tip',
  'projects.fin.col.category': 'Categorie',
  'projects.fin.col.description': 'Descriere',
  'projects.fin.col.amount': 'Sumă',
  'projects.fin.cost': 'Cheltuială',
  'projects.fin.income': 'Venit',
  'projects.fin.addEntryWithAI': 'Adaugă o înregistrare financiară cu AI',
  'projects.fin.issued.title': 'Facturi emise pentru proiect',
  'projects.fin.issued.invoice': 'factură',
  'projects.fin.issued.invoices': 'facturi',
  'projects.fin.issued.total': 'Facturat (trimise la ANAF):',
  'projects.fin.issued.empty':
    'Nicio factură emisă nu e legată de acest proiect. Alege proiectul în editorul de facturi, la „Proiect (doar intern)”.',
  'projects.fin.issued.col.number': 'Număr',
  'projects.fin.issued.col.customer': 'Client',
  'projects.fin.issued.col.company': 'Firma emitentă',
  'projects.fin.issued.col.status': 'Stare',
  'projects.fin.issued.draft': 'ciornă',
  'projects.fin.issued.storno': 'storno',
  'projects.company.mainFirm':
    'Firma principală a proiectului. Orice firmă poate factura proiectul.',

  // Tasks — view chrome (CLE-278)
  'tasks.title': 'Sarcini',
  'tasks.subtitle': '{count} sarcini · {hours} estimate',
  'tasks.statusBadge': '{label}: {count} sarcini · {hours} estimate',
  'tasks.view.list': 'Listă',
  'tasks.view.board': 'Panou',
  'tasks.labels': 'Etichete',
  'tasks.new': 'Sarcină nouă',
  // Tasks — apply a task template to the current project
  'tasks.applyTemplate.button': 'Aplică șablon task-uri',
  'tasks.applyTemplate.title': 'Aplică un șablon de task-uri',
  'tasks.applyTemplate.subtitle':
    'Task-urile din șablon sunt create în acest proiect, în ordinea din șablon.',
  'tasks.applyTemplate.field': 'Șablon',
  'tasks.applyTemplate.placeholder': 'Alege un șablon…',
  'tasks.applyTemplate.itemCount': '{count} task-uri',
  'tasks.applyTemplate.preview': 'Se vor crea {count} task-uri',
  'tasks.applyTemplate.submit': 'Aplică șablonul',
  'tasks.applyTemplate.applied': '{count} task-uri au fost create din șablon.',
  'tasks.applyTemplate.none': 'Niciun șablon definit. Creează unul în Setări → Șabloane sarcini.',
  'tasks.applyTemplate.loadError': 'Încărcarea șabloanelor a eșuat.',
  'tasks.applyTemplate.error': 'Aplicarea șablonului a eșuat. Încearcă din nou.',
  'tasks.applyTemplate.forbidden': 'Nu ai dreptul să creezi task-uri în acest proiect.',
  'tasks.searchPlaceholder': 'Caută titlu, descriere, comentarii, etichete…',
  'tasks.allStatuses': 'Toate statusurile',
  'tasks.allPriorities': 'Toate prioritățile',
  'tasks.allProjects': 'Toate proiectele',
  'tasks.allClients': 'Toți clienții',
  'tasks.allLabels': 'Toate etichetele',
  'tasks.allDepartments': 'Toate departamentele',
  'tasks.allAssignees': 'Toți responsabilii',
  'tasks.sort.newest': 'Cele mai noi',
  'tasks.sort.oldest': 'Cele mai vechi',
  'tasks.sort.priority': 'Prioritate (mare→mică)',
  'tasks.sort.dueDate': 'Termen (cel mai apropiat)',
  'tasks.sort.estimate': 'Estimare (cea mai mare)',
  'tasks.sort.label': 'Sortare',
  'tasks.filter.search': 'Căutare',
  'tasks.filter.status': 'Status',
  'tasks.filter.priority': 'Prioritate',
  'tasks.filter.project': 'Proiect',
  'tasks.filter.client': 'Client',
  'tasks.filter.department': 'Departament',
  'tasks.filter.labels': 'Etichete',
  'tasks.filter.dueFrom': 'Termen de la',
  'tasks.filter.dueTo': 'Termen până la',
  'tasks.statusCategory.BACKLOG': 'Backlog',
  'tasks.statusCategory.UNSTARTED': 'Neînceput',
  'tasks.statusCategory.STARTED': 'În lucru',
  'tasks.statusCategory.COMPLETED': 'Finalizat',
  'tasks.statusCategory.CANCELLED': 'Anulat',
  'tasks.clearFilters': 'Șterge filtrele',
  'tasks.moreFilters': 'Mai multe filtre',
  'tasks.error.list': 'Încărcarea sarcinilor a eșuat.',
  'tasks.empty.filtered': 'Nicio sarcină nu corespunde filtrelor selectate.',
  'tasks.empty.title': 'Încă nu există sarcini.',
  'tasks.empty.hintProject': 'Începe prin a crea prima sarcină pentru acest proiect.',
  'tasks.empty.hintGlobal': 'Creează prima ta sarcină pentru a începe.',
  'tasks.empty.cta': 'Creează prima ta sarcină',
  'tasks.pageInfo': 'Pagina {page} din {totalPages} · {total} sarcini',
  'tasks.prev': 'Anterior',
  'tasks.next': 'Următor',
  'tasks.createTitle': 'Sarcină nouă',
  'tasks.createSubmit': 'Creează sarcina',
  'tasks.createError': 'Crearea sarcinii a eșuat.',
  'tasks.ai.label': 'Generează cu AI',
  'tasks.ai.placeholder': 'Descrie sarcina (ex: „task urgent pentru vineri: pregătește oferta X”)',
  'tasks.ai.generate': 'Generează',
  'tasks.ai.generating': 'Se generează…',
  'tasks.ai.applied': 'Ciorna a fost completată mai jos — revizuiește și salvează.',
  'tasks.ai.budgetExceeded': 'Bugetul AI al lunii a fost depășit. Completează formularul manual.',
  'tasks.ai.notConfigured': 'AI-ul nu este configurat. Completează formularul manual.',
  'tasks.ai.failed': 'Generarea ciornei a eșuat. Încearcă din nou sau completează manual.',
  'tasks.addToColumn': 'Adaugă sarcină',
  'tasks.quickAdd.pickProject': 'Alege un proiect…',
  'tasks.quickAdd.pickProjectFirst': 'Alege întâi un proiect…',
  'tasks.quickAdd.titlePlaceholder': 'Titlu sarcină…',
  'tasks.list.movedMany': '{count} sarcini mutate în {label}',
  'tasks.list.movedOne': '{ref} mutată în {label}',
  'tasks.list.taskFallback': 'Sarcina',
  'tasks.list.dropToMove': 'Eliberează pentru a muta în {label}',
  'tasks.list.noTask': 'Nicio sarcină',
  'tasks.list.expand': 'Extinde',
  'tasks.list.collapse': 'Restrânge',
  'tasks.list.addTo': 'Adaugă sarcină în {label}',
  'tasks.list.ghostCount': '{count} sarcini',
  'tasks.row.estimate': 'Estimare',
  'tasks.row.menu': 'Mai multe acțiuni',
  'tasks.row.delete': 'Șterge',
  'tasks.row.confirmDelete': 'Ștergi această sarcină?',
  'tasks.row.deleted': 'Sarcină ștearsă',
  'tasks.row.deleteFailed': 'Ștergerea a eșuat',
  // Duplicare sarcină (meniul ⋯ din pagina de detaliu)
  'tasks.row.duplicate': 'Duplică',
  'tasks.duplicate.title': 'Duplică sarcina',
  'tasks.duplicate.hint':
    'Se creează o sarcină nouă, cu identificator propriu, pornind de la {identifier}: descriere, status, prioritate, responsabil, termen, estimare și etichete. Comentariile și timpul lucrat nu se copiază.',
  'tasks.duplicate.defaultTitle': '{title} (copie)',
  'tasks.duplicate.newTitle': 'Titlul sarcinii noi',
  'tasks.duplicate.project': 'Proiect',
  'tasks.duplicate.otherProject':
    'Copia primește identificatorul proiectului ales; dacă sarcina e o sub-sarcină, copia devine sarcină de sine stătătoare.',
  'tasks.duplicate.includeSubtasks': 'Copiază și sub-sarcinile ({count})',
  'tasks.duplicate.submit': 'Duplică',
  'tasks.duplicate.working': 'Se duplică…',
  'tasks.duplicate.failed': 'Duplicarea a eșuat.',
  'tasks.duplicate.done': 'Sarcina {identifier} a fost creată.',
  'tasks.quickAdd.projectShort': 'Proiect…',
  'tasks.quickAdd.newTitle': 'Sarcină nouă…',
  'tasks.quickAdd.failed': 'Eșuat',

  // Previzualizarea unei sarcini la hover (chipsuri din calendar)
  'taskPreview.status': 'Status',
  'taskPreview.priority': 'Prioritate',
  'taskPreview.assignee': 'Responsabil',
  'taskPreview.unassigned': 'Nealocată',
  'taskPreview.due': 'Termen',
  'taskPreview.noDue': 'Fără termen',
  'taskPreview.estimate': 'Estimare',
  'taskPreview.logged': '{spent} lucrat',
  'taskPreview.labels': 'Etichete',

  // Tasks — priority labels
  'tasks.priority.NONE': 'Fără prioritate',
  'tasks.priority.URGENT': 'Urgentă',
  'tasks.priority.HIGH': 'Ridicată',
  'tasks.priority.MEDIUM': 'Medie',
  'tasks.priority.LOW': 'Scăzută',

  // Daily summary (Rezumat zilnic)
  'daily.title': 'Rezumat zilnic',
  'daily.date': 'Dată',
  'daily.exportPdf': 'Exportă PDF',
  'daily.exporting': 'Se exportă…',
  'daily.sendEmail': 'Trimite email',
  'daily.sendingEmail': 'Se trimite…',
  'daily.exportFailed': 'Exportul a eșuat',
  'daily.emailSent': 'Email trimis cu succes către {to}',
  'daily.emailFailed': 'Trimiterea email-ului a eșuat',
  'daily.loadError': 'Încărcarea rezumatului zilnic a eșuat.',
  'daily.allUsers': 'Toți utilizatorii',
  'daily.allUsersOverview': 'Toți utilizatorii (Prezentare generală)',
  'daily.tasks': 'Task-uri',
  'daily.tasks.empty': 'Niciun task pentru această perioadă.',
  'daily.tasks.mine': 'Task-urile mele',
  'daily.tasks.team': 'Task-urile echipei',
  'daily.bucket.overdue': 'Întârziate',
  'daily.bucket.today': 'Astăzi',
  'daily.bucket.tomorrow': 'Mâine',
  'daily.bucket.next': 'Următoarele 2-3 zile',
  'daily.calendar': 'Calendar',
  'daily.calendar.empty': 'Niciun eveniment pentru această zi.',
  'daily.calendar.allDay': 'Toată ziua',
  'daily.event.conference': 'Conferință',
  'daily.event.speech': 'Speech',
  'daily.event.team': 'Echipă',
  'daily.event.admin': 'Admin',
  'daily.event.personal': 'Personal',
  'daily.event.client': 'Clienți',
  'daily.recommendations': 'Recomandări AI',
  'daily.allTasks': 'Toate task-urile',

  // Daily status check-ins (Statusul meu de azi)
  'dailyStatus.title': 'Statusul meu de azi',
  'dailyStatus.titleOther': 'Statusul zilei · {name}',
  'dailyStatus.progress': '{done}/{total} completate',
  'dailyStatus.report': 'Raport',
  'dailyStatus.empty': 'Niciun task pe azi. Poți adăuga unul din Task-uri.',
  'dailyStatus.loadError': 'Încărcarea statusului zilnic a eșuat.',
  'dailyStatus.readOnly': 'doar ziua curentă poate fi completată',
  'dailyStatus.unreported': 'status necompletat',
  'dailyStatus.hours': '{hours}h',
  'dailyStatus.status.DONE': 'Done',
  'dailyStatus.status.IN_PROGRESS': 'În lucru',
  'dailyStatus.status.BLOCKED': 'Blocat',
  'dailyStatus.status.NOT_STARTED': 'Neînceput',
  'dailyStatus.reason.due_today': 'Azi',
  'dailyStatus.reason.overdue': 'Întârziat',
  'dailyStatus.reason.started': 'În lucru',
  'dailyStatus.reason.worked_on': 'Lucrat azi',
  'dailyStatus.reason.completed_today': 'Finalizat azi',
  'dailyStatus.reason.checked_in': 'Completat',
  'dailyStatus.commentPlaceholder':
    'Ce ai făcut azi pe task? (opțional) — poți atașa poze sau fișiere',
  'dailyStatus.blockedPlaceholder': 'De ce e blocat? (obligatoriu)',
  'dailyStatus.saving': 'Se salvează…',
  'dailyStatus.saved': 'Salvat ✓',
  'dailyStatus.saveError': 'Eroare, reîncearcă',
  'dailyStatus.section.done': 'Finalizate',
  'dailyStatus.section.inProgress': 'În lucru',
  'dailyStatus.section.blocked': 'Blocate',
  'dailyStatus.section.notStarted': 'Neîncepute',
  'dailyStatus.drawer.title': 'Raport zilnic',
  'dailyStatus.drawer.copyMd': 'Copiază Markdown',
  'dailyStatus.drawer.copied': 'Copiat!',
  'dailyStatus.drawer.copyFailed': 'Nu am putut copia în clipboard.',
  'dailyStatus.drawer.pdf': 'Descarcă PDF',
  'dailyStatus.drawer.sendEmail': 'Trimite pe email',
  'dailyStatus.drawer.sent': 'Trimis către {to}',
  'dailyStatus.drawer.persisted': 'snapshot din {time}',
  'dailyStatus.drawer.live': 'generat acum',
  'dailyStatus.drawer.history': 'Istoric (14 zile)',
  'dailyStatus.drawer.hoursToday': 'Ore pontate: {hours}',
  'dailyStatus.drawer.unreportedCount': 'Fără status: {count}',
  'dailyStatus.drawer.noTasks': 'Niciun task pe azi.',
  'dailyStatus.team.title': 'Raport zilnic echipă',
  'dailyStatus.team.reported': 'Raportat {reported}/{members}',
  'dailyStatus.team.notReported': 'Neraportat',
  'dailyStatus.team.blocked': 'Blocaje',
  'dailyStatus.team.hours': 'Ore',
  'dailyStatus.team.sendAll': 'Trimite raportul echipei acum',
  'dailyStatus.team.sentAll': '{individual} emailuri individuale, {team} emailuri echipă',
  'dailyStatus.team.idle': 'Fără task-uri pe azi: {names}',
  'dailyStatus.team.personal': '+{count} sarcini personale',
  'dailyStatus.card.reported': 'Raportat {done}/{total}',
  'dailyStatus.card.notReported': 'Neraportat',
  'dailyStatus.bar.text': 'Ai {count} task-uri fără status pe azi',
  'dailyStatus.bar.title': 'Completează raportul zilnic',
  'dailyStatus.bar.pending': '{count} task-uri fără status',
  'dailyStatus.pickStatus': 'Alege status',
  'dailyStatus.clearStatus': 'Fără status',
  'dailyStatus.note': 'Notă',
  'dailyStatus.addComment': 'Adaugă comentariu',
  'dailyStatus.editComment': 'Editează comentariul',
  'dailyStatus.saveComment': 'Salvează',
  'dailyStatus.cancel': 'Renunță',
  'dailyStatus.bar.cta': 'Completează',
  'dailyStatus.bar.dismiss': 'Închide pentru azi',
  'daily.allTasks.empty': 'Niciun task activ.',
  'daily.allTasks.filtered': 'Niciun task nu corespunde filtrelor selectate.',
  'daily.filter.project': 'Proiect',
  'daily.due': 'Scadență',
  'daily.priority': 'prioritate {priority}',
  'daily.business.empty': 'Niciun task activ în echipă pentru această perioadă.',
  'daily.business.noTasks': 'Fără task-uri ({count})',
  // Task status labels (shared by daily/weekly)
  'status.BACKLOG': 'Backlog',
  'status.TODO': 'De făcut',
  'status.IN_PROGRESS': 'În lucru',
  'status.IN_REVIEW': 'În review',
  'status.QA': 'QA',
  'status.DONE': 'Finalizat',
  'status.BLOCKED': 'Blocat',
  'status.CANCELLED': 'Anulat',

  // Weekly summary (Rezumat săptămânal)
  'weekly.title': 'Rezumat săptămânal',
  'weekly.allUsers': 'Toți utilizatorii',
  'weekly.user': 'Utilizator',
  'weekly.loadError': 'Încărcarea rezumatului săptămânal a eșuat.',
  'weekly.empty': 'Nicio schimbare de status înregistrată pentru această perioadă.',
  'weekly.viewLabel': 'Vizualizare',
  'weekly.weekLabel': 'Săptămână',
  'weekly.thisWeek': 'Mergi la săptămâna curentă',
  'weekly.statusesSelected': '{count} statusuri',
  'weekly.stats.title': 'Săptămâna în cifre',
  'weekly.view.day': 'Pe zi',
  'weekly.view.status': 'Pe status',
  'weekly.stats.totalChanged': 'Total modificate',
  'weekly.stats.done': 'Finalizate',
  'weekly.stats.inProgress': 'În lucru',
  'weekly.stats.newlyStarted': 'Nou începute',
  'weekly.stats.blocked': 'Blocate',
  'weekly.created': 'Creat',
  'weekly.movedTo': 'mutat în {status}',
  'weekly.movedFromTo': 'din {from} în {to}',
  'weekly.fromWord': 'din',
  'weekly.toWord': 'în',
  'weekly.createdAs': 'creat direct în {to}',
  'weekly.taskChangedOne': '{count} task modificat',
  'weekly.taskChangedMany': '{count} task-uri modificate',
  'weekly.noMatchingChanges': 'Nicio schimbare corespunzătoare în această săptămână',
  'weekly.noStatusChanges': 'Nicio schimbare de status',
  'weekly.prevWeek': 'Săptămâna anterioară',
  'weekly.prevWeekShort': 'Săpt. anterioară',
  'weekly.nextWeek': 'Săptămâna următoare',
  'weekly.nextWeekShort': 'Săpt. următoare',

  // Chat / Firmio AI
  'chat.subtitle': 'Asistentul tău financiar',
  'chat.model': 'Model',
  'chat.modelAria': 'Model AI',
  'chat.modelTitle': 'Alege modelul AI',
  'chat.modelLabel': 'Model: {model}',
  'chat.newConversation': 'Conversație nouă',
  'chat.searchConversations': 'Caută conversații…',
  'chat.noResults': 'Niciun rezultat.',
  'chat.noConversations': 'Nicio conversație încă.',
  'chat.untitled': 'Conversație fără titlu',
  'chat.backToApp': 'Înapoi la aplicație',
  'chat.pin': 'Fixează',
  'chat.actions': 'Acțiuni conversație',
  'chat.unpin': 'Anulează fixarea',
  'chat.rename': 'Redenumește',
  'chat.delete': 'Șterge',
  'chat.save': 'Salvează',
  'chat.send': 'Trimite',
  'chat.stop': 'Oprește',
  'chat.attach': 'Atașează fișier',
  'chat.attachTitle': 'Atașează factură (PDF, PNG, JPG)',
  'chat.remove': 'Elimină',
  'chat.placeholder': 'Scrie un mesaj sau trage o factură aici… (Enter pentru a trimite)',
  'chat.placeholderMobile': 'Scrie un mesaj…',
  'chat.intro': 'Dă o comandă în limbaj natural. De exemplu:',
  'chat.demoLocked':
    'Conversația cu Firmio AI este disponibilă doar în conturile plătite. În contul demo poți explora restul aplicației.',
  'chat.demoPlaceholder': 'Disponibil doar în conturile plătite',
  'chat.uploading': 'Se încarcă…',
  'chat.notConfigured.before': 'Asistentul AI nu este configurat. Setează',
  'chat.notConfigured.after': 'în mediul backend pentru a-l activa.',
  'chat.deleteConfirm': 'Ștergi conversația „{title}”?',
  'chat.error.load': 'Conversația nu a putut fi încărcată',
  'chat.error.pin': 'Conversația nu a putut fi fixată',
  'chat.error.rename': 'Conversația nu a putut fi redenumită',
  'chat.error.delete': 'Conversația nu a putut fi ștearsă',
  'chat.error.unsupported': 'Tip nesuportat: {name}. Accept doar PDF, PNG, JPG.',
  'chat.error.tooLarge': '{name} depășește 10MB.',
  'chat.error.upload': 'Upload eșuat',
  'chat.budgetExceededLink': 'Mergi la Setări → Buget AI',
  'chat.noAccess':
    'Contul tău nu are acces la Firmio AI. Un administrator ți-l poate acorda din Setări → Roluri și permisiuni.',
  'chat.attachedFile': '{count} fișier atașat',
  'chat.attachedFiles': '{count} fișiere atașate',
  'chat.suggestion.financial': 'Care e statusul financiar luna aceasta?',
  'chat.suggestion.team': 'Arată-mi alocarea echipei pe proiecte',
  'chat.suggestion.unpaid': 'Ce facturi am neîncasate?',
  'chat.suggestion.payments': 'Ce plăți trebuie să efectuăm?',
  'chat.processAttachments': 'Procesează fișierele atașate.',
  'chat.relative.now': 'acum',
  'chat.relative.minuteOne': 'acum {count} minut',
  'chat.relative.minuteMany': 'acum {count} minute',
  'chat.relative.hourOne': 'acum {count} oră',
  'chat.relative.hourMany': 'acum {count} ore',
  'chat.relative.yesterday': 'ieri',
  'chat.relative.daysMany': 'acum {count} zile',

  // Knowledge Base
  'kb.title': 'Knowledge Base',
  'kb.subtitle': 'Wiki-ul inteligent al firmei · Markdown-first · Semantic search',
  'kb.demoContent': 'Conținut demo',
  'kb.categories': 'Categorii',
  'kb.viewAggregated': 'Vezi notițe agregate',
  'kb.search': 'Caută în Knowledge Base… (semantic)',
  'kb.clearSearch': 'Șterge căutarea',
  'kb.filterCategory': 'Filtru categorie',
  'kb.filterAuthor': 'Filtru autor',
  'kb.allCategories': 'Toate categoriile',
  'kb.allAuthors': 'Toți autorii',
  'kb.resultsRanked': '{count} rezultate · ranked by relevance',
  'kb.resultRanked': '{count} rezultat · ranked by relevance',
  'kb.semantic': 'semantic',
  'kb.noResults': 'Niciun rezultat. Încearcă alți termeni.',
  'kb.toc': 'Cuprins',
  'kb.action.edit': 'Editează',
  'kb.action.share': 'Distribuie',
  'kb.action.print': 'Print',
  'kb.action.exportPdf': 'Export PDF',
  'kb.version': 'Versiune {version}',
  'kb.created': 'Creat {date}',
  'kb.updated': 'Actualizat {date}',
  'kb.related': 'Documente conexe',

  // Login
  'login.title': 'Autentificare',
  'login.email': 'Email',
  'login.password': 'Parolă',
  'login.submit': 'Autentificare',
  'login.submitting': 'Se autentifică…',
  'login.demo': 'Sau încearcă modul demo →',
  'login.noAccount': 'Nu ai cont?',
  'login.register': 'Înregistrare',
  'login.failed': 'Autentificare eșuată',
  'instance.suspended.title': 'Acces suspendat',
  'instance.suspended.defaultMessage': 'Accesul la Firmio a fost suspendat pentru această firmă.',
  'instance.suspended.contact': 'Pentru reactivare, contactați furnizorul Firmio.',
  'instance.suspended.retry': 'Reîncearcă',
  'instance.suspended.checking': 'Se verifică…',
  'instance.suspended.still': 'Accesul este încă suspendat.',
  'login.demoUnavailable': 'Demo Mode indisponibil',
  'login.forgotPassword': 'Ai uitat parola?',
  'login.emailNotVerified': 'Emailul contului nu este verificat.',
  'login.resendVerification': 'Retrimite emailul de verificare',
  'login.verificationSent': 'Email trimis. Verifică-ți inboxul.',
  'login.tagline': 'Tot businessul tău. Într-un singur loc.',
  'login.subtitle': 'Proiecte, clienți, facturi, echipă — conectate printr-un asistent AI.',
  'login.feature.1': 'Proiecte, echipă și task-uri',
  'login.feature.2': 'Facturi, SmartBill & financiar',
  'login.feature.3': 'Asistent AI care învață afacerea ta',
  'login.integrations': 'Integrat cu',
  'login.poweredBy': 'Un produs Clean Code Culture',
  'legal.link': 'Termeni și confidențialitate',
  'legal.registerNotice': 'Creând un cont, accepți',
  'legal.registerLink': 'termenii și politica de confidențialitate',

  // Mobile app: organization code (first launch) and switching organization
  'connect.title': 'Conectează-te la organizația ta',
  'connect.subtitle': 'Introdu codul organizației primit de la administratorul firmei.',
  'connect.codeLabel': 'Codul organizației',
  'connect.codePlaceholder': 'ex. firma-mea',
  'connect.submit': 'Continuă',
  'connect.checking': 'Se verifică…',
  'connect.advanced': 'Avansat: adresă server',
  'connect.advancedHide': 'Folosește codul organizației',
  'connect.urlLabel': 'Adresa serverului',
  'connect.urlPlaceholder': 'https://firma.firmio.ro',
  'connect.urlHint': 'Doar în versiunile de test. Exemplu local: http://localhost:24002',
  'connect.confirmTitle': 'Te conectezi la',
  'connect.confirm': 'Continuă',
  'connect.back': 'Înapoi',
  'connect.suspended': 'Accesul acestei organizații este suspendat momentan.',
  'connect.help': 'Nu ai un cod? Cere-l administratorului firmei tale.',
  'connect.error.invalidCode': 'Codul are doar litere mici, cifre și cratimă (ex. firma-mea).',
  'connect.error.invalidUrl': 'Adresa nu este validă. Exemplu: https://firma.firmio.ro',
  'connect.error.notFound': 'Nu am găsit o organizație cu acest cod.',
  'connect.error.rateLimited': 'Prea multe încercări. Reîncearcă peste câteva minute.',
  'connect.error.offline': 'Nu ai conexiune la internet.',
  'connect.error.consoleUnreachable':
    'Nu am putut verifica codul acum. Reîncearcă în câteva momente.',
  'connect.error.serverUnreachable': 'Serverul organizației nu răspunde. Reîncearcă mai târziu.',
  'connect.error.notFirmio': 'La această adresă nu răspunde un server Firmio.',
  'login.orgLabel': 'Organizație',
  'login.orgChange': 'Schimbă organizația',
  'profile.orgTitle': 'Organizație',
  'profile.orgCode': 'Cod',
  'profile.orgServer': 'Server',
  'profile.orgChange': 'Schimbă organizația',
  'profile.orgConfirmTitle': 'Schimbi organizația?',
  'profile.orgConfirmBody':
    'Vei fi deconectat de la {name}. Notificările acestei organizații se opresc pe acest telefon.',
  'profile.orgConfirmAction': 'Schimbă',
  'profile.orgLeaving': 'Se deconectează…',

  // Register
  'register.title': 'Creează cont',
  'register.name': 'Nume',
  'register.email': 'Email',
  'register.password': 'Parolă',
  'register.submit': 'Creează cont',
  'register.submitting': 'Se creează…',
  'register.hasAccount': 'Ai deja un cont?',
  'register.login': 'Autentificare',
  'register.passwordMin': 'Parola trebuie să aibă cel puțin 8 caractere.',
  'register.failed': 'Înregistrare eșuată',
  'register.checkEmail.title': 'Verifică-ți emailul',
  'register.checkEmail.body':
    'Ți-am trimis un link de verificare la {email}. Deschide-l pentru a-ți activa contul.',
  'register.checkEmail.backToLogin': 'Înapoi la autentificare',

  // Auth flows (forgot / reset / verify / invite)
  'auth.forgot.title': 'Ai uitat parola?',
  'auth.forgot.subtitle': 'Îți trimitem un link de resetare pe email.',
  'auth.forgot.submit': 'Trimite linkul',
  'auth.forgot.submitting': 'Se trimite…',
  'auth.forgot.success': 'Dacă adresa există, vei primi un email cu linkul de resetare.',
  'auth.forgot.backToLogin': 'Înapoi la autentificare',
  'auth.reset.title': 'Setează o parolă nouă',
  'auth.reset.password': 'Parolă nouă',
  'auth.reset.confirm': 'Confirmă parola',
  'auth.reset.mismatch': 'Parolele nu coincid',
  'auth.reset.weak': 'Parola trebuie să aibă minim 8 caractere, o literă mare și o cifră',
  'auth.reset.submit': 'Salvează parola',
  'auth.reset.submitting': 'Se salvează…',
  'auth.reset.success': 'Parola a fost schimbată. Te poți autentifica.',
  'auth.reset.goToLogin': 'Mergi la autentificare',
  'auth.reset.failed': 'Linkul de resetare este invalid sau expirat.',
  'auth.verify.title': 'Verificare email',
  'auth.verify.pending': 'Se verifică emailul…',
  'auth.verify.success': 'Email verificat! Te redirecționăm…',
  'auth.verify.failed': 'Linkul de verificare este invalid sau expirat.',
  'auth.verify.resendEmailLabel': 'Emailul contului',
  'auth.verify.resend': 'Retrimite emailul de verificare',
  'auth.verify.resent': 'Email trimis. Verifică-ți inboxul.',
  'auth.invite.title': 'Bun venit!',
  'auth.invite.greeting': 'Salut, {name}! Setează-ți parola pentru contul {email}.',
  'auth.invite.invalid': 'Linkul de invitație este invalid sau expirat.',
  'auth.invite.loading': 'Se validează invitația…',
  'auth.invite.password': 'Parolă',
  'auth.invite.confirm': 'Confirmă parola',
  'auth.invite.submit': 'Activează contul',
  'auth.invite.submitting': 'Se activează…',

  // Settings (Setări)
  'settings.title': 'Setări',
  'settings.subtitle': 'Configurează integrările și nomenclatoarele aplicației.',
  'settings.tab.companies': 'Firme',
  'settings.tab.bankAccounts': 'Conturi bancare',
  'settings.tab.integrations': 'Integrări',
  'settings.tab.aiBudget': 'Buget AI',
  'settings.tab.categories': 'Categorii cheltuieli',
  'settings.tab.supplierCategories': 'Categorii furnizori',
  'settings.tab.subscriptionCategories': 'Categorii abonamente',
  'settings.tab.bankDefaults': 'Valori implicite extrase bancare',
  'settings.tab.leads': 'Pipeline-uri',
  'settings.tab.departments': 'Departamente',
  'settings.tab.services': 'Servicii',
  'settings.tab.taskStatuses': 'Statusuri sarcini',
  'settings.tab.taskTemplates': 'Șabloane sarcini',
  'settings.tab.emailTemplates': 'Template-uri email',
  'settings.tab.emailHistory': 'Istoric emailuri',
  'settings.tab.team': 'Administrare echipă',
  'settings.tab.roles': 'Roluri și permisiuni',
  'settings.tab.modules': 'Module',
  'settings.modules.title': 'Modulele aplicației',
  'settings.modules.description':
    'Pornește sau oprește module pentru toată lumea. Un modul oprit dispare din meniu și nu mai poate fi folosit, nici de admini; când îl pornești la loc, permisiunile fiecăruia rămân cum erau.',
  'settings.modules.core': 'Modul de bază, nu poate fi oprit.',
  'settings.modules.loadError': 'Lista modulelor nu a putut fi citită.',
  'settings.modules.consoleLocked':
    'Nu este inclus în abonamentul instanței. Pentru activare, contactați Firmio.',
  'settings.modules.parentOff': 'Oprit odată cu modulul din care face parte.',
  'settings.tab.kb': 'Knowledge Base',
  'settings.section': 'Secțiune',
  'settings.tab.pushNotifications': 'Notificări push',
  'settings.tab.alerts': 'Alerte',
  'settings.tab.onboarding': 'Onboarding',

  // System alerts (Settings → Alerte)
  'alerts.intro':
    'Aici apar problemele pe care Firmio nu le poate rezolva singur: un serviciu extern a refuzat cererile. Fiecare alertă spune ce nu mai funcționează și ce ai de făcut.',
  'alerts.deliveryHint':
    'Administratorii primesc fiecare alertă nouă prin notificare push și email, apoi o dată pe zi cât timp rămâne activă.',
  'alerts.loading': 'Se încarcă alertele…',
  'alerts.loadError': 'Alertele nu au putut fi încărcate',
  'alerts.openTitle': 'Active ({count})',
  'alerts.none': 'Nicio alertă activă. Toate serviciile au răspuns normal la ultima verificare.',
  'alerts.resolvedTitle': 'Rezolvate recent',
  'alerts.severity.critical': 'Critică',
  'alerts.severity.warning': 'Avertisment',
  'alerts.count': 'de {count} ori',
  'alerts.firstSeen': 'Prima dată:',
  'alerts.lastSeen': 'Ultima dată:',
  'alerts.resolvedAt': 'Rezolvată:',
  'alerts.acknowledgedBy': 'văzută de {name}',
  'alerts.openProvider': 'Deschide contul furnizorului',
  'alerts.acknowledge': 'Am văzut',
  'alerts.resolve': 'Marchează rezolvată',

  // Push notifications (Settings → Notificări push)
  'push.title': 'Notificări push',
  'push.description':
    'Primește o notificare pe telefon sau pe calculator când ți se atribuie o sarcină, când se scrie în chat sau când pornește o discuție pe voce.',
  'push.notConfigured':
    'Notificările push nu sunt configurate pe această instalare. Administratorul trebuie să adauge cheile VAPID pe server.',
  'push.unsupported': 'Browserul acesta nu acceptă notificări push.',
  'push.desktopActive': 'Notificările sunt active pe acest computer.',
  'push.desktopActiveHint':
    'Aplicația Firmio le arată ca notificări de sistem, și cu fereastra închisă în bara de sistem.',
  'push.desktopPaused':
    'Notificările sunt oprite din meniul pictogramei Firmio (bara de sistem). Bifează „Notificări” acolo ca să le pornești.',
  'push.enableHere': 'Pornește notificările pe acest dispozitiv',
  'push.disableHere': 'Oprește notificările pe acest dispozitiv',
  'push.onHere': 'Notificările sunt pornite pe acest dispozitiv.',
  'push.offHere': 'Notificările sunt oprite pe acest dispozitiv.',
  'push.deviceCount': '{count} dispozitive conectate',
  'push.enabledNotice': 'Gata. Trimite-ți o notificare de test ca să fii sigur.',
  'push.sendTest': 'Trimite-mi o notificare de test',
  'push.testSent': 'Notificarea a plecat. Ar trebui să apară în câteva secunde.',
  'push.errors.denied':
    'Browserul a blocat notificările și nu va mai întreba. Le poți reporni din setările browserului, la permisiunile acestui site.',
  'push.errors.notConfigured': 'Serverul nu are cheile de notificare configurate.',
  'push.categoriesTitle': 'Pentru ce vrei notificări',
  'push.categoriesHint':
    'Aceste opțiuni sunt ale contului tău și se aplică pe toate dispozitivele.',
  'push.category.tasks': 'Sarcini atribuite mie',
  'push.category.tasksHint': 'Când cineva îți atribuie o sarcină nouă sau ți-o pasează ție.',
  'push.category.chat': 'Mesaje în chat',
  'push.category.chatHint': 'Mesaje directe și mesaje din canalele text din care faci parte.',
  'push.category.voice': 'Canale de voce',
  'push.category.voiceHint': 'Doar când cineva pornește o discuție într-un canal gol.',
  'push.category.alerts': 'Alerte de sistem',
  'push.category.alertsHint':
    'Când un serviciu extern pică: OpenAI fără credit, cheie invalidă, ANAF sau Drive neautorizat, email refuzat. Doar administratorii.',
  'push.category.meetings': 'Întâlniri video (Meet)',
  'push.category.meetingsHint':
    'Cu câteva minute înainte să înceapă o întâlnire și când un invitat așteaptă să fie admis.',
  'push.category.leaves': 'Concedii',
  'push.category.leavesHint':
    'Când cineva depune o cerere pe care o poți aproba, și când cererea ta primește răspuns.',
  'push.iosTitle': 'Pe iPhone, instalează întâi aplicația',
  'push.iosBody':
    'Apple permite notificări doar din aplicația adăugată pe ecranul principal, nu dintr-un tab obișnuit de Safari. Durează un minut.',
  'push.iosStep1': 'Apasă butonul de Share, din bara de jos a Safari.',
  'push.iosStep2': 'Alege „Add to Home Screen”.',
  'push.iosStep3': 'Deschide Firmio de pe ecranul principal și revino aici.',
  'push.sendTestNeedsEnable':
    'Pornește întâi notificările pe acest dispozitiv, ca să ai unde primi testul.',
  'push.soundsTitle': 'Sunete în aplicație',
  'push.soundsHint':
    'Se aud cât timp aplicația e deschisă. Pe telefon, notificările primite cu aplicația închisă folosesc aceleași sunete; pe calculator, sunetul lor e al sistemului de operare.',
  'push.soundsToggle': 'Sunete pentru mesaje, întâlniri, task-uri și voce',
  'push.soundsToggleHint': 'Valabil pe acest dispozitiv.',
  'push.testSoundsLabel': 'Ascultă sunetele:',
  'push.sound.message': 'Mesaj nou',
  'push.sound.meeting': 'Întâlnire care începe',
  'push.sound.task': 'Task nou',
  'push.sound.join': 'Intrare în voce',
  'push.sound.leave': 'Ieșire din voce',
  'push.sound.knock': 'Cerere de intrare în Meet',
  'settings.tab.dailyStatus': 'Raport zilnic',
  'settings.tab.bugReports': 'Bug-uri raportate',

  // In-app bug reporting: the floating widget everyone sees, and the admin
  // triage view in Settings.
  'bugReport.widget.open': 'Raportează un bug',
  'bugReport.widget.close': 'Închide',
  'bugReport.widget.title': 'Raportează un bug',
  'bugReport.widget.intro': 'Spune-ne ce nu a mers. Ajută mult un screenshot.',
  'bugReport.field.title': 'Titlu',
  'bugReport.field.titlePlaceholder': 'Pe scurt, ce s-a întâmplat',
  'bugReport.field.description': 'Descriere',
  'bugReport.field.descriptionPlaceholder':
    'Ce făceai, ce te așteptai să se întâmple și ce s-a întâmplat de fapt.',
  'bugReport.attachments.hint': 'Trage screenshot-uri aici, lipește-le cu Ctrl+V sau alege fișiere',
  'bugReport.attachments.pick': 'Atașează un fișier',
  'bugReport.attachments.remove': 'Elimină atașamentul',
  'bugReport.submit': 'Trimite',
  'bugReport.submitting': 'Se trimite…',
  'bugReport.error.generic': 'Nu am putut trimite raportarea. Încearcă din nou.',
  'bugReport.error.upload': 'Încărcarea fișierului a eșuat.',
  'bugReport.success.title': 'Mulțumim!',
  'bugReport.success.body': 'Am înregistrat raportarea ca {identifier}.',
  'bugReport.success.again': 'Raportează altceva',

  'bugReports.intro':
    'Tot ce au raportat colegii din butonul de raportare: cine, când, pe ce pagină și cu ce screenshot-uri.',
  'bugReports.search': 'Caută în titlu și descriere',
  'bugReports.allStatuses': 'Toate stările',
  'bugReports.allPriorities': 'Toate prioritățile',
  'bugReports.clearFilters': 'Șterge filtrele',
  'bugReports.column.identifier': 'Bug',
  'bugReports.column.title': 'Titlu',
  'bugReports.column.status': 'Status',
  'bugReports.column.priority': 'Prioritate',
  'bugReports.column.reporter': 'Raportat de',
  'bugReports.column.createdAt': 'Raportat la',
  'bugReports.empty': 'Niciun bug raportat încă.',
  'bugReports.emptyFiltered': 'Niciun bug nu se potrivește cu filtrele.',
  'bugReports.loadError': 'Nu am putut încărca lista de bug-uri',
  'bugReports.pageOf': 'Pagina {page} din {totalPages} ({total} în total)',
  'bugReports.previous': 'Anterior',
  'bugReports.next': 'Următor',
  'bugReports.unknownReporter': 'Utilizator șters',
  'bugReports.delete': 'Șterge raportarea',
  'bugReports.menu': 'Acțiuni',
  'bugReports.changeStatus': 'Schimbă statusul',
  'bugReports.changePriority': 'Schimbă prioritatea',
  'bugReports.deleteComment': 'Șterge comentariul',
  'bugReports.deleteConfirm': 'Ștergi definitiv această raportare, cu tot cu comentarii?',
  'bugReports.detail.attachments': 'Screenshot-uri',
  'bugReports.detail.comments': 'Comentarii',
  'bugReports.detail.activity': 'Activitate',
  'bugReports.detail.noComments': 'Încă niciun comentariu.',
  'bugReports.detail.noActivity': 'Nicio schimbare încă.',
  'bugReports.detail.commentPlaceholder': 'Scrie un comentariu…',
  'bugReports.detail.comment': 'Comentează',
  'bugReports.detail.notFound': 'Raportarea nu a fost găsită.',
  'bugReports.detail.loading': 'Se încarcă raportarea…',
  'bugReports.props.title': 'Detalii',
  'bugReports.props.reporter': 'Raportat de',
  'bugReports.props.createdAt': 'Raportat la',
  'bugReports.props.resolvedAt': 'Rezolvat la',
  'bugReports.props.page': 'Pagina',
  'bugReports.props.browser': 'Browser',
  'bugReports.props.viewport': 'Rezoluție',
  'bugReports.props.appVersion': 'Versiune',
  'bugReports.activity.created': 'a raportat bug-ul',
  'bugReports.activity.changed': 'a schimbat {field} din {from} în {to}',
  'bugReports.activity.field.status': 'statusul',
  'bugReports.activity.field.priority': 'prioritatea',
  'bugReports.status.NEW': 'Nou',
  'bugReports.status.IN_PROGRESS': 'În lucru',
  'bugReports.status.RESOLVED': 'Rezolvat',
  'bugReports.status.REJECTED': 'Respins',
  'bugReports.priority.LOW': 'Scăzută',
  'bugReports.priority.MEDIUM': 'Medie',
  'bugReports.priority.HIGH': 'Ridicată',
  'bugReports.priority.CRITICAL': 'Critică',
  'settings.dailyStatus.title': 'Raport zilnic al echipei',
  'settings.dailyStatus.description':
    'Reminder pentru completarea statusului și raportul automat de final de zi (luni–vineri). Orele sunt în Europe/Bucharest.',
  'settings.dailyStatus.enabled': 'Activ (reminder + raport automat)',
  'settings.dailyStatus.reminderTime': 'Ora reminderului',
  'settings.dailyStatus.reportTime': 'Ora raportului',
  'settings.dailyStatus.sender': 'Expeditorul reminderului din Team Chat',
  'settings.dailyStatus.senderAuto': 'Automat (primul administrator activ)',
  'settings.dailyStatus.senderHint':
    'Mesajul „[Reminder automat]” pleacă în DM din contul acestei persoane.',
  'settings.dailyStatus.save': 'Salvează',
  'settings.dailyStatus.saved': 'Setările au fost salvate.',
  'settings.dailyStatus.runReminder': 'Trimite reminderul acum',
  'settings.dailyStatus.runReport': 'Trimite raportul acum',
  'settings.dailyStatus.schedule': 'Programat: reminder {reminder}, raport {report} ({zone})',
  'settings.dailyStatus.scheduleOff': 'Schedulerul nu rulează în acest proces.',
  'settings.dailyStatus.nextReminder': 'următorul reminder {date}',
  'settings.dailyStatus.nextReport': 'următorul raport {date}',
  'settings.dailyStatus.lastReminder': 'Ultimul reminder',
  'settings.dailyStatus.lastReport': 'Ultimul raport',
  'settings.dailyStatus.lastManual': 'Ultima rulare manuală',
  'settings.dailyStatus.testHint': 'Test doar către tine (marcat [Test], fără raportul echipei):',
  'settings.dailyStatus.pushToApp': 'Afișează reminderul în aplicație, tuturor',
  'settings.dailyStatus.pushHint':
    'Bara roșie de reminder apare imediat, la toți colegii cu task-uri fără status, indiferent de oră. Nu trimite email sau mesaj în chat.',
  'settings.dailyStatus.pushed':
    'Reminderul e activ în aplicație pentru {count} colegi cu task-uri necompletate: {names}',
  'settings.dailyStatus.testReminder': 'Trimite-mi reminderul',
  'settings.dailyStatus.testReport': 'Trimite-mi raportul',
  'settings.unknownError': 'Eroare necunoscută.',

  // Settings → Istoric emailuri (doar administratori)
  'settings.emailHistory.intro':
    'Jurnalul de trimitere și urmărirea deschiderilor pentru fiecare email trimis de aplicație.',
  'settings.emailHistory.statsError': 'Statisticile nu au putut fi încărcate',
  'settings.emailHistory.loadError': 'Emailurile nu au putut fi încărcate',
  'settings.emailHistory.pageOf': 'Pagina {page} din {totalPages} ({total} în total)',
  'settings.emailHistory.previous': 'Înapoi',
  'settings.emailHistory.next': 'Înainte',

  // Settings → Firme (multi-company)
  'companies.description':
    'Firmele contului tău. Firma implicită dă brandul aplicației, emailurile și facturile; datele fără firmă asociată aparțin firmei implicite.',
  'companies.add': 'Adaugă firmă',
  'companies.addTitle': 'Firmă nouă',
  'companies.editTitle': 'Editează firma',
  'companies.loading': 'Se încarcă firmele…',
  'companies.defaultBadge': 'Implicită',
  'companies.setDefault': 'Setează implicită',
  'companies.noCui': 'Fără CUI',
  'companies.saving': 'Se salvează…',
  'companies.legalNameRequired': 'Numele legal este obligatoriu.',
  'companies.deleteConfirm':
    'Ștergi firma „{name}”? Clienții, furnizorii, proiectele, facturile și extrasele asociate rămân în aplicație, dar fără firmă, iar facturile SmartBill ale firmei se șterg. Acțiunea nu poate fi anulată.',
  'companies.deleted':
    'Firma „{name}” a fost ștearsă. Detașate: {clients} clienți, {suppliers} furnizori, {projects} proiecte, {invoices} facturi; {smartbill} facturi SmartBill șterse.',
  'companies.error.load': 'Firmele nu au putut fi încărcate.',
  'companies.error.generic': 'Operațiunea a eșuat.',
  'companies.detail.back': 'Înapoi la firme',
  'companies.detail.notFound': 'Firma nu a fost găsită.',
  'companies.detail.forbidden': 'Nu ai permisiunea de a administra firmele.',
  'companies.detail.dataHeading': 'Date firmă',
  'companies.detail.saved': 'Datele firmei au fost salvate.',
  'companies.detail.smartbillHeading': 'Integrare SmartBill',
  'companies.detail.efacturaHeading': 'ANAF e-Factura (SPV)',
  'companies.detail.efacturaHint':
    'Facturile primite și trimise prin SPV, doar citire. Salvează CIF-ul, apoi autorizează firma la ANAF din browserul cu certificatul calificat.',
  'companies.detail.smartbillHint':
    'Credențialele SmartBill se configurează separat pentru fiecare firmă și sunt folosite la sincronizarea facturilor acesteia.',

  // Firme — administratori / asociat unic (istoric mandate)
  'companies.admins.heading': 'Administratori / Asociat unic',
  'companies.admins.hint':
    'Persoanele cu mandat de administrator sau asociat unic. Plățile către ele din extrasele de cont sunt marcate la reconciliere în perioada mandatului.',
  'companies.admins.add': 'Adaugă mandat',
  'companies.admins.empty': 'Niciun mandat înregistrat.',
  'companies.admins.person': 'Persoană',
  'companies.admins.personPlaceholder': 'Alege persoana…',
  'companies.admins.role': 'Rol',
  'companies.admins.role.ADMINISTRATOR': 'Administrator',
  'companies.admins.role.ASOCIAT_UNIC': 'Asociat unic',
  'companies.admins.startDate': 'Început mandat',
  'companies.admins.endDate': 'Sfârșit mandat (opțional)',
  'companies.admins.endMandate': 'Încheie mandatul',
  'companies.admins.periodActive': 'din {start} · activ',
  'companies.admins.deleteConfirm': 'Ștergi mandatul lui {name}? Acțiunea nu poate fi anulată.',
  'companies.admins.overlapError':
    'Persoana are deja un mandat care se suprapune cu perioada aleasă.',
  'companies.admins.personRequired': 'Alege persoana.',
  'companies.admins.startRequired': 'Data de început este obligatorie.',
  'companies.admins.paymentBadge': 'Plată către administrator: {name} — {role}',
  'companies.admins.loanBadge': 'Încasare de la administrator: {name} — {role}',
  'companies.admins.personName': 'Nume persoană',
  'companies.admins.personNameHint': 'Modificarea numelui se propagă în tot sistemul.',

  // Extrase bancare — firmă (multi-company)
  'bank.company.auto': 'Firmă: automat (după CUI)',
  'bank.company.noneWarning':
    'Firma nu a putut fi detectată din extras (CUI lipsă sau necunoscut). Extrasul a fost atribuit firmei implicite — îl poți reasigna din pagina de reconciliere.',
  'bank.company.reassigned': 'Extrasul a fost reasignat firmei selectate.',
  'bank.company.reassignFailed': 'Reasignarea firmei a eșuat.',
  'bank.company.finalizedLocked': 'Extrasul este finalizat — firma nu mai poate fi schimbată.',

  // Extrase bancare — ștergere
  'bankStatements.delete.button': 'Șterge extrasul',
  'bankStatements.filter.from': 'De la',
  'bankStatements.filter.to': 'Până la',
  'bankStatements.filter.anyDate': 'Oricând',
  'bankStatements.filter.clear': 'Șterge perioada',
  'bankStatements.filter.empty': 'Niciun extras în perioada aleasă.',
  'bankStatements.filter.mode': 'Perioadă',
  'bankStatements.filter.mode.all': 'Toate',
  'bankStatements.filter.mode.month': 'Lună',
  'bankStatements.filter.mode.year': 'An',
  'bankStatements.filter.mode.custom': 'Interval',
  'bankStatements.filter.year': 'Anul',
  'bankStatements.filter.month': 'Luna',
  'bankStatements.delete.title': 'Ștergi acest extras?',
  'bankStatements.delete.message':
    'Se șterg extrasul {name} și toate tranzacțiile lui. Plățile create în Financiar la reconciliere vor fi eliminate, iar facturile marcate ca plătite de acest extras revin la „Trimisă”. Acțiunea nu poate fi anulată.',
  'bankStatements.delete.confirm': 'Șterge definitiv',
  'bankStatements.delete.success':
    'Extrasul a fost șters ({financials} plăți eliminate din Financiar, {invoices} facturi revenite la „Trimisă”).',
  'bankStatements.delete.error': 'Ștergerea extrasului a eșuat.',

  // AI budget (Buget AI)
  'aiBudget.intro':
    'Urmărește consumul lunar de AI (chat, rezumate, embeddings, transcrieri) și setează un plafon în EUR. Când plafonul este atins, apelurile AI sunt oprite până luna următoare sau până mărești limita.',
  'aiBudget.loading': 'Se încarcă consumul…',
  'aiBudget.loadError': 'Consumul AI nu a putut fi încărcat',
  'aiBudget.currentSpend': 'Consum în {month}',
  'aiBudget.ofLimit': 'din limita de {limit} €',
  'aiBudget.calls': '{count} apeluri AI',
  'aiBudget.progressLabel': 'Consum din limita lunară',
  'aiBudget.warnTick': 'La 80% adminii primesc o alertă',
  'aiBudget.usedPct': '{pct}% folosit',
  'aiBudget.remaining': 'mai sunt {amount}',
  'aiBudget.overBy': 'depășit cu {amount}',
  'aiBudget.noLimit': 'Nicio limită setată — consumul este nelimitat.',
  'aiBudget.limitLabel': 'Limită lunară (EUR)',
  'aiBudget.limitHint': 'Lasă gol pentru consum nelimitat.',
  'aiBudget.managedByFirmio':
    'Limita lunară e inclusă în abonamentul Firmio. Pentru modificare, contactați Firmio.',
  'aiBudget.limitInvalid': 'Limita trebuie să fie un număr pozitiv.',
  'aiBudget.save': 'Salvează limita',
  'aiBudget.saving': 'Se salvează…',
  'aiBudget.saved': 'Limita a fost salvată.',
  'aiBudget.saveError': 'Limita nu a putut fi salvată',
  'aiBudget.exceededBanner':
    'Bugetul lunar pentru AI a fost epuizat. Apelurile AI sunt oprite până luna următoare sau până mărești limita.',
  'aiBudget.perModel': 'Defalcare pe model',
  'aiBudget.perModelEmpty': 'Niciun apel AI înregistrat în perioada aleasă.',
  'aiBudget.period.month': 'Lună',
  'aiBudget.period.range': 'Interval',
  'aiBudget.period.monthLabel': 'Luna',
  'aiBudget.period.from': 'De la',
  'aiBudget.period.to': 'Până la',
  'aiBudget.periodTotal': '{cost} € · {count} apeluri AI',
  'aiBudget.rangeInvalid': 'Data de început trebuie să fie înaintea celei de sfârșit.',
  'aiBudget.dailySince':
    'Consumul pe zile se înregistrează începând cu {date}. Pentru perioade dinainte, alege luna.',
  'aiBudget.dailyNone':
    'Consumul pe zile se înregistrează de acum înainte; până se strâng date, alege luna.',
  'aiBudget.col.model': 'Model',
  'aiBudget.col.provider': 'Furnizor',
  'aiBudget.col.calls': 'Apeluri',
  'aiBudget.col.inputTokens': 'Tokeni intrare',
  'aiBudget.col.outputTokens': 'Tokeni ieșire',
  'aiBudget.col.cost': 'Cost (EUR)',
  // Settings → Template-uri email
  'settings.emailTemplates.intro':
    'Personalizează emailurile trimise de aplicație. Variabilele {{nume}} sunt înlocuite automat la trimitere; folosește Previzualizare pentru a vedea rezultatul cu date de exemplu.',
  'settings.emailTemplates.loading': 'Se încarcă template-urile…',
  'settings.emailTemplates.loadError': 'Template-urile nu au putut fi încărcate',
  'settings.emailTemplates.template': 'Template',
  'settings.emailTemplates.name.overdue_invoice': 'Reminder facturi întârziate',
  'settings.emailTemplates.name.welcome': 'Bun venit (cont nou)',
  'settings.emailTemplates.customized': 'Personalizat',
  'settings.emailTemplates.default': 'Implicit',
  'settings.emailTemplates.subject': 'Subiect',
  'settings.emailTemplates.variables': 'Variabile disponibile',
  'settings.emailTemplates.variablesHint':
    'Click pe o variabilă pentru a o insera la poziția cursorului în corpul emailului.',
  'settings.emailTemplates.insertVariable': 'Inserează variabila',
  'settings.emailTemplates.body': 'Corp email (HTML)',
  'settings.emailTemplates.save': 'Salvează',
  'settings.emailTemplates.saving': 'Se salvează…',
  'settings.emailTemplates.saved': 'Template-ul a fost salvat.',
  'settings.emailTemplates.saveError': 'Operațiunea a eșuat',
  'settings.emailTemplates.preview': 'Previzualizare',
  'settings.emailTemplates.previewLoading': 'Se generează…',
  'settings.emailTemplates.previewTitle': 'Previzualizare cu date de exemplu',
  'settings.emailTemplates.reset': 'Resetează la implicit',

  'settings.bank.intro':
    'Fiecare linie importată dintr-un extras bancar primește automat o categorie de cheltuială de bază: încasări (credite) și plăți (debite). Poți totuși suprascrie categoria pentru orice tranzacție individuală în timpul reconcilierii.',
  'settings.bank.loading': 'Se încarcă valorile implicite…',
  'settings.bank.loadError': 'Nu s-au putut încărca valorile implicite',
  'settings.bank.heading': 'Categorii implicite',
  'settings.bank.headingHint':
    'Aplicate la import dacă potrivirea automată nu a ales deja o categorie mai specifică.',
  'settings.bank.credit': 'Credite (încasări)',
  'settings.bank.debit': 'Debite (plăți)',
  'settings.bank.save': 'Salvează valorile implicite',
  'settings.bank.saving': 'Se salvează…',
  'settings.bank.saved': 'Valorile implicite au fost salvate.',
  'settings.bank.saveError': 'Salvarea a eșuat',
  'settings.kb.intro':
    'Sincronizează Knowledge Base (fișierele markdown citite de asistentul AI) cu baza de date. Folosește această acțiune dacă datele din interfață (clienți, reconcilieri bancare) nu apar corect în răspunsurile asistentului.',
  'settings.kb.open': 'Deschide Knowledge Base',
  'settings.kb.heading': 'Regenerează Knowledge Base',
  'settings.kb.headingHint':
    'Sincronizare completă: rescrie toate proiecțiile din baza de date (clienți, furnizori, abonamente, leads, financiar, extrase bancare, SmartBill, sarcini pe proiect), reindexează căutarea semantică și oglindește întregul Knowledge Base în Google Drive (dacă e configurat). Poate dura câteva minute.',
  'settings.kb.regenerate': 'Regenerează Knowledge Base',
  'settings.kb.regenerating': 'Se regenerează…',
  'settings.kb.success': 'Knowledge Base a fost regenerat cu succes.',
  'settings.kb.result.clients': '{count} clienți',
  'settings.kb.result.suppliers': '{count} furnizori',
  'settings.kb.result.projects': '{count} proiecte cu date financiare',
  'settings.kb.result.firmCosts': '{count} cheltuieli ale firmei',
  'settings.kb.result.reports': '{count} rapoarte lunare',
  'settings.kb.result.leads': 'index leads regenerat',
  'settings.kb.result.bankMonths': '{count} luni cu extrase bancare',
  'settings.kb.result.smartbillYears': '{count} ani SmartBill',
  'settings.kb.result.tasks': '{count} proiecte cu sarcini',
  'settings.kb.result.reindexed': '{count} fragmente reindexate pentru căutare',
  'settings.kb.result.driveMirrored': '{count} fișiere oglindite în Google Drive',
  'settings.kb.result.driveOff': 'Google Drive neconfigurat — KB doar local',
  'settings.kb.result.aiSummaries': 'Sumare AI: {state}',
  'settings.kb.ai.enabled': 'generate',
  'settings.kb.ai.disabled': 'dezactivate (Anthropic neconfigurat)',
  'settings.kb.error': 'Regenerarea a eșuat',
  'settings.integrations.intro':
    'Configurează datele de autentificare pentru integrări. Sunt stocate criptat în baza de date — nu este nevoie să editezi fișiere .env.',
  'settings.integrations.loading': 'Se încarcă integrările…',
  'settings.integrations.loadError': 'Nu s-au putut încărca integrările',
  'settings.integrations.wipe.button': 'Șterge toate credențialele salvate',
  'settings.integrations.wipe.title': 'Ștergi toate credențialele salvate?',
  // Spells out what falls over, and — just as important — what does not: an
  // integration with values in .env keeps working from .env, which otherwise
  // badly surprises someone who has just pressed "delete everything".
  'settings.integrations.wipe.message':
    'Se șterg din baza de date credențialele pentru Google Drive, OpenAI, Anthropic, SMTP, Google Calendar, Gmail și SmartBill pentru toate firmele. Integrările care au valori în fișierul .env al serverului vor continua să funcționeze din .env. Acțiunea nu poate fi anulată.',
  'settings.integrations.wipe.confirm': 'Șterge tot',

  // Backup complet al aplicației (Setări → Integrări, sub cardul Google Drive)
  'settings.uploadLimit.title': 'Dimensiunea maximă a unui fișier',
  'settings.uploadLimit.description':
    'Cel mai mare fișier care poate fi încărcat o dată în Google Drive: resursele proiectelor, clienților, furnizorilor, oamenilor și firmei, atașamentele de la sarcini și lead-uri, notițele de întâlnire. Fișierele mai mari sunt refuzate înainte să înceapă încărcarea.',
  'settings.uploadLimit.label': 'Limită per fișier',
  'settings.uploadLimit.save': 'Salvează',
  'settings.uploadLimit.reset': 'Revino la maximul de {cap} MB',
  'settings.uploadLimit.range': 'Între {min} și {cap} MB. {cap} MB e plafonul serverului.',
  'settings.uploadLimit.saved': 'Limita a fost salvată.',
  'settings.uploadLimit.loadError': 'Limita de încărcare nu a putut fi citită.',
  'settings.backup.title': 'Backup aplicație',
  'settings.backup.description':
    'Salvează pe Google Drive o copie completă a aplicației: baza de date (dump SQL comprimat) și Knowledge Base-ul (arhivă zip), în folderul „_Sistem / Backup-uri DB”.',
  'settings.backup.run': 'Fă backup acum',
  'settings.backup.running': 'Se face backup…',
  'settings.backup.runningHint':
    'Poate dura câteva minute, în funcție de mărimea bazei de date. Nu închide pagina.',
  'settings.backup.driveOff':
    'Configurează și salvează Google Drive mai sus ca să poți face backup.',
  'settings.backup.success': 'Backup încărcat pe Google Drive în {duration}s.',
  'settings.backup.error': 'Backup-ul a eșuat',
  'settings.backup.database': 'Baza de date',
  'settings.backup.knowledgeBase': 'Knowledge Base',
  'settings.backup.files': '{count} fișiere',
  'settings.backup.recent': 'Ultimele backup-uri din Drive',
  'settings.backup.none': 'Niciun backup încă.',
  'settings.backup.loadError': 'Nu s-au putut citi backup-urile din Drive',
  'settings.backup.schedule.daily': 'Backup automat zilnic la {time} ({zone})',
  'settings.backup.schedule.next': 'următorul: {date}',
  'settings.backup.schedule.off': 'Backup-ul automat este dezactivat (BACKUP_DAILY_ENABLED).',
  'settings.backup.schedule.lastOk': 'Ultimul backup automat: {date} — reușit.',
  'settings.backup.schedule.lastFailed': 'Ultimul backup automat: {date} — a eșuat: {error}',

  // Time tracking (worklog pe sarcini)
  'time.title': 'Timp lucrat',
  'time.logTime': 'Adaugă timp',
  'time.duration': 'Durată',
  'time.durationPlaceholder': 'ex: 45m, 1h 30m',
  'time.date': 'Dată',
  'time.note': 'Notă',
  'time.notePlaceholder': 'Ce ai lucrat? (opțional)',
  'time.total': 'Total',
  'time.ofEstimate': 'din {estimate} estimate',
  'time.noEstimate': 'fără estimare',
  'time.save': 'Salvează',
  'time.cancel': 'Anulează',
  'time.edit': 'Editează',
  'time.delete': 'Șterge',
  'time.empty': 'Nicio înregistrare de timp.',
  'time.loading': 'Se încarcă…',
  'time.logged': 'Timp înregistrat',
  'time.invalidDuration': 'Introdu o durată validă (ex: 45m, 1h 30m).',
  'time.deletedUser': 'Utilizator șters',

  // Cronometru pe task (bara globală + butoanele din „Timp lucrat").
  'time.live.running': 'În lucru acum',
  'time.live.paused': 'Pe pauză',
  'timer.start': 'Pornește cronometru',
  'timer.pause': 'Pauză',
  'timer.resume': 'Reia',
  'timer.stop': 'Stop',
  'timer.startedAt': 'pornit la {time}',
  'timer.startFailed': 'Cronometrul nu a putut porni.',
  'timer.untitledTask': 'Sarcină fără titlu',
  // Notificarea persistentă a cronometrului în aplicația Android.
  'timer.notification.channel': 'Cronometru',
  'timer.notification.running': 'Ai pornit timerul pentru task-ul',
  'timer.notification.paused': 'Timerul e pe pauză',
  'timer.notification.stopped': 'Timp salvat pentru task-ul',
  'timer.notification.reset': 'Reset',
  'timer.notification.close': 'Închide',

  // Calendar operațional (sidebar + /calendar). Numele de luni/zile vin din
  // Intl.DateTimeFormat, nu din dicționar.
  'sidebar.calendar': 'Calendar',
  'calendar.title': 'Calendar',
  'calendar.viewCalendar': 'Lună',
  'calendar.viewWeek': 'Săptămână',
  'calendar.viewPosts': 'Postări',
  'calendar.filterClient': 'Client',
  'calendar.allClients': 'Toți clienții',
  'calendar.viewGanttProjects': 'Gantt proiecte',
  'calendar.viewGanttTeam': 'Gantt echipă',
  'calendar.today': 'Azi',
  'calendar.prev': 'Înapoi',
  'calendar.next': 'Înainte',
  'calendar.more': '+{count}',
  'calendar.noRange': 'Fără interval',
  'calendar.cappedNotice': 'Se afișează primele {count} sarcini',
  'calendar.allProjects': 'Toate proiectele',
  'calendar.allAssignees': 'Toți asignații',
  'calendar.filterProject': 'Proiect',
  'calendar.filterAssignee': 'Asignat',
  'calendar.estimatedTotal': 'Total estimat',
  'calendar.remainingTotal': 'Total rămas',
  'calendar.weekEmpty': 'Nicio sarcină',
  'calendar.closeDay': 'Închide ziua',
  'calendar.moveFailed': 'Sarcina nu a putut fi mutată.',
  'calendar.noTasks': 'Nicio înregistrare în această perioadă.',
  'calendar.noAccess': 'Nu ai permisiunea de a vedea calendarul.',
  'calendar.loadError': 'Încărcarea calendarului a eșuat.',
  'calendar.googleEvent': 'Google Calendar',
  'calendar.allDay': 'Toată ziua',
  'calendarPreview.location': 'Locație',

  // Rapoarte sarcini (sidebar + /reports/tasks)
  'sidebar.taskReports': 'Rapoarte sarcini',
  'reports.title': 'Rapoarte sarcini',
  'reports.subtitle': 'Estimat vs. real, depășiri, viteză și încărcare pe echipă.',
  'reports.noAccess': 'Nu ai permisiunea de a vedea rapoartele de sarcini.',
  'reports.loading': 'Se încarcă…',
  'reports.error': 'Încărcarea raportului a eșuat.',
  'reports.filters.period': 'Perioadă',
  'reports.filters.thisMonth': 'Luna aceasta',
  'reports.filters.lastMonth': 'Luna trecută',
  'reports.filters.last30': 'Ultimele 30 de zile',
  'reports.filters.thisQuarter': 'Acest trimestru',
  'reports.filters.custom': 'Personalizat',
  'reports.filters.from': 'De la',
  'reports.filters.to': 'Până la',
  'reports.filters.project': 'Proiect',
  'reports.filters.person': 'Persoană',
  'reports.filters.department': 'Departament',
  'reports.filters.label': 'Etichetă',
  'reports.filters.all': 'Toate',
  'reports.filters.reset': 'Resetează filtrele',
  'reports.kpi.spentHours': 'Ore lucrate',
  'reports.kpi.estimatedHours': 'Ore estimate',
  'reports.kpi.estimateAccuracy': 'Acuratețe estimări',
  'reports.kpi.created': 'Sarcini create',
  'reports.kpi.completed': 'Sarcini finalizate',
  'reports.kpi.onTimeRate': 'Finalizate la timp',
  'reports.kpi.overruns': 'Sarcini depășite',
  'reports.kpi.activePeople': 'Persoane active',
  'reports.hours.title': 'Estimat vs. real',
  'reports.hours.byProject': 'Pe proiecte',
  'reports.hours.byPerson': 'Pe persoane',
  'reports.hours.byTask': 'Pe sarcini',
  'reports.hours.task': 'Sarcină',
  'reports.hours.project': 'Proiect',
  'reports.hours.person': 'Persoană',
  'reports.hours.status': 'Status',
  'reports.hours.estimated': 'Estimat (h)',
  'reports.hours.spent': 'Lucrat (h)',
  'reports.hours.delta': 'Diferență (h)',
  'reports.hours.pct': '% din estimare',
  'reports.hours.completedAt': 'Finalizat la',
  'reports.hours.export': 'Export Excel',
  'reports.hours.empty': 'Nicio sarcină în perioada selectată.',
  'reports.overruns.title': 'Depășiri de estimare',
  'reports.overruns.byPerson': 'Total depășiri pe persoană',
  'reports.overruns.taskCount': 'Sarcini',
  'reports.overruns.totalHours': 'Ore peste estimare',
  'reports.overruns.unassigned': 'Neatribuit',
  'reports.overruns.empty': 'Nicio depășire în perioada selectată.',
  'reports.throughput.title': 'Creat vs. finalizat',
  'reports.throughput.weekly': 'Săptămânal',
  'reports.throughput.monthly': 'Lunar',
  'reports.throughput.created': 'Create',
  'reports.throughput.completed': 'Finalizate',
  'reports.cycle.title': 'Cycle & lead time',
  'reports.cycle.avgCycle': 'Cycle time mediu',
  'reports.cycle.medianCycle': 'Cycle time median',
  'reports.cycle.avgLead': 'Lead time mediu',
  'reports.cycle.medianLead': 'Lead time median',
  'reports.cycle.days': '{value} zile',
  'reports.cycle.count': 'Sarcini finalizate',
  'reports.cycle.byProject': 'Pe proiecte',
  'reports.cycle.empty': 'Nicio sarcină finalizată în perioada selectată.',
  'reports.workload.title': 'Încărcare vs. capacitate',
  'reports.workload.openTasks': 'Sarcini deschise',
  'reports.workload.estimated': 'Ore estimate deschise',
  'reports.workload.remaining': 'Ore rămase',
  'reports.workload.capacity': 'Capacitate (h/săpt.)',
  'reports.workload.utilization': 'Grad de încărcare',
  'reports.workload.empty': 'Nicio persoană activă cu sarcini deschise.',
  'reports.overdue.title': 'Sarcini restante',
  'reports.overdue.d1_7': '1–7 zile',
  'reports.overdue.d7_30': '8–30 zile',
  'reports.overdue.d30plus': '30+ zile',
  'reports.overdue.daysOverdue': 'Zile întârziere',
  'reports.overdue.dueDate': 'Termen',
  'reports.overdue.byPerson': 'Pe persoane',
  'reports.overdue.byProject': 'Pe proiecte',
  'reports.overdue.empty': 'Nicio sarcină restantă.',

  // Mobile filter bottom sheet (Linear-style)
  'mobileFilters.trigger': 'Filtre',
  'mobileFilters.title': 'Filtre',
  'mobileFilters.searchAll': 'Caută în toate filtrele',
  'mobileFilters.applied': 'Filtre aplicate',
  'mobileFilters.all': 'Toate filtrele',
  'mobileFilters.is': 'este',
  'mobileFilters.dueDate': 'Termen',
  'mobileFilters.rangeFrom': 'De la',
  'mobileFilters.rangeTo': 'Până la',
  'mobileFilters.done': 'Gata',
  'mobileFilters.back': 'Înapoi',
  'mobileFilters.remove': 'Elimină filtrul',
  'mobileFilters.appliedCount': '{count} filtre aplicate',
};

const en: Dict = {
  // Date picker
  'datePicker.placeholder': 'Pick a date',
  'datePicker.clear': 'Clear date',
  'datePicker.previousMonth': 'Previous month',
  'datePicker.nextMonth': 'Next month',
  'datePicker.today': 'Today',
  'timePicker.placeholder': 'Pick a time',
  'timePicker.clear': 'Clear time',
  'timePicker.hour': 'Hour',
  'timePicker.minute': 'Minute',
  'timePicker.now': 'Now',

  // Sidebar
  'sidebar.brand': 'Firmio',
  'sidebar.dashboard': 'Dashboard',
  'sidebar.dailySummary': 'Daily Summary',
  'sidebar.weeklySummary': 'Weekly Summary',
  'sidebar.chat': 'Firmio AI',
  'sidebar.mcp': 'Firmio MCP',
  'mcp.title': 'Firmio MCP',
  'mcp.subtitle': 'Firmio AI inside Claude Desktop, claude.ai, Claude Code or your IDE.',
  'mcp.intro':
    'Firmio exposes the Firmio AI tools through an MCP (Model Context Protocol) server. Add your instance as a “connector” in Claude, sign in with your Firmio account, and Claude works in Firmio on your behalf.',
  'mcp.intro.sameTools':
    'The same tools as the Firmio AI chat: tasks, projects, leads, clients, finance, meetings, KB.',
  'mcp.intro.samePermissions':
    'Exactly your Firmio account permissions, nothing more; an administrator changes them in Settings → Roles and permissions.',
  'mcp.intro.yourPlan':
    'The model and the conversation belong to the Claude app, on your Claude plan; Firmio spends no API credit.',
  'mcp.intro.noFiles':
    'Not available on this channel: uploading invoices and contracts from attached files (use the in-app chat).',
  'mcp.address.title': 'Server address',
  'mcp.address.subtitle': 'The same address for every MCP client.',
  'mcp.copy': 'Copy',
  'mcp.claude.title': 'Claude Desktop and claude.ai',
  'mcp.claude.subtitle': 'Custom connector (paid Claude plan).',
  'mcp.claude.step1': 'In Claude: Settings → Connectors → “Add custom connector”.',
  'mcp.claude.step2': 'Name: Firmio. Paste the server address above and press “Add”.',
  'mcp.claude.step3':
    'Press “Connect”: the Firmio login page opens; sign in and press “Allow access”.',
  'mcp.claude.step4':
    'In a new conversation, enable the Firmio connector and ask, for example, “what are my tasks this week?”.',
  'mcp.claude.button': 'Copy the address and open Connectors in Claude',
  'mcp.claude.buttonCopied': 'Address copied, opening Claude…',
  'mcp.claude.buttonHint':
    'Claude has no install link for remote connectors yet, so the button copies the address and opens the page where you paste it. On a Team/Enterprise plan an owner adds the connector in Organization settings → Connectors, then each person connects with their own Firmio account.',
  'mcp.code.title': 'Claude Code',
  'mcp.code.subtitle': 'One terminal command, then /mcp → Firmio → Authenticate.',
  'mcp.code.hint':
    'The login happens in the browser, on the same Firmio page; the token stays in Claude Code.',
  'mcp.ide.title': 'Cursor and VS Code',
  'mcp.ide.subtitle': 'One-click install, if the app is installed.',
  'mcp.ide.cursor': 'Add to Cursor',
  'mcp.ide.vscode': 'Add to VS Code',
  'mcp.ide.hint':
    'The link opens the app with the server pre-filled; the first use asks for the Firmio login.',
  'mcp.security.title': 'Security',
  'mcp.security.subtitle': 'How access is protected.',
  'mcp.security.oauth':
    'OAuth 2.1 with PKCE: no password reaches Claude; you only sign in on the Firmio page.',
  'mcp.security.rbac':
    'The tool list is filtered by your role, and every call is re-checked on the server.',
  'mcp.security.tokens':
    'The access token expires after an hour and renews itself for up to 90 days of inactivity; a password reset or a deactivated account invalidates it.',
  'mcp.security.revoke':
    'You can disconnect an app any time from the list below or from your Profile.',
  'mcp.security.conversations':
    'Conversations stay in the Claude app; Firmio records only the executed actions.',
  'mcp.tips.title': 'Tips',
  'mcp.tips.context':
    'At the start of a discussion Claude calls “firmio_context” on its own to learn the date, the company snapshot and the relevant memories; if it seems unsure what day it is, ask it to call it.',
  'mcp.tips.plan': 'Custom connectors require a paid Claude plan (Pro, Max, Team or Enterprise).',
  'mcp.tips.noAccess':
    'If you get “no access to Firmio AI”, an administrator has to enable Firmio AI for your role in Settings → Roles and permissions.',
  'profile.connectedApps.guideLink': 'How to connect: the Firmio MCP guide →',
  'sidebar.teamChat': 'Team Chat',
  // Firmio Voice — the spoken assistant (GPT-Live) over the knowledge base.
  'sidebar.voice': 'Firmio Voice',
  'chat.voiceMode': 'Voice mode',
  'voice.backToChat': 'Back to Firmio AI',
  'voice.title': 'Firmio Voice',
  'voice.subtitle':
    'Talk to Firmio AI. It answers out loud, in Romanian, from the knowledge base and company data.',
  'voice.modelLabel': 'Voice: {voice} ({model}) · Reasoning: {backend}',
  'voice.start': 'Start conversation',
  'voice.end': 'End conversation',
  'voice.mute': 'Mute microphone',
  'voice.unmute': 'Unmute microphone',
  'voice.newSession': 'Press the microphone for a new conversation',
  'voice.hintLive': 'Speak naturally. You can interrupt at any time.',
  'voice.phase.idle': 'Press the microphone to start',
  'voice.phase.requestingMic': 'Requesting microphone access…',
  'voice.phase.connecting': 'Connecting…',
  'voice.phase.live': 'Live',
  'voice.phase.ending': 'Ending conversation…',
  'voice.phase.ended': 'Conversation ended',
  'voice.phase.error': 'Could not start the conversation',
  'voice.activity.thinking': 'Thinking…',
  'voice.activity.tool': 'Looking it up in Firmio…',
  'voice.activity.toolNamed': 'Looking up {label}…',
  'voice.captions.empty': 'The transcript shows up here. Try, for example:',
  'voice.captions.listening': 'Listening. Ask anything about the company.',
  'voice.captions.you': 'You',
  'voice.captions.assistant': 'Firmio',
  'voice.example1': 'Which projects are in progress and which are behind?',
  'voice.example2': 'What did we last discuss with the client RetuRO?',
  'voice.example3': 'Create a task in ADNV: review the contract, due Friday.',
  'voice.elapsed': 'Duration',
  'voice.cost': 'Estimated cost (audio)',
  'voice.endedSummary': 'The conversation lasted {duration}.',
  'voice.viewConversation': 'Open the conversation in Firmio AI',
  'voice.closeReason.expired': 'The maximum session length was reached.',
  'voice.closeReason.budget': 'The monthly AI budget is exhausted.',
  'voice.closeReason.connectionLost': 'The connection was lost.',
  'voice.notConfigured':
    'The voice assistant is not configured. Add the OpenAI key in Settings → Integrations (or OPENAI_API_KEY on the server).',
  'voice.demoMode': 'The voice assistant is not available in demo mode.',
  'voice.error.micDenied':
    'Microphone access was denied. Allow the microphone for this site in your browser settings and try again.',
  'voice.error.micUnavailable': 'No microphone was found.',
  'voice.error.notConfigured': 'The voice assistant is not configured on the server.',
  'voice.error.noCredits':
    'The OpenAI account is out of credit, so Firmio Voice cannot start. The admins have been alerted.',
  'voice.error.budget':
    'The monthly AI budget is exhausted. An administrator can adjust it in Settings.',
  'voice.error.busy':
    'Too many voice conversations are active right now. Try again in a few minutes.',
  'voice.error.network':
    'Could not start the session with OpenAI. Check the connection and the OpenAI key, then try again.',
  'voice.error.generic': 'Something went wrong. Please try again.',
  'voice.settings.button': 'Voice: {voice}',
  'voice.settings.title': 'Assistant voice',
  'voice.settings.subtitle':
    'Listen to the samples and pick the voice Firmio answers you with. The choice is yours, not the company’s.',
  'voice.settings.appliesNext':
    'The current conversation keeps its voice; the pick applies to the next one.',
  'voice.settings.greetingHint': 'Every sample is generated by the real GPT-Live voice and says:',
  'voice.settings.group.recommended': 'Recommended',
  'voice.settings.group.live': 'GPT-Live catalog',
  'voice.settings.group.classic': 'Classic OpenAI voices',
  'voice.settings.play': 'Play sample',
  'voice.settings.use': 'Use',
  'voice.settings.selected': 'Selected',
  'voice.settings.default': 'default',
  'voice.settings.sampleError': 'The sample could not be played. Please try again.',
  'voice.endedSuggestions': '{count} proposals to approve',
  'voice.reviewSuggestions': 'Review the proposals in Firmio AI',
  'voice.suggestions.title': 'Proposals from the voice conversation',
  'voice.suggestions.pendingCount': '{count} to approve',
  'voice.suggestions.allDecided': 'all decided',
  'voice.suggestions.hint':
    'Nothing has been created yet. Approve what looks right: tasks and reminders are created then, and Meet meetings are scheduled — only then do the email invitations go out.',
  'voice.suggestions.accept': 'Create',
  'voice.suggestions.run': 'Run',
  'voice.suggestions.acceptMeeting': 'Schedule and invite',
  'voice.suggestions.dismiss': 'Dismiss',
  'voice.suggestions.restore': 'Restore',
  'voice.suggestions.accepted': 'Created',
  'voice.suggestions.failed': 'Failed',
  'voice.suggestions.open': 'Open {label}',
  'voice.suggestions.openMeeting': 'Open the Meet meeting',
  'voice.suggestions.showDecided': 'Show decided ones too ({count})',
  'voice.suggestions.hideDecided': 'Hide decided ones',
  'voice.suggestions.actionError': 'The action failed. Please try again.',
  // Firmio Meet — video meetings with guests by link.
  'sidebar.meet': 'Firmio Meet',
  'meet.title': 'Firmio Meet',
  'meet.subtitle':
    'Video meetings with the team and with clients, right in the browser. Calls are transcribed automatically.',
  'meet.instant': 'Meet now',
  'meet.instantNamePrompt':
    'What is the meeting called? It shows in the history and the meetings log.',
  'meet.instantStart': 'Start',
  'meet.schedule': 'Schedule',
  'meet.testMode': 'Meet is disabled in Test Mode.',
  'meet.tabs.upcoming': 'Upcoming',
  'meet.tabs.past': 'History',
  'meet.emptyUpcoming': 'No meetings scheduled. Start one now or schedule one.',
  'meet.emptyPast': 'No finished meetings yet.',
  'meet.joinPlaceholder': 'Enter a meeting code or link',
  'meet.joinAction': 'Join',
  'meet.joinHint': 'Got a link? Paste it here to go straight in.',
  'meet.hero.title': 'Video meetings, inside Firmio',
  'meet.hero.subtitle':
    'Start a meeting in a second, invite anyone with a link, and the transcript lands in the meetings log by itself.',
  'meet.empty.upcomingTitle': 'No meetings scheduled',
  'meet.empty.upcomingHint': 'Start one now, or schedule it for later.',
  'meet.empty.pastTitle': 'No meeting has finished yet',
  'meet.empty.pastHint': 'After each meeting you will find its transcript and participants here.',
  'meet.search.placeholder': 'Search by title, person, client or project',
  'meet.search.clear': 'Clear search',
  'meet.search.emptyTitle': 'No meetings match “{q}”',
  'meet.search.emptyHint': 'Try another word, or look in the other tab.',
  'meet.day.today': 'Today',
  'meet.day.tomorrow': 'Tomorrow',
  'meet.day.yesterday': 'Yesterday',
  'meet.cancel': 'Cancel',
  'meet.confirmCancel': 'Cancel the meeting? Invitees get a cancellation email.',
  'meet.hostedBy': 'hosted by {name}',
  'meet.details.when': '{day} at {time}',
  'meet.details.joinLink': 'Join link: {url}',
  'meet.status.scheduled': 'Scheduled',
  'meet.status.live': 'Live',
  'meet.status.ended': 'Ended',
  'meet.status.cancelled': 'Cancelled',
  'meetPreview.when': 'When',
  'meetPreview.duration': 'Duration',
  'meetPreview.host': 'Host',
  'meetPreview.client': 'Client',
  'meetPreview.participants': 'Participants',
  'meet.join': 'Join',
  'meet.open': 'Open',
  'meet.viewTranscript': 'View transcript',
  'meet.copyLink': 'Copy link',
  'meet.copyDetails': 'Copy details',
  'meet.copied': 'Copied',
  'meet.you': 'You',
  'meet.guest': 'guest',
  'meet.notFound': 'This meeting does not exist or was deleted.',
  'meet.back': 'Back to Meet',
  'meet.backToApp': 'Back to Firmio',
  'meet.delete': 'Delete meeting',
  'meet.confirmDelete':
    'Delete the meeting “{title}” and its transcript from the history? The meetings-log entry stays.',
  'meet.noParticipants': 'Nobody joined this meeting.',
  'meet.participants': '{count} in the meeting',
  'meet.sharingYours': 'You are sharing your screen. The others see it now.',
  'meet.waitingRoom': 'Waiting room',
  'meet.admit': 'Admit',
  'meet.deny': 'Deny',
  'meet.admitPopup.title': '{name} wants to join',
  'meet.admitPopup.subtitle': 'the meeting “{title}”',
  'meet.admitPopup.guest': 'Guest from outside the company',
  'meet.admitPopup.accept': 'Accept',
  'meet.admitPopup.deny': 'Deny',
  'meet.admitPopup.later': 'Later',
  'meet.admitPopup.acceptAll': 'Accept all ({count})',
  'meet.admitPopup.more': '+{count} waiting',
  'meet.admitPopup.keys': 'Enter = Accept · Esc = Later',
  'meet.inMeeting': 'In the meeting',
  'meet.layout.grid': 'Everyone on screen',
  'meet.layout.speaker': 'Speaker large',
  'meet.transcript.off': 'Transcription is not configured on the server.',
  'meet.transcript.empty':
    'Nothing transcribed yet. Lines appear a few seconds after someone speaks.',
  'meet.transcript.hint':
    'The meeting is transcribed automatically. Guests are told, but do not see the transcript.',
  'meet.transcript.liveElsewhere': 'The transcript shows inside the meeting while it is running.',
  'meet.log.written':
    'The transcript was written into the meetings log, with summary, decisions and action items.',
  'meet.log.open': 'Open in Meetings',
  'meet.log.skipped': 'Nobody joined this meeting, so it was not written into the log.',
  'meet.log.pending': 'The transcript is written into the meetings log within a few minutes.',
  'meet.log.create': 'Write to log',
  'meet.controls.mute': 'Mute',
  'meet.controls.unmute': 'Unmute',
  'meet.controls.muted': 'Muted',
  'meet.controls.cameraOn': 'Turn camera on',
  'meet.controls.cameraOff': 'Turn camera off',
  'meet.controls.share': 'Share screen',
  'meet.controls.stopSharing': 'Stop sharing',
  'meet.controls.people': 'People',
  'meet.controls.transcript': 'Transcript',
  'meet.controls.chat': 'Chat',
  'meet.controls.leave': 'Leave',
  // In-meeting chat (everyone in the room; kept with the meeting)
  'meet.chat.title': 'Chat',
  'meet.chat.placeholder': 'Write a message…',
  'meet.chat.send': 'Send',
  'meet.chat.empty':
    'No messages yet. Everyone in the meeting sees what you write here, and it stays with the meeting.',
  'meet.chat.none': 'Nothing was written in the chat.',
  'meet.chat.sendFailed': 'The message could not be sent. Try again.',
  // Recording (host's screen + everyone's audio → Google Drive)
  'meet.recording.start': 'Record',
  'meet.recording.stop': 'Stop recording',
  'meet.recording.badge': 'REC {time}',
  'meet.recording.you': 'You are recording ({seconds} s)',
  'meet.recording.by': '{name} is recording',
  'meet.recording.error.denied': 'No screen was chosen to record.',
  'meet.recording.error.unsupported': 'This browser cannot record (try Chrome on desktop).',
  'meet.recording.error.upload': 'Recording stopped: the data could not be sent to the server.',
  'meet.recording.error.generic': 'The recording could not start.',
  'meet.recordings.title': 'Recordings',
  'meet.recordings.hint': 'Saved to Google Drive under Meet / <date> <title>.',
  'meet.recordings.none': 'No recording for this meeting.',
  'meet.recordings.disabled': 'Recording needs Google Drive connected in Settings → Integrations.',
  'meet.recordings.status.RECORDING': 'recording',
  'meet.recordings.status.UPLOADING': 'uploading to Drive…',
  'meet.recordings.status.READY': 'ready',
  'meet.recordings.status.FAILED': 'failed',
  'meet.recordings.download': 'Download',
  'meet.recordings.open': 'Open',
  'meet.recordings.play': 'Play',
  'meet.recordings.badge': 'Recording',
  'meet.recordings.watch': 'Watch recording',
  'meet.recordings.playerTitle': 'Recording',
  'meet.recordings.close': 'Close',
  'meet.recordings.playError':
    'The browser cannot play this recording here. Download it and open it in a player (VLC, Chrome).',
  'meet.controls.endForAll': 'End for everyone',
  'meet.controls.confirmEndForAll':
    'End the meeting for all participants? The transcript is saved and written into the log.',
  'meet.error.micDenied': 'The microphone is blocked. Allow access in your browser settings.',
  'meet.error.cameraDenied': 'The camera is blocked. Allow access in your browser settings.',
  'meet.error.connect': 'Could not connect to the meeting. Try again.',
  'meet.lobby.notOpen': 'The meeting is not open yet.',
  'meet.lobby.full': 'The meeting is full.',
  'meet.lobby.ended': 'The meeting has ended.',
  'meet.lobby.left': 'You left the meeting. You can rejoin from the same link.',
  'meet.lobby.cancelled': 'The meeting was cancelled.',
  'meet.lobby.notConfigured':
    'Video meetings are not configured. An administrator can set them up in Settings → Integrations → LiveKit.',
  'meet.lobby.checkLink': 'Check the link you received or ask the host for a new one.',
  'meet.lobby.denied': 'The host did not admit you to the meeting.',
  'meet.lobby.removed': 'The host removed you from the meeting.',
  'meet.moderate.mute': 'Mute their microphone',
  'meet.moderate.alreadyMuted': 'Microphone is already off',
  'meet.moderate.cameraOff': 'Turn their camera off',
  'meet.moderate.cameraAlreadyOff': 'Camera is already off',
  'meet.moderate.remove': 'Remove from meeting',
  'meet.moderate.removeAsk': 'Remove?',
  'meet.moderate.removeConfirm': 'Remove',
  'meet.moderate.removeMessage':
    'Remove {name} from the meeting? They cannot come back unless you admit them again.',
  'meet.lobby.thanks': 'Thank you for joining.',
  'meet.lobby.joining': 'Connecting…',
  'meet.lobby.waiting': 'Waiting to be admitted…',
  'meet.lobby.hostNotIn':
    'The host is not in yet. You will be admitted when someone from the team joins.',
  'meet.lobby.transcribedNotice':
    'This meeting is transcribed automatically for the host team’s notes.',
  'meet.lobby.joiningAs': 'Joining as {name}.',
  'meet.lobby.name': 'Your name',
  'meet.lobby.namePlaceholder': 'e.g. Jane Smith',
  'meet.lobby.email': 'Your email',
  'meet.lobby.emailPlaceholder': 'e.g. jane@company.com',
  'meet.lobby.emailNotice': 'Your email will be used to send you the meeting minutes.',
  'meet.lobby.colleagueSignIn': 'Part of the team?',
  'meet.lobby.signIn': 'Sign in',
  'meet.lobby.micOn': 'Mic on',
  'meet.lobby.micOff': 'Mic off',
  'meet.lobby.camOn': 'Camera on',
  'meet.lobby.camOff': 'Camera off',
  'meet.lobby.opensAt': 'The link opens at {time}.',
  'meet.lobby.join': 'Join meeting',
  'meet.lobby.askToJoin': 'Ask to join',
  'meet.form.title': 'Title',
  'meet.form.titlePlaceholder': 'e.g. Construct Vest proposal',
  'meet.form.date': 'Date',
  'meet.form.time': 'Time',
  'meet.form.duration': 'Duration',
  'meet.form.colleagues': 'Colleagues',
  'meet.form.noColleagues': 'No colleagues to invite.',
  'meet.form.guests': 'Outside guests',
  'meet.form.guestsPlaceholder': 'email@client.com, Enter',
  'meet.form.guestsHint':
    'They get an email with the link and a calendar invitation. On arrival they wait to be admitted.',
  'meet.form.client': 'Client',
  'meet.form.project': 'Project',
  'meet.form.none': 'None',
  'meet.form.projectHint':
    'After the meeting the transcript lands in the meetings log; linked to a project, action items become tasks.',
  'meet.form.submit': 'Schedule and send invitations',
  'meet.form.editTitle': 'Edit meeting',
  'meet.form.liveNoReschedule':
    'The meeting is running: you can change its name and guests, but not its time.',
  'meet.form.invalidEmail': 'That email address is not valid.',
  'meet.form.hostEmail':
    'You are the host: the meeting is added to your connected Google Calendar automatically, no need to invite your own address.',
  'meet.form.titleRequired': 'Give the meeting a title.',
  'meet.form.whenRequired': 'Pick a date and time.',
  'sidebar.tasks': 'Tasks',

  // Team chat (internal Discord-like messaging)
  'teamChat.backToApp': 'Back to app',
  'teamChat.groups': 'Groups',
  'teamChat.newGroup': 'New group',
  'teamChat.noGroups': 'No groups yet.',
  'teamChat.directMessages': 'Direct messages',
  'teamChat.newDm': 'New message',
  'teamChat.noConversations': 'No conversations yet.',
  'teamChat.searchPeople': 'Search people…',
  'teamChat.noPeople': 'No people found.',
  'teamChat.groupName': 'Group name',
  'teamChat.members': 'Members',
  'teamChat.groupSettings': 'Group settings',
  'teamChat.deleteGroup': 'Delete group',
  'teamChat.confirmDeleteGroup':
    'Delete the group “{name}” and all its messages? This cannot be undone.',
  'teamChat.addReaction': 'Add reaction',
  // Hover text on a reaction pill: who reacted, and with what.
  'teamChat.reaction.nameList': '{names} and {last}',
  'teamChat.reaction.nameListMore': '{names} and {count} more',
  'teamChat.reaction.tooltipOne': '{names} reacted with {emoji}',
  'teamChat.reaction.tooltipMany': '{names} reacted with {emoji}',
  'teamChat.addEmoji': 'Add emoji',
  'teamChat.reply': 'Reply',
  'teamChat.replyingTo': 'Replying to {name}',
  'teamChat.edited': 'edited',
  'teamChat.deletedMessage': 'Message deleted',
  'teamChat.confirmDeleteMessage': 'Delete this message?',
  'teamChat.pin': 'Pin',
  'teamChat.unpin': 'Unpin',
  'teamChat.pinned': 'Pinned messages',
  'teamChat.noPins': 'No pinned messages.',
  'teamChat.jumpLoading': 'Looking for the message in the history…',
  'teamChat.jumpTooOld': 'That message is too far back to open automatically.',
  'teamChat.sent': 'Sent',
  'teamChat.seen': 'Seen',
  'teamChat.seenBy': 'Seen by',
  'teamChat.notSeenYet': 'Not seen yet',
  'teamChat.searchPlaceholder': 'Search chat…',
  'teamChat.searchInChat': 'Search in conversation',
  'teamChat.searchMessages': 'Messages',
  'teamChat.searchNoResults': 'No results.',
  'teamChat.searching': 'Searching…',
  'teamChat.searchClear': 'Clear search',
  'teamChat.attachFile': 'Attach file',
  'teamChat.send': 'Send',
  'teamChat.uploadTooLarge': '{name} exceeds 10MB.',
  'teamChat.unsupportedType': 'Unsupported file type: {name}.',
  'teamChat.emptyConversation': 'No messages yet — write the first one!',
  'teamChat.messagePlaceholder': 'Message {name}',
  'teamChat.notFound': 'This conversation doesn’t exist or you don’t have access to it.',
  'chatWidget.open': 'Team chat',
  'chatWidget.close': 'Close chat',
  'chatWidget.title': 'Team chat',
  'chatWidget.empty': 'No conversations yet.',
  'chatWidget.back': 'Back to conversations',
  'chatWidget.openFull': 'Open full chat',
  'teamChat.pickConversation': 'Pick a conversation on the left or start a new one.',
  'teamChat.online': 'Online',
  'teamChat.offline': 'Offline',
  'teamChat.showMembers': 'Show members',
  'teamChat.hideMembers': 'Hide members',
  'teamChat.allColleagues': 'All colleagues',
  'teamChat.profile.back': 'Back to members',
  'teamChat.profile.close': 'Close profile',
  'teamChat.profile.loadError': 'This profile couldn’t be loaded.',
  'teamChat.profile.inactive': 'Deactivated account',
  'teamChat.profile.role.ADMIN': 'Administrator',
  'teamChat.profile.role.MANAGER': 'Manager',
  'teamChat.profile.role.MEMBER': 'Member',
  'teamChat.profile.role.VIEWER': 'Viewer',
  'teamChat.profile.contact': 'Contact',
  'teamChat.profile.phone': 'Phone',
  'teamChat.profile.location': 'Location',
  'teamChat.profile.department': 'Department',
  'teamChat.profile.memberSince': 'Member since',
  'teamChat.profile.lastSeen': 'Last seen',
  'teamChat.profile.lastSeenNever': 'Never',
  'teamChat.profile.projects': 'Projects',
  'teamChat.profile.noProjects': 'No projects with open tasks.',
  'teamChat.profile.projectTasks': '{count} tasks',
  'teamChat.profile.activeTasks': 'Active tasks',
  'teamChat.profile.noTasks': 'No open tasks.',
  'teamChat.profile.inProgress': '{count} in progress',
  'teamChat.profile.overdue': '{count} overdue',
  'teamChat.profile.andMore': 'and {count} more',
  'teamChat.profile.viewFull': 'View full profile',
  'teamChat.voiceChannel': 'Voice',
  'teamChat.voiceJoin': 'Join the voice channel',
  'teamChat.voiceTestMode': 'Voice is disabled in Test Mode.',
  'teamChat.voiceConnecting': 'Connecting…',
  'teamChat.voiceConnected': 'Voice connected',
  'teamChat.voiceReconnecting': 'Reconnecting…',
  'teamChat.voiceLive': 'Someone is sharing their screen',
  'teamChat.voiceMobileHint': 'Keep the app open — locking the screen ends the call.',
  'teamChat.voiceYou': 'You',
  'teamChat.voiceMute': 'Mute',
  'teamChat.voiceUnmute': 'Unmute',
  'teamChat.voiceLeave': 'Leave',
  'teamChat.soundboard': 'Sounds',
  'teamChat.soundboard.bullshit': 'Bullshit',
  'teamChat.soundboard.rimshot': 'Drums',
  'teamChat.soundboard.quack': 'Duck',
  'teamChat.soundboard.airhorn': 'Air horn',
  'teamChat.soundboard.applause': 'Applause',
  'teamChat.soundboard.crickets': 'Crickets',
  'teamChat.voiceShare': 'Share screen',
  'teamChat.voiceStopSharing': 'Stop sharing',
  'teamChat.voiceSharingBy': '{name} is sharing their screen',
  'teamChat.voiceSharingYours': 'You are sharing your screen.',
  'teamChat.voiceErrorMicDenied':
    'The microphone is blocked. Allow access in your browser settings.',
  'teamChat.voiceErrorUnavailable':
    'Voice channels are not configured. An administrator can set them up in Settings → Integrations → LiveKit.',
  'teamChat.voiceErrorGeneric': 'Could not connect to the voice channel.',
  'teamChat.voiceChannels': 'Voice channels',
  'teamChat.textChannels': 'Text channels',
  'teamChat.newVoiceChannel': 'New voice channel',
  'teamChat.voiceChannelName': 'Channel name',
  'teamChat.noVoiceChannels': 'No voice channels yet.',
  'teamChat.voiceChannelSettings': 'Voice channel settings',
  'teamChat.deleteVoiceChannel': 'Delete channel',
  'teamChat.confirmDeleteVoiceChannel':
    'Delete the voice channel “{name}”? Everyone inside will be disconnected.',
  'teamChat.voiceChannelNotFound': 'This voice channel doesn’t exist or was deleted.',
  'teamChat.voiceInVoice': 'In voice',
  'teamChat.voiceOpenChannel': 'Open the channel',
  'teamChat.voiceMuted': 'Muted',
  'teamChat.voiceLiveBadge': 'LIVE',
  'teamChat.voiceWatch': 'Watch screen',
  'teamChat.voiceWatchOf': 'Watch {name}’s screen',
  'teamChat.voiceParticipants': '{count} in channel',
  'teamChat.voiceEmptyChannel': 'Nobody in the channel yet. Be the first to join.',
  'teamChat.voiceJoinToWatch': 'Join the channel to watch the shared screen.',
  'teamChat.voiceNoShares': 'Nobody is sharing their screen right now.',
  'teamChat.voiceSharedScreens': 'Shared screens',
  'teamChat.voiceMembers': 'In channel',
  // Automatic call transcripts and their history.
  'teamChat.voiceTranscript': 'Transcript',
  'teamChat.voiceTranscriptLive': 'Live transcript',
  'teamChat.voiceTranscriptShow': 'Show transcript',
  'teamChat.voiceTranscriptHide': 'Hide transcript',
  'teamChat.voiceTranscriptCount': '{count} lines',
  'teamChat.voiceTranscriptOn': 'Transcript on',
  'teamChat.voiceTranscriptHint':
    'Calls are transcribed automatically. Every line is labelled with who said it.',
  'teamChat.voiceTranscriptOff': 'Call transcription is not configured on the server.',
  'teamChat.voiceTranscriptEmpty':
    'Nothing transcribed yet. Lines appear a few seconds after someone speaks.',
  'teamChat.voiceTranscriptBudget':
    'Your transcription stopped: the monthly AI budget is used up. The call goes on.',
  'teamChat.voiceTranscriptUnsupported':
    'Your browser cannot record the microphone for the transcript. The call goes on.',
  'teamChat.voiceTranscriptStopped': 'Your transcription stopped. The call goes on.',
  'teamChat.voiceTranscriptUpstream':
    'Transcription is not working: the transcription provider refused the requests (no credit or invalid key). The admins have been alerted. The call goes on.',
  'teamChat.voiceCallHistory': 'Call history',
  'teamChat.voiceRecentCalls': 'Recent calls',
  'teamChat.voiceCallAllCalls': 'All calls',
  'teamChat.voiceNoCalls': 'No calls yet.',
  'teamChat.voiceCallNotFound': 'This call does not exist or was deleted.',
  'teamChat.voiceCallBack': 'Back to history',
  'teamChat.voiceCallParticipants': 'Participants',
  'teamChat.voiceCallSegments': '{count} lines',
  'teamChat.voiceCallNoTranscript': 'No transcript',
  'teamChat.voiceCallPendingSuggestions': '{count} task suggestions',
  'teamChat.suggestions.hint':
    'After every call the AI reads the transcript and proposes tasks — on a project, on a person, with a deadline. Nothing is created on its own: you choose what goes in.',
  'teamChat.suggestions.empty':
    'No tasks proposed from this call. You can ask for another reading any time.',
  'teamChat.suggestions.needTranscript':
    'This call has no transcript, so there is nothing to read for suggestions.',
  'teamChat.suggestions.live': 'The call is still going on. Suggestions appear once it ends.',
  // Minutes of a voice-room call — the same document the meetings page writes.
  'teamChat.minutes.title': 'Call minutes',
  'teamChat.minutes.hint':
    'On request, Firmio writes the minutes of the call: what was discussed, what was decided, who took what on. Read them here or download the Word document.',
  'teamChat.minutes.generate': 'Generate minutes',
  'teamChat.minutes.regenerate': 'Generate again',
  'teamChat.minutes.generating': 'Writing the minutes…',
  'teamChat.minutes.download': 'Download .docx',
  'teamChat.minutes.sendEmail': 'Send to participants',
  'teamChat.minutes.sending': 'Sending…',
  'teamChat.minutes.emailedTo': 'Emailed to',
  'teamChat.minutes.empty': 'No minutes for this call yet.',
  'teamChat.minutes.live': 'The call is still going on. The minutes are written once it ends.',
  'teamChat.minutes.needsTranscript': 'This call has no transcript, so there is nothing to read.',
  'teamChat.minutes.generatedAt': 'Generated',
  'teamChat.minutes.cost': 'Cost',
  'teamChat.minutes.tokens': 'tokens',
  'teamChat.minutes.history': 'Previous versions',
  'teamChat.minutes.failed': 'Generating the minutes failed.',
  'teamChat.minutes.keyFacts': 'Key facts',
  'teamChat.minutes.decisions': 'Decisions',
  'teamChat.minutes.actions': 'Actions',
  'teamChat.minutes.openQuestions': 'Open questions',
  'teamChat.minutes.nextSteps': 'Next steps',
  'teamChat.minutes.noDueDate': 'no deadline',
  'teamChat.voiceCallCopy': 'Copy transcript',
  'teamChat.voiceCallCopied': 'Copied',
  'teamChat.voiceCallDelete': 'Delete call',
  'teamChat.voiceCallConfirmDelete':
    'Delete the call from {date} and its transcript? This cannot be undone.',
  'teamChat.voiceDurationH': '{h} h {m} min',
  'teamChat.voiceDurationM': '{m} min',
  'teamChat.voiceDurationS': '{s} s',
  'teamChat.voiceFullscreen': 'Full screen',
  'teamChat.voiceExitFullscreen': 'Exit full screen',
  'teamChat.selectAll': 'Select everyone',
  'teamChat.selectNone': 'Clear selection',
  'sidebar.personalTasks': 'Personal Tasks',
  'personalTasks.title': 'Personal Tasks',
  'personalTasks.error': 'Could not load personal tasks.',
  'personalTasks.transfer.menuHeader': 'Send to…',
  'personalTasks.transfer.confirm':
    'Send this task to {name}? It will move into their personal list and disappear from yours.',
  'personalTasks.transfer.success': 'Task sent to {name}.',
  'personalTasks.transfer.failed': 'The task could not be sent.',
  'sidebar.reminders': 'Reminders',

  // Leave (concedii)
  'sidebar.leave': 'Leave',
  'leave.title': 'Leave',
  'leave.subtitle': 'Your leave requests and the days you have left.',
  'leave.tab.mine': 'My requests',
  'leave.tab.team': 'All requests',
  'leave.tab.report': 'Report',
  'leave.new': 'New request',
  'leave.edit': 'Edit request',
  'leave.empty': 'No leave requests yet. Add one above.',
  'leave.emptyFiltered': 'No request matches these filters.',
  'leave.emptyTeam': 'No requests from the team.',
  'leave.error': 'Could not load the leave requests.',
  'leave.noAccess': 'You do not have access to the team’s leave.',
  'leave.loading': 'Loading…',

  'leave.type.ANNUAL': 'Annual leave',
  'leave.type.SICK': 'Sick leave',
  'leave.type.UNPAID': 'Unpaid leave',
  'leave.type.SPECIAL_EVENT': 'Special event',
  'leave.type.SHORT_ABSENCE': 'Time off (hours)',
  'leave.status.PENDING': 'Pending',
  'leave.status.APPROVED': 'Approved',
  'leave.status.REJECTED': 'Rejected',
  'leave.status.CANCELLED': 'Withdrawn',

  'leave.field.type': 'Leave type',
  'leave.field.start': 'From',
  'leave.field.end': 'To',
  'leave.field.day': 'Day',
  'leave.field.hours': 'Hours',
  'leave.field.reason': 'Reason (optional)',
  'leave.field.reasonPlaceholder': 'A short note, if it helps the approval.',
  'leave.field.person': 'For whom',
  'leave.field.note': 'Message (optional)',
  'leave.save': 'Send request',
  'leave.saveEdit': 'Save',
  'leave.cancel': 'Cancel',
  'leave.cost': '{count} working days',
  'leave.costOne': '1 working day',
  'leave.costHours': '{count} hours',
  'leave.costNone': 'This range contains no working day.',
  'leave.costHint': 'Weekends and public holidays are not deducted.',
  'leave.lengthDay': '1 day',
  'leave.lengthDays': '{count} days',
  'leave.lengthHours': '{count} hours',

  'leave.approve': 'Approve',
  'leave.reject': 'Reject',
  'leave.withdraw': 'Withdraw request',
  'leave.delete': 'Delete',
  'leave.confirmWithdraw': 'Withdraw this leave request?',
  'leave.confirmDelete': 'Delete this request for good? It cannot be recovered.',
  'leave.rejectPrompt': 'Why are you rejecting it? (optional)',
  'leave.decidedBy': 'Decided by {name}',

  'leave.balance.title': 'Annual leave {year}',
  'leave.balance.entitled': 'Entitled',
  'leave.balance.used': 'Taken',
  'leave.balance.pending': 'Pending',
  'leave.balance.remaining': 'Remaining',
  'leave.balance.default': 'Default allowance; change it on the person’s page.',
  'leave.balance.hint': 'Only annual leave draws down the balance.',
  'leave.entitlement': 'Leave days per year',
  'leave.entitlementSave': 'Save days',
  'leave.entitlementReset': 'Back to default',

  'leave.column.person': 'Person',
  'leave.column.type': 'Type',
  'leave.column.period': 'Period',
  'leave.column.length': 'Length',
  'leave.column.status': 'Status',
  'leave.column.filed': 'Filed',
  'leave.column.decision': 'Decision',
  'leave.filter.status': 'Status',
  'leave.filter.type': 'Type',
  'leave.filter.person': 'Person',
  'leave.filter.all': 'All',

  'leave.report.title': 'Leave report {year}',
  'leave.report.export': 'Download CSV',
  'leave.report.empty': 'No employee with a login account.',

  'leave.calendarChip': '{name} — {type}',
  'leave.onLeaveWarning': '{name} is on leave from {range}.',

  'reminders.title': 'Reminders',
  'reminders.add': 'Add a reminder',
  'reminders.titlePlaceholder': 'Title',
  'reminders.descriptionPlaceholder': 'Description (optional)',
  'reminders.estimatePlaceholder': 'Estimate (min)',
  'reminders.save': 'Save',
  'reminders.empty': 'No reminders. Add one above.',
  'reminders.completed': 'Completed ({count})',
  'reminders.edit': 'Edit',
  'reminders.delete': 'Delete',
  'reminders.markDone': 'Mark done',
  'reminders.markOpen': 'Reopen',
  'reminders.done': 'Done',
  'reminders.snooze': 'Snooze',
  'reminders.snooze10m': '+10 min',
  'reminders.snooze1h': '+1 hour',
  'reminders.snoozeTomorrow': 'Tomorrow at 09:00',
  'reminders.dismiss': 'Dismiss notification',
  'reminders.today': 'Today',
  'reminders.tomorrow': 'Tomorrow',
  'reminders.error': 'Could not load reminders.',
  // Meetings module
  'sidebar.meetings': 'Meetings',
  'meetings.title': 'Meetings',
  'meetings.subtitle': 'The team’s meetings with clients, partners and other external people',
  'meetings.new': 'New meeting',
  'meetings.tabMeetings': 'Meetings',
  'meetings.tabContacts': 'People',
  'meetings.search': 'Search by title, person, company…',
  'meetings.filterClient': 'Client',
  'meetings.filterProject': 'Project',
  'meetings.filterOwner': 'Responsible',
  'meetings.filterPeriod': 'Period',
  'meetings.filterFrom': 'From',
  'meetings.filterTo': 'To',
  'meetings.allClients': 'All clients',
  'meetings.allProjects': 'All projects',
  'meetings.allOwners': 'Everyone',
  'meetings.filteredByContact': 'Showing only meetings with the selected person.',
  'meetings.colDate': 'Date',
  'meetings.colDuration': 'Duration',
  'meetings.colTitle': 'Meeting',
  'meetings.colAttendees': 'Attendees',
  'meetings.colContext': 'Client / Project',
  'meetings.colOwner': 'Responsible',
  'meetings.colNotes': 'Notes',
  'meetings.empty': 'No meetings logged yet. Add the first one.',
  'meetings.emptyFiltered': 'No meetings match the current filters.',
  'meetings.error': 'Could not load meetings.',
  'meetings.noNotes': 'no notes',
  'meetings.notFound': 'Meeting not found.',
  'meetings.access.title': 'You don’t have access to this meeting',
  'meetings.access.body':
    'Only the person responsible and the colleagues on it can see it. You can ask for access and they can add you in one click.',
  'meetings.access.request': 'Request access',
  'meetings.access.sending': 'Sending…',
  'meetings.access.sent': 'Request sent to {owner}. You’ll see the meeting once they add you.',
  'meetings.access.failed': 'That didn’t work. Try again.',
  'meetings.access.banner': '{name} is asking for access to this meeting.',
  'meetings.access.added': '{name} now has access and can open the meeting.',
  'meetings.share.title': 'Has access',
  'meetings.share.hint':
    'Colleagues who can open this meeting without having been in it. Anyone whose role sees every meeting does not need adding.',
  'meetings.share.empty': 'Nobody besides the participants.',
  'meetings.share.view': 'Can view',
  'meetings.share.edit': 'Can edit',
  'meetings.share.colleague': 'Colleague',
  'meetings.share.pick': 'Pick a colleague',
  'meetings.share.level': 'Access level',
  'meetings.share.levelFor': 'Access level for {name}',
  'meetings.share.add': 'Give access',
  'meetings.share.remove': 'Remove access for {name}',
  'meetings.back': 'Meetings',
  'meetings.confirmDelete': 'Delete this meeting together with its notes and transcripts?',
  'meetings.details': 'Details',
  'meetings.responsible': 'Responsible',
  'meetings.team': 'Colleagues present',
  'meetings.client': 'Client',
  'meetings.project': 'Project',
  'meetings.lead': 'Lead',
  'meetings.filteredByLead': 'Only the meetings on this lead.',
  'meetings.createdBy': 'Added by',
  'meetings.externalAttendees': 'External attendees',
  'meetings.noAttendees': 'No external people added.',
  'meetings.manualNotes': 'Notes',
  'meetings.transcripts': 'Transcript & AI notes',
  'meetings.transcriptsHint':
    'Paste the transcript from your recording device or upload the audio/PDF file. Firmio summarises it, extracts decisions and action items and indexes it in the knowledge base, so you can ask what was discussed at any time.',
  'meetings.notesForbidden': 'You do not have access to meeting notes and transcripts.',
  'leads.suggestions.title': 'Action suggestions',
  'leads.suggestions.hint':
    'Actions proposed by Firmio AI (from conversations or notes) wait here. Nothing is created on its own — you review each suggestion, adjust the type, owner and deadline, and add it to the actions or dismiss it.',
  'leads.suggestions.generate': 'Generate suggestions',
  'leads.suggestions.regenerate': 'Regenerate',
  'leads.suggestions.generating': 'Generating…',
  'leads.suggestions.generateFailed': 'Generating suggestions failed.',
  'leads.suggestions.empty':
    'No suggestions yet. Press “Generate suggestions” or ask Firmio AI to propose actions.',
  'leads.suggestions.allDecided': 'Every suggestion has been decided.',
  'leads.suggestions.accept': 'Add to actions',
  'leads.suggestions.accepting': 'Adding…',
  'leads.suggestions.acceptFailed': 'Adding the action failed.',
  'leads.suggestions.dismiss': 'Dismiss',
  'leads.suggestions.dismissFailed': 'Dismissing failed.',
  'leads.suggestions.restore': 'Restore',
  'leads.suggestions.accepted': 'Added ({count})',
  'leads.suggestions.dismissed': 'Dismissed ({count})',
  'leads.suggestions.decidedBy': 'decided by {name}',
  'leads.suggestions.assigneeHint': 'AI proposed owner: {name} (not on the team)',
  'leads.suggestions.field.title': 'Title',
  'leads.suggestions.field.type': 'Type',
  'leads.suggestions.field.assignee': 'Owner',
  'leads.suggestions.field.dueDate': 'Due date',
  'leads.suggestions.field.description': 'Details',
  'leads.suggestions.noAssignee': 'No owner',
  'leads.suggestions.addDescription': 'add details',
  'meetings.minutes.title': 'Meeting minutes',
  'meetings.minutes.hint':
    'Firmio writes the minutes from the transcript: summary, what was discussed, key facts, decisions, actions with owners and deadlines, open questions. Download them as a Word document, ready to send to the participants.',
  'meetings.minutes.generate': 'Generate minutes',
  'meetings.minutes.regenerate': 'Generate again',
  'meetings.minutes.generating': 'Writing the minutes…',
  'meetings.minutes.download': 'Download .docx',
  'meetings.minutes.openInDrive': 'Open in Drive',
  'meetings.minutes.saveToDrive': 'Save to Drive',
  'meetings.minutes.sendEmail': 'Send by email',
  'meetings.minutes.sending': 'Sending…',
  'meetings.minutes.emailedTo': 'Emailed to',
  'meetings.minutes.empty': 'No minutes for this meeting yet.',
  'meetings.minutes.needsTranscript': 'Add the meeting transcript first.',
  'meetings.minutes.generatedAt': 'Generated',
  'meetings.minutes.cost': 'Cost',
  'meetings.minutes.tokens': 'tokens',
  'meetings.minutes.history': 'Previous versions',
  'meetings.minutes.failed': 'Generating the minutes failed.',
  'meetings.suggestions.title': 'Task suggestions',
  'meetings.suggestions.hint':
    'Once the transcript is analysed, Firmio proposes the tasks that follow from the discussion: which project, who owns it, how urgent, by when. Nothing is created on its own — you review each suggestion and add it to the project or dismiss it.',
  'meetings.suggestions.generate': 'Generate suggestions',
  'meetings.suggestions.regenerate': 'Regenerate',
  'meetings.suggestions.generating': 'Analysing…',
  'meetings.suggestions.generateFailed': 'The suggestions could not be generated. Try again.',
  'meetings.suggestions.processing':
    'The transcript is being analysed. Suggestions show up here as soon as processing ends.',
  'meetings.suggestions.needTranscript':
    'Add the meeting transcript first (above) so there is something to propose tasks from.',
  'meetings.suggestions.empty':
    'No suggestions yet. Press “Generate suggestions” to read the transcript.',
  'meetings.suggestions.allDecided':
    'Every suggestion has been decided. You can regenerate any time.',
  'meetings.suggestions.accepted': 'Added to the platform ({count})',
  'meetings.suggestions.dismissed': 'Dismissed ({count})',
  'meetings.suggestions.decidedBy': 'by {name}',
  'meetings.suggestions.assigneeHint': 'AI mentioned: {name} (not a team member here)',
  'meetings.suggestions.projectHint': 'Project mentioned: {name} (not found)',
  'meetings.suggestions.field.title': 'Task title',
  'meetings.suggestions.field.project': 'Project',
  'meetings.suggestions.field.assignee': 'Responsible',
  'meetings.suggestions.field.priority': 'Priority',
  'meetings.suggestions.field.dueDate': 'Due date',
  'meetings.suggestions.field.estimate': 'Estimate (hours)',
  'meetings.suggestions.estimatePlaceholder': 'e.g. 2 or 1.5',
  'meetings.suggestions.field.description': 'Details',
  'meetings.suggestions.addDescription': 'Add details',
  'meetings.suggestions.pickProject': 'Pick a project',
  'meetings.suggestions.noAssignee': 'No responsible',
  'meetings.suggestions.accept': 'Add to project',
  'meetings.suggestions.accepting': 'Adding…',
  'meetings.suggestions.acceptedNotice': 'Task {task} was added to {project}.',
  'meetings.suggestions.openTask': 'Open task',
  'meetings.suggestions.acceptFailed': 'The task could not be created.',
  'meetings.suggestions.dismiss': 'Dismiss',
  'meetings.suggestions.dismissFailed': 'The suggestion could not be dismissed.',
  'meetings.suggestions.restore': 'Restore',
  'meetings.form.createTitle': 'New meeting',
  'meetings.form.editTitle': 'Edit meeting',
  'meetings.form.title': 'Title *',
  'meetings.form.date': 'Date *',
  'meetings.form.time': 'Time *',
  'meetings.form.endTime': 'End time',
  'meetings.form.type': 'Meeting type',
  'meetings.form.location': 'Location',
  'meetings.form.locationPlaceholder': 'e.g. client office, Google Meet',
  'meetings.form.owner': 'Responsible',
  'meetings.form.team': 'Other colleagues present',
  'meetings.form.teamNone': 'No colleagues',
  'meetings.form.teamSummary': '{count} colleagues',
  'meetings.form.client': 'Client',
  'meetings.form.noClient': 'No client',
  'meetings.form.project': 'Project',
  'meetings.form.noProject': 'No project',
  'meetings.form.lead': 'Lead',
  'meetings.form.noLead': 'No lead',
  'leads.meetings.title': 'Meetings',
  'leads.meetings.hint':
    'Logged meetings linked to this lead; each one carries its transcript, minutes and proposed tasks.',
  'leads.meetings.empty': 'No meeting linked to this lead yet.',
  'leads.meetings.add': 'Add meeting',
  'leads.meetings.viewAll': 'Open in Meetings',
  'leads.meetings.error': 'Could not load the meetings.',
  'meetings.form.notes': 'Notes (markdown)',
  'meetings.form.transcript': 'Meeting transcript',
  'meetings.form.transcriptPlaceholder': 'Paste the transcript from your recording device here…',
  'meetings.form.transcriptHint':
    'Optional. It is processed by AI (summary, decisions, action items) and indexed in the knowledge base. You can also add it later from the meeting page.',
  'meetings.form.attendees': 'External people',
  'meetings.form.titleRequired': 'The title is required.',
  'meetings.form.dateRequired': 'Date and time are required.',
  'meetings.attendees.search': 'Search an existing person…',
  'meetings.attendees.new': 'New person',
  'meetings.attendees.newBadge': 'new',
  'meetings.attendees.createFromQuery': 'Add “{name}” as a new person',
  'meetings.attendees.firstName': 'First name *',
  'meetings.attendees.lastName': 'Last name',
  'meetings.attendees.email': 'Email',
  'meetings.attendees.phone': 'Phone',
  'meetings.attendees.company': 'Company',
  'meetings.attendees.role': 'Role',
  'meetings.attendees.client': 'Linked client',
  'meetings.attendees.noClient': 'No client',
  'meetings.attendees.add': 'Add',
  'meetings.attendees.noResults': 'No people found',
  'meetings.attendees.firstNameRequired': 'The first name is required.',
  'meetings.contacts.new': 'New person',
  'meetings.contacts.search': 'Search by name, company, email…',
  'meetings.contacts.colName': 'Name',
  'meetings.contacts.colCompany': 'Company',
  'meetings.contacts.colClient': 'Client',
  'meetings.contacts.colContact': 'Contact',
  'meetings.contacts.colMeetings': 'Meetings',
  'meetings.contacts.empty': 'No people recorded yet.',
  'meetings.contacts.error': 'Could not load people.',
  'meetings.contacts.confirmDelete':
    'Delete this person? They will be removed from the meetings they attended.',
  'meetings.contacts.editTitle': 'Edit person',
  'meetings.contacts.createTitle': 'New person',
  'meetings.contacts.notes': 'Notes',
  'common.remove': 'Remove',
  'common.pageOf': 'Page {page} of {total}',
  'common.company': 'Company',
  'common.defaultCompany': 'Default company',
  'common.clearFilter': 'Clear filter',
  'sidebar.leads': 'Pipeline',
  'sidebar.projects': 'Projects',
  'sidebar.allProjects': 'All projects',
  'sidebar.clients': 'Clients',
  'sidebar.allClients': 'All clients',
  'sidebar.noClients': 'No clients yet',
  'sidebar.addClient': 'Add client',
  'sidebar.suppliers': 'Suppliers',
  'sidebar.allSuppliers': 'All suppliers',
  'sidebar.noSuppliers': 'No suppliers yet',
  'sidebar.addSupplier': 'Add supplier',
  'sidebar.subscriptions': 'Subscriptions',
  'sidebar.allSubscriptions': 'All subscriptions',
  'sidebar.noSubscriptions': 'No subscriptions',
  'sidebar.financials': 'Financials',
  'sidebar.financialsOverview': 'Overview (P&L)',
  'sidebar.invoices': 'Invoices',
  'sidebar.smartbill': 'SmartBill',
  'sidebar.spv': 'SPV e-Factura',
  'sidebar.issuedInvoices': 'Issued invoices',
  'sidebar.bankStatements': 'Bank statements',
  'sidebar.reconciliation': 'Bank reconciliation',
  'sidebar.cashflow': 'Cash flow',
  'sidebar.monthlyReports': 'Monthly reports',
  'sidebar.people': 'Team',
  'sidebar.allMembers': 'All members',
  'sidebar.addMember': 'Add member',
  'sidebar.settings': 'Settings',
  'sidebar.bugReports': 'Reported bugs',
  'tasks.share.copyLink': 'Copy task link',
  'tasks.share.copied': 'Link copied.',
  'tasks.share.copyFailed': 'The link could not be copied.',
  'sidebar.testMode': 'Test Mode',
  'sidebar.language': 'Language',
  'sidebar.profile': 'Profile',
  'sidebar.logout': 'Log out',
  'sidebar.closeMenu': 'Close menu',
  'sidebar.resize': 'Resize sidebar',

  // Profile page (CLE-434)
  'profile.title': 'Profile',
  'profile.subtitle': 'Your account details, preferred language and push notifications.',
  'profile.firstName': 'First name',
  'profile.lastName': 'Last name',
  'profile.email': 'Email',
  'profile.language': 'Language',
  'profile.languageHint': 'Choose the interface language.',
  'profile.notProvided': '—',
  'profile.connectedApps.title': 'Connected apps',
  'profile.connectedApps.subtitle':
    'MCP clients (Claude Desktop, claude.ai, Claude Code) working in Firmio on your behalf, with your account permissions.',
  'profile.connectedApps.howTo': 'Add a custom connector in Claude with this address:',
  'profile.connectedApps.empty': 'No connected apps.',
  'profile.connectedApps.loadFailed': 'The list could not be loaded.',
  'profile.connectedApps.revokeFailed': 'Disconnecting failed.',
  'profile.connectedApps.unknownClient': 'Unknown app',
  'profile.connectedApps.connectedAt': 'Connected',
  'profile.connectedApps.lastUsed': 'last used',
  'profile.connectedApps.disconnect': 'Disconnect',
  'oauth.consent.title': 'Access to Firmio',
  'oauth.consent.missing':
    'The authorization request is missing. Start the connection again from the Claude app.',
  'oauth.consent.failed':
    'The authorization could not be processed. Try again from the Claude app.',
  'oauth.consent.unknownClient': 'An application',
  'oauth.consent.asks': 'wants to use Firmio AI on your behalf.',
  'oauth.consent.account': 'Account:',
  'oauth.consent.scopeTools':
    'It can read and change data in Firmio through the same tools as Firmio AI (tasks, projects, leads, finance, etc.).',
  'oauth.consent.scopeSame': 'It gets exactly your account permissions, nothing more.',
  'oauth.consent.scopeRevoke': 'You can revoke access any time from Profile → Connected apps.',
  'oauth.consent.approve': 'Allow access',
  'oauth.consent.deny': 'Deny',
  'oauth.consent.redirecting': 'Returning to the app…',
  'oauth.consent.footer':
    'Conversations stay in the Claude app; Firmio only runs the requested actions.',
  'profile.password.title': 'Change password',
  'profile.password.subtitle': 'Update your account password.',
  'profile.password.current': 'Old password',
  'profile.password.new': 'New password',
  'profile.password.confirm': 'Confirm password',
  'profile.password.show': 'Show password',
  'profile.password.hide': 'Hide password',
  'profile.password.submit': 'Save password',
  'profile.password.submitting': 'Saving…',
  'profile.password.success': 'Your password has been changed.',
  'profile.password.mismatch': 'Passwords do not match.',
  'profile.password.weak':
    'Password must be at least 8 characters with 1 uppercase letter and 1 number.',
  'profile.password.wrongCurrent': 'The old password is incorrect.',
  'profile.password.failed': 'Could not change the password. Please try again.',
  'profile.deleteAccount.title': 'Delete account',
  'profile.deleteAccount.subtitle': 'Ask for your Firmio account to be deleted.',
  'profile.deleteAccount.explain':
    'Your account is deactivated right away: you can no longer sign in and notifications stop. Your company admins get the request and finish deleting the account data.',
  'profile.deleteAccount.details': 'What is deleted and what stays',
  'profile.deleteAccount.password': 'Your password, to confirm',
  'profile.deleteAccount.submit': 'Request account deletion',
  'profile.deleteAccount.submitting': 'Sending…',
  'profile.deleteAccount.confirmTitle': 'Delete your account?',
  'profile.deleteAccount.confirmMessage':
    'The account is deactivated now and you will be signed out. Only a company admin can reactivate it.',
  'profile.deleteAccount.confirmLabel': 'Yes, request deletion',
  'profile.deleteAccount.wrongPassword': 'The password is not correct.',
  'profile.deleteAccount.lastAdmin':
    'You are the only active admin. Make someone else an admin before asking for your account to be deleted.',
  'profile.deleteAccount.failed': 'The request could not be sent. Please try again.',

  // Multi-company: global firm switcher + Financials multi-select
  'company.switcher.label': 'Firm',
  'company.switcher.all': 'All firms',
  'financials.companyFilter.label': 'Firms',
  'financials.companyFilter.all': 'All firms',
  'financials.companyFilter.selected': '{count} firms',
  'financials.yearsSelected': '{count} years',
  'financials.monthsSelected': '{count} months',

  // Dashboard
  'dashboard.title': 'Command Center',
  'dashboard.subtitle': 'Your business at a glance — urgent items, money and team.',
  'dashboard.revenue': 'Revenue',
  'dashboard.expenses': 'Expenses',
  'dashboard.profit': 'Profit',
  'dashboard.margin': 'Margin',
  'dashboard.activeProjects': 'Active Projects',
  'dashboard.teamTasks': 'Team Tasks',
  'dashboard.upcomingPayments': 'Upcoming Payments',
  'dashboard.revenueVsExpenses': 'Revenue vs Expenses',
  'dashboard.error.kpis': 'Failed to load indicators.',
  'dashboard.error.charts': 'Failed to load charts.',
  'dashboard.error.today': 'Failed to load the “Today” panel.',
  'dashboard.error.upcoming': 'Failed to load upcoming payments.',
  'dashboard.error.team': 'Failed to load team allocation.',
  'dashboard.error.teamTasks': 'Failed to load team tasks.',
  'dashboard.error.projects': 'Failed to load projects.',
  'dashboard.error.activity': 'Failed to load activity.',
  'dashboard.error.overdueInvoices': 'Failed to load overdue invoices.',

  // Dashboard — configurable layout (edit mode + widget catalog)
  'dashboard.edit.enter': 'Edit dashboard',
  'dashboard.edit.widgets': 'Widgets',
  'dashboard.edit.save': 'Save',
  'dashboard.edit.cancel': 'Cancel',
  'dashboard.edit.reset': 'Reset to default',
  'dashboard.edit.resetShort': 'Reset',
  'dashboard.edit.enterShort': 'Edit',
  'dashboard.edit.resetConfirm':
    'Reset the dashboard to the default layout? Your saved layout will be deleted.',
  'dashboard.edit.addWidget': 'Add widget',
  'dashboard.edit.remove': 'Remove widget',
  'dashboard.edit.moveUp': 'Move up',
  'dashboard.edit.moveDown': 'Move down',
  'dashboard.edit.width': 'Change width',
  'dashboard.edit.dragHint': 'Drag to rearrange',
  'dashboard.edit.empty': 'No widgets on the dashboard. Add one from the catalog.',
  'dashboard.edit.saveError': 'Saving the layout failed. Please try again.',
  'dashboard.catalog.title': 'Add widgets',
  'dashboard.catalog.add': 'Add',
  'dashboard.catalog.remove': 'Remove',
  'dashboard.catalog.empty': 'No widgets available.',
  'dashboard.category.financial': 'Financial',
  'dashboard.category.team': 'Team',
  'dashboard.category.projects': 'Projects & Tasks',
  'dashboard.category.activity': 'Today & Activity',
  'dashboard.widget.kpi.revenue.name': 'Revenue this month',
  'dashboard.widget.kpi.revenue.desc': 'KPI card: current-month revenue with trend and sparkline.',
  'dashboard.widget.kpi.expenses.name': 'Expenses this month',
  'dashboard.widget.kpi.expenses.desc':
    'KPI card: current-month expenses with trend and sparkline.',
  'dashboard.widget.kpi.profit.name': 'Net profit this month',
  'dashboard.widget.kpi.profit.desc': 'KPI card: current-month net profit and margin.',
  'dashboard.widget.kpi.receivables.name': 'Unpaid receivables',
  'dashboard.widget.kpi.receivables.desc': 'KPI card: total issued invoices not yet collected.',
  'dashboard.widget.kpi.payables.name': 'Payments due',
  'dashboard.widget.kpi.payables.desc': 'KPI card: supplier invoices due for payment.',
  'dashboard.widget.kpi.urgentTasks.name': 'Urgent tasks',
  'dashboard.widget.kpi.urgentTasks.desc': 'KPI card: overdue and critical tasks.',
  'dashboard.widget.chart.revenueDonut.name': 'Revenue by project',
  'dashboard.widget.chart.revenueDonut.desc':
    'Donut chart of current-year revenue split by project.',
  'dashboard.widget.chart.incomeExpense.name': 'Income vs expenses',
  'dashboard.widget.chart.incomeExpense.desc':
    'Bar chart with the last 6 months of income and expenses.',
  'dashboard.widget.payments.upcoming.name': 'Upcoming payments',
  'dashboard.widget.payments.upcoming.desc':
    'Due dates over the next 14 days, chronological, with totals.',
  'dashboard.widget.invoices.overdue.name': 'Overdue invoices',
  'dashboard.widget.invoices.overdue.desc':
    'SmartBill invoices past due date, grouped by client, with email reminders.',
  'dashboard.widget.today.panel.name': '"Today" panel',
  'dashboard.widget.today.panel.desc': 'Today’s invoices and the day’s urgent tasks.',
  'dashboard.widget.team.allocation.name': 'Team allocation',
  'dashboard.widget.team.allocation.desc':
    'Per-person utilization, active projects and load status.',
  'dashboard.widget.team.tasks.name': 'Team tasks',
  'dashboard.widget.team.tasks.desc': 'Real tasks grouped per person.',
  'dashboard.widget.projects.status.name': 'Project status',
  'dashboard.widget.projects.status.desc': 'Cards with active projects, health and progress.',
  'dashboard.widget.activity.feed.name': 'Recent activity',
  'dashboard.widget.activity.feed.desc': 'Latest events across projects, invoices and tasks.',

  // Dashboard — overdue invoices widget + reminder modal
  'dashboard.overdue.title': 'Overdue invoices',
  'dashboard.overdue.empty': 'No invoices past due date.',
  'dashboard.overdue.invoiceCount': '{count} invoices',
  'dashboard.overdue.invoiceCountOne': '1 invoice',
  'dashboard.overdue.daysOverdue': '{days} days overdue',
  'dashboard.overdue.daysOverdueShort': '{days} days',
  'dashboard.overdue.sendReminderShort': 'Reminder',
  'dashboard.overdue.lastReminder': 'Last reminder: {when}',
  'dashboard.overdue.noReminder': 'No reminder sent',
  'dashboard.overdue.sendReminder': 'Send reminder',
  'dashboard.overdue.unmatchedClient': 'unmatched client',
  'reminder.modal.title': 'Invoice reminder — {client}',
  'reminder.modal.loading': 'Preparing the email…',
  'reminder.modal.error': 'An error occurred. Please try again.',
  'reminder.modal.summary': '{count} overdue invoices, total amount due {total}.',
  'reminder.modal.to': 'Recipient',
  'reminder.modal.subject': 'Subject',
  'reminder.modal.body': 'Email body',
  'reminder.modal.preview': 'Preview',
  'reminder.modal.edit': 'Edit',
  'reminder.modal.send': 'Send email',
  'reminder.modal.sending': 'Sending…',

  // Common actions / states
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.create': 'Create',
  'common.add': 'Add',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.search': 'Search',
  'common.loading': 'Loading…',
  'common.error': 'An error occurred.',
  'common.empty': 'No results.',
  'common.comingSoon': 'This module will be available soon.',
  'common.notFound': 'Page not found',
  'notFound.title': 'This page does not exist in Firmio',
  'notFound.sectionHint':
    'The link looks like one from {section}, but it leads nowhere. Whatever was there was moved or deleted, or the link is incomplete.',
  'notFound.genericHint':
    'No page in Firmio answers to this address. Check the link you were given, or start from the menu on the left.',
  'notFound.goTo': 'Go to {section}',
  'notFound.back': 'Back',
  'notFound.elsewhere': 'Or carry on from',
  'notFound.footer': 'Did a link inside Firmio bring you here? Tell us:',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.all': 'All',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.status': 'Status',
  'common.type': 'Type',
  'common.category': 'Category',
  'common.author': 'Author',
  'common.user': 'User',
  'common.exportExcel': 'Export Excel',
  'common.exporting': 'Exporting…',
  'common.importExcel': 'Import Excel',
  'common.list': 'List',

  // Bulk selection (directory list pages)
  'bulk.selected': '{count} selected',
  'bulk.delete': 'Delete',
  'bulk.clear': 'Clear',
  'bulk.error': 'Bulk action failed.',

  // Custom Select component
  'ui.select.searchPlaceholder': 'Search…',
  'ui.select.noResults': 'No results',
  'ui.select.clear': 'Clear selection',
  'ui.select.loading': 'Loading…',
  'ui.select.create': 'Add “{name}”',

  // Leads
  'leads.title': 'Pipeline',
  'leads.subtitle': 'Sales and onboarding, from first contact to client.',
  'leads.view.board': 'Board',
  'leads.view.list': 'List',
  'leads.add': 'Add lead',
  'leads.new': 'New lead',
  'leads.search': 'Search leads…',
  'leads.filter.allTemperatures': 'All temperatures',
  'leads.filter.allSources': 'All sources',
  'leads.filter.allAssignees': 'All assignees',
  'leads.filter.allLabels': 'All labels',
  'leads.filter.selectedCount': '{count} selected',
  'leads.filter.overdue': 'Overdue actions',
  'leads.filter.overdueOnly': 'Only with overdue actions',
  'leads.filter.clear': 'Clear filters',
  'leads.filter.archived': 'Archived',
  'leads.filter.archivedHint': 'Show archived leads',
  'leads.empty.archived': 'No archived leads.',
  'leads.archivedBanner':
    'Showing archived leads. Open one and press “Restore” to bring it back into the pipeline.',
  'leads.filter.search': 'Search',
  'leads.filter.temperature': 'Temperature',
  'leads.filter.source': 'Source',
  'leads.filter.assignee': 'Assignee',
  'leads.filter.labels': 'Labels',
  'leads.loading': 'Loading leads…',
  'leads.error': 'Failed to load leads.',
  'leads.empty.noStages': 'No pipeline stages to display.',
  'leads.empty.filtered': 'No lead matches the selected filters.',
  'leads.empty.none': 'No leads yet.',
  'leads.pagination': 'Page {page} of {totalPages} · {total} leads',
  'leads.won.toast': '“{title}” became a client ({client}) and moved to {pipeline}.',
  'leads.won.toastClientOnly': '“{title}” became a client ({client}).',
  'leads.won.toastBulk': 'Leads that became clients: {count}.',
  'leads.won.openClient': 'Open client',
  'leads.tabs': 'Pipelines',
  'leads.clientBadge': 'Client',
  'leads.pipelineKind.SALES': 'Sales',
  'leads.pipelineKind.ONBOARDING': 'Onboarding',
  'leads.lost.prompt': 'Mark as lost. Reason (optional):',
  'leads.lost.prefix': 'Lost',
  'leads.created': 'Lead {identifier} created',
  // Temperature
  'leads.temp.HOT': 'Hot',
  'leads.temp.WARM': 'Warm',
  'leads.temp.COLD': 'Cold',
  // Source
  'leads.source.MANUAL': 'Manual',
  'leads.source.AI_ASSISTANT': 'AI Assistant',
  'leads.source.REFERRAL': 'Referral',
  'leads.source.WEBSITE': 'Website',
  'leads.source.EMAIL': 'Email',
  'leads.source.PHONE': 'Phone',
  'leads.source.SOCIAL_MEDIA': 'Social media',
  'leads.source.EVENT': 'Event',
  'leads.source.OTHER': 'Other',

  // Clients
  'clients.title': 'Clients',
  'clients.subtitle': 'CRM-lite: invoices, projects, resources and meeting notes per client.',
  'clients.seedFromInvoices': 'Import from invoices',
  'clients.seeding': 'Importing…',
  'clients.new': 'New client',
  'clients.search': 'Search by name, VAT ID, email…',
  'clients.filter.allProjects': 'All projects',
  // Actori (opt-in module, Genius Media) + client industry
  'clients.industry': 'Industry',
  'clients.filter.allIndustries': 'All industries',
  'sidebar.actors': 'Actors',
  'actors.title': 'Actors',
  'actors.subtitle': 'The actors who appear in shoots and the clients they were used for.',
  'actors.add': 'Add actor',
  'actors.search': 'Search actors…',
  'actors.filter.industry': 'Industry',
  'actors.filter.client': 'Client',
  'actors.filter.allIndustries': 'All industries',
  'actors.filter.allClients': 'All clients',
  'actors.empty': 'No actors yet. Add the first one.',
  'actors.emptyFiltered': 'No actor matches the filters.',
  'actors.noClients': 'Not used for any client yet',
  'actors.conflictBadge': 'Conflict',
  'actors.conflictTooltip': 'Used for competing clients ({industry}): {clients}',
  'actors.conflictMessage':
    'Warning, this actor was used for {clients}, a potential conflict of interest.',
  'actors.field.firstName': 'First name',
  'actors.field.lastName': 'Last name',
  'actors.field.phone': 'Phone',
  'actors.field.email': 'Email',
  'actors.field.notes': 'Notes',
  'actors.field.photo': 'Photo',
  'actors.form.createTitle': 'New actor',
  'actors.form.editTitle': 'Edit actor',
  'actors.form.required': 'First and last name are required.',
  'actors.detail.back': 'Back to actors',
  'actors.detail.clients': 'Clients they appeared for',
  'actors.detail.linkClient': 'Link client',
  'actors.detail.edit': 'Edit',
  'actors.detail.delete': 'Delete',
  'actors.detail.deleteConfirm':
    'Delete {name}? They leave the list; the links to clients stay in the history.',
  'actors.col.client': 'Client',
  'actors.col.industry': 'Industry',
  'actors.col.usedAt': 'Last shoot',
  'actors.col.note': 'Note',
  'actors.unlink': 'Remove link',
  'actors.unlinkConfirm': 'Remove the link with {client}?',
  'actors.acknowledged': 'Conflict confirmed by {name} on {date}',
  'actors.link.title': 'Link {name} to a client',
  'actors.link.titleForClient': 'Add an actor to {client}',
  'actors.link.client': 'Client',
  'actors.link.actor': 'Actor',
  'actors.link.usedAt': 'Shoot date',
  'actors.link.note': 'Note (optional)',
  'actors.link.notePlaceholder': 'e.g. Christmas campaign',
  'actors.link.check': 'Check and link',
  'actors.link.checking': 'Checking…',
  'actors.link.conflictTitle': 'Potential conflict of interest',
  'actors.link.conflictLastShoot': 'last shoot {date}',
  'actors.link.confirm': 'Link anyway',
  'actors.link.cancel': 'Cancel',
  'actors.link.unknownIndustry':
    "The client has no industry set, so we can't check whether it competes with the actor's other clients. Set the industry on the client's page.",
  'actors.link.alreadyLinked': 'The actor is already linked to this client.',
  'actors.link.unknownIndustryPick':
    'The client has no industry set. The industry belongs to the client (two clients in one industry are competitors); pick it here so we can check:',
  'actors.setIndustry': 'Set industry',
  'actors.detail.industries': 'Industries (from the clients they appeared for)',
  'actors.client.tab': 'Actors',
  'actors.client.add': 'Add actor',
  'whatsapp.client.group': 'WhatsApp group',
  'whatsapp.client.synced': 'Synced: {when}',
  'whatsapp.client.notSynced': 'Not synced yet',
  'whatsapp.client.older': 'Older messages',
  'whatsapp.client.loading': 'Loading…',
  'whatsapp.client.noMessages': 'No message synced from this group yet.',
  'whatsapp.client.empty':
    'No WhatsApp group linked to this client. An admin links it in Settings → Integrations → WhatsApp.',
  'whatsapp.client.loadError': 'The WhatsApp conversations could not be loaded.',
  'whatsapp.settings.title': 'WhatsApp connection',
  'whatsapp.settings.notConfigured':
    'Fill in the WhatsApp service address and token above, then save.',
  'whatsapp.settings.unreachable': 'The WhatsApp service does not answer: {error}',
  'whatsapp.settings.connected': 'Connected',
  'whatsapp.settings.pairing': 'Waiting for the QR scan',
  'whatsapp.settings.disconnected': 'Disconnected',
  'whatsapp.settings.qrExpired':
    'The QR code expired (WhatsApp keeps it valid for only 2-3 minutes). Generate a new one and scan it right away.',
  'whatsapp.settings.connecting': 'Connecting to WhatsApp…',
  'whatsapp.settings.newQr': 'Generate a new QR code',
  'whatsapp.settings.scanHelp':
    'On the phone with the dedicated number: WhatsApp → Settings → Linked devices → Link a device, then scan the code. It refreshes by itself.',
  'whatsapp.settings.groups': 'Groups and clients',
  'whatsapp.settings.groupsCount': '· {linked} linked of {total}',
  'whatsapp.settings.searchGroups': 'Search group…',
  'whatsapp.settings.groupsHelp':
    'Link each group to its client. Only linked groups are synced, every {hours} hours.',
  'whatsapp.settings.noGroups':
    'No group yet. They appear after the first sync with the number connected.',
  'whatsapp.settings.col.group': 'Group',
  'whatsapp.settings.col.client': 'Client',
  'whatsapp.settings.col.messages': 'Messages',
  'whatsapp.settings.col.synced': 'Last sync',
  'whatsapp.settings.noClient': 'Not linked',
  'whatsapp.settings.syncNow': 'Sync now',
  'whatsapp.settings.syncStarted': 'The sync has started.',
  'whatsapp.settings.lastSync': 'Last sync: {when} · {created} new messages from {synced} groups',
  'whatsapp.settings.lastSyncStatus': 'Last attempt: {when} · {message}',
  'whatsapp.settings.autoOff': 'Automatic sync is turned off on this server.',
  'actors.client.empty': 'No actors used for this client yet.',
  'actors.client.alsoAt': 'Also used for competitors: {clients}',
  'settings.modules.optInLocked': 'On-demand module — enabled from the Firmio Console.',
  'settings.modules.optInLocal': 'On-demand module — on only if you enable it here.',
  'clients.filter.withProjects': 'With projects',
  'clients.filter.withoutProjects': 'Without projects',
  'clients.exportError': 'Could not export clients. Please try again.',
  'clients.loadError': 'Could not load clients.',
  'clients.empty': 'No clients. Create a new one or import from existing invoices.',
  'clients.col.client': 'Client',
  'clients.col.cui': 'VAT ID',
  'clients.col.projects': 'Projects',
  'clients.col.invoiced': 'Invoiced',
  'clients.col.received': 'Received',
  'clients.col.outstanding': 'Outstanding',
  'clients.pagination': 'Page {page} of {totalPages} ({total} total)',
  'clients.bulk.confirmTitle': 'Delete selected clients',
  'clients.bulk.confirmMessage':
    'Delete {count} client(s)? They will be marked inactive; invoices, projects and history remain.',

  // Suppliers
  'suppliers.title': 'Suppliers',
  'suppliers.subtitle':
    'Suppliers and recurring costs used to automatically match expenses from bank statements.',
  'suppliers.new': 'New supplier',
  'suppliers.search': 'Search by name, VAT ID, IBAN, contact…',
  'suppliers.filter.allCategories': 'All categories',
  'suppliers.filter.recurring': 'Recurring',
  'suppliers.filter.oneTime': 'One-time',
  'suppliers.filter.anyStatus': 'Any status',
  'suppliers.exportError': 'Could not export suppliers. Please try again.',
  'suppliers.loadError': 'Could not load suppliers.',
  'suppliers.empty':
    'No suppliers yet. Create one or add it directly while reconciling a bank statement.',
  'suppliers.col.supplier': 'Supplier',
  'suppliers.col.cui': 'VAT ID',
  'suppliers.col.category': 'Category',
  'suppliers.col.totalSpent': 'Total spent',
  'suppliers.col.recurring': 'Recurring',
  'suppliers.recurring.yes': 'Recurring',
  'suppliers.recurring.oneTime': 'One-time',
  'suppliers.pagination': 'Page {page} of {totalPages} ({total} total)',
  'suppliers.bulk.confirmTitle': 'Delete selected suppliers',
  'suppliers.bulk.confirmMessage':
    'Delete {count} supplier(s)? They will be marked inactive; financial history remains.',

  // Subscriptions
  'subscriptions.title': 'Subscriptions & Utilities',
  'subscriptions.subtitle': 'POS subscriptions, utilities, SaaS — matched by merchant name.',
  'subscriptions.create': 'Add subscription',
  'subscriptions.search': 'Search by name or pattern…',
  'subscriptions.name': 'Name',
  'subscriptions.category': 'Category',
  'subscriptions.supplier': 'Supplier',
  'subscriptions.patterns': 'POS patterns',
  'subscriptions.amount': 'Expected amount',
  'subscriptions.frequency': 'Frequency',
  'subscriptions.filter.allCategories': 'All categories',
  'subscriptions.filter.anyStatus': 'Any status',
  'subscriptions.exportError': 'Could not export subscriptions. Please try again.',
  'subscriptions.loadError': 'Could not load subscriptions.',
  'subscriptions.empty': 'No subscriptions. Add the first one for automatic POS matching.',
  'subscriptions.pagination': 'Page {page} of {totalPages} ({total} total)',
  'subscriptions.bulk.confirmTitle': 'Delete selected subscriptions',
  'subscriptions.bulk.confirmMessage':
    'Delete {count} subscription(s)? They will be marked inactive; matched payments remain.',
  'subscriptions.saas': 'SaaS',
  'subscriptions.utility': 'Utility',
  'subscriptions.telecom': 'Telecom',
  'subscriptions.fuel': 'Fuel',
  'subscriptions.office': 'Office',
  'subscriptions.hosting': 'Hosting',
  'subscriptions.other': 'Other',
  'subscriptions.monthly': 'Monthly',
  'subscriptions.quarterly': 'Quarterly',
  'subscriptions.annual': 'Annual',
  'subscriptions.usage': 'Usage-based',
  'financials.subscription': 'Subscriptions',
  'financials.expenseCategory': 'Expense category',
  'recon.subscription': 'Subscription',
  'recon.addSubscription': 'Add subscription',

  // People / Team
  'people.title': 'Team Directory',
  'people.addMember': 'Add member',
  'people.capacityPlanning': 'Capacity planning',
  'people.capacityShort': 'Capacity',
  'people.view.chart': 'Org chart',
  'people.view.list': 'List',
  'people.view.label': 'Team view',
  'people.editOrg': 'Edit org chart',
  'people.editOrgDone': 'Done editing',
  'people.search': 'Search by name or email…',
  'people.filter.allDepartments': 'All departments',
  'people.error.org': 'Failed to load org chart.',
  'people.error.directory': 'Failed to load team directory.',
  'people.editList': 'Edit list',
  'people.editListDone': 'Done editing',
  'people.col.name': 'Name',
  'people.col.department': 'Department',
  'people.col.allocations': 'Allocations (hrs/wk)',
  'people.col.utilization': 'Utilization',
  'people.noDepartment': 'No department',
  'people.noAllocations': 'No allocations',
  'people.hoursPerWeekSuffix': 'h/wk',
  'people.edit.saveFailed': 'Save failed.',
  'people.edit.hoursRange': 'Hours must be between 0 and 168.',

  // People — Employment tab (CIM/B2B)
  'people.employment.tab': 'Employment',
  'people.employment.loading': 'Loading employment data…',
  'people.employment.loadError': 'Failed to load employment data.',
  'people.employment.typeTitle': 'Engagement type',
  'people.employment.contractor': 'Contractor (B2B)',
  'people.employment.contractorHint': 'Invoices through their own company (SRL/PFA)',
  'people.employment.employee': 'Employee (CIM)',
  'people.employment.employeeHint': 'Individual employment contract',
  'people.employment.companyTitle': 'Company details',
  'people.employment.cui': 'CUI',
  'people.employment.anafLookup': 'Look up at ANAF',
  'people.employment.anafInvalidCui':
    'Invalid CUI — enter 2–10 digits (with or without the RO prefix).',
  'people.employment.anafNotFound': 'Company not found at ANAF.',
  'people.employment.anafFailed': 'ANAF lookup failed.',
  'people.employment.anafSuccess': 'Company details filled in from ANAF.',
  'people.employment.companyName': 'Company name',
  'people.employment.regCom': 'Trade Registry no.',
  'people.employment.address': 'Address',
  'people.employment.iban': 'IBAN',
  'people.employment.save': 'Save',
  'people.employment.saved': 'Employment details saved.',
  'people.employment.saveFailed': 'Failed to save employment details.',
  'people.employment.cimTitle': 'Employment contract (CIM)',
  'people.employment.cimHint': 'PDF, up to 10MB',
  'people.employment.cimEmpty': 'No contract uploaded.',
  'people.employment.cimUploading': 'Uploading contract…',
  'people.employment.cimUploaded': 'Contract uploaded.',
  'people.employment.cimUploadFailed': 'Failed to upload the contract.',
  'people.employment.cimTooLarge': 'The file exceeds the 10MB limit.',
  'people.employment.cimPreview': 'Preview',
  'people.employment.cimPreviewFailed': 'Failed to preview the contract.',
  'people.employment.cimDelete': 'Delete',
  'people.employment.cimDeleteConfirm': 'Delete the uploaded CIM contract?',
  'people.employment.cimDeleted': 'Contract deleted.',
  'people.employment.cimDeleteFailed': 'Failed to delete the contract.',

  // People — payroll
  'people.payroll.title': 'Payroll',
  'people.payroll.upload': 'Upload payroll sheet',
  'people.payroll.loading': 'Loading payroll history…',
  'people.payroll.loadError': 'Failed to load payroll history.',
  'people.payroll.empty': 'No payroll entries recorded.',
  'people.payroll.month': 'Month',
  'people.payroll.gross': 'Gross',
  'people.payroll.net': 'Net',
  'people.payroll.employeeContributions': 'Contributions',
  'people.payroll.incomeTax': 'Income tax',
  'people.payroll.cam': 'CAM',
  'people.payroll.totalCost': 'Total cost',
  'people.payroll.source': 'Source',
  'people.payroll.sourceFailed': 'Failed to open the source PDF.',
  'people.payroll.dialogTitle': 'Upload payroll sheet',
  'people.payroll.pickIntro':
    'Upload the payroll PDF received from the accountant. The data is extracted automatically, and you can review and correct every row before saving.',
  'people.payroll.monthOverride': 'Month (optional)',
  'people.payroll.yearOverride': 'Year (optional)',
  'people.payroll.autoDetect': 'Detect automatically',
  'people.payroll.pickHint': 'PDF, up to 10MB',
  'people.payroll.parsing': 'Extracting data from the PDF…',
  'people.payroll.fileTooLarge': 'The file exceeds the 10MB limit.',
  'people.payroll.previewFailed': 'Data extraction failed.',
  'people.payroll.reviewIntro': '{matched} of {total} rows matched to team members.',
  'people.payroll.extractedName': 'Extracted name',
  'people.payroll.matchedPerson': 'Person',
  'people.payroll.unmatched': '— unmatched —',
  'people.payroll.unmatchedHint':
    'Unmatched rows (highlighted) will not be saved. Pick the person from the list to include them.',
  'people.payroll.removeRow': 'Remove row',
  'people.payroll.duplicatePerson': 'The same person appears on multiple rows.',
  'people.payroll.confirm': 'Confirm ({count})',
  'people.payroll.confirmed': '{count} payroll entries saved.',
  'people.payroll.confirmFailed': 'Failed to save payroll entries.',
  'people.payroll.dash.title': 'Financial dashboard',
  'people.payroll.dash.subtitle': 'Total monthly cost (net + taxes), last {count} recorded months',
  'people.payroll.dash.totalPaid': 'Total paid ({count} months)',
  'people.payroll.dash.avgMonthly': 'Monthly average',
  'people.payroll.dash.lastMonth': 'Last month',
  'people.payroll.dash.lastMonthDetail': 'net {net} · taxes {taxes}',

  // Financials / Financiar
  // Cash flow (Financials › Upcoming payments & receipts)
  'cashflow.title': 'Upcoming payments & receipts',
  'cashflow.horizon': 'Horizon',
  'cashflow.monthsShort': '{count} mo',
  'cashflow.addPlanned': 'Add payment/receipt',
  'cashflow.editPlanned': 'Edit payment/receipt',
  'cashflow.chartTitle': 'Inflows vs outflows per month',
  'cashflow.monthlyBreakdown': 'Monthly breakdown',
  'cashflow.upcomingPayments': 'Upcoming payments',
  'cashflow.upcomingReceipts': 'Upcoming receipts',
  'cashflow.net': 'Net',
  'cashflow.overdue': 'Overdue',
  'cashflow.unconfirmed': 'Unconfirmed',
  'cashflow.view.months': 'By month',
  'cashflow.view.days': 'By day',
  'cashflow.days.date': 'Date',
  'cashflow.days.inflow': 'Receipts',
  'cashflow.days.outflow': 'Payments',
  'cashflow.days.balance': 'Balance',
  'cashflow.days.what': 'What happens',
  'cashflow.days.opening': 'Opening balance',
  'cashflow.days.empty': 'No movements in the selected horizon.',
  'cashflow.currency': 'Currency',
  'cashflow.noMovements': 'No movements this month.',
  'cashflow.noPayments': 'No upcoming payments in the selected horizon.',
  'cashflow.noReceipts': 'No upcoming receipts in the selected horizon.',
  'cashflow.loadError': 'Could not load the cash-flow projection.',
  'cashflow.excludedNote':
    'Excluded from projection: {quarterlyAnnual} quarterly/annual subscriptions and {usage} usage-based (uncertain amount/date).',
  'cashflow.source.subscription': 'Subscription',
  'cashflow.source.planned': 'Planned',
  'cashflow.source.invoice': 'Invoice',
  'cashflow.direction': 'Type',
  'cashflow.directionPayment': 'Payment',
  'cashflow.directionReceipt': 'Receipt',
  'cashflow.dueDate': 'Due date',
  'cashflow.form.name': 'Name',
  'cashflow.form.amount': 'Amount',
  'cashflow.form.currency': 'Currency',
  'cashflow.form.company': 'Company',
  'cashflow.form.defaultCompany': 'Default company',
  'cashflow.form.status': 'Status',
  'cashflow.form.notes': 'Notes',
  'cashflow.form.nameRequired': 'Name is required.',
  'cashflow.form.amountInvalid': 'Amount must be a positive number.',
  'cashflow.form.dueDateRequired': 'Due date is required.',
  'cashflow.status.planned': 'Planned',
  'cashflow.status.done': 'Done',
  'cashflow.status.cancelled': 'Cancelled',
  'cashflow.deleteTitle': 'Delete planned payment/receipt',
  'cashflow.deleteMessage': 'Delete "{name}"? This action cannot be undone.',
  'cashflow.projectedBalance': 'Projected balance',
  'cashflow.balances.title': 'Money in accounts',
  'cashflow.balances.current': 'Current balance',
  'cashflow.balances.asOf': 'as of {date}',
  'cashflow.balances.accounts': '{count} accounts',
  'cashflow.balances.empty': 'No active bank accounts. Add them in Settings → Bank accounts.',
  'cashflow.balances.unknown':
    'Balance unknown — upload a bank statement or set a manual balance per account.',
  'cashflow.balances.unknownAccount': 'Balance unknown',
  'cashflow.balances.source.statement': 'from statement',
  'cashflow.balances.source.manual': 'set manually',
  'cashflow.balances.editLabel': 'Set manual balance',
  'cashflow.balances.clear': 'Use statement',
  'cashflow.balances.clearHint': 'Clear the manual balance and fall back to the statement.',
  'subscriptions.paymentDay': 'Payment day',

  'financials.title': 'Financials',
  'financials.exportCsv': 'Export CSV',
  'financials.exportPdf': 'Export PDF',
  'financials.exportFailed': 'Export failed',
  'financials.allYears': 'All years',
  'financials.allMonths': 'Whole year',
  'financials.yearLabel': 'Year',
  'financials.monthLabel': 'Month',
  'financials.compare': 'Compare with previous period',
  'financials.revenueVsCosts': 'Revenue vs Costs',
  'financials.error.overview': 'Could not load the financial overview.',
  'financials.error.trends': 'Could not load trends.',
  'financials.forecastNote':
    'The forecast projects the next {count} periods based on the historical trend (cash-flow forecast).',
  'financials.metric.totalRevenue': 'Total revenue',
  'financials.metric.totalExpenses': 'Total expenses',
  'financials.metric.profit': 'Profit',
  'financials.metric.profitMargin': 'Profit margin',
  'financials.range.3m': '3 months',
  'financials.range.6m': '6 months',
  'financials.range.1y': '1 year',
  'financials.range.all': 'All time',
  'financials.month.1': 'January',
  'financials.month.2': 'February',
  'financials.month.3': 'March',
  'financials.month.4': 'April',
  'financials.month.5': 'May',
  'financials.month.6': 'June',
  'financials.month.7': 'July',
  'financials.month.8': 'August',
  'financials.month.9': 'September',
  'financials.month.10': 'October',
  'financials.month.11': 'November',
  'financials.month.12': 'December',
  'financials.chart.revenue': 'Revenue',
  'financials.chart.costs': 'Costs',
  'financials.chart.profit': 'Profit',
  'financials.chart.ariaLabel': 'Revenue, costs and profit over time',
  'financials.chart.empty': 'No trend data available.',
  'financials.chart.legend': '— solid line: actuals · - - dotted line: forecast',
  'financials.tab.overview': 'Overview',
  'financials.tab.analytics': 'Analytics',
  'financials.analytics.dimension': 'Dimension',
  'financials.analytics.dim.firm': 'Company',
  'financials.analytics.dim.department': 'Department',
  'financials.analytics.dim.service': 'Service',
  'financials.analytics.dim.client': 'Client',
  'financials.analytics.dim.supplier': 'Supplier',
  'financials.analytics.dim.subscription': 'Subscription',
  'financials.analytics.dim.person': 'Colleague',
  'financials.analytics.col.label': 'Label',
  'financials.analytics.col.income': 'Income',
  'financials.analytics.col.expenses': 'Expenses',
  'financials.analytics.col.profit': 'Profit',
  'financials.analytics.total': 'Total',
  'financials.analytics.untagged': 'Untagged',
  'financials.analytics.empty': 'No tagged financial data for the selected period.',
  'financials.analytics.error': 'Could not load the financial analytics.',
  'financials.analytics.notesTitle': 'How this is calculated',
  'financials.analytics.note.smartbill-overlap':
    'The same real invoice may exist both in SmartBill and as a manually uploaded invoice — amounts are not deduplicated across the two sources.',
  'financials.analytics.note.ledger-differs':
    'Totals may differ from the Overview tab, which reads the financial ledger (P&L).',
  'financials.analytics.note.finalized-only':
    'From bank statements, only reconciled lines of finalized statements count; lines matched to an invoice are not double-counted.',
  'financials.analytics.note.method-filter-channel-only':
    'The method filter only includes bank/cash lines (and payroll for Transfer); invoices have no payment method.',

  // Financials → Monthly reports (the entrepreneur's monthly report)
  'monthlyReport.title': 'Monthly report',
  'monthlyReport.subtitle':
    'Everything that happened financially in the selected month: cash, invoices, VAT and what comes next.',
  'monthlyReport.prevMonth': 'Previous month',
  'monthlyReport.nextMonth': 'Next month',
  'monthlyReport.yearLabel': 'Year',
  'monthlyReport.monthLabel': 'Month',
  'monthlyReport.closed': 'Month closed',
  'monthlyReport.open': 'Month open',
  'monthlyReport.closedAt': 'Closed on {date}',
  'monthlyReport.closedAtBy': 'Closed on {date} by {name}',
  'monthlyReport.snapshotNote': 'Figures are frozen as they were when the month was closed.',
  'monthlyReport.closeMonth': 'Close month',
  'monthlyReport.reopenMonth': 'Reopen month',
  'monthlyReport.downloadPdf': 'Download PDF',
  'monthlyReport.closeConfirm':
    'Close {period}? The figures are frozen as they are now; you can reopen the month at any time.',
  'monthlyReport.reopenConfirm':
    'Reopen {period}? The snapshot is deleted and the report is recomputed from current data.',
  'monthlyReport.actionFailed': 'The action failed. Please try again.',
  'monthlyReport.loadError': 'The report could not be loaded.',
  'monthlyReport.generatedAt': 'Computed on {date}',
  'monthlyReport.section.summary': 'Month summary',
  'monthlyReport.section.accounts': 'Cash flow by account',
  'monthlyReport.section.revenue': 'Revenue',
  'monthlyReport.section.expenses': 'Expenses by category',
  'monthlyReport.section.receivables': 'Unpaid invoices (receivables)',
  'monthlyReport.section.payables': 'Invoices to pay (payables)',
  'monthlyReport.section.vat': 'VAT position',
  'monthlyReport.section.dataQuality': 'Reconciliation & data quality',
  'monthlyReport.section.projects': 'By project',
  'monthlyReport.section.trends': '12-month trends',
  'monthlyReport.summary.inflows': 'Cash in',
  'monthlyReport.summary.outflows': 'Cash out',
  'monthlyReport.summary.net': 'Net result (cash)',
  'monthlyReport.summary.bankBalance': 'Bank balance at month end',
  'monthlyReport.summary.bankBalanceAsOf': 'as of {date}',
  'monthlyReport.summary.bankBalanceUnknown': 'No statement with a known balance',
  'monthlyReport.summary.invoiced': 'Invoiced',
  'monthlyReport.summary.collected': 'Collected',
  'monthlyReport.summary.invoicedVsCollected': 'Invoiced vs collected',
  'monthlyReport.summary.vsPrevious': 'vs previous month',
  'monthlyReport.summary.noPrevious': 'Nothing in the ledger last month',
  'monthlyReport.summary.otherCurrencies': 'Other currencies (not converted)',
  'monthlyReport.accounts.account': 'Account',
  'monthlyReport.accounts.opening': 'Opening balance',
  'monthlyReport.accounts.credits': 'In',
  'monthlyReport.accounts.debits': 'Out',
  'monthlyReport.accounts.closing': 'Closing balance',
  'monthlyReport.accounts.net': 'Net',
  'monthlyReport.accounts.transactions': '{n} transactions',
  'monthlyReport.accounts.internal': '{n} internal transfers excluded',
  'monthlyReport.accounts.unreconciled': '{n} unreconciled',
  'monthlyReport.accounts.unfinalized': '{n} unfinalized statements',
  'monthlyReport.accounts.missing': 'No statement for this month',
  'monthlyReport.accounts.unregistered': 'Unregistered account',
  'monthlyReport.accounts.none': 'No bank account and no statement for this month.',
  'monthlyReport.revenue.invoices': 'Invoices issued',
  'monthlyReport.revenue.creditNotes': '{n} credit notes',
  'monthlyReport.revenue.net': 'Excl. VAT',
  'monthlyReport.revenue.vat': 'VAT',
  'monthlyReport.revenue.total': 'Total invoiced',
  'monthlyReport.revenue.collected': 'Collected this month',
  'monthlyReport.revenue.topInvoiced': 'Top clients by invoiced',
  'monthlyReport.revenue.topCollected': 'Top clients by collected',
  'monthlyReport.revenue.concentration': '{name} accounts for {share}% of invoiced revenue',
  'monthlyReport.revenue.concentrationWarning':
    'More than half of the revenue comes from a single client.',
  'monthlyReport.revenue.none': 'No invoice issued and nothing collected this month.',
  'monthlyReport.expenses.category': 'Category',
  'monthlyReport.expenses.amount': 'This month',
  'monthlyReport.expenses.previous': 'Last month',
  'monthlyReport.expenses.average3m': '3-month avg',
  'monthlyReport.expenses.share': 'Share',
  'monthlyReport.expenses.topSuppliers': 'Top suppliers',
  'monthlyReport.expenses.subscriptions': 'Subscriptions',
  'monthlyReport.expenses.subscriptionsTotal': 'Subscriptions paid',
  'monthlyReport.expenses.expected': 'expected {amount}',
  'monthlyReport.expenses.status.paid': 'Paid',
  'monthlyReport.expenses.status.missing': 'Missing',
  'monthlyReport.expenses.status.amount_differs': 'Amount differs',
  'monthlyReport.expenses.status.not_due': 'Not due',
  'monthlyReport.expenses.none': 'No expense in the ledger for this month.',
  'monthlyReport.open.total': 'Total',
  'monthlyReport.open.count': '{n} invoices',
  'monthlyReport.open.bucket.current': 'Not yet due',
  'monthlyReport.open.bucket.d1_30': '1–30 days',
  'monthlyReport.open.bucket.d31_60': '31–60 days',
  'monthlyReport.open.bucket.d61_90': '61–90 days',
  'monthlyReport.open.bucket.d90plus': 'Over 90 days',
  'monthlyReport.open.topParty': 'Largest outstanding',
  'monthlyReport.open.invoice': 'Invoice',
  'monthlyReport.open.party': 'Party',
  'monthlyReport.open.amount': 'Amount',
  'monthlyReport.open.dueDate': 'Due',
  'monthlyReport.open.daysOverdue': '+{n} days',
  'monthlyReport.open.showAll': 'Show all ({n})',
  'monthlyReport.open.showLess': 'Show fewer',
  'monthlyReport.open.otherCurrencies': 'In other currencies',
  'monthlyReport.open.noneReceivables': 'No unpaid invoice at month end.',
  'monthlyReport.open.nonePayables': 'Nothing to pay at month end.',
  'monthlyReport.open.scheduledNext': 'Scheduled for next month',
  'monthlyReport.open.scheduledPayments': 'Payments',
  'monthlyReport.open.scheduledReceipts': 'Receipts',
  'monthlyReport.vat.notApplicable': 'The firm is not a VAT payer.',
  'monthlyReport.vat.collected': 'VAT collected',
  'monthlyReport.vat.deductible': 'VAT deductible',
  'monthlyReport.vat.toPay': 'To pay',
  'monthlyReport.vat.toRecover': 'To recover',
  'monthlyReport.vat.month': 'Month',
  'monthlyReport.vat.quarter': 'Quarter (cumulative)',
  'monthlyReport.vat.note':
    'Estimate from invoices issued and received through SPV in the period; the accountant files the final return.',
  'monthlyReport.quality.transactions': 'Transactions in month',
  'monthlyReport.quality.reconciled': 'Reconciled',
  'monthlyReport.quality.unmatched': 'Unreconciled',
  'monthlyReport.quality.ignored': 'Ignored',
  'monthlyReport.quality.statements': 'Statements',
  'monthlyReport.quality.finalized': 'finalized',
  'monthlyReport.quality.accountsWithoutStatement': 'Accounts without statement',
  'monthlyReport.quality.receivedUnpaid': 'Invoices received this month, unpaid',
  'monthlyReport.quality.ledgerRows': 'Ledger rows',
  'monthlyReport.quality.allGood': "The month's data is complete and reconciled.",
  'monthlyReport.quality.warning.no_statements':
    'No bank statement uploaded for this month — the cash sections are empty.',
  'monthlyReport.quality.warning.statements_unfinalized':
    'Some statements are not finalized: their lines have not reached the ledger yet.',
  'monthlyReport.quality.warning.unmatched_lines':
    'There are unreconciled transactions; category totals are incomplete.',
  'monthlyReport.quality.warning.ledger_empty':
    'Statements exist but nothing reached the ledger — finalize the reconciliation.',
  'monthlyReport.quality.warning.accounts_without_statement':
    'Some bank accounts have no statement for this month.',
  'monthlyReport.quality.goToStatements': 'Go to statements',
  'monthlyReport.projects.project': 'Project',
  'monthlyReport.projects.revenue': 'Revenue',
  'monthlyReport.projects.expenses': 'Cost',
  'monthlyReport.projects.margin': 'Margin',
  'monthlyReport.projects.unallocated': 'No project',
  'monthlyReport.projects.unallocatedShare': '{share}% of expenses have no project.',
  'monthlyReport.projects.none': 'No ledger line has a project this month.',
  'monthlyReport.trends.cashflow': 'Cash in vs cash out',
  'monthlyReport.trends.cumulative': 'Cumulative net result',
  'monthlyReport.trends.balance': 'Bank balance at month end',
  'monthlyReport.trends.balanceNone': 'No statement with a balance in the last 12 months.',
  'monthlyReport.trends.runway': 'Estimated runway',
  'monthlyReport.trends.runwayValue': '{n} months',
  'monthlyReport.trends.runwayUnknown': 'unknown',
  'monthlyReport.trends.runwayHint': 'Month-end balance divided by the 3-month average spend.',
  'monthlyReport.trends.average3m': 'Average spend (3 months)',
  // Reports: the period picker, the tabs and the synthesis
  'monthlyReport.titleGeneric': 'Financial report',
  'monthlyReport.period.kindLabel': 'Period',
  'monthlyReport.period.kind.month': 'Month',
  'monthlyReport.period.kind.quarter': 'Quarter',
  'monthlyReport.period.kind.year': 'Year',
  'monthlyReport.period.kind.custom': 'Range',
  'monthlyReport.period.quarterLabel': 'Quarter',
  'monthlyReport.period.fromLabel': 'From',
  'monthlyReport.period.toLabel': 'To',
  'monthlyReport.period.prev': 'Previous period',
  'monthlyReport.period.next': 'Next period',
  'monthlyReport.period.invalidRange': 'The start date must come before the end date.',
  'monthlyReport.period.closedMonth': 'closed',
  'monthlyReport.period.compareLabel': 'Compared with',
  'monthlyReport.period.compare.prev': 'Previous period',
  'monthlyReport.period.compare.yoy': 'Same period last year',
  'monthlyReport.period.comparedWith': 'Compared with {period}',
  'monthlyReport.downloadXlsx': 'Download Excel',
  'monthlyReport.staleSnapshot':
    'This report was frozen with an earlier version, so the newer sections are missing.',
  'monthlyReport.recompute': 'Recompute',
  'monthlyReport.tab.synthesis': 'Synthesis',
  'monthlyReport.tab.revenue': 'Revenue and clients',
  'monthlyReport.tab.expenses': 'Spend and subscriptions',
  'monthlyReport.tab.people': 'People',
  'monthlyReport.tab.projects': 'Projects',
  'monthlyReport.tab.cash': 'Cash and banking',
  'monthlyReport.tab.tax': 'Tax',
  'monthlyReport.tab.quality': 'Data quality',
  'monthlyReport.section.synthesis': 'At a glance',
  'monthlyReport.section.people': 'Cost of people',
  'monthlyReport.section.clients': 'Clients',
  'monthlyReport.synthesis.marginPct': 'Net margin',
  'monthlyReport.synthesis.peopleCost': 'Cost of people',
  'monthlyReport.synthesis.burn': 'Average monthly spend',
  'monthlyReport.synthesis.topClients': 'Largest clients',
  'monthlyReport.synthesis.topExpenses': 'Largest expenses',
  'monthlyReport.synthesis.attention': 'Worth checking',
  'monthlyReport.synthesis.allGood': 'Nothing to flag: the period looks complete.',
  // People
  'monthlyReport.people.empty': 'No payslip was uploaded for this period.',
  'monthlyReport.people.totalCost': 'Total cost',
  'monthlyReport.people.headcount': 'People',
  'monthlyReport.people.headcountValue': '{n} people',
  'monthlyReport.people.shareOfOutflows': 'Of cash out',
  'monthlyReport.people.shareOfInflows': 'Of cash in',
  'monthlyReport.people.revenuePerPerson': 'Cash in per person',
  'monthlyReport.people.returnPerLeu': 'Cash in per leu of cost',
  'monthlyReport.people.breakdown': 'What the cost is made of',
  'monthlyReport.people.gross': 'Gross salary',
  'monthlyReport.people.contributions': 'Employee contributions',
  'monthlyReport.people.incomeTax': 'Income tax',
  'monthlyReport.people.cam': 'Employer CAM',
  'monthlyReport.people.net': 'Take-home pay',
  'monthlyReport.people.reconciliation': 'Paid from the bank vs accrued cost',
  'monthlyReport.people.ledgerPaid': 'Paid from the bank',
  'monthlyReport.people.ledgerVsPayroll': 'Difference',
  'monthlyReport.people.ledgerDividends': 'Dividends paid',
  'monthlyReport.people.reconciliationHint':
    'The accrued cost includes contributions transferred the following month, so the two figures need not match. The cost of people is NOT added to the period spend — the bank payments are already counted there.',
  'monthlyReport.people.person': 'Person',
  'monthlyReport.people.department': 'Department',
  'monthlyReport.people.hours': 'Hours',
  'monthlyReport.people.costPerHour': 'Cost / hour',
  'monthlyReport.people.leaveDays': 'Leave days',
  'monthlyReport.people.byDepartment': 'By department',
  'monthlyReport.people.hoursTotal': 'Hours booked',
  'monthlyReport.people.hoursUnbooked': 'Hours without a payslip',
  'monthlyReport.people.trend': 'Cost of people over time',
  'monthlyReport.people.warning.payroll_missing':
    'There are no payslips for this period, so the cost of people shows as zero.',
  'monthlyReport.people.warning.payroll_partial':
    'Payslips are missing for some months of the period, so the cost is incomplete.',
  'monthlyReport.people.warning.payroll_period_rounded':
    'Payslips are monthly, so the cost covers the whole months the range touches, not only the selected days.',
  'monthlyReport.people.warning.ledger_payroll_mismatch':
    'Salary payments in the statement differ materially from the accrued cost. Check that the right payslip was uploaded and when the contributions were transferred.',
  'monthlyReport.people.warning.hours_without_payroll':
    'Some hours were booked by people with no payslip in this period.',
  // Clients
  'monthlyReport.clients.empty': 'No client invoices and no client payments in this period.',
  'monthlyReport.clients.client': 'Client',
  'monthlyReport.clients.invoiced': 'Invoiced',
  'monthlyReport.clients.collected': 'Collected',
  'monthlyReport.clients.open': 'Outstanding',
  'monthlyReport.clients.overdue': 'Overdue',
  'monthlyReport.clients.invoiceCount': 'Invoices',
  'monthlyReport.clients.dso': 'Days to payment',
  'monthlyReport.clients.dsoShort': 'DSO',
  'monthlyReport.clients.dsoValue': '{n} days',
  'monthlyReport.clients.dsoHint':
    'The amount-weighted average number of days between issuing an invoice and marking it collected, for invoices collected in this period.',
  'monthlyReport.clients.newBadge': 'new',
  'monthlyReport.clients.newClients': 'New clients',
  'monthlyReport.clients.concentration': 'Concentration',
  'monthlyReport.clients.top1': 'Largest client',
  'monthlyReport.clients.top3': 'Top three',
  'monthlyReport.clients.dormant': 'Dormant clients',
  'monthlyReport.clients.dormantHint':
    'They invoiced in the last 12 months, but nothing in this period.',
  'monthlyReport.clients.dormantRow': 'last invoice {date}, {days} days ago',
  'monthlyReport.clients.overdueByClient': 'Who is late',
  'monthlyReport.clients.trend': 'Invoiced vs collected',
  'monthlyReport.clients.showAll': 'Show all {n} clients',
  'monthlyReport.clients.showLess': 'Show fewer',
  // Projects: labour cost and budget
  'monthlyReport.projects.hours': 'Hours',
  'monthlyReport.projects.laborCost': 'Labour cost',
  'monthlyReport.projects.marginWithLabor': 'Margin with labour',
  'monthlyReport.projects.budget': 'Budget',
  'monthlyReport.projects.budgetUsed': 'Budget used',
  'monthlyReport.projects.barter': 'barter',
  'monthlyReport.projects.losing': '{n} projects at a loss',
  'monthlyReport.projects.warning.labor_estimated':
    'Labour cost is an estimate: hours booked × an hourly rate derived from the payslip, or the rate on the person profile.',
  'monthlyReport.projects.warning.hours_without_rate':
    '{n} hours were worked by people with no payslip and no hourly rate, so they are left out of the labour cost.',
  'monthlyReport.projects.warning.budget_other_currency':
    'Some budgets are not in RON, so their consumption is not computed.',
  // Subscriptions: recurring cost
  'monthlyReport.expenses.runRate': 'Recurring cost',
  'monthlyReport.expenses.runRateValue': '{monthly} per month · {annual} per year',
  'monthlyReport.expenses.expectedForPeriod': 'Expected in the period',
  // VAT by quarter (yearly report)
  'monthlyReport.vat.quarters': 'By quarter',
  'monthlyReport.vat.periodLabel': 'Reported period',
  'monthlyReport.trends.inflows': 'Cash in',
  'monthlyReport.trends.outflows': 'Cash out',
  'monthlyReport.trends.net': 'Net',

  // Payment method (Transfer / Card / Cash)
  'paymentMethod.label': 'Payment method',
  'paymentMethod.all': 'All methods',
  'paymentMethod.BANK_TRANSFER': 'Transfer',
  'paymentMethod.CARD': 'Card',
  'paymentMethod.CASH': 'Cash',

  // Cash register (manual cash payments)
  'cash.addButton': 'Add cash payment',
  'cash.modalTitle': 'Add cash payment',
  'cash.direction': 'Operation type',
  'cash.direction.OUTCOME': 'Payment',
  'cash.direction.INCOME': 'Collection',
  'cash.date': 'Date',
  'cash.amount': 'Amount',
  'cash.counterpartyType': 'Counterparty',
  'cash.counterparty.person': 'Person',
  'cash.counterparty.supplier': 'Supplier',
  'cash.counterparty.client': 'Client',
  'cash.counterpartyName': 'Display name (optional)',
  'cash.category': 'Expense category',
  'cash.project': 'Project (optional)',
  'cash.description': 'Description',
  'cash.save': 'Save',
  'cash.created': 'The cash payment was recorded. You can find it in the cash register.',
  'cash.deleted': 'The cash entry was deleted.',
  'cash.deleteConfirm': 'Delete this cash entry? It will also be removed from Financials.',
  'cash.registerName': 'Cash register',
  'cash.error.amount': 'Enter a positive amount.',
  'cash.error.counterparty': 'Select a counterparty (person, supplier or client).',
  'cash.error.generic': 'Something went wrong. Please try again.',

  // Invoices / Facturi
  'invoices.title': 'Invoices',
  'invoices.subtitle': 'Track income and expense invoices.',
  'invoices.home': 'Home',
  'invoices.newWithAI': 'New invoice with AI',
  'invoices.error': 'Could not load invoices',
  'invoices.pagination': 'Page {page} of {totalPages} ({total} total)',
  'invoices.empty': 'No invoices found.',
  'invoices.col.number': 'Number',
  'invoices.col.type': 'Type',
  'invoices.col.issueDate': 'Issue date',
  'invoices.col.dueDate': 'Due date',
  'invoices.col.amount': 'Amount',
  'invoices.col.status': 'Status',
  'invoices.col.project': 'Project',
  'invoices.col.actions': 'Actions',
  'invoices.direction.income': 'Income',
  'invoices.direction.outcome': 'Expense',
  'invoices.overdue': 'overdue',
  'invoices.action.download': 'Download',
  'invoices.action.markPaid': 'Mark paid',
  'invoices.action.delete': 'Delete',
  'invoices.status.DRAFT': 'Draft',
  'invoices.status.SENT': 'Issued',
  'invoices.status.PAID': 'Paid',
  'invoices.status.OVERDUE': 'Due',
  'invoices.status.CANCELLED': 'Cancelled',
  'invoices.filter.type': 'Type',
  'invoices.filter.status': 'Status',
  'invoices.filter.issueFrom': 'Issued from',
  'invoices.filter.issueTo': 'Issued until',
  'invoices.filter.all': 'All',
  'invoices.filter.clear': 'Clear filters',

  // Projects — list page (CLE-278)
  'projects.title': 'Projects',
  'projects.createWithAI': 'Create with AI',
  'projects.create': 'Create project',
  'projects.searchPlaceholder': 'Search by name or description…',
  'projects.allStatuses': 'All statuses',
  'projects.allHealths': 'All health states',
  'projects.sort.label': 'Sort',
  'projects.sort.newest': 'Newest',
  'projects.sort.oldest': 'Oldest',
  'projects.sort.nameAsc': 'Name (A–Z)',
  'projects.sort.nameDesc': 'Name (Z–A)',
  'projects.sort.endDateDesc': 'Deadline (latest first)',
  'projects.sort.endDateAsc': 'Deadline (earliest first)',
  'projects.clearFilters': 'Clear filters',
  'projects.error.list': 'Failed to load projects.',
  'projects.empty': 'No projects found.',
  'projects.col.name': 'Name',
  'projects.col.status': 'Status',
  'projects.col.health': 'Health',
  'projects.col.owner': 'Owner',
  'projects.col.team': 'Team',
  'projects.col.deadline': 'Deadline',
  'projects.pageInfo': 'Page {page} of {totalPages} · {total} projects',
  'projects.prev': 'Previous',
  'projects.next': 'Next',

  // Projects — status labels
  'projects.status.PLANNING': 'Planning',
  'projects.status.ACTIVE': 'Active',
  'projects.status.ON_HOLD': 'On hold',
  'projects.status.COMPLETED': 'Completed',
  'projects.status.CANCELLED': 'Cancelled',
  'projects.status.ALMOST_DONE': 'Almost done',
  'projects.status.LEAD': 'Lead',
  'projects.status.IN_HOUSE': 'In-house',

  // Projects — health labels
  'projects.health.ON_TRACK': 'On track',
  'projects.health.AT_RISK': 'At risk',
  'projects.health.OFF_TRACK': 'Off track',

  // Projects — detail page tabs + header
  'projects.back': '← Projects',
  'projects.notFound': 'Project not found.',
  'projects.backToList': '← Back to projects',
  'projects.edit': 'Edit project',
  'projects.editWithAI': 'Edit with AI',
  'projects.deleteWithAI': 'Delete with AI',
  'projects.tab.overview': 'Overview',
  'projects.tab.team': 'Team',
  'projects.tab.financials': 'Financials',
  'projects.tab.tasks': 'Tasks',
  'projects.tab.resources': 'Resources',
  'projects.tab.vault': 'Vault',
  'projects.tab.notes': 'Meeting notes',
  'projects.tab.context': 'Context',
  'projects.context.title': 'Project context',
  'projects.context.intro':
    'The story of the project: how you won it, the team, the client, the challenges, the journey, the decisions. Firmio AI takes it into account whenever you work on this project, and a new colleague reads it during onboarding.',
  'projects.context.empty':
    'Nothing has been told about this project yet. Talk to Firmio AI in the panel next to this or add a note directly.',
  'projects.context.updatedAt': 'Updated {date}',
  'projects.context.kbPath': 'In KB: {path}',
  'projects.context.copy': 'Copy',
  'projects.context.copied': 'Copied',
  'projects.context.edit': 'Edit document',
  'projects.context.editHint':
    'You are editing the consolidated document directly (markdown). Future additions will be merged on top of this version.',
  'projects.context.save': 'Save',
  'projects.context.cancel': 'Cancel',
  'projects.context.saved': 'Document saved.',
  'projects.context.chat.title': 'Tell Firmio AI about it',
  'projects.context.chat.hint':
    'Everything you say here about the project is saved into its context and the document is rewritten automatically.',
  'projects.context.chat.placeholder':
    'E.g.: We won the project in 2024 through a referral from…, the team is…, the biggest challenge was…',
  'projects.context.chat.send': 'Send',
  'projects.context.chat.stop': 'Stop',
  'projects.context.chat.newConversation': 'New conversation',
  'projects.context.chat.openFull': 'Open in Firmio AI',
  'projects.context.chat.welcome':
    "Hi! I'm here to remember everything that matters about **{name}**. Start with: how you won the project, who the client is and what they want, who works on it, what the challenges were, what a new colleague should know. Write freely, I will organise it.",
  'projects.context.chat.thinking': 'Thinking…',
  'projects.context.chat.saving': 'Saving to context…',
  'projects.context.manual.title': 'Add a note directly',
  'projects.context.manual.placeholder':
    'Write what should go into the context (no AI in between; the document is consolidated automatically).',
  'projects.context.manual.add': 'Add to context',
  'projects.context.manual.added': 'Note added and document updated.',
  'projects.context.manual.addedAppended':
    'Note appended to the end of the document (AI consolidation was unavailable).',
  'projects.context.journal.title': 'Journal of additions',
  'projects.context.journal.hint':
    'Everything said about the project, newest first. The document above is the consolidated version.',
  'projects.context.journal.empty': 'No additions yet.',
  'projects.context.journal.source.CHAT': 'from chat',
  'projects.context.journal.source.MANUAL': 'direct note',
  'projects.context.journal.source.VOICE': 'from voice',
  'projects.context.error.load': 'Could not load the project context.',
  'projects.context.error.save': 'Could not save.',

  // Projects — credentials vault
  'vault.search': 'Search by title or field',
  'vault.allTypes': 'All types',
  'vault.add': 'Add credential',
  'vault.auditToggle': 'Audit log',
  'vault.entriesToggle': 'Credentials',
  'vault.empty': 'No credentials for this project yet.',
  'vault.emptyFiltered': 'No credentials match the selected filters.',
  'vault.loadFailed': 'Loading the vault failed.',
  'vault.decryptError': 'This entry could not be decrypted. Did the encryption secret change?',
  'vault.noRevealPermission': "You don't have permission to reveal passwords.",
  'vault.reveal': 'Reveal value',
  'vault.hide': 'Hide value',
  'vault.copy': 'Copy',
  'vault.copied': 'Copied',
  'vault.copyFailed': 'Copying failed.',
  'vault.revealFailed': 'Revealing failed.',
  'vault.editEntry': 'Edit',
  'vault.deleteEntry': 'Delete',
  'vault.deleteConfirm': 'Permanently delete the credential "{title}"? The audit log is kept.',
  'vault.deleteFailed': 'Deleting failed.',
  'vault.noFields': 'No fields.',
  'vault.updatedBy': 'Updated by {name}',
  'vault.audit.empty': 'No activity recorded.',
  'vault.audit.date': 'Date',
  'vault.audit.user': 'User',
  'vault.audit.action': 'Action',
  'vault.audit.entry': 'Entry',
  'vault.audit.details': 'Details',
  'vault.audit.loadFailed': 'Loading the audit log failed.',
  'vault.action.CREATE': 'Create',
  'vault.action.UPDATE': 'Update',
  'vault.action.DELETE': 'Delete',
  'vault.action.REVEAL': 'Reveal',
  'vault.action.COPY': 'Copy',
  'vault.type.SERVER': 'Server',
  'vault.type.GITHUB': 'GitHub',
  'vault.type.GITLAB': 'GitLab',
  'vault.type.AZURE': 'Azure',
  'vault.type.AWS': 'AWS',
  'vault.type.DATABASE': 'Database',
  'vault.type.EMAIL': 'Email',
  'vault.type.FTP': 'FTP',
  'vault.type.API': 'API',
  'vault.type.HOSTING': 'Hosting',
  'vault.type.DOMAIN': 'Domain',
  'vault.type.ACCOUNT': 'Account',
  'vault.type.OTHER': 'Other',
  'vault.modal.createTitle': 'New credential',
  'vault.modal.editTitle': 'Edit credential',
  'vault.modal.title': 'Title',
  'vault.modal.titlePlaceholder': 'e.g. Production server',
  'vault.modal.type': 'Type',
  'vault.modal.url': 'URL',
  'vault.modal.notes': 'Notes',
  'vault.modal.fields': 'Fields',
  'vault.modal.addField': 'Add field',
  'vault.modal.fieldLabel': 'Label',
  'vault.modal.fieldValue': 'Value',
  'vault.modal.secret': 'Secret',
  'vault.modal.removeField': 'Remove field',
  'vault.modal.moveUp': 'Move up',
  'vault.modal.moveDown': 'Move down',
  'vault.modal.unchanged': '(unchanged)',
  'vault.modal.loadSecrets': 'Load the real values',
  'vault.modal.noFields': 'No fields yet. Add one below.',
  'vault.modal.titleRequired': 'A title is required.',
  'vault.modal.labelRequired': 'Every field needs a label.',
  'vault.modal.saveFailed': 'Saving failed.',

  // Projects — services card (services nomenclature)
  'projects.services.title': 'Services',
  'projects.services.empty': 'No services attached.',
  'projects.services.edit': 'Edit',
  'projects.services.none': 'No services defined. Add services in Settings.',
  'projects.services.error': 'Failed to update services.',

  // Projects — overview tab
  'projects.overview.error': 'Failed to load the summary.',
  'projects.overview.general': 'General',
  'projects.overview.barter': 'Barter',
  'projects.overview.client': 'Client',
  'projects.overview.start': 'Start',
  'projects.overview.deadline': 'Deadline',
  'projects.overview.budget': 'Budget',
  'projects.overview.consumed': 'Consumed',
  'projects.overview.team': 'Team',
  'projects.overview.noTeam': 'No team members assigned yet.',
  'projects.overview.allocPerWeek': 'h/wk',
  'projects.overview.financialSummary': 'Financial summary (P&L)',
  'projects.overview.revenue': 'Revenue',
  'projects.overview.expenses': 'Expenses',
  'projects.overview.profit': 'Profit',
  'projects.overview.loss': 'Loss',
  'projects.overview.unpaidIn': 'Unpaid (in)',
  'projects.overview.payableOut': 'Payable (out)',
  'projects.overview.noMonthly': 'No monthly financial data yet.',
  'projects.overview.monthlyTrend': 'Monthly trend',
  'projects.overview.margin': 'Margin',
  'projects.overview.milestones': 'Milestones',
  'projects.overview.noMilestones': 'No milestones defined yet.',
  'projects.overview.overdue': 'Overdue',
  'projects.overview.tasks': 'Tasks',
  'projects.overview.noTasks': 'No tasks yet.',
  'projects.overview.active': 'Active',
  'projects.overview.total': 'Total',
  'projects.overview.overdueTasks': 'Overdue',
  'projects.overview.risks': 'Active risks',
  'projects.overview.noRisks': 'No active risks.',
  'projects.overview.critical': 'Critical',
  'projects.overview.impact': 'Impact:',
  'projects.overview.action': 'Action:',
  'projects.overview.recentActivity': 'Recent activity',
  'projects.overview.noActivity': 'No activity recorded yet.',
  'projects.overview.timeline': 'Schedule elapsed',
  'projects.overview.daysLeft': '{n} days left',
  'projects.overview.daysOverdue': '{n} days overdue',
  'projects.overview.inTotal': 'in total',
  'projects.overview.seeAllTasks': 'All tasks',
  'projects.overview.doneTasks': 'Done',
  'projects.overview.nextTasks': 'Up next',
  'projects.overview.allDone': 'No open tasks.',
  'projects.overview.noDueDate': 'no due date',
  'projects.overview.dueToday': 'today',
  'projects.overview.completion': 'Tasks completed',

  // Projects — team tab
  'projects.team.noMembers': 'No team members assigned yet.',
  'projects.team.noMembersHint':
    'People are added to a project through allocations in the Knowledge Base. Assign someone via AI Chat and they will appear here.',
  'projects.team.assignWithAI': 'Assign a member with AI',
  'projects.team.member': 'member',
  'projects.team.members': 'members',
  'projects.team.orgChart': 'Org chart',
  'projects.team.list': 'List',
  'projects.team.col.name': 'Name',
  'projects.team.col.title': 'Title',
  'projects.team.col.department': 'Department',
  'projects.team.col.allocation': 'Allocation (hrs/wk)',
  'projects.team.col.email': 'Email',
  'projects.team.inactive': 'inactive',
  'projects.team.hoursPerWeekSuffix': 'h/wk',
  'projects.team.addMember': 'Add member',
  'projects.team.removeMember': 'Remove from team',
  'projects.team.editMember': 'Edit member',
  'projects.team.removeConfirm': 'Remove {name} from the project team?',
  'projects.team.removeFailed': 'Removing the member failed.',
  'projects.team.edit.title': 'Edit team member',
  'projects.team.edit.submit': 'Save changes',
  'projects.team.edit.saving': 'Saving…',
  'projects.team.add.title': 'Add team member',
  'projects.team.add.person': 'Person',
  'projects.team.add.personPlaceholder': 'Pick a person',
  'projects.team.add.role': 'Role',
  'projects.team.add.allocation': 'Allocation (hrs/wk)',
  'projects.team.add.startDate': 'Start date',
  'projects.team.add.endDate': 'End date',
  'projects.team.add.errPerson': 'Pick a person.',
  'projects.team.add.errAllocation':
    'Allocation must be a whole number of hours between 0 and 168.',
  'projects.team.add.errDates': 'Start date must be on or before the end date.',
  'projects.team.add.errServer': 'Adding the member failed.',
  'projects.team.add.saving': 'Adding…',
  'projects.team.add.submit': 'Add member',
  'projects.team.role.LEAD': 'Lead',
  'projects.team.role.CONTRIBUTOR': 'Contributor',
  'projects.team.role.REVIEWER': 'Reviewer',
  'projects.team.role.STAKEHOLDER': 'Stakeholder',

  // Projects — resources tab (manual upload/delete)
  'resources.uploadFolder': 'Upload folder',
  'resources.maxPerFile': 'max {mb} MB per file',
  'projects.resources.dropHint':
    'Drag and drop files or whole folders here, or click to choose files',
  'projects.resources.savedIn': 'Saved in {folder}',
  'projects.resources.invoicesDerived':
    'The Invoices folder is generated automatically from invoices; the file will be saved in Other resources.',
  'projects.resources.uploadFailed': 'Upload failed.',
  'projects.resources.deleteFile': 'Delete file',
  'projects.resources.deleteConfirm': 'Delete file "{name}"?',
  'projects.resources.deleteFailed': 'Failed to delete the file.',
  'projects.resources.tooLarge': 'Over the {mb} MB per-file limit, not uploaded: {names}.',
  'projects.resources.someFailed': 'Could not upload: {names}.',
  'projects.resources.folderExists': 'A folder with this name already exists here.',
  'projects.resources.folderCreateFailed': 'The folder could not be created.',
  'projects.resources.deleteFolderConfirm': 'Delete the empty folder "{name}"?',
  'projects.resources.deleteFolderFailed': 'Failed to delete the folder.',
  'projects.resources.cat.CONTRACT': 'Contracts',
  'projects.resources.cat.DESIGN': 'Design',
  'projects.resources.cat.OTHER': 'Other resources',

  // Upload progress — shared by every upload surface (dropzones, composers)
  'upload.progressLabel': 'Upload progress',
  'upload.uploading': 'Uploading…',
  'upload.processing': 'Finishing on the server…',
  'upload.processingFile': 'Processing the file…',
  'upload.processingStatement': 'Reading the transactions from the PDF…',
  'upload.processingTranscript': 'Transcribing the recording…',
  'upload.processingExtract': 'The AI is extracting the document data…',

  // Projects — financials tab
  'projects.fin.revenue': 'Revenue',
  'projects.fin.expenses': 'Expenses',
  'projects.fin.budgetUsed': '{percent}% of budget',
  'projects.fin.profitLoss': 'Profit / Loss',
  'projects.fin.margin': 'Margin {percent}%',
  'projects.fin.budget': 'Budget',
  'projects.fin.budgetRemaining': '{amount} remaining',
  'projects.fin.smartbillTitle': 'SmartBill — associated invoices',
  'projects.fin.smartbillEmpty': 'No SmartBill invoices associated with this project yet.',
  'projects.fin.chart.revenueVsExpenses': 'Revenue vs. Expenses',
  'projects.fin.chart.noMonthly': 'No monthly data to display.',
  'projects.fin.chart.expensesByCategory': 'Expenses by category',
  'projects.fin.chart.noExpenses': 'No expenses to break down.',
  'projects.fin.chart.profitTrend': 'Profit / loss trend',
  'projects.fin.chart.noTrend': 'No trend data yet.',
  'projects.fin.chart.cumulativeProfit': 'Cumulative profit',
  'projects.fin.chart.revVsExpTrend': 'Revenue vs. expenses trend',
  'projects.fin.chart.cashflow': 'Cashflow — inflows vs. outflows & cumulative balance',
  'projects.fin.chart.noCashflow': 'No cashflow data yet.',
  'projects.fin.chart.income': 'Inflows',
  'projects.fin.chart.outcome': 'Outflows',
  'projects.fin.chart.balance': 'Cumulative balance',
  'projects.fin.chart.expenseTrend': 'Expense evolution by category (stacked)',
  'projects.fin.chart.noExpenseTrend': 'No expenses to display over time.',
  'projects.fin.transactions': 'Transactions',
  'projects.fin.record': 'record',
  'projects.fin.records': 'records',
  'projects.fin.noEntries': 'No financial records yet.',
  'projects.fin.col.date': 'Date',
  'projects.fin.col.type': 'Type',
  'projects.fin.col.category': 'Category',
  'projects.fin.col.description': 'Description',
  'projects.fin.col.amount': 'Amount',
  'projects.fin.cost': 'Expense',
  'projects.fin.income': 'Income',
  'projects.fin.addEntryWithAI': 'Add a financial record with AI',
  'projects.fin.issued.title': 'Invoices issued for this project',
  'projects.fin.issued.invoice': 'invoice',
  'projects.fin.issued.invoices': 'invoices',
  'projects.fin.issued.total': 'Invoiced (sent to ANAF):',
  'projects.fin.issued.empty':
    'No issued invoice is linked to this project. Pick the project in the invoice editor, under “Project (internal only)”.',
  'projects.fin.issued.col.number': 'Number',
  'projects.fin.issued.col.customer': 'Customer',
  'projects.fin.issued.col.company': 'Issuing firm',
  'projects.fin.issued.col.status': 'Status',
  'projects.fin.issued.draft': 'draft',
  'projects.fin.issued.storno': 'storno',
  'projects.company.mainFirm': "The project's main firm. Any firm can invoice the project.",

  // Tasks — view chrome (CLE-278)
  'tasks.title': 'Tasks',
  'tasks.subtitle': '{count} tasks · {hours} estimated',
  'tasks.statusBadge': '{label}: {count} tasks · {hours} estimated',
  'tasks.view.list': 'List',
  'tasks.view.board': 'Board',
  'tasks.labels': 'Labels',
  'tasks.new': 'New task',
  // Tasks — apply a task template to the current project
  'tasks.applyTemplate.button': 'Apply task template',
  'tasks.applyTemplate.title': 'Apply a task template',
  'tasks.applyTemplate.subtitle':
    "The template's tasks are created in this project, in template order.",
  'tasks.applyTemplate.field': 'Template',
  'tasks.applyTemplate.placeholder': 'Pick a template…',
  'tasks.applyTemplate.itemCount': '{count} tasks',
  'tasks.applyTemplate.preview': '{count} tasks will be created',
  'tasks.applyTemplate.submit': 'Apply template',
  'tasks.applyTemplate.applied': '{count} tasks were created from the template.',
  'tasks.applyTemplate.none': 'No templates yet. Create one in Settings → Task templates.',
  'tasks.applyTemplate.loadError': 'Could not load the templates.',
  'tasks.applyTemplate.error': 'Applying the template failed. Try again.',
  'tasks.applyTemplate.forbidden': 'You are not allowed to create tasks in this project.',
  'tasks.searchPlaceholder': 'Search title, description, comments, labels…',
  'tasks.allStatuses': 'All statuses',
  'tasks.allPriorities': 'All priorities',
  'tasks.allProjects': 'All projects',
  'tasks.allClients': 'All clients',
  'tasks.allLabels': 'All labels',
  'tasks.allDepartments': 'All departments',
  'tasks.allAssignees': 'All assignees',
  'tasks.sort.newest': 'Newest',
  'tasks.sort.oldest': 'Oldest',
  'tasks.sort.priority': 'Priority (high→low)',
  'tasks.sort.dueDate': 'Due date (soonest)',
  'tasks.sort.estimate': 'Estimate (largest)',
  'tasks.sort.label': 'Sort by',
  'tasks.filter.search': 'Search',
  'tasks.filter.status': 'Status',
  'tasks.filter.priority': 'Priority',
  'tasks.filter.project': 'Project',
  'tasks.filter.client': 'Client',
  'tasks.filter.department': 'Department',
  'tasks.filter.labels': 'Labels',
  'tasks.filter.dueFrom': 'Due from',
  'tasks.filter.dueTo': 'Due until',
  'tasks.statusCategory.BACKLOG': 'Backlog',
  'tasks.statusCategory.UNSTARTED': 'Unstarted',
  'tasks.statusCategory.STARTED': 'Started',
  'tasks.statusCategory.COMPLETED': 'Completed',
  'tasks.statusCategory.CANCELLED': 'Cancelled',
  'tasks.clearFilters': 'Clear filters',
  'tasks.moreFilters': 'More filters',
  'tasks.error.list': 'Failed to load tasks.',
  'tasks.empty.filtered': 'No task matches the selected filters.',
  'tasks.empty.title': 'No tasks yet.',
  'tasks.empty.hintProject': 'Start by creating the first task for this project.',
  'tasks.empty.hintGlobal': 'Create your first task to get started.',
  'tasks.empty.cta': 'Create your first task',
  'tasks.pageInfo': 'Page {page} of {totalPages} · {total} tasks',
  'tasks.prev': 'Previous',
  'tasks.next': 'Next',
  'tasks.createTitle': 'New task',
  'tasks.createSubmit': 'Create task',
  'tasks.createError': 'Failed to create the task.',
  'tasks.ai.label': 'Generate with AI',
  'tasks.ai.placeholder': 'Describe the task (e.g. "urgent task for Friday: prepare offer X")',
  'tasks.ai.generate': 'Generate',
  'tasks.ai.generating': 'Generating…',
  'tasks.ai.applied': 'The draft was filled in below — review and save.',
  'tasks.ai.budgetExceeded': "This month's AI budget is exhausted. Fill in the form manually.",
  'tasks.ai.notConfigured': 'AI is not configured. Fill in the form manually.',
  'tasks.ai.failed': 'Draft generation failed. Try again or fill in the form manually.',
  'tasks.addToColumn': 'Add task',
  'tasks.quickAdd.pickProject': 'Pick a project…',
  'tasks.quickAdd.pickProjectFirst': 'Pick a project first…',
  'tasks.quickAdd.titlePlaceholder': 'Task title…',
  'tasks.list.movedMany': '{count} tasks moved to {label}',
  'tasks.list.movedOne': '{ref} moved to {label}',
  'tasks.list.taskFallback': 'Task',
  'tasks.list.dropToMove': 'Drop to move to {label}',
  'tasks.list.noTask': 'No tasks',
  'tasks.list.expand': 'Expand',
  'tasks.list.collapse': 'Collapse',
  'tasks.list.addTo': 'Add task to {label}',
  'tasks.list.ghostCount': '{count} tasks',
  'tasks.row.estimate': 'Estimate',
  'tasks.row.menu': 'More actions',
  'tasks.row.delete': 'Delete',
  'tasks.row.confirmDelete': 'Delete this task?',
  'tasks.row.deleted': 'Task deleted',
  'tasks.row.deleteFailed': 'Delete failed',
  'tasks.row.duplicate': 'Duplicate',
  'tasks.duplicate.title': 'Duplicate task',
  'tasks.duplicate.hint':
    'Creates a new task with its own identifier from {identifier}: description, status, priority, assignee, due date, estimate and labels. Comments and logged time are not copied.',
  'tasks.duplicate.defaultTitle': '{title} (copy)',
  'tasks.duplicate.newTitle': 'New task title',
  'tasks.duplicate.project': 'Project',
  'tasks.duplicate.otherProject':
    'The copy gets the chosen project’s identifier; a sub-issue becomes a top-level task there.',
  'tasks.duplicate.includeSubtasks': 'Copy the sub-issues too ({count})',
  'tasks.duplicate.submit': 'Duplicate',
  'tasks.duplicate.working': 'Duplicating…',
  'tasks.duplicate.failed': 'Duplicating failed.',
  'tasks.duplicate.done': 'Task {identifier} created.',
  'tasks.quickAdd.projectShort': 'Project…',
  'tasks.quickAdd.newTitle': 'New task…',
  'tasks.quickAdd.failed': 'Failed',

  // Task hover preview (calendar chips)
  'taskPreview.status': 'Status',
  'taskPreview.priority': 'Priority',
  'taskPreview.assignee': 'Assignee',
  'taskPreview.unassigned': 'Unassigned',
  'taskPreview.due': 'Due',
  'taskPreview.noDue': 'No due date',
  'taskPreview.estimate': 'Estimate',
  'taskPreview.logged': '{spent} logged',
  'taskPreview.labels': 'Labels',

  // Tasks — priority labels
  'tasks.priority.NONE': 'No priority',
  'tasks.priority.URGENT': 'Urgent',
  'tasks.priority.HIGH': 'High',
  'tasks.priority.MEDIUM': 'Medium',
  'tasks.priority.LOW': 'Low',

  // Daily summary
  'daily.title': 'Daily Summary',
  'daily.date': 'Date',
  'daily.exportPdf': 'Export PDF',
  'daily.exporting': 'Exporting…',
  'daily.sendEmail': 'Send email',
  'daily.sendingEmail': 'Sending…',
  'daily.exportFailed': 'Export failed',
  'daily.emailSent': 'Email sent successfully to {to}',
  'daily.emailFailed': 'Failed to send email',
  'daily.loadError': 'Failed to load the daily summary.',
  'daily.allUsers': 'All users',
  'daily.allUsersOverview': 'All users (overview)',
  'daily.tasks': 'Tasks',
  'daily.tasks.empty': 'No tasks for this period.',
  'daily.tasks.mine': 'My tasks',
  'daily.tasks.team': 'Team tasks',
  'daily.bucket.overdue': 'Overdue',
  'daily.bucket.today': 'Today',
  'daily.bucket.tomorrow': 'Tomorrow',
  'daily.bucket.next': 'Next 2-3 days',
  'daily.calendar': 'Calendar',
  'daily.calendar.empty': 'No events for this day.',
  'daily.calendar.allDay': 'All day',
  'daily.event.conference': 'Conference',
  'daily.event.speech': 'Speech',
  'daily.event.team': 'Team',
  'daily.event.admin': 'Admin',
  'daily.event.personal': 'Personal',
  'daily.event.client': 'Clients',
  'daily.recommendations': 'AI Recommendations',
  'daily.allTasks': 'All tasks',

  // Daily status check-ins
  'dailyStatus.title': 'My status today',
  'dailyStatus.titleOther': "Today's status · {name}",
  'dailyStatus.progress': '{done}/{total} completed',
  'dailyStatus.report': 'Report',
  'dailyStatus.empty': 'No tasks for today. You can add one from Tasks.',
  'dailyStatus.loadError': 'Could not load the daily status.',
  'dailyStatus.readOnly': 'only the current day can be filled in',
  'dailyStatus.unreported': 'status missing',
  'dailyStatus.hours': '{hours}h',
  'dailyStatus.status.DONE': 'Done',
  'dailyStatus.status.IN_PROGRESS': 'In progress',
  'dailyStatus.status.BLOCKED': 'Blocked',
  'dailyStatus.status.NOT_STARTED': 'Not started',
  'dailyStatus.reason.due_today': 'Today',
  'dailyStatus.reason.overdue': 'Overdue',
  'dailyStatus.reason.started': 'In progress',
  'dailyStatus.reason.worked_on': 'Worked on today',
  'dailyStatus.reason.completed_today': 'Completed today',
  'dailyStatus.reason.checked_in': 'Checked in',
  'dailyStatus.commentPlaceholder':
    'What did you do on it today? (optional) — photos and files welcome',
  'dailyStatus.blockedPlaceholder': 'Why is it blocked? (required)',
  'dailyStatus.saving': 'Saving…',
  'dailyStatus.saved': 'Saved ✓',
  'dailyStatus.saveError': 'Error, retry',
  'dailyStatus.section.done': 'Done',
  'dailyStatus.section.inProgress': 'In progress',
  'dailyStatus.section.blocked': 'Blocked',
  'dailyStatus.section.notStarted': 'Not started',
  'dailyStatus.drawer.title': 'Daily report',
  'dailyStatus.drawer.copyMd': 'Copy Markdown',
  'dailyStatus.drawer.copied': 'Copied!',
  'dailyStatus.drawer.copyFailed': 'Could not copy to the clipboard.',
  'dailyStatus.drawer.pdf': 'Download PDF',
  'dailyStatus.drawer.sendEmail': 'Send by email',
  'dailyStatus.drawer.sent': 'Sent to {to}',
  'dailyStatus.drawer.persisted': 'snapshot from {time}',
  'dailyStatus.drawer.live': 'generated now',
  'dailyStatus.drawer.history': 'History (14 days)',
  'dailyStatus.drawer.hoursToday': 'Hours logged: {hours}',
  'dailyStatus.drawer.unreportedCount': 'Missing status: {count}',
  'dailyStatus.drawer.noTasks': 'No tasks today.',
  'dailyStatus.team.title': 'Team daily report',
  'dailyStatus.team.reported': 'Reported {reported}/{members}',
  'dailyStatus.team.notReported': 'Not reported',
  'dailyStatus.team.blocked': 'Blockers',
  'dailyStatus.team.hours': 'Hours',
  'dailyStatus.team.sendAll': 'Send the team report now',
  'dailyStatus.team.sentAll': '{individual} individual emails, {team} team emails',
  'dailyStatus.team.idle': 'No tasks today: {names}',
  'dailyStatus.team.personal': '+{count} personal tasks',
  'dailyStatus.card.reported': 'Reported {done}/{total}',
  'dailyStatus.card.notReported': 'Not reported',
  'dailyStatus.bar.text': 'You have {count} tasks without a status today',
  'dailyStatus.bar.title': 'Fill in your daily report',
  'dailyStatus.bar.pending': '{count} tasks without a status',
  'dailyStatus.pickStatus': 'Pick a status',
  'dailyStatus.clearStatus': 'No status',
  'dailyStatus.note': 'Note',
  'dailyStatus.addComment': 'Add a comment',
  'dailyStatus.editComment': 'Edit the comment',
  'dailyStatus.saveComment': 'Save',
  'dailyStatus.cancel': 'Cancel',
  'dailyStatus.bar.cta': 'Fill in',
  'dailyStatus.bar.dismiss': 'Dismiss for today',
  'daily.allTasks.empty': 'No active tasks.',
  'daily.allTasks.filtered': 'No tasks match the selected filters.',
  'daily.filter.project': 'Project',
  'daily.due': 'Due',
  'daily.priority': '{priority} priority',
  'daily.business.empty': 'No active team tasks for this period.',
  'daily.business.noTasks': 'No tasks ({count})',
  // Task status labels (shared by daily/weekly)
  'status.BACKLOG': 'Backlog',
  'status.TODO': 'To do',
  'status.IN_PROGRESS': 'In progress',
  'status.IN_REVIEW': 'In review',
  'status.QA': 'QA',
  'status.DONE': 'Done',
  'status.BLOCKED': 'Blocked',
  'status.CANCELLED': 'Cancelled',

  // Weekly summary
  'weekly.title': 'Weekly Summary',
  'weekly.allUsers': 'All users',
  'weekly.user': 'User',
  'weekly.loadError': 'Failed to load the weekly summary.',
  'weekly.empty': 'No status changes recorded for this period.',
  'weekly.viewLabel': 'View',
  'weekly.weekLabel': 'Week',
  'weekly.thisWeek': 'Go to the current week',
  'weekly.statusesSelected': '{count} statuses',
  'weekly.stats.title': 'The week in numbers',
  'weekly.view.day': 'By day',
  'weekly.view.status': 'By status',
  'weekly.stats.totalChanged': 'Total changed',
  'weekly.stats.done': 'Completed',
  'weekly.stats.inProgress': 'In progress',
  'weekly.stats.newlyStarted': 'Newly started',
  'weekly.stats.blocked': 'Blocked',
  'weekly.created': 'Created',
  'weekly.movedTo': 'moved to {status}',
  'weekly.movedFromTo': 'from {from} to {to}',
  'weekly.fromWord': 'from',
  'weekly.toWord': 'to',
  'weekly.createdAs': 'created straight into {to}',
  'weekly.taskChangedOne': '{count} task changed',
  'weekly.taskChangedMany': '{count} tasks changed',
  'weekly.noMatchingChanges': 'No matching changes this week',
  'weekly.noStatusChanges': 'No status changes',
  'weekly.prevWeek': 'Previous week',
  'weekly.prevWeekShort': 'Prev. week',
  'weekly.nextWeek': 'Next week',
  'weekly.nextWeekShort': 'Next week',

  // Chat / Firmio AI
  'chat.subtitle': 'Your financial assistant',
  'chat.model': 'Model',
  'chat.modelAria': 'AI model',
  'chat.modelTitle': 'Choose the AI model',
  'chat.modelLabel': 'Model: {model}',
  'chat.newConversation': 'New conversation',
  'chat.searchConversations': 'Search conversations…',
  'chat.noResults': 'No results.',
  'chat.noConversations': 'No conversations yet.',
  'chat.untitled': 'Untitled conversation',
  'chat.backToApp': 'Back to the app',
  'chat.pin': 'Pin',
  'chat.actions': 'Conversation actions',
  'chat.unpin': 'Unpin',
  'chat.rename': 'Rename',
  'chat.delete': 'Delete',
  'chat.save': 'Save',
  'chat.send': 'Send',
  'chat.stop': 'Stop',
  'chat.attach': 'Attach file',
  'chat.attachTitle': 'Attach invoice (PDF, PNG, JPG)',
  'chat.remove': 'Remove',
  'chat.placeholder': 'Type a message or drop an invoice here… (Enter to send)',
  'chat.placeholderMobile': 'Type a message…',
  'chat.intro': 'Give a command in natural language. For example:',
  'chat.demoLocked':
    'Chatting with Firmio AI is available on paid accounts only. The demo account can explore the rest of the app.',
  'chat.demoPlaceholder': 'Available on paid accounts only',
  'chat.uploading': 'Uploading…',
  'chat.notConfigured.before': 'The AI assistant is not configured. Set',
  'chat.notConfigured.after': 'in the backend environment to enable it.',
  'chat.deleteConfirm': 'Delete the conversation “{title}”?',
  'chat.error.load': 'The conversation could not be loaded',
  'chat.error.pin': 'The conversation could not be pinned',
  'chat.error.rename': 'The conversation could not be renamed',
  'chat.error.delete': 'The conversation could not be deleted',
  'chat.error.unsupported': 'Unsupported type: {name}. Only PDF, PNG, JPG are accepted.',
  'chat.error.tooLarge': '{name} exceeds 10MB.',
  'chat.error.upload': 'Upload failed',
  'chat.budgetExceededLink': 'Go to Settings → AI Budget',
  'chat.noAccess':
    'Your account has no access to Firmio AI. An administrator can grant it under Settings → Roles & permissions.',
  'chat.attachedFile': '{count} file attached',
  'chat.attachedFiles': '{count} files attached',
  'chat.suggestion.financial': 'What is the financial status this month?',
  'chat.suggestion.team': 'Show me the team allocation across projects',
  'chat.suggestion.unpaid': 'Which invoices are unpaid?',
  'chat.suggestion.payments': 'What payments do we need to make?',
  'chat.processAttachments': 'Process the attached files.',
  'chat.relative.now': 'just now',
  'chat.relative.minuteOne': '{count} minute ago',
  'chat.relative.minuteMany': '{count} minutes ago',
  'chat.relative.hourOne': '{count} hour ago',
  'chat.relative.hourMany': '{count} hours ago',
  'chat.relative.yesterday': 'yesterday',
  'chat.relative.daysMany': '{count} days ago',

  // Knowledge Base
  'kb.title': 'Knowledge Base',
  'kb.subtitle': "The company's smart wiki · Markdown-first · Semantic search",
  'kb.demoContent': 'Demo content',
  'kb.categories': 'Categories',
  'kb.viewAggregated': 'View aggregated notes',
  'kb.search': 'Search the Knowledge Base… (semantic)',
  'kb.clearSearch': 'Clear search',
  'kb.filterCategory': 'Category filter',
  'kb.filterAuthor': 'Author filter',
  'kb.allCategories': 'All categories',
  'kb.allAuthors': 'All authors',
  'kb.resultsRanked': '{count} results · ranked by relevance',
  'kb.resultRanked': '{count} result · ranked by relevance',
  'kb.semantic': 'semantic',
  'kb.noResults': 'No results. Try other terms.',
  'kb.toc': 'Table of contents',
  'kb.action.edit': 'Edit',
  'kb.action.share': 'Share',
  'kb.action.print': 'Print',
  'kb.action.exportPdf': 'Export PDF',
  'kb.version': 'Version {version}',
  'kb.created': 'Created {date}',
  'kb.updated': 'Updated {date}',
  'kb.related': 'Related documents',

  // Login
  'login.title': 'Sign in',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.demo': 'Or try demo mode →',
  'login.noAccount': "Don't have an account?",
  'login.register': 'Register',
  'login.failed': 'Sign in failed',
  'instance.suspended.title': 'Access suspended',
  'instance.suspended.defaultMessage': 'Access to Firmio has been suspended for this company.',
  'instance.suspended.contact': 'To reactivate it, please contact your Firmio provider.',
  'instance.suspended.retry': 'Try again',
  'instance.suspended.checking': 'Checking…',
  'instance.suspended.still': 'Access is still suspended.',
  'login.demoUnavailable': 'Demo Mode unavailable',
  'login.forgotPassword': 'Forgot your password?',
  'login.emailNotVerified': 'This account email is not verified.',
  'login.resendVerification': 'Resend verification email',
  'login.verificationSent': 'Email sent. Check your inbox.',
  'login.tagline': 'Your entire business. In one place.',
  'login.subtitle': 'Projects, clients, invoices, team — connected through an AI assistant.',
  'login.feature.1': 'Projects, team & tasks',
  'login.feature.2': 'Invoices, SmartBill & financials',
  'login.feature.3': 'AI assistant that learns your business',
  'login.integrations': 'Integrated with',
  'login.poweredBy': 'A Clean Code Culture product',
  'legal.link': 'Terms and privacy',
  'legal.registerNotice': 'By creating an account you accept the',
  'legal.registerLink': 'terms and privacy policy',

  // Mobile app: organization code (first launch) and switching organization
  'connect.title': 'Connect to your organization',
  'connect.subtitle': 'Enter the organization code you got from your company administrator.',
  'connect.codeLabel': 'Organization code',
  'connect.codePlaceholder': 'e.g. my-company',
  'connect.submit': 'Continue',
  'connect.checking': 'Checking…',
  'connect.advanced': 'Advanced: server address',
  'connect.advancedHide': 'Use the organization code',
  'connect.urlLabel': 'Server address',
  'connect.urlPlaceholder': 'https://company.firmio.ro',
  'connect.urlHint': 'Test builds only. Local example: http://localhost:24002',
  'connect.confirmTitle': 'You are connecting to',
  'connect.confirm': 'Continue',
  'connect.back': 'Back',
  'connect.suspended': "This organization's access is currently suspended.",
  'connect.help': "Don't have a code? Ask your company administrator.",
  'connect.error.invalidCode':
    'The code has only lowercase letters, digits and hyphens (e.g. my-company).',
  'connect.error.invalidUrl': 'The address is not valid. Example: https://company.firmio.ro',
  'connect.error.notFound': 'No organization was found with this code.',
  'connect.error.rateLimited': 'Too many attempts. Try again in a few minutes.',
  'connect.error.offline': 'You are offline.',
  'connect.error.consoleUnreachable': 'The code could not be checked right now. Try again shortly.',
  'connect.error.serverUnreachable':
    "The organization's server is not responding. Try again later.",
  'connect.error.notFirmio': 'No Firmio server answers at this address.',
  'login.orgLabel': 'Organization',
  'login.orgChange': 'Change organization',
  'profile.orgTitle': 'Organization',
  'profile.orgCode': 'Code',
  'profile.orgServer': 'Server',
  'profile.orgChange': 'Change organization',
  'profile.orgConfirmTitle': 'Change organization?',
  'profile.orgConfirmBody':
    "You will be signed out of {name}. This organization's notifications stop on this phone.",
  'profile.orgConfirmAction': 'Change',
  'profile.orgLeaving': 'Signing out…',

  // Register
  'register.title': 'Create account',
  'register.name': 'Name',
  'register.email': 'Email',
  'register.password': 'Password',
  'register.submit': 'Create account',
  'register.submitting': 'Creating…',
  'register.hasAccount': 'Already have an account?',
  'register.login': 'Sign in',
  'register.passwordMin': 'Password must be at least 8 characters.',
  'register.failed': 'Registration failed',
  'register.checkEmail.title': 'Check your email',
  'register.checkEmail.body':
    'We sent a verification link to {email}. Open it to activate your account.',
  'register.checkEmail.backToLogin': 'Back to sign in',

  // Auth flows (forgot / reset / verify / invite)
  'auth.forgot.title': 'Forgot your password?',
  'auth.forgot.subtitle': "We'll send a reset link to your email.",
  'auth.forgot.submit': 'Send link',
  'auth.forgot.submitting': 'Sending…',
  'auth.forgot.success': 'If the address exists, you will receive an email with the reset link.',
  'auth.forgot.backToLogin': 'Back to sign in',
  'auth.reset.title': 'Set a new password',
  'auth.reset.password': 'New password',
  'auth.reset.confirm': 'Confirm password',
  'auth.reset.mismatch': 'Passwords do not match',
  'auth.reset.weak': 'Password must be at least 8 characters, with an uppercase letter and a digit',
  'auth.reset.submit': 'Save password',
  'auth.reset.submitting': 'Saving…',
  'auth.reset.success': 'Your password has been changed. You can sign in now.',
  'auth.reset.goToLogin': 'Go to sign in',
  'auth.reset.failed': 'The reset link is invalid or expired.',
  'auth.verify.title': 'Email verification',
  'auth.verify.pending': 'Verifying your email…',
  'auth.verify.success': 'Email verified! Redirecting…',
  'auth.verify.failed': 'The verification link is invalid or expired.',
  'auth.verify.resendEmailLabel': 'Account email',
  'auth.verify.resend': 'Resend verification email',
  'auth.verify.resent': 'Email sent. Check your inbox.',
  'auth.invite.title': 'Welcome!',
  'auth.invite.greeting': 'Hi, {name}! Set your password for the {email} account.',
  'auth.invite.invalid': 'The invite link is invalid or expired.',
  'auth.invite.loading': 'Validating invite…',
  'auth.invite.password': 'Password',
  'auth.invite.confirm': 'Confirm password',
  'auth.invite.submit': 'Activate account',
  'auth.invite.submitting': 'Activating…',

  // Settings
  'settings.title': 'Settings',
  'settings.subtitle': 'Configure the application integrations and nomenclatures.',
  'settings.tab.companies': 'Companies',
  'settings.tab.bankAccounts': 'Bank accounts',
  'settings.tab.integrations': 'Integrations',
  'settings.tab.aiBudget': 'AI Budget',
  'settings.tab.categories': 'Expense categories',
  'settings.tab.supplierCategories': 'Supplier categories',
  'settings.tab.subscriptionCategories': 'Subscription categories',
  'settings.tab.bankDefaults': 'Bank statement defaults',
  'settings.tab.leads': 'Pipelines',
  'settings.tab.departments': 'Departments',
  'settings.tab.services': 'Services',
  'settings.tab.taskStatuses': 'Task statuses',
  'settings.tab.taskTemplates': 'Task templates',
  'settings.tab.emailTemplates': 'Email templates',
  'settings.tab.emailHistory': 'Email history',
  'settings.tab.team': 'Team management',
  'settings.tab.roles': 'Roles and permissions',
  'settings.tab.modules': 'Modules',
  'settings.modules.title': 'App modules',
  'settings.modules.description':
    "Turn modules on or off for everyone. A disabled module disappears from the menu and cannot be used, not even by admins; turning it back on restores everyone's permissions as they were.",
  'settings.modules.core': 'Core module, cannot be turned off.',
  'settings.modules.loadError': 'The module list could not be loaded.',
  'settings.modules.consoleLocked':
    "Not included in this instance's subscription. Contact Firmio to enable it.",
  'settings.modules.parentOff': 'Off together with the module it belongs to.',
  'settings.tab.kb': 'Knowledge Base',
  'settings.section': 'Section',
  'settings.tab.pushNotifications': 'Push notifications',
  'settings.tab.alerts': 'Alerts',
  'settings.tab.onboarding': 'Onboarding',

  // Push notifications (Settings → Push notifications)
  'alerts.intro':
    'Problems Firmio cannot fix on its own: an external service refused its requests. Each alert says what stopped working and what to do.',
  'alerts.deliveryHint':
    'Admins get every new alert by push notification and email, then once a day while it stays open.',
  'alerts.loading': 'Loading alerts…',
  'alerts.loadError': 'Could not load the alerts',
  'alerts.openTitle': 'Open ({count})',
  'alerts.none': 'No open alerts. Every service answered normally at the last check.',
  'alerts.resolvedTitle': 'Recently resolved',
  'alerts.severity.critical': 'Critical',
  'alerts.severity.warning': 'Warning',
  'alerts.count': '{count} times',
  'alerts.firstSeen': 'First seen:',
  'alerts.lastSeen': 'Last seen:',
  'alerts.resolvedAt': 'Resolved:',
  'alerts.acknowledgedBy': 'seen by {name}',
  'alerts.openProvider': 'Open the provider account',
  'alerts.acknowledge': 'Seen',
  'alerts.resolve': 'Mark resolved',
  'push.title': 'Push notifications',
  'push.description':
    'Get a notification on your phone or computer when a task is assigned to you, when someone writes in chat, or when a voice conversation starts.',
  'push.notConfigured':
    'Push notifications are not configured on this installation. An administrator needs to add the VAPID keys on the server.',
  'push.unsupported': 'This browser does not support push notifications.',
  'push.desktopActive': 'Notifications are on for this computer.',
  'push.desktopActiveHint':
    'The Firmio app shows them as system notifications, even with its window closed to the tray.',
  'push.desktopPaused':
    'Notifications are paused from the Firmio tray icon menu. Tick “Notificări” there to turn them back on.',
  'push.enableHere': 'Turn on notifications on this device',
  'push.disableHere': 'Turn off notifications on this device',
  'push.onHere': 'Notifications are on for this device.',
  'push.offHere': 'Notifications are off for this device.',
  'push.deviceCount': '{count} devices connected',
  'push.enabledNotice': 'Done. Send yourself a test to be sure.',
  'push.sendTest': 'Send me a test notification',
  'push.testSent': 'It is on its way. It should arrive in a few seconds.',
  'push.errors.denied':
    'The browser blocked notifications and will not ask again. You can turn them back on from the browser settings, under this site permissions.',
  'push.errors.notConfigured': 'The server has no notification keys configured.',
  'push.categoriesTitle': 'What to notify you about',
  'push.categoriesHint': 'These belong to your account and apply on every device.',
  'push.category.tasks': 'Tasks assigned to me',
  'push.category.tasksHint': 'When someone assigns you a new task or hands one over to you.',
  'push.category.chat': 'Chat messages',
  'push.category.chatHint': 'Direct messages and messages in the text channels you are in.',
  'push.category.voice': 'Voice channels',
  'push.category.voiceHint': 'Only when someone starts a conversation in an empty channel.',
  'push.category.alerts': 'System alerts',
  'push.category.alertsHint':
    'When an external service fails: OpenAI out of credit, invalid key, ANAF or Drive unauthorised, email refused. Admins only.',
  'push.category.meetings': 'Video meetings (Meet)',
  'push.category.meetingsHint':
    'A few minutes before a meeting starts, and when a guest is waiting to be admitted.',
  'push.category.leaves': 'Leave',
  'push.category.leavesHint':
    'When someone files a request you can approve, and when yours is answered.',
  'push.iosTitle': 'On iPhone, install the app first',
  'push.iosBody':
    'Apple only allows notifications from the app added to your Home Screen, not from a regular Safari tab. It takes a minute.',
  'push.iosStep1': 'Tap the Share button in the Safari bottom bar.',
  'push.iosStep2': 'Choose "Add to Home Screen".',
  'push.iosStep3': 'Open Firmio from the Home Screen and come back here.',
  'push.sendTestNeedsEnable':
    'Turn notifications on for this device first, so the test has somewhere to arrive.',
  'push.soundsTitle': 'In-app sounds',
  'push.soundsHint':
    'These play while the app is open. On a phone, notifications that arrive with the app closed use the same sounds; on a computer their sound belongs to the operating system.',
  'push.soundsToggle': 'Sounds for messages, meetings, tasks and voice',
  'push.soundsToggleHint': 'Applies to this device.',
  'push.testSoundsLabel': 'Listen to the sounds:',
  'push.sound.message': 'New message',
  'push.sound.meeting': 'Meeting starting',
  'push.sound.task': 'New task',
  'push.sound.join': 'Someone joins voice',
  'push.sound.leave': 'Someone leaves voice',
  'push.sound.knock': 'Meet join request',
  'settings.tab.dailyStatus': 'Daily report',
  'settings.tab.bugReports': 'Reported bugs',

  // In-app bug reporting: the floating widget everyone sees, and the admin
  // triage view in Settings.
  'bugReport.widget.open': 'Report a bug',
  'bugReport.widget.close': 'Close',
  'bugReport.widget.title': 'Report a bug',
  'bugReport.widget.intro': 'Tell us what went wrong. A screenshot helps a lot.',
  'bugReport.field.title': 'Title',
  'bugReport.field.titlePlaceholder': 'In short, what happened',
  'bugReport.field.description': 'Description',
  'bugReport.field.descriptionPlaceholder':
    'What you were doing, what you expected, and what actually happened.',
  'bugReport.attachments.hint': 'Drop screenshots here, paste them with Ctrl+V, or pick files',
  'bugReport.attachments.pick': 'Attach a file',
  'bugReport.attachments.remove': 'Remove attachment',
  'bugReport.submit': 'Send',
  'bugReport.submitting': 'Sending…',
  'bugReport.error.generic': 'We could not send the report. Please try again.',
  'bugReport.error.upload': 'The file upload failed.',
  'bugReport.success.title': 'Thank you!',
  'bugReport.success.body': 'We filed it as {identifier}.',
  'bugReport.success.again': 'Report something else',

  'bugReports.intro':
    'Everything your team reported from the bug button: who, when, on which page, and with which screenshots.',
  'bugReports.search': 'Search title and description',
  'bugReports.allStatuses': 'All statuses',
  'bugReports.allPriorities': 'All priorities',
  'bugReports.clearFilters': 'Clear filters',
  'bugReports.column.identifier': 'Bug',
  'bugReports.column.title': 'Title',
  'bugReports.column.status': 'Status',
  'bugReports.column.priority': 'Priority',
  'bugReports.column.reporter': 'Reported by',
  'bugReports.column.createdAt': 'Reported at',
  'bugReports.empty': 'No bugs reported yet.',
  'bugReports.emptyFiltered': 'No bug matches these filters.',
  'bugReports.loadError': 'We could not load the bug list',
  'bugReports.pageOf': 'Page {page} of {totalPages} ({total} in total)',
  'bugReports.previous': 'Previous',
  'bugReports.next': 'Next',
  'bugReports.unknownReporter': 'Deleted user',
  'bugReports.delete': 'Delete report',
  'bugReports.menu': 'Actions',
  'bugReports.changeStatus': 'Change status',
  'bugReports.changePriority': 'Change priority',
  'bugReports.deleteComment': 'Delete comment',
  'bugReports.deleteConfirm': 'Permanently delete this report, comments included?',
  'bugReports.detail.attachments': 'Screenshots',
  'bugReports.detail.comments': 'Comments',
  'bugReports.detail.activity': 'Activity',
  'bugReports.detail.noComments': 'No comments yet.',
  'bugReports.detail.noActivity': 'No changes yet.',
  'bugReports.detail.commentPlaceholder': 'Write a comment…',
  'bugReports.detail.comment': 'Comment',
  'bugReports.detail.notFound': 'Report not found.',
  'bugReports.detail.loading': 'Loading the report…',
  'bugReports.props.title': 'Details',
  'bugReports.props.reporter': 'Reported by',
  'bugReports.props.createdAt': 'Reported at',
  'bugReports.props.resolvedAt': 'Resolved at',
  'bugReports.props.page': 'Page',
  'bugReports.props.browser': 'Browser',
  'bugReports.props.viewport': 'Viewport',
  'bugReports.props.appVersion': 'Version',
  'bugReports.activity.created': 'reported the bug',
  'bugReports.activity.changed': 'changed {field} from {from} to {to}',
  'bugReports.activity.field.status': 'the status',
  'bugReports.activity.field.priority': 'the priority',
  'bugReports.status.NEW': 'New',
  'bugReports.status.IN_PROGRESS': 'In progress',
  'bugReports.status.RESOLVED': 'Resolved',
  'bugReports.status.REJECTED': 'Rejected',
  'bugReports.priority.LOW': 'Low',
  'bugReports.priority.MEDIUM': 'Medium',
  'bugReports.priority.HIGH': 'High',
  'bugReports.priority.CRITICAL': 'Critical',
  'settings.dailyStatus.title': 'Team daily report',
  'settings.dailyStatus.description':
    'Reminder to fill in the status and the automatic end-of-day report (Monday–Friday). Times are in Europe/Bucharest.',
  'settings.dailyStatus.enabled': 'Active (reminder + automatic report)',
  'settings.dailyStatus.reminderTime': 'Reminder time',
  'settings.dailyStatus.reportTime': 'Report time',
  'settings.dailyStatus.sender': 'Team Chat reminder sender',
  'settings.dailyStatus.senderAuto': 'Automatic (first active administrator)',
  'settings.dailyStatus.senderHint':
    'The "[Reminder automat]" message is sent as a DM from this account.',
  'settings.dailyStatus.save': 'Save',
  'settings.dailyStatus.saved': 'Settings saved.',
  'settings.dailyStatus.runReminder': 'Send the reminder now',
  'settings.dailyStatus.runReport': 'Send the report now',
  'settings.dailyStatus.schedule': 'Scheduled: reminder {reminder}, report {report} ({zone})',
  'settings.dailyStatus.scheduleOff': 'The scheduler is not running in this process.',
  'settings.dailyStatus.nextReminder': 'next reminder {date}',
  'settings.dailyStatus.nextReport': 'next report {date}',
  'settings.dailyStatus.lastReminder': 'Last reminder',
  'settings.dailyStatus.lastReport': 'Last report',
  'settings.dailyStatus.lastManual': 'Last manual run',
  'settings.dailyStatus.testHint': 'Test to yourself only (marked [Test], no team roll-up):',
  'settings.dailyStatus.pushToApp': 'Show the reminder in the app to everyone',
  'settings.dailyStatus.pushHint':
    'The red reminder strip appears right away for every colleague with unreported tasks, whatever the time. No email, no chat message.',
  'settings.dailyStatus.pushed':
    'The reminder is now live in the app for {count} colleagues with unreported tasks: {names}',
  'settings.dailyStatus.testReminder': 'Send me the reminder',
  'settings.dailyStatus.testReport': 'Send me the report',
  'settings.unknownError': 'Unknown error.',

  // Settings → Email history (administrators only)
  'settings.emailHistory.intro':
    'Delivery log and open tracking for every email the platform sends.',
  'settings.emailHistory.statsError': 'Failed to load stats',
  'settings.emailHistory.loadError': 'Failed to load emails',
  'settings.emailHistory.pageOf': 'Page {page} of {totalPages} ({total} total)',
  'settings.emailHistory.previous': 'Previous',
  'settings.emailHistory.next': 'Next',

  // Settings → Companies (multi-company)
  'companies.description':
    'The firms of your account. The default firm drives the app brand, emails and invoices; data without an assigned firm belongs to the default firm.',
  'companies.add': 'Add firm',
  'companies.addTitle': 'New firm',
  'companies.editTitle': 'Edit firm',
  'companies.loading': 'Loading firms…',
  'companies.defaultBadge': 'Default',
  'companies.setDefault': 'Set as default',
  'companies.noCui': 'No CUI',
  'companies.saving': 'Saving…',
  'companies.legalNameRequired': 'The legal name is required.',
  'companies.deleteConfirm':
    'Delete firm “{name}”? Its clients, suppliers, projects, invoices and bank statements stay in the app but lose the firm association, and the firm’s SmartBill invoices are deleted. This cannot be undone.',
  'companies.deleted':
    'Firm “{name}” was deleted. Detached: {clients} clients, {suppliers} suppliers, {projects} projects, {invoices} invoices; {smartbill} SmartBill invoices deleted.',
  'companies.error.load': 'Firms could not be loaded.',
  'companies.error.generic': 'The operation failed.',
  'companies.detail.back': 'Back to firms',
  'companies.detail.notFound': 'The firm was not found.',
  'companies.detail.forbidden': 'You do not have permission to manage firms.',
  'companies.detail.dataHeading': 'Firm details',
  'companies.detail.saved': 'The firm details were saved.',
  'companies.detail.smartbillHeading': 'SmartBill integration',
  'companies.detail.efacturaHeading': 'ANAF e-Factura (SPV)',
  'companies.detail.efacturaHint':
    'Invoices received and sent through SPV, read-only. Save the CIF, then authorize the firm at ANAF from the browser holding the qualified certificate.',
  'companies.detail.smartbillHint':
    'SmartBill credentials are configured separately for each firm and are used when syncing its invoices.',

  // Companies — administrators / sole associate (mandate history)
  'companies.admins.heading': 'Administrators / Sole associate',
  'companies.admins.hint':
    'People holding an administrator or sole-associate mandate. Bank statement payments to them are flagged during the mandate window at reconciliation.',
  'companies.admins.add': 'Add mandate',
  'companies.admins.empty': 'No mandates recorded.',
  'companies.admins.person': 'Person',
  'companies.admins.personPlaceholder': 'Pick a person…',
  'companies.admins.role': 'Role',
  'companies.admins.role.ADMINISTRATOR': 'Administrator',
  'companies.admins.role.ASOCIAT_UNIC': 'Sole associate',
  'companies.admins.startDate': 'Mandate start',
  'companies.admins.endDate': 'Mandate end (optional)',
  'companies.admins.endMandate': 'End mandate',
  'companies.admins.periodActive': 'since {start} · active',
  'companies.admins.deleteConfirm': "Delete {name}'s mandate? This cannot be undone.",
  'companies.admins.overlapError':
    'This person already has a mandate overlapping the chosen period.',
  'companies.admins.personRequired': 'Pick a person.',
  'companies.admins.startRequired': 'The start date is required.',
  'companies.admins.paymentBadge': 'Payment to administrator: {name} — {role}',
  'companies.admins.loanBadge': 'Incoming payment from administrator: {name} — {role}',
  'companies.admins.personName': 'Person name',
  'companies.admins.personNameHint': 'Renaming the person propagates across the whole system.',

  // Bank statements — firm (multi-company)
  'bank.company.auto': 'Firm: automatic (by CUI)',
  'bank.company.noneWarning':
    'The firm could not be detected from the statement (missing or unknown CUI). The statement was assigned to the default firm — you can reassign it from the reconciliation page.',
  'bank.company.reassigned': 'The statement was reassigned to the selected firm.',
  'bank.company.reassignFailed': 'Reassigning the firm failed.',
  'bank.company.finalizedLocked': 'The statement is finalized — the firm can no longer be changed.',

  // Bank statements — deletion
  'bankStatements.delete.button': 'Delete statement',
  'bankStatements.filter.from': 'From',
  'bankStatements.filter.to': 'To',
  'bankStatements.filter.anyDate': 'Any date',
  'bankStatements.filter.clear': 'Clear period',
  'bankStatements.filter.empty': 'No statements in the selected period.',
  'bankStatements.filter.mode': 'Period',
  'bankStatements.filter.mode.all': 'All',
  'bankStatements.filter.mode.month': 'Month',
  'bankStatements.filter.mode.year': 'Year',
  'bankStatements.filter.mode.custom': 'Range',
  'bankStatements.filter.year': 'Year',
  'bankStatements.filter.month': 'Month',
  'bankStatements.delete.title': 'Delete this statement?',
  'bankStatements.delete.message':
    'The statement {name} and all its transactions will be deleted. Payments created in Financials during reconciliation will be removed, and invoices marked as paid by this statement revert to "Sent". This cannot be undone.',
  'bankStatements.delete.confirm': 'Delete permanently',
  'bankStatements.delete.success':
    'The statement was deleted ({financials} payments removed from Financials, {invoices} invoices reverted to "Sent").',
  'bankStatements.delete.error': 'Deleting the statement failed.',

  // AI budget
  'aiBudget.intro':
    'Track the monthly AI spend (chat, summaries, embeddings, transcriptions) and set a EUR cap. When the cap is reached, AI calls are stopped until next month or until you raise the limit.',
  'aiBudget.loading': 'Loading usage…',
  'aiBudget.loadError': 'Could not load the AI usage',
  'aiBudget.currentSpend': 'Spend in {month}',
  'aiBudget.ofLimit': 'of the {limit} € limit',
  'aiBudget.calls': '{count} AI calls',
  'aiBudget.progressLabel': 'Spend against the monthly limit',
  'aiBudget.warnTick': 'Admins get an alert at 80%',
  'aiBudget.usedPct': '{pct}% used',
  'aiBudget.remaining': '{amount} left',
  'aiBudget.overBy': 'over by {amount}',
  'aiBudget.noLimit': 'No limit set — usage is unlimited.',
  'aiBudget.limitLabel': 'Monthly limit (EUR)',
  'aiBudget.limitHint': 'Leave empty for unlimited usage.',
  'aiBudget.managedByFirmio':
    'The monthly limit comes with your Firmio subscription. Contact Firmio to change it.',
  'aiBudget.limitInvalid': 'The limit must be a positive number.',
  'aiBudget.save': 'Save limit',
  'aiBudget.saving': 'Saving…',
  'aiBudget.saved': 'The limit was saved.',
  'aiBudget.saveError': 'The limit could not be saved',
  'aiBudget.exceededBanner':
    'The monthly AI budget is exhausted. AI calls are stopped until next month or until you raise the limit.',
  'aiBudget.perModel': 'Per-model breakdown',
  'aiBudget.perModelEmpty': 'No AI calls recorded in the chosen period.',
  'aiBudget.period.month': 'Month',
  'aiBudget.period.range': 'Date range',
  'aiBudget.period.monthLabel': 'Month',
  'aiBudget.period.from': 'From',
  'aiBudget.period.to': 'To',
  'aiBudget.periodTotal': '{cost} € · {count} AI calls',
  'aiBudget.rangeInvalid': 'The start date must come before the end date.',
  'aiBudget.dailySince':
    'Per-day usage is recorded from {date} on. For earlier periods, pick a month.',
  'aiBudget.dailyNone': 'Per-day usage is recorded from now on; until data accrues, pick a month.',
  'aiBudget.col.model': 'Model',
  'aiBudget.col.provider': 'Provider',
  'aiBudget.col.calls': 'Calls',
  'aiBudget.col.inputTokens': 'Input tokens',
  'aiBudget.col.outputTokens': 'Output tokens',
  'aiBudget.col.cost': 'Cost (EUR)',

  // Settings → Email templates
  'settings.emailTemplates.intro':
    'Customize the emails sent by the application. {{variable}} placeholders are replaced automatically when sending; use Preview to see the result with sample data.',
  'settings.emailTemplates.loading': 'Loading templates…',
  'settings.emailTemplates.loadError': 'Could not load templates',
  'settings.emailTemplates.template': 'Template',
  'settings.emailTemplates.name.overdue_invoice': 'Overdue invoice reminder',
  'settings.emailTemplates.name.welcome': 'Welcome (new account)',
  'settings.emailTemplates.customized': 'Customized',
  'settings.emailTemplates.default': 'Default',
  'settings.emailTemplates.subject': 'Subject',
  'settings.emailTemplates.variables': 'Available variables',
  'settings.emailTemplates.variablesHint':
    'Click a variable to insert it at the cursor position in the email body.',
  'settings.emailTemplates.insertVariable': 'Insert variable',
  'settings.emailTemplates.body': 'Email body (HTML)',
  'settings.emailTemplates.save': 'Save',
  'settings.emailTemplates.saving': 'Saving…',
  'settings.emailTemplates.saved': 'The template has been saved.',
  'settings.emailTemplates.saveError': 'The operation failed',
  'settings.emailTemplates.preview': 'Preview',
  'settings.emailTemplates.previewLoading': 'Generating…',
  'settings.emailTemplates.previewTitle': 'Preview with sample data',
  'settings.emailTemplates.reset': 'Reset to default',
  'settings.bank.intro':
    'Each line imported from a bank statement automatically receives a base expense category: receipts (credits) and payments (debits). You can still override the category for any individual transaction during reconciliation.',
  'settings.bank.loading': 'Loading defaults…',
  'settings.bank.loadError': 'Could not load the defaults',
  'settings.bank.heading': 'Default categories',
  'settings.bank.headingHint':
    'Applied at import if automatic matching did not already pick a more specific category.',
  'settings.bank.credit': 'Credits (receipts)',
  'settings.bank.debit': 'Debits (payments)',
  'settings.bank.save': 'Save defaults',
  'settings.bank.saving': 'Saving…',
  'settings.bank.saved': 'The defaults have been saved.',
  'settings.bank.saveError': 'Save failed',
  'settings.kb.intro':
    'Sync the Knowledge Base (the markdown files read by the AI assistant) with the database. Use this action if the data in the interface (clients, bank reconciliations) does not appear correctly in the assistant responses.',
  'settings.kb.open': 'Open Knowledge Base',
  'settings.kb.heading': 'Regenerate Knowledge Base',
  'settings.kb.headingHint':
    'Full sync: rewrites every database projection (clients, suppliers, subscriptions, leads, financials, bank statements, SmartBill, per-project tasks), reindexes semantic search and mirrors the whole Knowledge Base to Google Drive (when configured). It may take a few minutes.',
  'settings.kb.regenerate': 'Regenerate Knowledge Base',
  'settings.kb.regenerating': 'Regenerating…',
  'settings.kb.success': 'The Knowledge Base was regenerated successfully.',
  'settings.kb.result.clients': '{count} clients',
  'settings.kb.result.suppliers': '{count} suppliers',
  'settings.kb.result.projects': '{count} projects with financial data',
  'settings.kb.result.firmCosts': '{count} company expenses',
  'settings.kb.result.reports': '{count} monthly reports',
  'settings.kb.result.leads': 'leads index regenerated',
  'settings.kb.result.bankMonths': '{count} months with bank statements',
  'settings.kb.result.smartbillYears': '{count} SmartBill years',
  'settings.kb.result.tasks': '{count} projects with tasks',
  'settings.kb.result.reindexed': '{count} chunks reindexed for search',
  'settings.kb.result.driveMirrored': '{count} files mirrored to Google Drive',
  'settings.kb.result.driveOff': 'Google Drive not configured — KB local only',
  'settings.kb.result.aiSummaries': 'AI summaries: {state}',
  'settings.kb.ai.enabled': 'generated',
  'settings.kb.ai.disabled': 'disabled (Anthropic not configured)',
  'settings.kb.error': 'Regeneration failed',
  'settings.integrations.intro':
    'Configure the credentials for integrations. They are stored encrypted in the database — there is no need to edit .env files.',
  'settings.integrations.loading': 'Loading integrations…',
  'settings.integrations.loadError': 'Could not load the integrations',
  'settings.integrations.wipe.button': 'Delete all stored credentials',
  'settings.integrations.wipe.title': 'Delete all stored credentials?',
  'settings.integrations.wipe.message':
    'This deletes the stored credentials for Google Drive, OpenAI, Anthropic, SMTP, Google Calendar, Gmail and SmartBill for every firm. Integrations that also have values in the server .env will keep working from .env. This cannot be undone.',
  'settings.integrations.wipe.confirm': 'Delete everything',
  'settings.uploadLimit.title': 'Maximum file size',
  'settings.uploadLimit.description':
    'The largest single file that can be uploaded to Google Drive: project, client, supplier, person and company resources, task and lead attachments, meeting notes. Larger files are refused before the upload starts.',
  'settings.uploadLimit.label': 'Limit per file',
  'settings.uploadLimit.save': 'Save',
  'settings.uploadLimit.reset': 'Back to the {cap} MB maximum',
  'settings.uploadLimit.range': 'Between {min} and {cap} MB. {cap} MB is the server ceiling.',
  'settings.uploadLimit.saved': 'The limit was saved.',
  'settings.uploadLimit.loadError': 'Could not read the upload limit.',
  'settings.backup.title': 'Application backup',
  'settings.backup.description':
    'Saves a complete copy of the application to Google Drive: the database (compressed SQL dump) and the Knowledge Base (zip archive), in the "_Sistem / Backup-uri DB" folder.',
  'settings.backup.run': 'Back up now',
  'settings.backup.running': 'Backing up…',
  'settings.backup.runningHint':
    'This can take a few minutes depending on the database size. Keep the page open.',
  'settings.backup.driveOff': 'Configure and save Google Drive above to enable backups.',
  'settings.backup.success': 'Backup uploaded to Google Drive in {duration}s.',
  'settings.backup.error': 'Backup failed',
  'settings.backup.database': 'Database',
  'settings.backup.knowledgeBase': 'Knowledge Base',
  'settings.backup.files': '{count} files',
  'settings.backup.recent': 'Latest backups in Drive',
  'settings.backup.none': 'No backups yet.',
  'settings.backup.loadError': 'Couldn’t read the backups from Drive',
  'settings.backup.schedule.daily': 'Automatic backup daily at {time} ({zone})',
  'settings.backup.schedule.next': 'next: {date}',
  'settings.backup.schedule.off': 'Automatic backup is disabled (BACKUP_DAILY_ENABLED).',
  'settings.backup.schedule.lastOk': 'Last automatic backup: {date} — succeeded.',
  'settings.backup.schedule.lastFailed': 'Last automatic backup: {date} — failed: {error}',

  // Time tracking (task worklog)
  'time.title': 'Time worked',
  'time.logTime': 'Log time',
  'time.duration': 'Duration',
  'time.durationPlaceholder': 'e.g. 45m, 1h 30m',
  'time.date': 'Date',
  'time.note': 'Note',
  'time.notePlaceholder': 'What did you work on? (optional)',
  'time.total': 'Total',
  'time.ofEstimate': 'of {estimate} estimated',
  'time.noEstimate': 'no estimate',
  'time.save': 'Save',
  'time.cancel': 'Cancel',
  'time.edit': 'Edit',
  'time.delete': 'Delete',
  'time.empty': 'No time entries.',
  'time.loading': 'Loading…',
  'time.logged': 'Time logged',
  'time.invalidDuration': 'Enter a valid duration (e.g. 45m, 1h 30m).',
  'time.deletedUser': 'Deleted user',

  // Task timer (global bar + the buttons in the "Time worked" section).
  'time.live.running': 'Working now',
  'time.live.paused': 'On pause',
  'timer.start': 'Start timer',
  'timer.pause': 'Pause',
  'timer.resume': 'Resume',
  'timer.stop': 'Stop',
  'timer.startedAt': 'started at {time}',
  'timer.startFailed': 'The timer could not be started.',
  'timer.untitledTask': 'Untitled task',
  'timer.notification.channel': 'Timer',
  'timer.notification.running': 'You started the timer for the task',
  'timer.notification.paused': 'The timer is paused',
  'timer.notification.stopped': 'Time saved for the task',
  'timer.notification.reset': 'Reset',
  'timer.notification.close': 'Close',

  // Operational calendar (sidebar + /calendar). Month/day names come from
  // Intl.DateTimeFormat, not the dictionary.
  'sidebar.calendar': 'Calendar',
  'calendar.title': 'Calendar',
  'calendar.viewCalendar': 'Month',
  'calendar.viewWeek': 'Week',
  'calendar.viewPosts': 'Posts',
  'calendar.filterClient': 'Client',
  'calendar.allClients': 'All clients',
  'calendar.viewGanttProjects': 'Projects Gantt',
  'calendar.viewGanttTeam': 'Team Gantt',
  'calendar.today': 'Today',
  'calendar.prev': 'Previous',
  'calendar.next': 'Next',
  'calendar.more': '+{count}',
  'calendar.noRange': 'No date range',
  'calendar.cappedNotice': 'Showing the first {count} tasks',
  'calendar.allProjects': 'All projects',
  'calendar.allAssignees': 'All assignees',
  'calendar.filterProject': 'Project',
  'calendar.filterAssignee': 'Assignee',
  'calendar.estimatedTotal': 'Estimated total',
  'calendar.remainingTotal': 'Remaining',
  'calendar.weekEmpty': 'No tasks',
  'calendar.closeDay': 'Close day',
  'calendar.moveFailed': 'The task could not be moved.',
  'calendar.noTasks': 'Nothing in this period.',
  'calendar.noAccess': 'You do not have permission to view the calendar.',
  'calendar.loadError': 'Loading the calendar failed.',
  'calendar.googleEvent': 'Google Calendar',
  'calendar.allDay': 'All day',
  'calendarPreview.location': 'Location',

  // Task reports (sidebar + /reports/tasks)
  'sidebar.taskReports': 'Task reports',
  'reports.title': 'Task reports',
  'reports.subtitle': 'Estimated vs. actual, overruns, throughput and team workload.',
  'reports.noAccess': 'You do not have permission to view task reports.',
  'reports.loading': 'Loading…',
  'reports.error': 'Loading the report failed.',
  'reports.filters.period': 'Period',
  'reports.filters.thisMonth': 'This month',
  'reports.filters.lastMonth': 'Last month',
  'reports.filters.last30': 'Last 30 days',
  'reports.filters.thisQuarter': 'This quarter',
  'reports.filters.custom': 'Custom',
  'reports.filters.from': 'From',
  'reports.filters.to': 'To',
  'reports.filters.project': 'Project',
  'reports.filters.person': 'Person',
  'reports.filters.department': 'Department',
  'reports.filters.label': 'Label',
  'reports.filters.all': 'All',
  'reports.filters.reset': 'Reset filters',
  'reports.kpi.spentHours': 'Hours worked',
  'reports.kpi.estimatedHours': 'Hours estimated',
  'reports.kpi.estimateAccuracy': 'Estimate accuracy',
  'reports.kpi.created': 'Tasks created',
  'reports.kpi.completed': 'Tasks completed',
  'reports.kpi.onTimeRate': 'Completed on time',
  'reports.kpi.overruns': 'Overrun tasks',
  'reports.kpi.activePeople': 'Active people',
  'reports.hours.title': 'Estimated vs. actual',
  'reports.hours.byProject': 'By project',
  'reports.hours.byPerson': 'By person',
  'reports.hours.byTask': 'By task',
  'reports.hours.task': 'Task',
  'reports.hours.project': 'Project',
  'reports.hours.person': 'Person',
  'reports.hours.status': 'Status',
  'reports.hours.estimated': 'Estimated (h)',
  'reports.hours.spent': 'Worked (h)',
  'reports.hours.delta': 'Delta (h)',
  'reports.hours.pct': '% of estimate',
  'reports.hours.completedAt': 'Completed at',
  'reports.hours.export': 'Export Excel',
  'reports.hours.empty': 'No tasks in the selected period.',
  'reports.overruns.title': 'Estimate overruns',
  'reports.overruns.byPerson': 'Total overruns per person',
  'reports.overruns.taskCount': 'Tasks',
  'reports.overruns.totalHours': 'Hours over estimate',
  'reports.overruns.unassigned': 'Unassigned',
  'reports.overruns.empty': 'No overruns in the selected period.',
  'reports.throughput.title': 'Created vs. completed',
  'reports.throughput.weekly': 'Weekly',
  'reports.throughput.monthly': 'Monthly',
  'reports.throughput.created': 'Created',
  'reports.throughput.completed': 'Completed',
  'reports.cycle.title': 'Cycle & lead time',
  'reports.cycle.avgCycle': 'Average cycle time',
  'reports.cycle.medianCycle': 'Median cycle time',
  'reports.cycle.avgLead': 'Average lead time',
  'reports.cycle.medianLead': 'Median lead time',
  'reports.cycle.days': '{value} days',
  'reports.cycle.count': 'Tasks completed',
  'reports.cycle.byProject': 'By project',
  'reports.cycle.empty': 'No tasks completed in the selected period.',
  'reports.workload.title': 'Workload vs. capacity',
  'reports.workload.openTasks': 'Open tasks',
  'reports.workload.estimated': 'Open estimated hours',
  'reports.workload.remaining': 'Remaining hours',
  'reports.workload.capacity': 'Capacity (h/week)',
  'reports.workload.utilization': 'Utilization',
  'reports.workload.empty': 'No active people with open tasks.',
  'reports.overdue.title': 'Overdue tasks',
  'reports.overdue.d1_7': '1–7 days',
  'reports.overdue.d7_30': '8–30 days',
  'reports.overdue.d30plus': '30+ days',
  'reports.overdue.daysOverdue': 'Days overdue',
  'reports.overdue.dueDate': 'Due date',
  'reports.overdue.byPerson': 'By person',
  'reports.overdue.byProject': 'By project',
  'reports.overdue.empty': 'No overdue tasks.',

  // Mobile filter bottom sheet (Linear-style)
  'mobileFilters.trigger': 'Filter',
  'mobileFilters.title': 'Filters',
  'mobileFilters.searchAll': 'Search all filters',
  'mobileFilters.applied': 'Applied filters',
  'mobileFilters.all': 'All filters',
  'mobileFilters.is': 'is',
  'mobileFilters.dueDate': 'Due date',
  'mobileFilters.rangeFrom': 'From',
  'mobileFilters.rangeTo': 'Until',
  'mobileFilters.done': 'Done',
  'mobileFilters.back': 'Back',
  'mobileFilters.remove': 'Remove filter',
  'mobileFilters.appliedCount': '{count} filters applied',
};

export const translations: Record<Locale, Dict> = { ro, en };

/** All known translation keys (RO is the source of truth for the key set). */
export type TranslationKey = keyof typeof ro;

/** Values that can be interpolated into a translation placeholder. */
export type TranslateParams = Record<string, string | number>;

/**
 * Translate a key for the active (or given) locale.
 * Fallback chain: requested locale → RO → raw key.
 *
 * Supports simple `{name}` placeholder interpolation when `params` is given,
 * e.g. translate('leads.pagination', 'en', { page: 1, totalPages: 3, total: 25 }).
 */
export function translate(key: string, loc: Locale = locale, params?: TranslateParams): string {
  const raw = translations[loc][key] ?? translations.ro[key] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}
